import type { BookPageTranslations } from "./book-page-i18n";
import {
  bookPageTranslationsDe,
  bookPageTranslationsEn,
  bookPageTranslationsFr,
} from "./book-page-i18n";

export type Language = 'en' | 'de' | 'fr' | 'zh-CN' | 'ko';

export interface Translations {
  // Common
  common: {
    share: string;
  };

  // Banner
  banner: {
    unitOf: string;
  };

  // Navigation
  nav: {
    home: string;
    rooms: string;
    wellness: string;
    dining: string;
    experiences: string;
    events: string;
    about: string;
    bookNow: string;
    activities: string;
    journal: string;
    language: string;
    /** Book bar: display-only price currency (Mews browse amounts). */
    displayCurrency: string;
  };

  // Hero Section
  hero: {
    title: string;
    subtitle: string;
    description: string;
    bookStay: string;
    swissHeritageHotel: string;
    acreResort: string;
    minsFromInterlaken: string;
    stunningAlpineViews: string;
    since1881: string;
    beatenbergsOnly: string;
    fourStarGastrosuisse: string;
    ratedResort: string;
    locationButton: string;
  };

  // Features
  features: {
    alpineSanctuary: string;
    alpineDescription: string;
    alpineDescription2: string;
    exclusiveRates: string;
    guestCardBenefits: string;
    beatenbergAccess: string;
    interlakenPrivileges: string;
    habkernDiscounts: string;
    freeShuttle: string;
    bookYourStay: string;
    integratedWellness: string;
    integratedWellnessDesc: string;
    guestCardBenefitsDesc: string;
    gastrosuisseExcellence: string;
    gastrosuisseExcellenceDesc: string;
    swisstainableCommitment: string;
    swisstainableCommitmentDesc: string;
    heritageCuisine: string;
    heritageCuisineDesc: string;
    acreResort: string;
    acreResortDesc: string;
    authenticHeritage: string;
    authenticHeritageDesc: string;
    alpineExperiences: string;
    alpineExperiencesDesc: string;
    dragToExplore: string;
    elegantBarLounge: string;
    elegantBarLoungeDesc: string;
    recreationGames: string;
    recreationGamesDesc: string;
    technogymFitness: string;
    technogymFitnessDesc: string;
    businessFacilities: string;
    businessFacilitiesDesc: string;
    alpineBistro: string;
    alpineBistroDesc: string;
    panoramicViews: string;
    panoramicViewsDesc: string;
  };

  // Rooms Section
  rooms: {
    title: string;
    description: string;
    exploreRooms: string;
    familySuite: string;
    juniorSuite: string;
    tripleRoom: string;
    premiumDouble: string;
    standardSingle: string;
    alpineViews: string;
    pineWoodInteriors: string;
    organicCottonSheets: string;
    privateBalconies: string;
    seniorFriendly: string;
    exploreRoom: string;
    viewRoomFeatures: string;
    familySuiteDesc: string;
    juniorSuiteDesc: string;
    tripleRoomDesc: string;
    premiumDoubleDesc: string;
    standardSingleDesc: string;
    alpineViewsDesc: string;
    familySuiteFeatures: {
      masterBedroom: string;
      additionalBedrooms: string;
      hallLivingRoom: string;
      privateBalcony: string;
    };
    familySuiteBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    familySuiteDescription: {
      mobile: string;
      desktop: string;
    };
    juniorSuiteFeatures: {
      loungeSeating: string;
      privateBalconyWithViews: string;
      spaceForAdditionalBeds: string;
    };
    juniorSuiteBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    juniorSuiteDescription: {
      mobile: string;
      desktop: string;
    };
    tripleRoomFeatures: {
      perfectForSmallFamilies: string;
      scenicBalconyWithViews: string;
    };
    tripleRoomBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    tripleRoomDescription: {
      mobile: string;
      desktop: string;
    };
    doubleRoomFeatures: {
      perfectForCouples: string;
      balconyWithViews: string;
    };
    doubleRoomBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    doubleRoomDescription: {
      mobile: string;
      desktop: string;
    };
    alpineDoubleTwin: string;
    alpineDoubleTwinFeatures: {
      flexibleBedding: string;
      privateBalconyWithViews: string;
      perfectForCouplesOrFriends: string;
    };
    alpineDoubleTwinBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    alpineDoubleTwinDescription: {
      mobile: string;
      desktop: string;
    };
    alpineClassicDoubleTwin: string;
    alpineClassicDoubleTwinFeatures: {
      flexibleBedding: string;
      villageViews: string;
    };
    alpineClassicDoubleTwinBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    alpineClassicDoubleTwinDescription: {
      mobile: string;
      desktop: string;
    };
    jungfrauSingle: string;
    jungfrauSingleBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    jungfrauSingleDescription: {
      mobile: string;
      desktop: string;
    };
    jungfrauSingleFeatures: {
      balconyWithLakeView: string;
      perfectForSoloTravelers: string;
    };
    heritageSingle: string;
    heritageSingleBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    heritageSingleDescription: {
      mobile: string;
      desktop: string;
    };
    heritageSingleFeatures: {
      villageViews: string;
      classicCharm: string;
    };
    singleRoomFeatures: {
      perfectForSoloTravelers: string;
      balconyWithViews: string;
    };
    singleRoomBadges: {
      occupancy: string;
      roomSize: string;
      bedding: string;
      bathrooms: string;
    };
    singleRoomDescription: {
      mobile: string;
      desktop: string;
    };
  };

  // Restaurant Section
  restaurant: {
    title: string;
    description: string;
    exploreRestaurant: string;

    // Hero Section
    hero: {
      logoAlt: string;
    };

    renovationNotice: {
      eyebrow: string;
      title: string;
      description: string;
      completion: string;
      ctaPrimary: string;
      ctaSecondary: string;
    };

    // About Section
    about: {
      backgroundText: string;
      tagline: string;
      title: string;
      description: string;
      phoneButton: string;
    };

    // Features Section
    features: {
      alpineViews: {
        title: string;
        description: string;
      };
      localSourcing: {
        title: string;
        description: string;
      };
      swissHeritage: {
        title: string;
        description: string;
      };
    };

    // Menu Section
    menu: {
      tagline: string;
      title: string;
      description: string;
      subtitle: string;
      tabs: {
        mainCourses: string;
        fiveCourseDinner: string;
        sides: string;
        beverages: string;
      };
      viewFullMenu: string;
      dinnerHours: string;
      reservations: string;
      pricesInclusive: string;
    };

    // Menu Items
    menuItems: {
      fiveCourseMenu: {
        name: string;
        description: string;
      };
      healthySwissSalad: {
        name: string;
        description: string;
      };
      vegetableSoup: {
        name: string;
        description: string;
      };
      jungfrauClassic: {
        name: string;
        description: string;
      };
      thunerseeDelight: {
        name: string;
        description: string;
      };
      alpineHutRosti: {
        name: string;
        description: string;
      };
      eigerPeakPinsa: {
        name: string;
        description: string;
      };
      asianCurry: {
        name: string;
        description: string;
      };
      frenchFries: {
        name: string;
        description: string;
      };
      sweetPotatoFries: {
        name: string;
        description: string;
      };
      falafel: {
        name: string;
        description: string;
      };
      potatoRostiFries: {
        name: string;
        description: string;
      };
      smokedSalmonSandwich: {
        name: string;
        description: string;
      };
      eggCheeseSandwich: {
        name: string;
        description: string;
      };
      healthySaladWithEgg: {
        name: string;
        description: string;
      };
      healthySalad: {
        name: string;
        description: string;
      };
      chickenNuggets: {
        name: string;
        description: string;
      };
      miniCroissant: {
        name: string;
        description: string;
      };
      clavienCornalin: {
        name: string;
        description: string;
      };
      yvorneRouge: {
        name: string;
        description: string;
      };
      yvorneBlanc: {
        name: string;
        description: string;
      };
      rugenbrauAmber: {
        name: string;
        description: string;
      };
      rugenbrauSpezial: {
        name: string;
        description: string;
      };
      rivella: {
        name: string;
        description: string;
      };
      iceTea: {
        name: string;
        description: string;
      };
      apfelschorle: {
        name: string;
        description: string;
      };
    };

    // Featured Items
    featuredItems: {
      tagline: string;
      title: string;
    };

    // Dining Venues
    diningVenues: {
      tagline: string;
      title: string;
      description: string;
      mainRestaurant: {
        title: string;
        subtitle: string;
        description: string;
        features: string[];
        perfectFor: string;
      };
      terraceDining: {
        title: string;
        subtitle: string;
        description: string;
        features: string[];
        perfectFor: string;
      };
      outdoorTable: {
        title: string;
        subtitle: string;
        description: string;
        features: string[];
        perfectFor: string;
      };
      alpineBreakfast: {
        title: string;
        subtitle: string;
        description: string;
        features: string[];
        perfectFor: string;
      };
    };

    // Scrolling Text
    scrollingText: {
      swissHeritage: string;
      alpineGastronomy: string;
      farmToTable: string;
      fiveCourseDinner: string;
      swissWines: string;
      localBeverages: string;
      alpineSides: string;
      freshSalads: string;
      gastrosuisseCertified: string;
    };

    // Testimonials
    testimonials: {
      // Testimonial content is kept in original language as they are real customer reviews
    };

    // Gallery
    gallery: {
      eyebrow: string;
      title: string;
      subtitle: string;
    };

    // GASTROSUISSE Certification
    gastrosuisse: {
      tagline: string;
      title: string;
      description: string;
      certificationBenefits: {
        title: string;
        benefits: string[];
      };
      qualityStandards: {
        title: string;
        standards: string[];
      };
      additionalAccreditations: {
        title: string;
        accreditations: string[];
      };
    };

    // Contact Section
    contact: {
      tagline: string;
      title: string;
      subtitle: string;
      visitLocation: {
        title: string;
        address: string[];
      };
      callUs: {
        title: string;
        details: string[];
      };
      email: {
        title: string;
        details: string[];
      };
    };

    // Christmas & New Year Dinner Registration Form
    christmasNewYearForm: {
      title: string;
      intro: string;
      pricingInfo: string;
      minimumParty: string;
      festiveMenusTitle: string;
      christmasMenuLabel: string;
      newYearMenuLabel: string;
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      mobileLabel: string;
      mobilePlaceholder: string;
      countLabel: string;
      countPlaceholder: string;
      eventLabel: string;
      eventChristmas: string;
      eventNewYear: string;
      timeSlotLabel: string;
      timeSlot1: string;
      timeSlot2: string;
      submitButton: string;
      submittingButton: string;
      successMessage: string;
      errorMessage: string;
      validationRequired: string;
      validationEmail: string;
      validationPhone: string;
      validationCount: string;
      honeypotLabel: string;
      modalTitle: string;
      modalSubtitle: string;
      modalNameLabel: string;
      modalEventLabel: string;
      modalTimeSlotLabel: string;
      modalGuestsLabel: string;
      modalCloseButton: string;
    };
  };

  // Spa Section
  spa: {
    serviceList: {
      addToList: string;
      addedToList: string;
      title: string;
      empty: string;
      details: string;
      name: string;
      email: string;
      phone: string;
      date: string;
      time: string;
      morning: string;
      afternoon: string;
      evening: string;
      subtotal: string;
      sendInquiry: string;
      successTitle: string;
      successMessage: string;
      errorTitle: string;
      errorMessage: string;
      closeList: string;
      tryAgain: string;
      browseServices: string;
      selections: string;
      catalog: string;
      selectDate: string;
      selectTime: string;
      preferredDateCET: string;
      requestAppointment: string;
      reviewSelections: string;
      yourRelaxationAwaits: string;
      selectTreatmentsMessage: string;
      viewSpaMenu: string;
      totalEstimated: string;
      thankYouReceived: string;
      requestedTimesNote: string;
    };
    title: string;
    description: string;
    exploreSpa: string;
    holisticWellnessTitle: string;
    holisticWellnessDesc: string;
    medicalSpaTitle: string;
    medicalSpaDesc: string;
    detoxTitle: string;
    detoxDesc: string;
    relaxationBalconyTitle: string;
    relaxationBalconyDesc: string;
    therapeuticMassageTitle: string;
    therapeuticMassageDesc: string;

    // Hero Section
    hero: {
      mainTitle: string;
      subMessage: string;
      ctaButton: string;
    };

    // Nature and Science Section
    natureScience: {
      title: string;
      subtitle: string;
      images: {
        massage: string;
        therapist: string;
        treatment: string;
        wellness: string;
        alpine: string;
        relaxation: string;
      };
      blocks: {
        air: {
          title: string;
          description: string;
        };
        water: {
          title: string;
          description: string;
        };
        science: {
          title: string;
          description: string;
        };
      };
    };

    renovationNotice: {
      eyebrow: string;
      title: string;
      description: string;
      completion: string;
      ctaPrimary: string;
      ctaSecondary: string;
      imageAlt: string;
    };

    // Introduction Section
    introduction: {
      title: string;
      description: string;
      perfectEndTitle: string;
      perfectEndDesc: string;
      mountainViews: string;
      alpineBotanicals: string;
      swissHospitality: string;
      nutraSwissScience: string;
      perfectForYouTitle: string;
      convenientLocation: string;
      flexibleScheduling: string;
      multilingualStaff: string;
      complimentaryConsultation: string;
    };

    // Power of Touch Section
    powerOfTouch: {
      category: string;
      title: string;
      description: string;
      ctaButton: string;
    };

    // Beauty Section
    beauty: {
      category: string;
      title: string;
      subtitle: string;
      ctaButton: string;
    };

    // Quick Relaxation Section
    quickRelaxation: {
      category: string;
      title: string;
      description: string;
      ctaButton: string;
      modal: {
        title: string;
        description: string;
        closeButton: string;
        options: {
          expressMassage: {
            title: string;
            description: string;
          };
          footTreatment: {
            title: string;
            description: string;
          };
          meditation: {
            title: string;
            description: string;
          };
          teaRitual: {
            title: string;
            description: string;
          };
        };
        benefits: {
          title: string;
          flexibleScheduling: {
            title: string;
            description: string;
          };
          alpineExperience: {
            title: string;
            description: string;
          };
          instantRelief: {
            title: string;
            description: string;
          };
          perfectForTourists: {
            title: string;
            description: string;
          };
        };
        booking: {
          title: string;
          description: string;
          button: string;
        };
      };
    };

    // Treatments & Services Section
    treatmentsAndServices: {
      title: string;
      introduction: string;
      learnMore: string;
      bookNow: string;
      therapeuticMassages: {
        title: string;
        fullBody: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        relaxing: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        sport: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        footReflexology: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        footBack: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        backNeck: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        peeling: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
        energetic: {
          name: string;
          pricing: string;
          duration: string;
          description: string;
        };
      };
      saunaThermal: {
        title: string;
        package: {
          title: string;
          price: string;
          description: string;
          hours: string;
        };
        finnish: {
          name: string;
          temperature: string;
          description: string;
        };
        bio: {
          name: string;
          temperature: string;
          description: string;
        };
        steam: {
          name: string;
          temperature: string;
          description: string;
        };
        icePlunge: {
          name: string;
          description: string;
        };
      };
      specialPrograms: {
        title: string;
        pelvipower: {
          name: string;
          price: string;
          type: string;
          description: string;
          note: string;
          benefitsTitle: string;
          benefits: {
            women: {
              title: string;
              desc: string;
            };
            men: {
              title: string;
              desc: string;
            };
            incontinence: {
              title: string;
              desc: string;
            };
            back: {
              title: string;
              desc: string;
            };
            mothers: {
              title: string;
              desc: string;
            };
            sports: {
              title: string;
              desc: string;
            };
          };
        };
      };
    };

    // Booking Section
    booking: {
      title: string;
      description: string;
      contactInfoTitle: string;
      address: string;
      reservations: string;
      email: string;
      accessibility: string;
      guestBenefitsTitle: string;
      complimentaryConsultation: string;
      detoxTea: string;
      winterGarden: string;
      guestCardBenefits: string;
      advanceReservations: string;
      packageDeals: string;
      groupPrograms: string;
      bookNowButton: string;
    };

    // Alpine Air Therapy Modal
    alpineAirTherapy: {
      title: string;
      description: string;
      relaxationAreas: string;
      coreFacilities: string;
      uniqueFeatures: string;
      benefitsTitle: string;
      pureMountainAir: string;
      pureMountainAirDesc: string;
      seasonalTherapy: string;
      seasonalTherapyDesc: string;
      readyToExperience: string;
      contactConciergeDesc: string;
      contactConciergeButton: string;
      facilities: {
        panoramicLounge: string;
        alpineGarden: string;
        swissSauna: string;
        steamRoom: string;
        infraredSauna: string;
        meditationCorner: string;
        bioSpa: string;
        fitnessCenter: string;
        yogaPavilion: string;
        wellnessTerrace: string;
        hydrotherapyPools: string;
        massageRooms: string;
        winterGarden: string;
        mountainSpringWater: string;
        alpineBotanicals: string;
        swissDesign: string;
        peptideTherapies: string;
        healingPractices: string;
        panoramicViews: string;
      };
    };

    // Beauty & Wellness Programs Modal
    beautyWellnessPrograms: {
      title: string;
      description: string;
      readyToTransform: string;
      contactConciergeDesc: string;
      contactConciergeButton: string;
      categories: {
        detoxRenewal: string;
        detoxRenewalDesc: string;
        aestheticEnhancement: string;
        aestheticEnhancementDesc: string;
        personalizedWellness: string;
        personalizedWellnessDesc: string;
      };
      programs: {
        alpineGlow: {
          title: string;
          duration: string;
          description: string;
        };
        renewalRadiance: {
          title: string;
          duration: string;
          description: string;
        };
        nutraVitality: {
          title: string;
          duration: string;
          description: string;
        };
        nutraCleanse: {
          title: string;
          duration: string;
          description: string;
        };
        nutraBalance: {
          title: string;
          duration: string;
          description: string;
        };
        nutraRadiance: {
          title: string;
          duration: string;
          description: string;
        };
        nutraGenomic: {
          title: string;
          duration: string;
          description: string;
        };
        nutraTotal: {
          title: string;
          duration: string;
          description: string;
        };
        nutraDerm: {
          title: string;
          duration: string;
          description: string;
        };
        nutraFlex: {
          title: string;
          duration: string;
          description: string;
        };
      };
    };

    // Therapeutic Treatments Modal
    therapeuticTreatments: {
      title: string;
      description: string;
      readyToBook: string;
      contactConciergeDesc: string;
      contactConciergeButton: string;
      relaxationRejuvenation: {
        title: string;
        treatments: {
          alpineMountainMassage: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          lakeThunAromatherapy: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          swissStoneTherapy: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          alpineHerbalSteamBath: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
        };
      };
      advancedWellness: {
        title: string;
        treatments: {
          bioScan: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          nutritionCoaching: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          lightTherapy: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          pelviPowerTraining: {
            title: string;
            price: string;
            duration: string;
            description: string;
          };
          infraredSauna: {
            title: string;
            price: string;
            description: string;
          };
        };
      };
    };
  };

  // Gym & Pool
  gymPool: {
    technogymTitle: string;
    technogymDesc: string;
    premiumEquipment: string;
    premiumEquipmentDesc: string;
    personalTraining: string;
    personalTrainingDesc: string;
    access24: string;
    access24Desc: string;
    alpinePool: string;
    alpinePoolDesc: string;
    heatedPool: string;
    heatedPoolDesc: string;
    alpineViews: string;
    alpineViewsDesc: string;
    familyFriendly: string;
    familyFriendlyDesc: string;
    availableToGuests: string;
    bookYourStay: string;
  };

  // Cinema Theatre
  cinema: {
    title: string;
    subtitle: string;
    seatingCapacity: string;
    seatingCapacityDesc: string;
    corporateEvents: string;
    corporateEventsDesc: string;
    exclusiveExperience: string;
    exclusiveExperienceDesc: string;
    bookTheatre: string;
    bookYourStay: string;
    locatedAt: string;
  };

  // Activity Arcade

  // Events
  events: {
    title: string;
    description: string;
    seminarRooms: string;
    seminarRoomsDesc: string;
    hdProjectors: string;
    videoConferencing: string;
    wirelessPresentation: string;
    breakoutSpaces: string;
    learnMore: string;
    banquetHalls: string;
    banquetHallsDesc: string;
    fineDiningSetup: string;
    professionalCatering: string;
    flexibleSeating: string;
    seating200: string;
    weddingVenues: string;
    weddingVenuesDesc: string;
    alpineViews: string;
    weddingPlanning: string;
    photographySpots: string;
    receptionAreas: string;
    premiumFeatures: string;
    digitalLedWall: string;
    highSpeedInternet: string;
    audioSystems: string;
    eventPlanning: string;
    perfectForOccasion: string;
    perfectForOccasionDesc: string;
    privateParties: string;
    privatePartiesDesc: string;
    groups: string;
    groupsDesc: string;
    wellnessRetreats: string;
    wellnessRetreatsDesc: string;
    corporateEvents: string;
    corporateEventsDesc: string;
    convenientParking: string;
    convenientParkingDesc: string;
    planYourEvent: string;
    requestQuote: string;
    professionalPlanning: string;
    // New comprehensive events page translations
    heroTitle: string;
    heroSubtitle: string;
    heroDescription: string;
    planWedding: string;
    corporateEventsButton: string;
    customVenueOffers: string;
    dreamWeddings: string;
    dreamWeddingsDescription: string;
    luxuryCeremonies: string;
    floralDesign: string;
    themeDesign: string;
    entertainment: string;
    discussWeddingPackages: string;
    corporateExcellence: string;
    corporateExcellenceDescription: string;
    conferences: string;
    productLaunches: string;
    teamBuilding: string;
    annualGalas: string;
    discussCorporatePackages: string;
    privatePartiesTitle: string;
    privatePartiesDescription: string;
    birthdayCelebrations: string;
    anniversaries: string;
    luxuryGalas: string;
    discussPrivatePartyPackages: string;
    completeEventExperiences: string;
    completeEventExperiencesDescription: string;
    weddingPlanningService: string;
    weddingPlanningDescription: string;
    weddingPlanningFeatures: string;
    weddingPlanningDetails: string;
    weddingPlanningHighlights: string;
    corporateEventsService: string;
    corporateEventsServiceDescription: string;
    corporateEventsServiceFeatures: string;
    corporateEventsServiceDetails: string;
    corporateEventsServiceHighlights: string;
    privateCelebrations: string;
    privateCelebrationsDescription: string;
    privateCelebrationsFeatures: string;
    privateCelebrationsDetails: string;
    privateCelebrationsHighlights: string;
    serviceOverview: string;
    keyFeatures: string;
    whatWeProvide: string;
    getStartedWith: string;
    premiumEventFeatures: string;
    fullServiceEventPlanning: string;
    fullServiceEventPlanningDesc: string;
    gourmetCateringServices: string;
    gourmetCateringServicesDesc: string;
    stateOfTheArtTechnology: string;
    stateOfTheArtTechnologyDesc: string;
    wellnessIntegration: string;
    wellnessIntegrationDesc: string;
    digitalLedWallFeature: string;
    digitalLedWallFeatureDesc: string;
    audioSystemsFeature: string;
    audioSystemsFeatureDesc: string;
    eventPlanningFeature: string;
    eventPlanningFeatureDesc: string;
    premiumAmenities: string;
    premiumAmenitiesDesc: string;
    convenientParkingTitle: string;
    convenientParkingCapacity: string;
    complimentaryParking: string;
    ourPortfolio: string;
    eventGallery: string;
    eventGallerySubtitle: string;
    whatOurClientsSay: string;
    whatOurClientsSayDescription: string;
    readyToCreate: string;
    readyToCreateDescription: string;
    planningTimeline: string;
    planningTimelineDesc: string;
    eventCapacity: string;
    eventCapacityDesc: string;
    documentation: string;
    documentationDesc: string;
    entertainmentFeature: string;
    entertainmentFeatureDesc: string;
    // Testimonials
    testimonial1Name: string;
    testimonial1Event: string;
    testimonial1Text: string;
    testimonial2Name: string;
    testimonial2Event: string;
    testimonial2Text: string;
    testimonial3Name: string;
    testimonial3Event: string;
    testimonial3Text: string;
  };

  // Local Attractions
  attractions: {
    title: string;
    description: string;
    niederhorn: {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "st-beatus-caves": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "harder-kulm": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "jungfraujoch": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "thun-castle": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "spiez-castle": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "schynige-platte": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "trummelebach-falls": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "giessbach-falls": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "lake-thun-brienz": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "adventure-park-interlaken": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "paragliding": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "cross-country-skiing": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "beatenberg-panorama-trail": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
    "saxeten-village": {
      name: string;
      description?: string;
      shortDescription: string;
      features?: string[];
      guestBenefits?: string[];
      distance?: string;
      duration?: string;
    };
  };

  // Guest Benefits
  guestBenefits: {
    freeTransport: {
      title: string;
      description: string;
    };
    libraryAccess: {
      title: string;
      description: string;
    };
    eBikeDiscount: {
      title: string;
      description: string;
    };
    miniGolfDiscount: {
      title: string;
      description: string;
    };
    schynigePlatteDiscount: {
      title: string;
      description: string;
    };
    niesenbahnDiscount: {
      title: string;
      description: string;
    };
    harderKulmDiscount: {
      title: string;
      description: string;
    };
    escapeRoomDiscount: {
      title: string;
      description: string;
    };
    niederhornDiscount: {
      title: string;
      description: string;
    };
    swimmingPoolAccess: {
      title: string;
      description: string;
    };
    busAlpinService: {
      title: string;
      description: string;
    };
    gameBorrowing: {
      title: string;
      description: string;
    };
  };

  // Local Attractions Page
  localAttractions: {
    hero: {
      title: string;
      subtitle: string;
      bookStay: string;
    };
    quickAccess: {
      title: string;
      description: string;
      allAttractions: string;
      within30min: string;
      natureHiking: string;
      cultureHistory: string;
      activities: string;
      allAttractionsDesc: string;
      within30minDesc: string;
      natureHikingDesc: string;
      cultureHistoryDesc: string;
      activitiesDesc: string;
      learnMore: string;
      noAttractionsFound: string;
    };
    seasonal: {
      title: string;
      description: string;
      summer: string;
      winter: string;
      yearRound: string;
      alpineHiking: string;
      alpineHikingDesc: string;
      alpineHikingActivities: string[];
      skiSnowboard: string;
      skiSnowboardDesc: string;
      skiSnowboardActivities: string[];
      lakeWaterSports: string;
      lakeWaterSportsDesc: string;
      lakeWaterSportsActivities: string[];
      photography: string;
      photographyDesc: string;
      photographyActivities: string[];
      culinary: string;
      culinaryDesc: string;
      culinaryActivities: string[];
      mountainAdventures: string;
      mountainAdventuresDesc: string;
      mountainAdventuresActivities: string[];
      activitiesInclude: string;
      bookYourStay: string;
    };
    guestBenefits: {
      title: string;
      description: string;
      freeBenefits: string;
      discountBenefits: string;
      howToGet: string;
      howToGetDesc: string;
      validDuringStay: string;
      partner: string;
      benefits: {
        freeTransport: {
          title: string;
          description: string;
        };
        libraryAccess: {
          title: string;
          description: string;
        };
        eBikeDiscount: {
          title: string;
          description: string;
        };
        miniGolfDiscount: {
          title: string;
          description: string;
        };
        schynigePlatteDiscount: {
          title: string;
          description: string;
        };
        niesenbahnDiscount: {
          title: string;
          description: string;
        };
        harderKulmDiscount: {
          title: string;
          description: string;
        };
        escapeRoomDiscount: {
          title: string;
          description: string;
        };
        niederhornDiscount: {
          title: string;
          description: string;
        };
        swimmingPoolAccess: {
          title: string;
          description: string;
        };
        busAlpinService: {
          title: string;
          description: string;
        };
        gameBorrowing: {
          title: string;
          description: string;
        };
      };
    };
    transportation: {
      title: string;
      description: string;
      publicTransport: string;
      keyRoutes: string;
      destination: string;
      method: string;
      duration: string;
      frequency: string;
      freeWithGuestCard: string;
      freeWithGuestCardDesc: string;
      hotelShuttles: string;
      bookingInfo: string;
      bookingInfoDesc: string;
      concierge: string;
      drivingDirections: string;
      fromZurich: string;
      fromGeneva: string;
      parkingAtHotel: string;
      parkingAtHotelDesc: string;
      accessibility: string;
      wheelchairAccessible: string;
      mobilityServices: string;
      contactConcierge: string;
      contactConciergeDesc: string;
      accessibilityAttractions: string[];
      mobilityServicesList: string[];
      drivingDirectionsZurich: string[];
      drivingDirectionsGeneva: string[];
      frequencies: {
        every30min: string;
        everyHour: string;
        every2Hours: string;
        asNeeded: string;
        onRequest: string;
        onDemand: string;
        scheduled: string;
        daily24_7: string;
      };
      shuttleServices: {
        skiShuttle: {
          service: string;
          destinations: string;
          schedule: string;
        };
        airportTransfer: {
          service: string;
          destinations: string;
          schedule: string;
        };
        cityTransfer: {
          service: string;
          destinations: string;
          schedule: string;
        };
        attractionShuttle: {
          service: string;
          destinations: string;
          schedule: string;
        };
      };
    };
    cta: {
      title: string;
      description: string;
      bookYourStay: string;
      speakToExpert: string;
      bookYourStayDesc: string;
      speakToExpertDesc: string;
      questions: string;
    };
  };

  // Moments of Joy
  moments: {
    title: string;
    description: string;
    showNewMoments: string;
  };

  // CTA Section
  cta: {
    title: string;
    description: string;
    bookYourStay: string;
    contactConcierge: string;
    happyGuests: string;
    trustedByGuests: string;
    certifiedExcellence: string;
    certifiedExcellenceDesc: string;
    gastrosuisseRated: string;
    swissHotelAssociation: string;
    swisstainableCommitted: string;
    qualityAssurance: string;
    startReservation: string;
    contactTeam: string;
  };

  // Ratings Display
  ratings: {
    hotel: {
      reviews: string;
    };
    restaurant: {
      reviews: string;
    };
  };

  // Contact Modals
  contactModals: {
    spa: {
      title: string;
      subtitle: string;
      phoneTitle: string;
      phoneDesc: string;
      emailTitle: string;
      emailDesc: string;
      locationTitle: string;
      locationDesc: string;
      bookingTitle: string;
      bookingDesc: string;
      whatsappTitle: string;
      whatsappDesc: string;
      servicesTitle: string;
      servicesList: string[];
      bookButton: string;
      closeButton: string;
    };
    theatre: {
      title: string;
      subtitle: string;
      phoneTitle: string;
      phoneDesc: string;
      emailTitle: string;
      emailDesc: string;
      bookingTitle: string;
      bookingDesc: string;
      whatsappTitle: string;
      whatsappDesc: string;
      servicesTitle: string;
      servicesList: string[];
      featuresTitle: string;
      features: {
        capacity: string;
        screen: string;
        sound: string;
        seating: string;
        lighting: string;
        climate: string;
      };
      bookButton: string;
      closeButton: string;
    };
    concierge: {
      title: string;
      subtitle: string;
      phoneTitle: string;
      phoneDesc: string;
      emailTitle: string;
      emailDesc: string;
      locationTitle: string;
      locationDesc: string;
      whatsappTitle: string;
      whatsappDesc: string;
      servicesTitle: string;
      servicesList: string[];
      bookButton: string;
      closeButton: string;
    };
  };

  // Testimonials
  testimonials: {
    title: string;
    description: string;
    testimonial1: {
      quote: string;
      name: string;
      country: string;
    };
    testimonial2: {
      quote: string;
      name: string;
      country: string;
    };
    testimonial3: {
      quote: string;
      name: string;
      country: string;
    };
    testimonial4: {
      quote: string;
      name: string;
      country: string;
    };
  };

  // Floating Features
  floatingFeatures: {
    freeWifi: {
      title: string;
      description: string;
    };
    flatScreenTv: {
      title: string;
      description: string;
    };
    inRoomSafe: {
      title: string;
      description: string;
    };
    alpineViews: {
      title: string;
      description: string;
    };
    ensuiteBathroom: {
      title: string;
      description: string;
    };
    luxuryBedding: {
      title: string;
      description: string;
    };
    rusticPineInteriors: {
      title: string;
      description: string;
    };
    seasonalTemperatures: {
      title: string;
      description: string;
    };
    ensuiteEspressoMachine: {
      title: string;
      description: string;
    };
  };

  // Footer
  footer: {
    contact: string;
    location: string;
    quickLinks: string;
    organization: string;
    allRightsReserved: string;
    hotelDescription: string;
    roomsSuites: string;
    wellnessSpa: string;
    dining: string;
    experiences: string;
    eventsWeddings: string;
    aboutUs: string;
    contactLink: string;
    privacyPolicy: string;
    termsOfService: string;
    cookies: string;
    hotelRegistration: string;
    nutraSwissDescription: string;
    cliniqueNutraSwissDescription: string;
    nahrinDescription: string;
    laMaisonPondicherryDescription: string;
    stBeatusCollegeDescription: string;
    restaurantJungfrauDescription: string;
  };

  // 404 page
  notFound: {
    title: string;
    description: string;
    ctaHome: string;
    ctaSpa: string;
  };

  // Activities Section
  activities: {
    hero: {
      title: string;
      subtitle: string;
      locatedAt: string;
    };
    grid: {
      everythingUnderOneRoof: string;
      viewDetails: string;
      bookNow: string;
      bookDayPass: string;
      includedInDayPass: string;
      viewAllActivities: string;
      showOneAtATime: string;
    };
    items: {
      bistro: {
        title: string;
        subtitle: string;
        description: string;
      };
      bistro_dine_in: {
        title: string;
        subtitle: string;
        description: string;
      };
      bistro_takeaway: {
        title: string;
        subtitle: string;
        description: string;
      };
      birthdays: {
        title: string;
        subtitle: string;
        description: string;
      };
      spa: {
        title: string;
        subtitle: string;
        description: string;
      };
      sauna: {
        title: string;
        subtitle: string;
        description: string;
      };
      dining: {
        title: string;
        subtitle: string;
        description: string;
      };
      cinema: {
        title: string;
        subtitle: string;
        description: string;
      };
      billiards: {
        title: string;
        subtitle: string;
        description: string;
      };
      pool: {
        title: string;
        subtitle: string;
        description: string;
      };
      sports: {
        title: string;
        subtitle: string;
        description: string;
      };
    };
    modal: {
      bookTitle: string;
      bookDesc: string;
      callNow: string;
      whatsapp: string;
      close: string;
      inquireNow: string;
      bookDayPass: string;
      contactSupport: string;
      phone: string;
      email: string;
      whatsappLabel: string;
      // Activity-specific modal content
      dining: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
        learnMore: string;
      };
      sauna: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
        learnMore: string;
      };
      bistro_dine_in: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
        learnMore: string;
        learnMoreTakeaway: string;
      };
      spa: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
        learnMore: string;
      };
      cinema: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
        dayPassValuePrivate: string;
        learnMore: string;
      };
      billiards: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
      };
      pool: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
      };
      sports: {
        description: string;
        description2: string;
        description3: string;
        dayPassValue: string;
      };
    };
    dayPass: {
      bookingTray: {
        title: string;
        dayPassDetails: string;
        perPerson: string;
        inclusionAllAmenities: string;
        inclusionMassage: string;
        inclusionMeal: string;
        inclusionCafe: string;
        inclusionSauna: string;
        includedValue: string;
        numberOfGuests: string;
        guestLimit: string;
        basePassPrice: string;
        massageUpgrade: string;
        total: string;
        upgradeMassage: string;
        changeUpgrade: string;
        bookNow: string;
        backToOverview: string;
        upgradeYourMassage: string;
        upgradeDescription: string;
        upgradeDescriptionMultiple: string;
        includedMassage: string;
        selectGuest: string;
        selectingForGuest: string;
        guest: string;
        upgradeOptions: string;
        upgradeFor: string;
        upgradeSelected: string;
        includedSelected: string;
        confirmUpgrade: string;
        bookingInformation: string;
        bookingDescription: string;
        fullName: string;
        email: string;
        phone: string;
        preferredDate: string;
        preferredTime: string;
        selectDate: string;
        morning: string;
        afternoon: string;
        evening: string;
        specialRequests: string;
        bookingSummary: string;
        requestedTimesNote: string;
        submitBooking: string;
        fillAllFields: string;
        submitError: string;
        networkError: string;
        successMessage: string;
        thankYouReceived: string;
        confirmationEmail: string;
        upgradedTo: string;
      };
      dayPassPricing: {
        title: string;
        subtitle: string;
        priceLabel: string;
        price: string;
        perPerson: string;
        inclusionsTitle: string;
        inclusions: {
          amenities: string;
          massage: string;
          massageValue: string;
          meal: string;
          mealValue: string;
          cafe: string;
          sauna: string;
        };
        valueBreakdown: {
          title: string;
          description: string;
          massage: string;
          massageValue: string;
          meal: string;
          mealValue: string;
          cafe: string;
          cafeValue: string;
          sauna: string;
          saunaValue: string;
          amenities: string;
          amenitiesValue: string;
          totalValueLabel: string;
          totalValue: string;
          passPriceLabel: string;
          passPrice: string;
          savings: string;
        };
        cta: string;
      };
    };
    experienceVideo: {
      title: string;
      subtitle: string;
    };
    ctaContact: {
      title: string;
      subtitle: string;
      ctaTitle: string;
      ctaDescription: string;
      ctaButtons: {
        bookVisit: string;
        contactConcierge: string;
        callNow: string;
      };
      contactTitle: string;
      contactMethods: {
        phone: {
          label: string;
        };
        email: {
          label: string;
        };
        whatsapp: {
          label: string;
          value: string;
        };
      };
      operatingHours: {
        title: string;
        days: string;
        hours: string;
      };
    };
    footer: {
      allInOne: string;
      benefits: string[];
      tagline: string;
    };
    whyChoose: {
      title: string;
      subtitle: string;
      imageAlt: string;
      premium: {
        title: string;
        description: string;
      };
      allInOne: {
        title: string;
        description: string;
      };
      flexible: {
        title: string;
        description: string;
      };
      easyAccess: {
        title: string;
        description: string;
      };
    };
    locationAccess: {
      title: string;
      subtitle: string;
      mapTitle: string;
      locatedAt: string;
      switzerland: string;
      bernerOberland: string;
      cantonBern: string;
      address: {
        title: string;
        description: string;
        details: string;
        street: string;
        number: string;
        city: string;
        country: string;
      };
      parking: {
        title: string;
        description: string;
        details: string;
      };
      bus: {
        title: string;
        description: string;
        details: string;
      };
      train: {
        title: string;
        description: string;
        details: string;
        beatenberg: string;
        interlaken: string;
      };
      air: {
        title: string;
        description: string;
        details: string;
        bern: string;
        zurich: string;
        geneva: string;
      };
    };
    amenitiesOverview: {
      title: string;
      subtitle: string;
      wellness: {
        title: string;
        clinique: {
          name: string;
          description: string;
        };
        whirlpool: {
          name: string;
          description: string;
        };
      };
      sports: {
        title: string;
        fitness: {
          name: string;
          description: string;
        };
        tennis: {
          name: string;
          description: string;
        };
        basketball: {
          name: string;
          description: string;
        };
        football: {
          name: string;
          description: string;
        };
        volleyball: {
          name: string;
          description: string;
        };
      };
      dining: {
        title: string;
        restaurant: {
          name: string;
          description: string;
          capacity: string;
        };
        cafe: {
          name: string;
          description: string;
          capacity: string;
        };
        lounge: {
          name: string;
          description: string;
          capacity: string;
        };
      };
      views: {
        title: string;
        panoramic: {
          name: string;
          description: string;
        };
      };
    };
    faq: {
      title: string;
      subtitle: string;
      ctaText: string;
      ctaButton: string;
      questions: {
        whatIncluded: {
          question: string;
          answer: string;
        };
        advanceBooking: {
          question: string;
          answer: string;
        };
        upgradeMassage: {
          question: string;
          answer: string;
        };
        guestLimit: {
          question: string;
          answer: string;
        };
        multipleActivities: {
          question: string;
          answer: string;
        };
        parking: {
          question: string;
          answer: string;
        };
        mealSelection: {
          question: string;
          answer: string;
        };
        whatToBring: {
          question: string;
          answer: string;
        };
        familyFriendly: {
          question: string;
          answer: string;
        };
        operatingHours: {
          question: string;
          answer: string;
        };
        cancellationPolicy: {
          question: string;
          answer: string;
        };
      };
    };
  };

  /** Room booking flow (/book) */
  book: BookPageTranslations;
}

export const translations: Record<string, Translations> = {
  en: {
    common: {
      share: "Share"
    },
    banner: {
      unitOf: "A Hotel by"
    },
    nav: {
      home: "Home",
      rooms: "Rooms & Suites",
      wellness: "Wellness & Spa",
      dining: "Dining",
      experiences: "Experiences",
      events: "Events & Weddings",
      about: "About",
      bookNow: "Book Now",
      activities: "Activity Arcade",
      journal: "Journal",
      language: "Language",
      displayCurrency: "Display currency"
    },
    hero: {
      title: "BEATENBERG",
      subtitle: "RESORT & SPA",
      description: "Rustic Luxury in the Swiss Alps",
      bookStay: "Book Your Stay",
      swissHeritageHotel: "Swiss Heritage Hotel • Beatenberg/Interlaken",
      acreResort: "15 Acre Resort",
      minsFromInterlaken: "15 mins from Interlaken",
      stunningAlpineViews: "Stunning Alpine Views",
      since1881: "Since 1881",
      beatenbergsOnly: "Beatenberg's Only",
      fourStarGastrosuisse: "4-Star Gastrosuisse",
      ratedResort: "Rated Resort",
      locationButton: "Beatenberg Resort and Spa Location"
    },
    features: {
      alpineSanctuary: "Rustic Alpine Luxury in Beatenberg/Interlaken",
      alpineDescription: "Beatenberg Resort & Spa is a 4★ Deluxe Heritage Resort located in Beatenberg, Europe's longest village, overlooking Lake Thun and framed by the majestic Jungfrau, Eiger and Mönch peaks. A Hidden Alpine Gem, the resort combines traditional Swiss charm with state-of-the-art facilities, making it an ideal choice for leisure travellers, groups, wellness seekers, weddings and corporate events.",
      alpineDescription2: "Here, every moment is a chance to reconnect with nature's quiet power. Our dedicated team is here to ensure your stay is as seamless as the views, providing a haven of peace whether you seek a restful retreat or a grand adventure. Let the Alps inspire your next journey.",
      exclusiveRates: "EXCLUSIVE RATES",
      guestCardBenefits: "GUEST CARD BENEFITS",
      beatenbergAccess: "BEATENBERG ACCESS",
      interlakenPrivileges: "INTERLAKEN PRIVILEGES",
      habkernDiscounts: "HABKERN DISCOUNTS",
      freeShuttle: "FREE SHUTTLE SERVICE",
      bookYourStay: "Book Your Stay",
      integratedWellness: "Integrated Swiss Wellness",
      integratedWellnessDesc: "Exclusive NutraSwiss spa & nutraceuticals",
      guestCardBenefitsDesc: "Exclusive discounts and local attractions access",
      gastrosuisseExcellence: "GASTROSUISSE Certified Excellence",
      gastrosuisseExcellenceDesc: "Recognized by Swiss hospitality association for quality standards",
      swisstainableCommitment: "Swisstainable Tourism Commitment",
      swisstainableCommitmentDesc: "Dedicated to sustainable tourism practices in Switzerland",
      heritageCuisine: "Swiss Heritage Cuisine",
      heritageCuisineDesc: "Farm-to-table traditional dishes reimagined",
      acreResort: "15-Acre Resort in Scenic Alpine Village",
      acreResortDesc: "Tranquil mountain setting above Lake Thun",
      authenticHeritage: "Authentic Heritage",
      authenticHeritageDesc: "Swiss hospitality since 1881",
      alpineExperiences: "Unforgettable Alpine Experiences",
      alpineExperiencesDesc: "Year-round activities & Swiss cultural immersion",
      dragToExplore: "Drag to explore",
      elegantBarLounge: "Elegant Bar & Lounge",
      elegantBarLoungeDesc: "Unwind, socialize, and enjoy good times",
      recreationGames: "Recreation & Games",
      recreationGamesDesc: "Entertainment and games for all ages",
      technogymFitness: "TECHNOGYM Fitness",
      technogymFitnessDesc: "State-of-the-art equipment in Alpine setting",
      businessFacilities: "Business Facilities",
      businessFacilitiesDesc: "Professional meeting spaces for events",
      alpineBistro: "Alpine Bistro",
      alpineBistroDesc: "Casual dining in relaxed atmosphere",
      panoramicViews: "Panoramic Views",
      panoramicViewsDesc: "Breathtaking vistas of Jungfrau & Lake Thun"
    },
    rooms: {
      title: "Our Rooms & Suites",
      description: "From cozy doubles to spacious family suites, each room offers Swiss comfort with breathtaking Alpine views.",
      exploreRooms: "Explore Rooms",
      familySuite: "Heritage Family Suite - Jungfrau View",
      juniorSuite: "Alpine Junior Suite - Panorama View",
      tripleRoom: "Classic Triple Room - Alpine Comfort",
      premiumDouble: "Jungfrau Superior Double - Balcony & Lake View",
      standardSingle: "Classic Single Room - Alpine Comfort",
      alpineViews: "Alpine Views",
      pineWoodInteriors: "Elegant pine wood interiors blending modern comfort with heritage character",
      organicCottonSheets: "Organic bio cotton sheets",
      privateBalconies: "Private balconies with views of Mönch, Niesen, Niederhorn & Lake Thun or Beatenberg village",
      seniorFriendly: "Options ranging from cozy singles to Family Suites",
      exploreRoom: "Explore Room",
      viewRoomFeatures: "Room Features",
      familySuiteDesc: "Spacious accommodation perfect for families, featuring separate bedrooms and stunning Alpine views.",
      juniorSuiteDesc: "Luxurious junior suite offering spacious living areas and premium Alpine comfort.",
      tripleRoomDesc: "Spacious triple accommodation perfect for small groups, featuring comfortable bedding and Alpine charm.",
      premiumDoubleDesc: "Sophisticated double room with modern amenities and panoramic Alpine vistas.",
      standardSingleDesc: "Classic single accommodation with Alpine comfort, featuring elegant wooden decor and modern amenities.",
      alpineViewsDesc: "Wake up to spectacular views of the Bernese Alps from your room's panoramic windows.",
      familySuiteFeatures: {
        masterBedroom: "1 Master Bedroom",
        additionalBedrooms: "2 Additional Bedrooms",
        hallLivingRoom: "Hall & Living Room",
        privateBalcony: "Private Balcony"
      },
      familySuiteBadges: {
        occupancy: "6",
        roomSize: "65m²",
        bedding: "1 Queen + 4 Single",
        bathrooms: "2 Bathrooms"
      },
      familySuiteDescription: {
        mobile: "Spacious 65m² suite with 1 Queen + 4 Single beds, kitchenette, and panoramic views of Lake Thun, Eiger, Mönch, Jungfrau, and Beatenberg village.",
        desktop: "Spacious 65m² suite with 1 Queen + 4 Single beds, kitchenette, and panoramic views of Lake Thun, Eiger, Mönch, Jungfrau, and Beatenberg village."
      },
      juniorSuiteFeatures: {
        loungeSeating: "Elegant Lounge Seating",
        privateBalconyWithViews: "Private Balcony with Mountain/Lake/Village View",
        spaceForAdditionalBeds: "Space for Additional Beds"
      },
      juniorSuiteBadges: {
        occupancy: "4",
        roomSize: "45m²",
        bedding: "1 Queen + 1 Sofa",
        bathrooms: "1 Bathroom"
      },
      juniorSuiteDescription: {
        mobile: "Luxurious 45m² junior suite with separate living area, sofa bed, and private balcony with stunning Alpine views.",
        desktop: "Luxurious 45m² junior suite with separate living area, sofa bed, and private balcony with stunning Alpine views."
      },
      tripleRoomFeatures: {
        perfectForSmallFamilies: "Perfect for small families",
        scenicBalconyWithViews: "Scenic balcony with mountain, lake, or village view"
      },
      tripleRoomBadges: {
        occupancy: "3",
        roomSize: "30m²",
        bedding: "3 Single beds",
        bathrooms: "1 Bathroom"
      },
      tripleRoomDescription: {
        mobile: "Comfortable 30m² triple room with three single beds, perfect for small families or groups.",
        desktop: "Comfortable 30m² triple room with three single beds, perfect for small families or groups."
      },
      doubleRoomFeatures: {
        perfectForCouples: "Perfect for couples",
        balconyWithViews: "With/without balcony; mountain, lake, or village views"
      },
      doubleRoomBadges: {
        occupancy: "2",
        roomSize: "30m²",
        bedding: "1 Queen bed",
        bathrooms: "1 Bathroom"
      },
      doubleRoomDescription: {
        mobile: "Elegant 30m² double room with queen bed and modern amenities, ideal for couples.",
        desktop: "Elegant 30m² double room with queen bed and modern amenities, ideal for couples."
      },
      alpineDoubleTwin: "Alpine Double/Twin - Balcony & Lake View",
      alpineDoubleTwinFeatures: {
        flexibleBedding: "Flexible double or twin bedding configuration",
        privateBalconyWithViews: "Private balcony with stunning lake views",
        perfectForCouplesOrFriends: "Perfect for couples or friends traveling together"
      },
      alpineDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "32m²",
        bedding: "1 Double or 2 Twin beds",
        bathrooms: "1 Bathroom"
      },
      alpineDoubleTwinDescription: {
        mobile: "Flexible Alpine room with balcony and stunning lake views, perfect for couples or friends.",
        desktop: "Our Alpine Double/Twin room offers 32m² of comfortable space with flexible bedding options and a private balcony overlooking Lake Thun. Perfect for couples or friends seeking both comfort and breathtaking Alpine scenery."
      },
      alpineClassicDoubleTwin: "Alpine Classic Double/Twin - Village View",
      alpineClassicDoubleTwinFeatures: {
        flexibleBedding: "Flexible double or twin bed configuration",
        villageViews: "Charming village views and peaceful atmosphere"
      },
      alpineClassicDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "28m²",
        bedding: "1 Double or 2 Twin beds",
        bathrooms: "1 Bathroom"
      },
      alpineClassicDoubleTwinDescription: {
        mobile: "Classic Alpine room with charming village views and flexible bedding, perfect for couples or friends.",
        desktop: "Our Alpine Classic Double/Twin room offers 28m² of comfortable space with flexible bedding options and peaceful village views. Perfect for couples or friends seeking authentic Alpine charm with the flexibility of double or twin bed configurations."
      },
      jungfrauSingle: "Jungfrau Single - Balcony & Lake View",
      jungfrauSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Single bed",
        bathrooms: "1 Bathroom"
      },
      jungfrauSingleDescription: {
        mobile: "Single room with private balcony and stunning lake views, perfect for solo travelers.",
        desktop: "Our Jungfrau Single room offers 20m² of comfortable space with a private balcony overlooking Lake Thun. Perfect for solo travelers seeking both comfort and breathtaking Alpine scenery with direct access to fresh mountain air."
      },
      jungfrauSingleFeatures: {
        balconyWithLakeView: "Private balcony with stunning lake views",
        perfectForSoloTravelers: "Perfect for solo travelers"
      },
      heritageSingle: "Heritage Single - Village View",
      heritageSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Single bed",
        bathrooms: "1 Bathroom"
      },
      heritageSingleDescription: {
        mobile: "Classic single room with charming village views and heritage character.",
        desktop: "Our Heritage Single room offers 20m² of comfortable space with peaceful village views. Perfect for solo travelers seeking authentic Alpine charm with classic Swiss hospitality and traditional character."
      },
      heritageSingleFeatures: {
        villageViews: "Charming village views and peaceful atmosphere",
        classicCharm: "Classic Alpine charm and heritage character"
      },
      singleRoomFeatures: {
        perfectForSoloTravelers: "Perfect for solo travelers",
        balconyWithViews: "With/without balcony; mountain, lake, or village views"
      },
      singleRoomBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Single bed",
        bathrooms: "1 Bathroom"
      },
      singleRoomDescription: {
        mobile: "Cozy 20m² single room with single bed, perfect for solo travelers seeking comfort.",
        desktop: "Cozy 20m² single room with single bed, perfect for solo travelers seeking comfort."
      }
    },
    restaurant: {
      title: "Culinary Excellence",
      description: "From traditional Swiss specialties to innovative fine dining, our restaurant celebrates the rich flavors of the Alps. Experience exceptional cuisine in an elegant mountain setting.",
      exploreRestaurant: "Explore Restaurant",

      // Hero Section
      hero: {
        logoAlt: "Restaurant Jungfrau Logo"
      },

      renovationNotice: {
        eyebrow: "Dining Update",
        title: "Dining by Appointment Only",
        description:
          "Restaurant Jungfrau is open by reservation. Reach out to arrange your alpine dining experience.",
        completion: "",
        ctaPrimary: "Got it",
        ctaSecondary: "Talk to us",
      },

      // About Section
      about: {
        backgroundText: "JUNGFRAU",
        tagline: "RESTAURANT JUNGFRAU",
        title: "ALPINE GASTRONOMY\nAT ITS FINEST.",
        description: "Restaurant Jungfrau embodies the pinnacle of Alpine gastronomy, where centuries-old Swiss culinary traditions harmoniously blend with contemporary international techniques. Our philosophy is rooted in three core principles: authenticity, sustainability, and innovation.",
        phoneButton: "+41 33 841 00 21"
      },

      // Features Section
      features: {
        alpineViews: {
          title: "ALPINE VIEWS",
          description: "Panoramic Lake Thun & Jungfrau"
        },
        localSourcing: {
          title: "LOCAL SOURCING",
          description: "Swiss farmers & artisans"
        },
        swissHeritage: {
          title: "SWISS HERITAGE",
          description: "Centuries-old traditions"
        }
      },

      // Menu Section
      menu: {
        tagline: "SWISS ALPINE CUISINE",
        title: "RESTAURANT JUNGFRAU MENU",
        description: "Experience our signature 5-course dinner featuring traditional Swiss Alpine cuisine with fresh local ingredients. Each course is carefully crafted to celebrate the rich culinary heritage of the Bernese Oberland region. This is just a glimpse - view our complete menu below.",
        subtitle: "A Taste of the Alps - Est. 1881",
        tabs: {
          mainCourses: "Main Courses",
          fiveCourseDinner: "5-Course Dinner",
          sides: "Sides",
          beverages: "Beverages"
        },
        viewFullMenu: "View Full Menu",
        dinnerHours: "Dinner from 18:30 to 21:00 | Reservations: +41 76 622 01 22",
        reservations: "Reservations: +41 76 622 01 22",
        pricesInclusive: "All prices inclusive of VAT"
      },

      // Menu Items
      menuItems: {
        fiveCourseMenu: {
          name: "5-Course Menu",
          description: "COURSE_1:Salad: Healthy Swiss Salad|COURSE_2:Soup: Vegetable Soup of the Day|COURSE_3:Main Course (choose one):|COURSE_3_OPTION_1:Jungfrau Grilled Chicken & Vegetables|COURSE_3_OPTION_2:Beatenberg Baked Norwegian Salmon|COURSE_3_OPTION_3:Eiger Mushroom-Cheese Pizza (Vegetarian)|COURSE_3_OPTION_4:Asian Curry (Chicken or Vegetarian) with Garlic Naan|COURSE_4:Dessert: Pastry of the Day / Ice Cream Selection / Chocolate Cake / Vanilla Ice Cream Cake|COURSE_5:Hot Beverage: Coffee or Tea"
        },
        healthySwissSalad: {
          name: "Gesunder Schweizer Salat",
          description: "Healthy Swiss Salad with Fresh Local Ingredients"
        },
        vegetableSoup: {
          name: "Vegetable Soup of the Day",
          description: "Comforting vegetable soup prepared fresh each day."
        },
        jungfrauClassic: {
          name: "Jungfrau Classic",
          description: "Grilled Chicken Breast with Vegetables"
        },
        thunerseeDelight: {
          name: "Thunersee Delight",
          description: "Grilled Salmon Fillet with Vegetables"
        },
        alpineHutRosti: {
          name: "Alpine Hut Rösti",
          description: "Vegetarian Rösti with Homemade Cheese"
        },
        eigerPeakPinsa: {
          name: "Eiger Peak Pinsa",
          description: "Vegetarian PIZZA with Mushrooms & Cheese"
        },
        asianCurry: {
          name: "Asian Curry",
          description: "Chicken or Vegetarian Curry with Rice and Raita"
        },
        frenchFries: {
          name: "French Fries",
          description: "Crispy golden French fries"
        },
        smokedSalmonSandwich: {
          name: "Smoked Salmon Sandwich",
          description: "Smoked salmon sandwich on fresh bread with a light salad garnish."
        },
        eggCheeseSandwich: {
          name: "Egg & Cheese Sandwich",
          description: "Warm egg and Swiss cheese sandwich, perfect for a quick alpine bite."
        },
        healthySaladWithEgg: {
          name: "Healthy Salad with Egg",
          description: "Crisp garden salad topped with egg and seasonal vegetables."
        },
        healthySalad: {
          name: "Healthy Salad",
          description: "Fresh mixed salad with alpine herbs and light dressing."
        },
        chickenNuggets: {
          name: "Chicken Nuggets (6 Pcs)",
          description: "Six crispy chicken nuggets, ideal as a sharing side or snack."
        },
        miniCroissant: {
          name: "Mini Croissant (2 Pcs)",
          description: "Two buttery mini croissants, a light sweet side or afternoon treat."
        },
        sweetPotatoFries: {
          name: "Sweet Potato Fries",
          description: "Sweet potato fries with Alpine seasoning"
        },
        falafel: {
          name: "Falafel",
          description: "Traditional Middle Eastern falafel"
        },
        potatoRostiFries: {
          name: "Potato Rösti Fries",
          description: "Swiss-style potato rösti fries"
        },
        clavienCornalin: {
          name: "Clavien Cornalin",
          description: "Swiss Red Wine (10 cl) - AOC Valais"
        },
        yvorneRouge: {
          name: "Yvorne Rouge AOC",
          description: "Swiss Red Wine (10 cl) - Chablais"
        },
        yvorneBlanc: {
          name: "Yvorne Blanc",
          description: "Swiss White Wine (10 cl) - Chablais"
        },
        rugenbrauAmber: {
          name: "Rugenbräu Amber",
          description: "Swiss Amber Beer (33 cl) - Locally brewed"
        },
        rugenbrauSpezial: {
          name: "Rugenbräu Spezial Hell",
          description: "Swiss Premium Lager (33 cl)"
        },
        rivella: {
          name: "Rivella Original",
          description: "Iconic Swiss Soft Drink (33 cl)"
        },
        iceTea: {
          name: "Ramseier Ice Tea",
          description: "Swiss Mint or Black Tea (33 cl)"
        },
        apfelschorle: {
          name: "Ramseier Apple Spritzer",
          description: "Swiss Apple Juice with Sparkling Water (33 cl)"
        }
      },

      // Featured Items
      featuredItems: {
        tagline: "MENU HIGHLIGHTS",
        title: "FEATURED ITEMS"
      },

      // Dining Venues
      diningVenues: {
        tagline: "DINING EXPERIENCE",
        title: "DINING VENUES",
        description: "Multiple dining experiences within Restaurant Jungfrau, each offering unique ambiance and breathtaking Alpine views",
        mainRestaurant: {
          title: "Main Restaurant",
          subtitle: "PANORAMIC DINING HALL",
          description: "Our main dining area offers seating capacity up to 200 guests with breathtaking views over Lake Thun and the Jungfrau mountain range.",
          features: [
            "Seating capacity: Up to 200 guests",
            "Panoramic views of Lake Thun",
            "Jungfrau mountain range backdrop",
            "Ideal for groups and events"
          ],
          perfectFor: "Groups, Events, Family Dining"
        },
        terraceDining: {
          title: "Terrace Dining",
          subtitle: "ALPINE TERRACE",
          description: "Covered outdoor space with retractable glass walls and heating systems, offering unobstructed lake views and ambient lighting.",
          features: [
            "Capacity: 60-80 guests",
            "Unobstructed lake views",
            "Retractable glass walls",
            "Integrated bar area"
          ],
          perfectFor: "Intimate Dining, Special Occasions"
        },
        outdoorTable: {
          title: "Outdoor Table",
          subtitle: "PRIVATE OUTDOOR TABLE",
          description: "Intimate culinary experiences with personalized menus, featuring live acoustic music and traditional Swiss pieces.",
          features: [
            "Intimate culinary experiences",
            "Personalized menus",
            "Alpine nature sounds",
            "Traditional Swiss pieces"
          ],
          perfectFor: "Special Celebrations, Wine Tastings"
        },
        alpineBreakfast: {
          title: "Alpine Breakfast",
          subtitle: "ALPINE BREAKFAST BUFFET",
          description: "Alpine breakfast buffet with locally sourced produce, featuring traditional Swiss breakfast items and fresh mountain ingredients.",
          features: [
            "Locally sourced produce",
            "Traditional Swiss breakfast items",
            "Fresh mountain ingredients",
            "Panoramic morning views"
          ],
          perfectFor: "Morning Dining, Hotel Guests"
        }
      },

      // Scrolling Text
      scrollingText: {
        swissHeritage: "SWISS HERITAGE",
        alpineGastronomy: "ALPINE GASTRONOMY",
        farmToTable: "FARM-TO-TABLE",
        fiveCourseDinner: "5-COURSE DINNER",
        swissWines: "SWISS WINES",
        localBeverages: "LOCAL BEVERAGES",
        alpineSides: "ALPINE SIDES",
        freshSalads: "FRESH SALADS",
        gastrosuisseCertified: "GASTROSUISSE CERTIFIED"
      },

      // Gallery
      gallery: {
        eyebrow: "VISUAL EXPERIENCE",
        title: "Restaurant Gallery",
        subtitle: "Experience our ambiance and culinary artistry"
      },

      // Testimonials
      testimonials: {
        // Testimonial content is kept in original language as they are real customer reviews
      },

      // GASTROSUISSE Certification
      gastrosuisse: {
        tagline: "SWISS EXCELLENCE",
        title: "GASTROSUISSE CERTIFIED",
        description: "Recognized by the Swiss hospitality association for maintaining the highest standards of quality, service, and culinary excellence",
        certificationBenefits: {
          title: "CERTIFICATION BENEFITS",
          benefits: [
            "Swiss hospitality association recognition",
            "Quality and service standards compliance",
            "Industry recognition and credibility",
            "Premium member status benefits"
          ]
        },
        qualityStandards: {
          title: "QUALITY STANDARDS",
          standards: [
            "Regular quality assessments and inspections",
            "Staff training and development programs",
            "Swiss culinary tradition preservation",
            "Guest satisfaction and experience excellence"
          ]
        },
        additionalAccreditations: {
          title: "ADDITIONAL ACCREDITATIONS",
          accreditations: [
            "Swiss Hotel Association - Premium Member",
            "Swisstainable Tourism Certification",
            "Bern Welcome Affiliation"
          ]
        }
      },

      // Contact Section
      contact: {
        tagline: "GET IN TOUCH",
        title: "Visit Us Today",
        subtitle: "We'd love to serve you an unforgettable experience",
        visitLocation: {
          title: "Visit Our Location",
          address: [
            "Beatenberg Resort & Spa",
            "Spirenwaldstrasse 351",
            "3803 Beatenberg, Switzerland",
            "Europe's Longest Village"
          ]
        },
        callUs: {
          title: "Call Us Now",
          details: [
            "+41 33 841 00 21",
            "Restaurant Jungfrau",
            "Beatenberg Resort & Spa"
          ]
        },
        email: {
          title: "Email Address",
          details: [
            "info@beatenberg-resortspa.ch"
          ]
        }
      },

      // Christmas & New Year Dinner Registration Form
      christmasNewYearForm: {
        title: "Christmas & New Year Dinner Reservation",
        intro: "Join us for a special festive dining experience. Please fill out the form below to reserve your table.",
        pricingInfo: "CHF 111 per person",
        minimumParty: "Reservations start from 2 pax minimum",
        festiveMenusTitle: "Festive Menus",
        christmasMenuLabel: "Christmas Menu",
        newYearMenuLabel: "New Year Menu",
        nameLabel: "Full Name",
        namePlaceholder: "Enter your full name",
        emailLabel: "Email Address",
        emailPlaceholder: "your.email@example.com",
        mobileLabel: "Mobile Number",
        mobilePlaceholder: "Enter your mobile number",
        countLabel: "Number of Guests",
        countPlaceholder: "Select number of guests",
        eventLabel: "Event",
        eventChristmas: "Christmas Eve - 24th December 2025",
        eventNewYear: "New Year Eve - 31st December 2025",
        timeSlotLabel: "Preferred Time Slot",
        timeSlot1: "18:00–20:00",
        timeSlot2: "20:15–22:15",
        submitButton: "Submit Reservation",
        submittingButton: "Submitting...",
        successMessage: "Thank you! Your reservation has been submitted successfully. We will contact you shortly to confirm your reservation.",
        errorMessage: "An error occurred while submitting your reservation. Please try again or contact us directly.",
        validationRequired: "This field is required",
        validationEmail: "Please enter a valid email address",
        validationPhone: "Please enter a valid phone number",
        validationCount: "Please select the number of guests (2-12)",
        honeypotLabel: "Leave this field empty",
        modalTitle: "Reservation Confirmed!",
        modalSubtitle: "Your reservation has been received",
        modalNameLabel: "Name",
        modalEventLabel: "Event",
        modalTimeSlotLabel: "Time Slot",
        modalGuestsLabel: "Guests",
        modalCloseButton: "Close"
      }
    },
    spa: {
      serviceList: {
        addToList: "Add to List",
        addedToList: "Added to List",
        title: "Your Service List",
        empty: "Your list is currently empty",
        details: "Inquiry Details",
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        date: "Preferred Date",
        time: "Preferred Time",
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening",
        subtotal: "Estimated Total",
        sendInquiry: "Send Inquiry",
        successTitle: "Thank You",
        successMessage: "Your inquiry has been received. Our concierge will contact you shortly.",
        errorTitle: "Submission Failed",
        errorMessage: "Something went wrong. Please try again or contact us directly.",
        closeList: "Close List",
        tryAgain: "Try Again",
        browseServices: "Browse Treatments",
        selections: "Selections",
        catalog: "Catalog",
        selectDate: "Select Date",
        selectTime: "Select Time",
        preferredDateCET: "Preferred Date (CET) *",
        requestAppointment: "Request Appointment",
        reviewSelections: "Review Selections",
        addToMyList: "Add to My List",
        viewMyList: "View My List",
        yourRelaxationAwaits: "Your relaxation awaits",
        selectTreatmentsMessage: "Select treatments from our menu to create your personalized wellness plan.",
        viewSpaMenu: "View Spa Menu",
        totalEstimated: "Total Estimated",
        thankYouReceived: "Thank you! Your request has been received.",
        requestedTimesNote: "*Requested times are subject to availability. We will contact you to confirm your appointment."
      },
      title: "Your Wellness Escape",
      description: "Escape to our tranquil Alpine sanctuary where relaxation meets rejuvenation. Experience world-class spa treatments, therapeutic massages, and wellness programs designed to restore your mind, body, and soul in the pristine Swiss mountains.",
      exploreSpa: "Explore Spa",
      holisticWellnessTitle: "Holistic Wellness Center",
      holisticWellnessDesc: "Experience comprehensive wellness programs combining medical expertise with traditional healing methods in our serene Alpine setting.",
      medicalSpaTitle: "Medical Spa Excellence",
      medicalSpaDesc: "State-of-the-art medical spa treatments designed to promote healing, rejuvenation, and optimal health in a luxurious environment.",
      detoxTitle: "Detox & Cleansing",
      detoxDesc: "Comprehensive detox programs to cleanse your body of toxins and restore natural balance through specialized treatments and nutrition.",
      relaxationBalconyTitle: "Relaxation Balcony",
      relaxationBalconyDesc: "Enjoy peaceful moments on our panoramic balcony with stunning Alpine views, perfect for meditation and quiet reflection.",
      therapeuticMassageTitle: "Therapeutic Massage",
      therapeuticMassageDesc: "Specialized massage treatments including full-body, head, and back massages to provide targeted relaxation and healing.",

      // Hero Section
      hero: {
        mainTitle: "Experience wellness enhanced by nature",
        subMessage: "Where pristine Alpine air and pure mountain water meet Swiss therapeutic excellence",
        ctaButton: "Experience Alpine Wellness"
      },

      // Nature and Science Section
      natureScience: {
        title: "Natural Healing & Swiss Excellence",
        subtitle: "Backed by NutraSwiss and Nahrin — combining nature's purity with decades of Swiss natural health expertise",
        images: {
          massage: "Therapeutic massage treatment",
          therapist: "Professional spa therapist",
          treatment: "Spa treatment room",
          wellness: "Wellness refreshments",
          alpine: "Alpine wellness experience",
          relaxation: "Relaxation area"
        },
        blocks: {
          air: {
            title: "Pristine Alpine Air",
            description: "Every breath you take in the Bernese Oberland at 1,200m altitude is naturally filtered by the Alpine environment, enhancing your body's natural healing processes. The clean, oxygen-rich air enhances respiratory health and boosts energy levels."
          },
          water: {
            title: "Pure Mountain Water",
            description: "Our treatments incorporate pure Alpine water sourced from mountain springs, adding natural minerals and purity to every experience. Mountain spring water treatments using pure Alpine water enhance the therapeutic benefits of every session."
          },
          science: {
            title: "Swiss Scientific Excellence",
            description: "Backed by over 60 years of Swiss natural health expertise through Nahrin and NutraSwiss, our treatments combine traditional Swiss wellness traditions with modern science, using premium botanical formulations crafted from 100% natural ingredients."
          }
        }
      },

      renovationNotice: {
        eyebrow: "Spa Update",
        title: "Free Sauna & Thermal Wellness for In-House Guests",
        description:
          "The sauna & thermal wellness package is complimentary for guests staying at the hotel.",
        completion: "",
        ctaPrimary: "Book your stay",
        ctaSecondary: "Talk to us",
        imageAlt: "Alpine spa and sauna at Beatenberg Resort",
      },

      // Introduction Section
      introduction: {
        title: "Welcome to Clinique NutraSwiss",
        description: "Your sanctuary of relaxation and rustic luxury in the heart of the Swiss Alps. After a day exploring the breathtaking beauty of Switzerland - from the majestic Jungfrau region to the crystal-clear waters of Lake Thun - retreat to our spa for the ultimate Alpine relaxation experience.",
        perfectEndTitle: "The Perfect End to Your Swiss Adventure",
        perfectEndDesc: "We combine traditional Swiss wellness traditions with modern comfort, offering you a perfect complement to your Swiss adventure. Whether you've been hiking the mountain trails, exploring historic castles, or simply taking in the spectacular Alpine scenery, our treatments will help you unwind and rejuvenate in authentic Swiss style.",
        mountainViews: "Panoramic mountain views from every treatment room",
        alpineBotanicals: "Traditional Swiss treatments using locally sourced Alpine botanicals",
        swissHospitality: "Authentic Swiss hospitality with warm, personalized service",
        nutraSwissScience: "NutraSwiss nutritional science enhancing traditional Swiss wellness with evidence-based treatments and personalized protocols",
        perfectForYouTitle: "Perfect for You",
        convenientLocation: "Convenient location within your hotel - no need to travel elsewhere",
        flexibleScheduling: "Flexible scheduling to fit your sightseeing itinerary",
        multilingualStaff: "Multilingual staff fluent in English, German, French, and Italian",
        complimentaryConsultation: "Complimentary consultation to choose the perfect treatment"
      },

      // Power of Touch Section
      powerOfTouch: {
        category: "Therapeutic healing",
        title: "THE POWER OF TOUCH",
        description: "Experience the transformative power of skilled therapeutic touch in our serene Alpine sanctuary. Our certified massage therapists blend ancient healing wisdom with contemporary techniques, using premium Swiss-made products to restore balance to both body and mind. From deep tissue therapy to gentle relaxation techniques, each treatment is tailored to your individual needs, creating a deeply personal journey of restoration and renewal.",
        ctaButton: "Book your therapeutic experience"
      },

      // Beauty Section
      beauty: {
        category: "Alpine wellness",
        title: "THE ART",
        subtitle: "of Renewal",
        ctaButton: "Discover beauty treatments"
      },

      // Quick Relaxation Section
      quickRelaxation: {
        category: "Quick relaxation",
        title: "QUICK RELAXATION",
        description: "Experience the transformative power of skilled therapeutic touch in our serene Alpine sanctuary. Our certified massage therapists blend ancient healing wisdom with contemporary techniques, using premium Swiss-made products to restore balance to both body and mind. From deep tissue therapy to gentle relaxation techniques, each treatment is tailored to your individual needs, creating a deeply personal journey of restoration and renewal.",
        ctaButton: "Book your quick relaxation",
        modal: {
          title: "Quick Relaxation Options",
          description: "Perfect for busy tourists who want to squeeze in some pampering between sightseeing. Our quick relaxation options are designed to fit into your Swiss adventure schedule while providing maximum relaxation benefits.",
          closeButton: "Close modal",
          options: {
            expressMassage: {
              title: "Express Head & Shoulder Massage",
              description: "Perfect for relieving tension from sightseeing. Quick and effective massage focusing on neck, shoulders, and head."
            },
            footTreatment: {
              title: "Swiss Alpine Foot Treatment",
              description: "Revitalize tired feet after exploring the mountains. Includes foot soak, massage, and Alpine herbal treatment."
            },
            meditation: {
              title: "Mountain View Meditation",
              description: "Guided meditation session with panoramic Alpine views. Perfect for mental relaxation and mindfulness."
            },
            teaRitual: {
              title: "Alpine Herbal Tea Ritual",
              description: "Traditional Swiss tea ceremony with locally sourced Alpine herbs. A peaceful moment of reflection."
            }
          },
          benefits: {
            title: "Why Choose Quick Relaxation?",
            flexibleScheduling: {
              title: "Flexible Scheduling",
              description: "Treatments designed to fit your sightseeing schedule, from 20-45 minutes."
            },
            alpineExperience: {
              title: "Alpine Experience",
              description: "Authentic Swiss treatments using local Alpine herbs and traditional techniques."
            },
            instantRelief: {
              title: "Instant Relief",
              description: "Quick and effective treatments to relieve tension from hiking and sightseeing."
            },
            perfectForTourists: {
              title: "Perfect for Tourists",
              description: "Designed specifically for visitors exploring Switzerland's attractions."
            }
          },
          booking: {
            title: "Ready to Book Your Quick Relaxation?",
            description: "Book your experience by contacting our concierge. Our dedicated team will help you schedule your treatments and answer any questions about our spa services. Contact us at the front desk during your stay.",
            button: "Contact Concierge"
          }
        }
      },

      // Treatments & Services Section
      treatmentsAndServices: {
        title: "Your Wellness Journey",
        introduction: "Your wellness journey is supported by the best of Swiss nature and science. We exclusively utilize premium botanical formulations crafted from 100% natural ingredients, drawing on over six decades of Swiss expertise in nutritional and natural health. Every application is designed to harmonize with the Alpine environment outside.",
        learnMore: "Learn More",
        bookNow: "Book Your Treatment",
        therapeuticMassages: {
          title: "Therapeutic Massage Treatments",
          fullBody: {
            name: "Ganzkörpermassage / Full Body Massage",
            pricing: "30 Min: 75 CHF",
            duration: "30 minutes",
            description: "Relaxing sore muscles and regeneration after exercise. Improves blood flow and stimulates metabolism."
          },
          relaxing: {
            name: "Entspannungsmassage / Relaxing Massage",
            pricing: "30 Min: 72 CHF",
            duration: "30 minutes",
            description: "Soothing full body massage with warm lavender oil. Relieves internal and external tension."
          },
          sport: {
            name: "Sportmassage / Sport Massage",
            pricing: "30 Min: 81 CHF",
            duration: "30 minutes",
            description: "Powerful massage techniques for muscles, connective tissue, and fascia after sports."
          },
          footReflexology: {
            name: "Fußreflexzonenmassage / Foot Reflexology Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Activates self-healing and improves organ functions through pressure on foot nerve points."
          },
          footBack: {
            name: "Fuß- & Rückenmassage / Foot & Back Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Combination of foot reflexology and intensive back massage."
          },
          backNeck: {
            name: "Massage bei Rücken- und Nackenschmerzen / Massage for Back and Neck Pain",
            pricing: "30 Min: 75 CHF",
            duration: "30 minutes",
            description: "Combined therapeutic and feel-good massage for back and neck using anti-inflammatory tinctures."
          },
          peeling: {
            name: "Peeling & Ganzkörpermassage / Peeling & Full Body Massage",
            pricing: "30 Min: 98 CHF",
            duration: "30 minutes",
            description: "Removal of dead skin cells with peeling glove, followed by a nourishing full body massage."
          },
          energetic: {
            name: "Energetische Massage / Energetic Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Individual support of self-healing through various types of body & energy work."
          }
        },
        saunaThermal: {
          title: "Sauna & Thermal Wellness",
          package: {
            title: "Sauna & Thermal Wellness Package",
            price: "45 CHF (per session)",
            description: "Enjoy exclusive access to our sauna zone. This package includes the use of all facilities as well as complimentary coffee, tea, and detox drinks.",
            hours: "SAUNA HOURS: 11:00 - 20:00"
          },
          finnish: {
            name: "Finnische Sauna / Finnish Sauna",
            temperature: "90°C",
            description: "The intense dry heat is traditionally used to support general well-being and stimulate metabolism. The alternation between heat and cooling can promote circulation and contribute to recovery."
          },
          bio: {
            name: "Bio Sauna",
            temperature: "60°C",
            description: "Gentle temperatures combined with light therapy create an atmosphere of calm. This milder variant is often perceived as particularly pleasant and offers a relaxing heat experience that is gentle on the circulation."
          },
          steam: {
            name: "Dampfbad / Steam Bath",
            temperature: "45°C",
            description: "Warm mist helps to open pores and can support skin cleansing. The high humidity is frequently perceived as soothing for the airways and ideal for relaxation."
          },
          icePlunge: {
            name: "Eistauchbecken / Ice Plunge Pool",
            description: "The cold kick after sweating has a revitalizing effect on body and mind. This cooling can stimulate circulation and provides a refreshing, invigorating skin feeling."
          }
        },
        specialPrograms: {
          title: "Specialized Wellness Programs",
          pelvipower: {
            name: "PelviPower™ Training",
            price: "29 CHF (30 Min)",
            type: "Magnetic Field Therapy",
            description: "The PelviPower pelvic floor chair strengthens your pelvic floor gently, safely, and effectively. Thanks to state-of-the-art magnetic stimulation without effort and in full clothing.",
            note: "Recommended by Doctors & Therapists",
            benefitsTitle: "Benefits",
            benefits: {
              women: {
                title: "Women",
                desc: "Menopause | Increased libido"
              },
              men: {
                title: "Men",
                desc: "Vitality | Increased potency"
              },
              incontinence: {
                title: "Incontinence",
                desc: "Bladder weakness / Leaking"
              },
              back: {
                title: "Back",
                desc: "Relaxation | Strengthening"
              },
              mothers: {
                title: "Young Mothers",
                desc: "Recovery after birth"
              },
              sports: {
                title: "Sports",
                desc: "Performance | Stability"
              }
            }
          }
        }
      },

      // Booking Section
      booking: {
        title: "Booking & Contact",
        description: "Ready to experience the ultimate Alpine wellness retreat? Contact us to book your treatments and begin your journey to wellness",
        contactInfoTitle: "Contact Information",
        address: "Clinique NutraSwiss\nBeatenberg Resort & Spa\nSpirenwaldstrasse 351\n3803 Beatenberg, Switzerland",
        reservations: "Direct booking through hotel concierge",
        email: "Contact through hotel reception",
        accessibility: "Located within Beatenberg Resort & Spa premises\nEasy access from all hotel accommodations",
        guestBenefitsTitle: "Guest Benefits",
        complimentaryConsultation: "Complimentary consultation for first-time visitors",
        detoxTea: "Detox tea and healthy snacks after treatments",
        winterGarden: "Access to exclusive winter garden with treatments",
        guestCardBenefits: "Guest card benefits for regional attractions",
        advanceReservations: "Advance reservations recommended for all treatments",
        packageDeals: "Package deals available for multiple treatments",
        groupPrograms: "Group wellness programs can be arranged",
        bookNowButton: "Book Your Treatment Now"
      },

      // Alpine Air Therapy Modal
      alpineAirTherapy: {
        title: "Alpine Air Therapy & Facilities",
        description: "Experience the transformative power of pure Alpine air in our state-of-the-art wellness facilities. Our unique Alpine air therapy combines the healing properties of mountain air with traditional Swiss wellness practices.",
        relaxationAreas: "Relaxation Areas",
        coreFacilities: "Core Facilities",
        uniqueFeatures: "Unique Features",
        benefitsTitle: "Benefits of Alpine Air Therapy",
        pureMountainAir: "Pure Mountain Air",
        pureMountainAirDesc: "Clean, oxygen-rich air at 1,200m altitude enhances respiratory health and boosts energy levels.",
        seasonalTherapy: "Seasonal Therapy",
        seasonalTherapyDesc: "Year-round Alpine air therapy with specialized seasonal treatments and herbal infusions.",
        readyToExperience: "Ready to Experience Alpine Air Therapy?",
        contactConciergeDesc: "Book your experience by contacting our concierge. Our dedicated team will help you schedule your treatments and answer any questions about our spa services. Contact us at the front desk during your stay.",
        contactConciergeButton: "Contact Concierge",
        facilities: {
          panoramicLounge: "Panoramic Relaxation Lounge with floor-to-ceiling windows",
          alpineGarden: "Alpine Garden Terrace for outdoor relaxation",
          swissSauna: "Traditional Swiss Sauna with Alpine herbal infusions",
          steamRoom: "Steam Room featuring locally sourced mountain herbs",
          infraredSauna: "Infrared Sauna for gentle, deep-heat relaxation",
          meditationCorner: "Quiet Meditation Corner with mountain views",
          bioSpa: "Sauna & Bio Spa with Alpine herbal therapies",
          fitnessCenter: "State-of-the-art fitness center with Technogym equipment",
          yogaPavilion: "Dedicated meditation and yoga pavilion",
          wellnessTerrace: "Outdoor wellness terrace for seasonal Alpine air therapy",
          hydrotherapyPools: "Hydrotherapy pools with varying temperatures",
          massageRooms: "Massage & Healing Therapy Rooms",
          winterGarden: "Exclusive winter garden with infrared sauna access",
          mountainSpringWater: "Mountain spring water treatments using pure Alpine water",
          alpineBotanicals: "Local Alpine botanicals in all treatments and products",
          swissDesign: "Traditional Swiss design with modern comfort",
          peptideTherapies: "Swiss-developed peptide therapies",
          healingPractices: "Traditional Swiss healing practices combined with modern wellness science",
          panoramicViews: "Panoramic views of Lake Thun and the Bernese Alps during treatments"
        }
      },

      // Beauty & Wellness Programs Modal
      beautyWellnessPrograms: {
        title: "Beauty & Wellness Programs",
        description: "Discover our comprehensive wellness retreats designed to transform your body, mind, and spirit in the heart of the Swiss Alps. Each program combines cutting-edge Swiss wellness technology with traditional Alpine healing practices.",
        readyToTransform: "Ready to Transform Your Wellness Journey?",
        contactConciergeDesc: "Book your experience by contacting our concierge. Our dedicated team will help you schedule your treatments and answer any questions about our spa services. Contact us at the front desk during your stay.",
        contactConciergeButton: "Contact Concierge",
        categories: {
          detoxRenewal: "Detox & Renewal",
          detoxRenewalDesc: "Comprehensive cleansing programs for cellular renewal and toxin elimination",
          aestheticEnhancement: "Aesthetic Enhancement",
          aestheticEnhancementDesc: "Advanced skin rejuvenation and beauty enhancement treatments",
          personalizedWellness: "Personalized Wellness",
          personalizedWellnessDesc: "Genetic analysis and personalized health optimization programs"
        },
        programs: {
          alpineGlow: {
            title: "Alpine Glow® Retreat",
            duration: "8 Days",
            description: "Discover Alpine Glow: An 8-day retreat offering weight loss, rejuvenation, and wellness through yoga, healthy nutrition, and mindfulness practices. Immerse yourself in the beauty of the Swiss Alps and return with a revitalized spirit."
          },
          renewalRadiance: {
            title: "Renewal Radiance® Detox",
            duration: "10 Days",
            description: "World-class detox program for chemotherapy and cancer patients. With a focus on cellular cleansing and toxin elimination, we offer a 10-day to 3-week stay with detox menus, therapies, and personalized nutrition planning."
          },
          nutraVitality: {
            title: "NutraVitality® Rejuvenation",
            duration: "7 Days",
            description: "Immerse yourself in our 7-day NutraVitality Rejuvenation Retreat for holistic rejuvenation. Experience a blend of cutting-edge science and luxurious comfort to revitalize body, mind, and spirit."
          },
          nutraCleanse: {
            title: "NutraCleanse® Renewal Retreat",
            duration: "7 Days",
            description: "Comprehensive detox and renewal program focusing on cellular cleansing and toxin elimination for optimal wellness and vitality."
          },
          nutraBalance: {
            title: "NutraBalance® Weight Wellness Retreat",
            duration: "7 Days",
            description: "Holistic approach to weight management combining nutrition science, metabolic optimization, and sustainable lifestyle changes."
          },
          nutraRadiance: {
            title: "NutraRadiance® Aesthetic Enhancement",
            duration: "7 Days",
            description: "Advanced aesthetic treatments and skin rejuvenation therapies for natural beauty enhancement and anti-aging results."
          },
          nutraGenomic: {
            title: "NutraGenomic® Insight Retreat",
            duration: "7 Days",
            description: "Personalized wellness program based on genetic analysis and nutritional genomics for optimized health outcomes."
          },
          nutraTotal: {
            title: "NutraTotal® Wellness Assessment",
            duration: "7 Days",
            description: "Comprehensive health evaluation and personalized wellness plan development through advanced diagnostic testing."
          },
          nutraDerm: {
            title: "NutraDerm® Radiance Renewal",
            duration: "7 Days",
            description: "Specialized dermatological treatments and skin renewal therapies for healthy, radiant, and youthful-looking skin."
          },
          nutraFlex: {
            title: "NutraFlex® Mobility Enhancement",
            duration: "7 Days",
            description: "Advanced mobility and flexibility training program combining physical therapy, movement science, and recovery techniques."
          }
        }
      },

      // Therapeutic Treatments Modal
      therapeuticTreatments: {
        title: "Therapeutic Treatments & Services",
        description: "Experience the transformative power of skilled therapeutic touch in our serene Alpine sanctuary. Our certified massage therapists blend ancient healing wisdom with contemporary techniques, using premium Swiss-made products.",
        readyToBook: "Ready to Book Your Therapeutic Experience?",
        contactConciergeDesc: "Book your experience by contacting our concierge. Our dedicated team will help you schedule your treatments and answer any questions about our spa services. Contact us at the front desk during your stay.",
        contactConciergeButton: "Contact Concierge",
        relaxationRejuvenation: {
          title: "Relaxation & Rejuvenation Treatments",
          treatments: {
            alpineMountainMassage: {
              title: "Alpine Mountain Massage",
              price: "CHF 120-180",
              duration: "60-90 minutes",
              description: "A deeply relaxing full-body massage using traditional Swiss techniques and Alpine herbal oils. Perfect after a day of hiking or sightseeing."
            },
            lakeThunAromatherapy: {
              title: "Lake Thun Aromatherapy",
              price: "CHF 100",
              duration: "60 minutes",
              description: "Immerse yourself in the scents of the Swiss Alps with our signature aromatherapy treatment using locally sourced Alpine herbs."
            },
            swissStoneTherapy: {
              title: "Swiss Stone Therapy",
              price: "CHF 140",
              duration: "75 minutes",
              description: "Experience the warmth of heated Swiss stones combined with traditional massage techniques for deep muscle relaxation."
            },
            alpineHerbalSteamBath: {
              title: "Alpine Herbal Steam Bath",
              price: "CHF 80",
              duration: "45 minutes",
              description: "Relax in our traditional steam room infused with Alpine herbs. Perfect for unwinding after exploring the mountains."
            }
          }
        },
        advancedWellness: {
          title: "Advanced Wellness Treatments",
          treatments: {
            bioScan: {
              title: "BioScan",
              price: "CHF 440",
              duration: "60-90 minutes",
              description: "In-depth analysis for optimal well-being using special measurement techniques. Includes personalized food list and homeopathic remedies."
            },
            nutritionCoaching: {
              title: "Nutrition Coaching",
              price: "CHF 240",
              duration: "60 minutes",
              description: "Food as medicine - individually tailored nutritional approaches for your best self, from Mediterranean diet to targeted detox programs."
            },
            lightTherapy: {
              title: "Light Therapy",
              price: "CHF 80",
              duration: "30 minutes",
              description: "Using biophotons, this gentle therapy strengthens the immune system and stimulates the body's own healing powers."
            },
            pelviPowerTraining: {
              title: "PelviPower Training",
              price: "CHF 29",
              duration: "20-30 minutes",
              description: "Innovative method for strengthening core muscles with approximately 25,000 gentle contractions during a session."
            },
            infraredSauna: {
              title: "Infrared Sauna",
              price: "Included",
              description: "Deep heat for deep relaxation - detoxify and relax at the cellular level. Included free with other treatments."
            }
          }
        }
      }
    },
    gymPool: {
      technogymTitle: "TECHNOGYM Fitness Center",
      technogymDesc: "Fully equipped with premium Technogym equipment",
      premiumEquipment: "Premium Equipment",
      premiumEquipmentDesc: "State-of-the-art Technogym machines for cardio, strength training, and functional fitness",
      personalTraining: "Personal Training",
      personalTrainingDesc: "Professional trainers available for personalized workout sessions",
      access24: "24/7 Access",
      access24Desc: "Round-the-clock access for guests to maintain their fitness routine",
      alpinePool: "Alpine Swimming Pool",
      alpinePoolDesc: "Indoor pool with panoramic mountain views",
      heatedPool: "Heated Pool",
      heatedPoolDesc: "Year-round swimming in our temperature-controlled indoor pool",
      alpineViews: "Alpine Views",
      alpineViewsDesc: "Swim while enjoying breathtaking views of the Swiss Alps",
      familyFriendly: "Family Friendly",
      familyFriendlyDesc: "Safe and comfortable environment for guests of all ages",
      availableToGuests: "Available to all resort guests",
      bookYourStay: "Book Your Stay"
    },
    cinema: {
      title: "Private Luxury Cinema Theatre",
      subtitle: "Your Private Cinema & Corporate Presentation Venue",
      seatingCapacity: "Seating Capacity",
      seatingCapacityDesc: "Accommodates up to 50 guests for private screenings and events",
      corporateEvents: "Corporate Events",
      corporateEventsDesc: "Perfect for MICE events, presentations, and team building activities",
      exclusiveExperience: "Exclusive Experience",
      exclusiveExperienceDesc: "The only private cinema with IMAX technology in the Bernese Oberland",
      bookTheatre: "Book Theatre",
      bookYourStay: "Book Your Stay",
      locatedAt: "Located at Beatenberg Resort & Spa"
    },
    events: {
      title: "Events & Celebrations",
      description: "Professional event infrastructure combined with authentic Swiss hospitality. Perfect for corporate meetings, weddings, and special celebrations.",
      seminarRooms: "Seminar Rooms",
      seminarRoomsDesc: "Modern AV technology for corporate meetings and workshops",
      hdProjectors: "HD Projectors",
      videoConferencing: "Video Conferencing",
      wirelessPresentation: "Wireless Presentation",
      breakoutSpaces: "Breakout Spaces",
      learnMore: "Learn More",
      banquetHalls: "Banquet Halls",
      banquetHallsDesc: "Elegant spaces for gala dinners, conferences and private functions",
      fineDiningSetup: "Fine Dining Setup",
      professionalCatering: "Professional Catering",
      flexibleSeating: "Flexible Seating",
      seating200: "Seating for up to 200 pax",
      weddingVenues: "Wedding Venues",
      weddingVenuesDesc: "Panoramic Alpine backdrops with full planning support",
      alpineViews: "Alpine Views",
      weddingPlanning: "Wedding Planning",
      photographySpots: "Photography Spots",
      receptionAreas: "Reception Areas",
      privateParties: "Private Parties",
      privatePartiesDesc: "Celebrations & special occasions",
      groups: "Groups",
      groupsDesc: "Tour groups & gatherings",
      wellnessRetreats: "Wellness Retreats",
      wellnessRetreatsDesc: "Health & wellness programs",
      corporateEvents: "Corporate Events",
      corporateEventsDesc: "Meetings & conferences",
      premiumFeatures: "Premium Event Features",
      digitalLedWall: "Digital LED Wall",
      highSpeedInternet: "High-Speed Internet",
      audioSystems: "Audio Systems",
      eventPlanning: "Event Planning",
      perfectForOccasion: "Perfect for Every Occasion",
      perfectForOccasionDesc: "Our versatile event spaces cater to a wide range of needs and celebrations",
      convenientParking: "Convenient Parking",
      convenientParkingDesc: "On-site capacity for up to 200 cars & buses",
      planYourEvent: "Plan Your Event",
      requestQuote: "Request Quote",
      professionalPlanning: "Professional event planning available",
      // New comprehensive events page translations
      heroTitle: "Creating Unforgettable",
      heroSubtitle: "Moments",
      heroDescription: "Luxury weddings and premium events crafted with precision, passion, and unparalleled attention to detail.",
      planWedding: "Plan Your Wedding",
      corporateEventsButton: "Corporate Events",
      customVenueOffers: "Custom Venue Offers",
      dreamWeddings: "Dream Weddings",
      dreamWeddingsDescription: "Every love story deserves a perfect beginning. We create magical wedding experiences that reflect your unique journey.",
      luxuryCeremonies: "Luxury Ceremonies",
      floralDesign: "Floral Design",
      themeDesign: "Theme Design",
      entertainment: "Entertainment",
      discussWeddingPackages: "Discuss Wedding Packages",
      corporateExcellence: "Corporate Excellence",
      corporateExcellenceDescription: "Professional events that strengthen relationships, inspire teams, and showcase your brand's excellence.",
      conferences: "Conferences",
      productLaunches: "Product Launches",
      teamBuilding: "Team Building",
      annualGalas: "Annual Galas",
      discussCorporatePackages: "Discuss Corporate Packages",
      privatePartiesTitle: "Private Parties",
      privatePartiesDescription: "Celebrate life's special moments with unforgettable parties tailored to your style and vision.",
      birthdayCelebrations: "Birthday Celebrations",
      anniversaries: "Anniversaries",
      luxuryGalas: "Luxury Galas",
      discussPrivatePartyPackages: "Discuss Private Party Packages",
      completeEventExperiences: "Complete Event Experiences",
      completeEventExperiencesDescription: "From intimate gatherings to grand celebrations, we bring your vision to life with meticulous planning and flawless execution.",
      weddingPlanningService: "Wedding Planning",
      weddingPlanningDescription: "Complete wedding coordination from proposal to reception, creating your perfect day with attention to every detail.",
      weddingPlanningFeatures: "Swiss Hospitality, Vendor Coordination, Timeline Management, 24/7 coordinating leading up to event",
      weddingPlanningDetails: "From intimate ceremonies to grand celebrations, we handle every aspect of your special day. Our experienced wedding planners work closely with you to create a personalized experience that reflects your unique love story.",
      weddingPlanningHighlights: "Custom ceremony design and setup, Professional vendor management, Detailed timeline coordination, On-site event supervision",
      corporateEventsService: "Corporate Events",
      corporateEventsServiceDescription: "Professional corporate gatherings that leave lasting impressions on clients and colleagues.",
      corporateEventsServiceFeatures: "Conference Planning, Product Launches, Team Building, Annual Galas",
      corporateEventsServiceDetails: "Elevate your business events with our comprehensive corporate event services. We understand the importance of professional presentation and seamless execution for your business objectives.",
      corporateEventsServiceHighlights: "Executive conference planning, Brand presentation setup, AV equipment and technology, Catering and hospitality",
      privateCelebrations: "Private Celebrations",
      privateCelebrationsDescription: "Intimate gatherings and milestone celebrations designed to reflect your unique style.",
      privateCelebrationsFeatures: "Birthday Parties, Anniversaries, Farewell Parties, Ring Ceremonies, Many more happy occasions",
      privateCelebrationsDetails: "Celebrate life's special moments with personalized events that capture the essence of your occasion. From milestone birthdays to intimate anniversaries, we create memorable experiences.",
      privateCelebrationsHighlights: "Personalized theme development, Custom entertainment booking, Gourmet catering options, Photography and documentation, Guest experience management",
      serviceOverview: "Service Overview",
      keyFeatures: "Key Features",
      whatWeProvide: "What We Provide",
      getStartedWith: "Get Started with",
      premiumEventFeatures: "Premium Event Features",
      fullServiceEventPlanning: "Full-Service Event Planning",
      fullServiceEventPlanningDesc: "Complete event coordination from concept to execution",
      gourmetCateringServices: "Gourmet Catering Services",
      gourmetCateringServicesDesc: "Swiss culinary expertise meets international flavors",
      stateOfTheArtTechnology: "State-of-the-Art Technology",
      stateOfTheArtTechnologyDesc: "Cutting-edge AV equipment and seamless connectivity",
      wellnessIntegration: "Wellness Integration",
      wellnessIntegrationDesc: "Alpine wellness experiences for event participants",
      digitalLedWallFeature: "Digital LED Wall",
      digitalLedWallFeatureDesc: "Flexible layouts for customized events",
      audioSystemsFeature: "Audio Systems",
      audioSystemsFeatureDesc: "Professional sound equipment included",
      eventPlanningFeature: "Event Planning",
      eventPlanningFeatureDesc: "Full-service event coordination",
      premiumAmenities: "Premium Amenities",
      premiumAmenitiesDesc: "Luxury facilities and services",
      convenientParkingTitle: "Convenient Parking",
      convenientParkingCapacity: "On-site capacity for up to 200 cars & buses",
      complimentaryParking: "Complimentary parking for all event guests",
      ourPortfolio: "OUR PORTFOLIO",
      eventGallery: "Event Gallery",
      eventGallerySubtitle: "A glimpse into the extraordinary events we've had the privilege to create",
      whatOurClientsSay: "What Our Clients Say",
      whatOurClientsSayDescription: "The joy and satisfaction of our clients is our greatest achievement.",
      readyToCreate: "Ready to Create Something Extraordinary?",
      readyToCreateDescription: "Let's bring your vision to life. Contact us today for a personalized consultation and discover how we can make your event unforgettable.",
      planningTimeline: "Planning Timeline",
      planningTimelineDesc: "6-18 months in advance",
      eventCapacity: "Event Capacity",
      eventCapacityDesc: "20-500 guests",
      documentation: "Documentation",
      documentationDesc: "Professional photography",
      entertainmentFeature: "Entertainment",
      entertainmentFeatureDesc: "Live music & DJ services",
      // Testimonials
      testimonial1Name: "Sarah & Michael",
      testimonial1Event: "Wedding Reception",
      testimonial1Text: "Our wedding was absolutely perfect. Every detail was handled with such care and professionalism. We couldn't have asked for better!",
      testimonial2Name: "David Chen",
      testimonial2Event: "Corporate Launch",
      testimonial2Text: "The product launch event exceeded all expectations. The attention to detail and flawless execution made our company shine.",
      testimonial3Name: "Jennifer Williams",
      testimonial3Event: "Anniversary Celebration",
      testimonial3Text: "They transformed our vision into reality. The celebration was elegant, sophisticated, and exactly what we dreamed of."
    },
    attractions: {
      title: "Discover the Bernese Oberland",
      description: "Explore Switzerland's most popular attractions, ranked by visitor popularity. From the iconic Jungfraujoch to hidden Alpine gems.",
      niederhorn: {
        name: "Niederhorn Peak",
        description: "Experience breathtaking panoramic views of Lake Thun, the Bernese Alps, and the Eiger, Mönch, and Jungfrau peaks. Accessible via cable car from Beatenberg, this peak offers hiking trails, a restaurant, and stunning photo opportunities.",
        shortDescription: "Panoramic views of Lake Thun and the Bernese Alps",
        features: ["Cable car access", "Hiking trails", "Panoramic views", "Restaurant", "Photography spots"],
        guestBenefits: ["10% discount on cable car tickets", "Free parking at cable car station", "Complimentary trail map", "Weather updates via hotel app"],
        distance: "15 min drive",
        duration: "2-4 hours exploration"
      },
      "st-beatus-caves": {
        name: "St. Beatus Caves",
        description: "Among the largest cave systems in Switzerland, these limestone caves feature stunning stalactites, underground lakes, and fascinating geological formations. Named after Saint Beatus, these caves have been a site of pilgrimage for centuries.",
        shortDescription: "Largest cave system in Switzerland with underground lakes",
        features: ["Guided tours", "Underground lake", "Stalactites", "Geological formations", "Museum"],
        guestBenefits: ["Priority booking", "Free audio guide", "Restaurant discount", "Hotel shuttle available"],
        distance: "25 min drive",
        duration: "1-2 hours cave tour"
      },
      "harder-kulm": {
        name: "Harder Kulm",
        description: "Reached by historic funicular railway, Harder Kulm offers spectacular views of Interlaken, Lake Thun, and Lake Brienz. The summit features walking paths, a restaurant, and the famous Harder Kulm viewpoint.",
        shortDescription: "Historic funicular to panoramic viewpoints",
        features: ["Funicular railway", "Panoramic views", "Walking paths", "Restaurant", "Viewpoint terrace"],
        guestBenefits: ["15% off funicular tickets", "Free hotel shuttle", "Restaurant reservation priority", "Weather updates"],
        distance: "20 min drive",
        duration: "2-3 hours visit"
      },
      "jungfraujoch": {
        name: "Jungfraujoch - Top of Europe",
        description: "The highest railway station in Europe at 3,454m, offering views of the Aletsch Glacier and the surrounding peaks. Features the Ice Palace, Sphinx Observatory, and breathtaking alpine scenery.",
        shortDescription: "Highest railway station in Europe with Ice Palace",
        features: ["Highest railway station", "Ice Palace", "Sphinx Observatory", "Aletsch Glacier views", "Restaurant"],
        guestBenefits: ["Complimentary shuttle service", "Priority booking", "Restaurant discount", "Weather updates"],
        distance: "45 min drive",
        duration: "Half-day excursion"
      },
      "thun-castle": {
        name: "Thun Castle",
        description: "Iconic medieval castle overlooking the city of Thun and Lake Thun. Features a museum with regional history, beautiful gardens, and panoramic views of the surrounding mountains.",
        shortDescription: "Medieval castle with museum and lake views",
        features: ["Medieval architecture", "Regional museum", "Beautiful gardens", "Panoramic views", "Historical exhibits"],
        guestBenefits: ["Free parking", "Audio guide included", "Garden access", "Photo permit included"],
        distance: "30 min drive",
        duration: "1-2 hours visit"
      },
      "spiez-castle": {
        name: "Spiez Castle",
        description: "Romanesque church and picturesque location on Lake Thun. Features a historic wine cellar, beautiful gardens, and stunning lake views. One of Switzerland's most photographed castles.",
        shortDescription: "Romanesque castle with wine cellar and lake views",
        features: ["Romanesque architecture", "Wine cellar", "Lake views", "Beautiful gardens", "Historical significance"],
        guestBenefits: ["Wine tasting discount", "Garden tour included", "Photography spots", "Free parking"],
        distance: "35 min drive",
        duration: "1-2 hours visit"
      },
      "schynige-platte": {
        name: "Schynige Platte",
        description: "Historic cogwheel train takes you to an alpine botanical garden at 2,000m. Features 650+ plant species, spectacular views of the Jungfrau region, and walking trails.",
        shortDescription: "Historic cogwheel train to alpine botanical garden",
        features: ["Historic cogwheel train", "Alpine botanical garden", "Panoramic views", "Walking trails", "Restaurant"],
        guestBenefits: ["20% off train tickets", "Free botanical garden guide", "Priority reservations", "Hotel shuttle service"],
        distance: "25 min drive",
        duration: "3-4 hours excursion"
      },
      "trummelebach-falls": {
        name: "Trümmelbach Falls",
        description: "UNESCO-recognized natural wonder featuring 10 glacier-fed waterfalls inside the mountain. Europe's largest subterranean waterfall system.",
        shortDescription: "10 glacier-fed waterfalls inside the mountain",
        features: ["Underground waterfalls", "Guided tours", "Elevator access", "UNESCO site", "Restaurant"],
        guestBenefits: ["Priority booking", "Audio guide included", "Restaurant discount", "Free parking"],
        distance: "30 min drive",
        duration: "1-2 hours visit"
      },
      "giessbach-falls": {
        name: "Giessbach Falls",
        description: "14 waterfalls cascading 500m down the mountain into Lake Brienz. Features historic Grand Hotel and scenic walking paths.",
        shortDescription: "14 waterfalls cascading 500m into Lake Brienz",
        features: ["14 waterfalls", "Historic Grand Hotel", "Walking paths", "Boat trips", "Panoramic views"],
        guestBenefits: ["Boat trip discount", "Hotel shuttle service", "Priority reservations", "Free parking"],
        distance: "40 min drive",
        duration: "1-2 hours visit"
      },
      "lake-thun-brienz": {
        name: "Lake Thun & Lake Brienz",
        description: "Two pristine alpine lakes offering boat cruises, water sports, and scenic villages. Lake Thun features dramatic mountain backdrops while Lake Brienz offers peaceful waters.",
        shortDescription: "Two pristine alpine lakes with boat cruises",
        features: ["Boat cruises", "Water sports", "Swimming areas", "Scenic villages", "Fishing"],
        guestBenefits: ["Boat cruise discount", "Water sports equipment rental", "Priority booking", "Free shuttle"],
        distance: "20 min drive",
        duration: "2-4 hours cruise"
      },
      "adventure-park-interlaken": {
        name: "Interlaken Ropes Park / Seilpark",
        description: "Thrilling adventure park with zip lines, rope courses, climbing walls, and team challenges. Perfect for families and adventure seekers.",
        shortDescription: "Zip lines, rope courses, and adventure activities",
        features: ["Zip lines", "Rope courses", "Climbing walls", "Team challenges", "Family activities"],
        guestBenefits: ["Priority booking", "Group discount", "Equipment included", "Hotel shuttle"],
        distance: "25 min drive",
        duration: "2-3 hours activity"
      },
      "paragliding": {
        name: "Paragliding",
        description: "Tandem paragliding flights from scenic launch sites offering breathtaking aerial views of the Jungfrau region and alpine valleys.",
        shortDescription: "Tandem paragliding with alpine views",
        features: ["Tandem flights", "Certified pilots", "Stunning views", "Photo service", "Safety equipment"],
        guestBenefits: ["Priority booking", "Photo/video package discount", "Hotel pickup", "Weather updates"],
        distance: "15 min drive",
        duration: "2-3 hours experience"
      },
      "cross-country-skiing": {
        name: "Cross-Country Skiing",
        description: "Groomed cross-country skiing trails around Beatenberg and Habkern. Perfect for all skill levels with stunning alpine scenery.",
        shortDescription: "Groomed trails for cross-country skiing",
        features: ["Groomed trails", "All skill levels", "Scenic routes", "Equipment rental", "Lessons available"],
        guestBenefits: ["Free trail access", "Equipment rental discount", "Lessons included", "Hotel shuttle"],
        distance: "5 min drive",
        duration: "2-4 hours skiing"
      },
      "beatenberg-panorama-trail": {
        name: "Panoramic Trails Around Beatenberg",
        description: "Scenic walking trail offering panoramic views of Lake Thun and the Bernese Alps. Easy to moderate paths with educational signposts.",
        shortDescription: "Scenic trail with Lake Thun panoramic views",
        features: ["Panoramic views", "Educational signposts", "Easy paths", "Wildlife spotting", "Photography spots"],
        guestBenefits: ["Free access", "Trail map at hotel", "Guided tours available", "Rest areas"],
        distance: "Walking distance",
        duration: "1-3 hours hiking"
      },
      "saxeten-village": {
        name: "Saxeten Village",
        description: "Hidden mountain village featuring traditional chalets, alpine meadows, and authentic Swiss mountain culture. Perfect for experiencing rural Switzerland.",
        shortDescription: "Traditional mountain village with chalets",
        features: ["Traditional chalets", "Mountain culture", "Alpine meadows", "Local crafts", "Peaceful atmosphere"],
        guestBenefits: ["Local guide included", "Traditional meal experience", "Photography permits", "Hotel shuttle"],
        distance: "20 min drive",
        duration: "2-3 hours visit"
      }
    },
    localAttractions: {
      hero: {
        title: "Discover Alpine Adventures",
        subtitle: "Explore the breathtaking beauty of the Bernese Oberland",
        bookStay: "Book Your Stay Now"
      },
      quickAccess: {
        title: "Explore Local Attractions",
        description: "Discover the rich cultural heritage, natural beauty, and adventure opportunities in the Bernese Oberland region, all within easy reach of Beatenberg Resort & Spa.",
        allAttractions: "All Attractions",
        within30min: "Within 30min",
        natureHiking: "Nature & Hiking",
        cultureHistory: "Culture & History",
        activities: "Activities",
        allAttractionsDesc: "Complete guide to all local attractions",
        within30minDesc: "Easily accessible attractions",
        natureHikingDesc: "Mountain trails and natural wonders",
        cultureHistoryDesc: "Historical sites and cultural experiences",
        activitiesDesc: "Adventure and recreational activities",
        learnMore: "Learn More",
        noAttractionsFound: "No attractions found in this category."
      },
      seasonal: {
        title: "Seasonal Activities",
        description: "Experience the best of each season with our curated selection of activities, from summer hiking to winter skiing adventures.",
        summer: "Summer",
        winter: "Winter",
        yearRound: "Year-round",
        alpineHiking: "Alpine Hiking Trails",
        alpineHikingDesc: "Discover over 200km of marked hiking trails with varying difficulty levels, from gentle lakeside walks to challenging mountain ascents.",
        alpineHikingActivities: ["Guided hiking tours", "Mountain peak ascents", "Lake shore walks", "Wildflower identification"],
        skiSnowboard: "Ski & Snowboard Paradise",
        skiSnowboardDesc: "Access world-class ski resorts within 30 minutes, including Grindelwald, Wengen, and Mürren with our complimentary shuttle service.",
        skiSnowboardActivities: ["Ski resort access", "Snowboard parks", "Cross-country skiing", "Snowshoe expeditions"],
        lakeWaterSports: "Lake Thun Water Sports",
        lakeWaterSportsDesc: "Experience crystal-clear waters with kayaking, paddleboarding, sailing, and scenic boat cruises on Switzerland's beautiful Lake Thun.",
        lakeWaterSportsActivities: ["Kayaking & canoeing", "Stand-up paddleboarding", "Sailing lessons", "Boat cruises"],
        photography: "Photography Expeditions",
        photographyDesc: "Capture stunning Alpine landscapes and hidden gems with professional photography tours and workshops led by local experts.",
        photographyActivities: ["Landscape photography", "Wildlife photography", "Sunrise/sunset shoots", "Photo workshops"],
        culinary: "Swiss Culinary Experiences",
        culinaryDesc: "Immerse yourself in Swiss culinary traditions with cooking classes, wine tastings, and visits to local producers and artisans.",
        culinaryActivities: ["Cooking classes", "Wine tastings", "Cheese making", "Farm visits"],
        mountainAdventures: "Mountain Adventures",
        mountainAdventuresDesc: "From paragliding to rock climbing, experience adrenaline-pumping activities in one of the most spectacular mountain settings in Europe.",
        mountainAdventuresActivities: ["Paragliding", "Rock climbing", "Via ferrata", "Canyoning"],
        activitiesInclude: "Activities include:",
        bookYourStay: "Book Your Stay"
      },
      guestBenefits: {
        title: "Guest Card Benefits",
        description: "As a guest of Beatenberg Resort & Spa, enjoy exclusive benefits and discounts at local attractions and services in the Beatenberg and Habkern region.",
        freeBenefits: "Free Benefits",
        discountBenefits: "Discount Benefits",
        howToGet: "How to Get Your Guest Card",
        howToGetDesc: "Your guest card is automatically included with your stay at Beatenberg Resort & Spa. Pick it up at reception upon check-in and start enjoying exclusive benefits immediately.",
        validDuringStay: "Valid during your stay • Regional partnerships included",
        partner: "Partner",
        benefits: {
          freeTransport: {
            title: "Free Public Transport",
            description: "Free travel on scheduled public transport within designated zones around Beatenberg and Habkern"
          },
          libraryAccess: {
            title: "Library Access",
            description: "Free borrowing of books, DVDs, and CDs from the Beatenberg Library"
          },
          eBikeDiscount: {
            title: "E-bike Rental",
            description: "50% off e-bike rentals at Beatenberg Tourism"
          },
          miniGolfDiscount: {
            title: "Mini Golf",
            description: "50% off mini-golf at Hotel Dorint Blüemlisalp"
          },
          schynigePlatteDiscount: {
            title: "Schynige Platte Railway",
            description: "20% off trips on the Schynige Platte Railway"
          },
          niesenbahnDiscount: {
            title: "Niesenbahn Funicular",
            description: "20% off tickets on the Niesenbahn funicular railway"
          },
          harderKulmDiscount: {
            title: "Harder Kulm Funicular",
            description: "15% off trips on the Harder Kulm funicular railway"
          },
          escapeRoomDiscount: {
            title: "Escape Rooms",
            description: "15% off Escape Rooms and Mystery Tours in Interlaken"
          },
          niederhornDiscount: {
            title: "Niederhorn Cable Car",
            description: "20% off trips on the Niederhorn gondola cable car"
          },
          swimmingPoolAccess: {
            title: "Habkern Swimming Pool",
            description: "50% off admission to the Habkern swimming pool"
          },
          busAlpinService: {
            title: "Bus Alpin Service",
            description: "50% off Bus Alpin services when staying at least two nights"
          },
          gameBorrowing: {
            title: "Game Library",
            description: "Free borrowing of games from Beatenberg Tourism"
          }
        }
      },
      transportation: {
        title: "Transportation & Accessibility",
        description: "Getting around the Bernese Oberland is easy with excellent public transport, hotel shuttles, and scenic routes connecting you to all major attractions.",
        publicTransport: "Public Transportation",
        keyRoutes: "Key Routes from Beatenberg Resort & Spa",
        destination: "Destination",
        method: "Method",
        duration: "Duration",
        frequency: "Frequency",
        freeWithGuestCard: "Free with Guest Card",
        freeWithGuestCardDesc: "Enjoy free public transportation within designated zones around Beatenberg and Habkern with your Beatenberg Resort & Spa guest card.",
        hotelShuttles: "Hotel Shuttle Services",
        bookingInfo: "Booking Information",
        bookingInfoDesc: "Shuttle services can be booked at reception or by calling our concierge. Advance booking is recommended for airport transfers and special occasions.",
        concierge: "Concierge: +41 33 841 00 21",
        drivingDirections: "Driving Directions & Parking",
        fromZurich: "From Zurich Airport (2.5 hours)",
        fromGeneva: "From Geneva Airport (3 hours)",
        parkingAtHotel: "Parking at Hotel",
        parkingAtHotelDesc: "Free parking is available for all guests. Our parking area offers scenic views and is conveniently located near the main entrance.",
        accessibility: "Accessibility Information",
        wheelchairAccessible: "Wheelchair Accessible Attractions",
        mobilityServices: "Mobility Services",
        contactConcierge: "Contact our concierge",
        contactConciergeDesc: "for assistance planning accessible itineraries and arranging specialized transportation. We're committed to ensuring all guests can enjoy the Bernese Oberland experience.",
        accessibilityAttractions: [
          "Niederhorn Peak (accessible via cable car)",
          "St. Beatus Caves (main areas wheelchair accessible)",
          "Thun Castle (ground floor accessible)",
          "Spiez Castle gardens (wheelchair friendly paths)",
          "Most bus and train stations in the region"
        ],
        mobilityServicesList: [
          "Wheelchair rental available at hotel",
          "Accessible taxi services can be arranged",
          "Most cable cars and funiculars are wheelchair accessible",
          "Hotel shuttle vehicles equipped for wheelchairs"
        ],
        drivingDirectionsZurich: [
          "Follow A1 motorway towards Bern",
          "Take exit 41 (Thun Süd) towards Thun",
          "Follow E27 towards Interlaken",
          "Take exit Beatenberg/Habkern",
          "Follow signs to Beatenberg Resort & Spa (approximately 10 minutes)"
        ],
        drivingDirectionsGeneva: [
          "Take A1 motorway towards Lausanne",
          "Continue on A9 towards Montreux",
          "Take exit 29 (Aigle) and follow signs to Interlaken",
          "Follow E27 towards Interlaken",
          "Take exit Beatenberg/Habkern"
        ],
        frequencies: {
          every30min: "Every 30 min",
          everyHour: "Every hour",
          every2Hours: "Every 2 hours",
          asNeeded: "As needed",
          onRequest: "On request",
          onDemand: "On demand",
          scheduled: "Scheduled",
          daily24_7: "24/7 booking"
        },
        shuttleServices: {
          skiShuttle: {
            service: "Ski Shuttle",
            destinations: "Jungfrau Region ski resorts",
            schedule: "Daily 8:00 AM - 5:00 PM"
          },
          airportTransfer: {
            service: "Airport Transfer",
            destinations: "Bern/Basel/Zurich airports",
            schedule: "On request"
          },
          cityTransfer: {
            service: "City Transfer",
            destinations: "Interlaken, Thun, Spiez",
            schedule: "Daily 7:00 AM - 10:00 PM"
          },
          attractionShuttle: {
            service: "Attraction Shuttle",
            destinations: "Major attractions within 30min",
            schedule: "Daily 9:00 AM - 6:00 PM"
          }
        }
      },
      cta: {
        title: "Ready to Explore?",
        description: "Book your stay at Beatenberg Resort & Spa and discover the magic of the Bernese Oberland. Your adventure awaits with world-class accommodations, exceptional dining, and unforgettable experiences.",
        bookYourStay: "Book Your Stay",
        speakToExpert: "Speak to Expert",
        bookYourStayDesc: "Reserve your room with exclusive rates and guest card benefits included.",
        speakToExpertDesc: "Our concierge team can help plan your perfect Bernese Oberland itinerary.",
        questions: "Questions? Call our reservations team at"
      }
    },
    guestBenefits: {
      freeTransport: {
        title: "Free Public Transport",
        description: "Free travel on scheduled public transport within designated zones around Beatenberg and Habkern"
      },
      libraryAccess: {
        title: "Library Access",
        description: "Free borrowing of books, DVDs, and CDs from the Beatenberg Library"
      },
      eBikeDiscount: {
        title: "E-bike Rental",
        description: "50% off e-bike rentals at Beatenberg Tourism"
      },
      miniGolfDiscount: {
        title: "Mini Golf",
        description: "50% off mini-golf at Hotel Dorint Blüemlisalp"
      },
      schynigePlatteDiscount: {
        title: "Schynige Platte Railway",
        description: "20% off trips on the Schynige Platte Railway"
      },
      niesenbahnDiscount: {
        title: "Niesenbahn Funicular",
        description: "20% off tickets on the Niesenbahn funicular railway"
      },
      harderKulmDiscount: {
        title: "Harder Kulm Funicular",
        description: "15% off trips on the Harder Kulm funicular railway"
      },
      escapeRoomDiscount: {
        title: "Escape Rooms",
        description: "15% off Escape Rooms and Mystery Tours in Interlaken"
      },
      niederhornDiscount: {
        title: "Niederhorn Cable Car",
        description: "20% off trips on the Niederhorn gondola cable car"
      },
      swimmingPoolAccess: {
        title: "Habkern Swimming Pool",
        description: "50% off admission to the Habkern swimming pool"
      },
      busAlpinService: {
        title: "Bus Alpin Service",
        description: "50% off Bus Alpin services when staying at least two nights"
      },
      gameBorrowing: {
        title: "Game Library",
        description: "Free borrowing of games from Beatenberg Tourism"
      }
    },
    moments: {
      title: "Moments of Joy at Beatenberg Resort & Spa",
      description: "Discover the beautiful moments and experiences that make every stay at our resort truly special",
      showNewMoments: "Show New Moments"
    },
    cta: {
      title: "Ready to Create Your Perfect Alpine Escape?",
      description: "Experience the magic of Swiss Alpine luxury, world-class wellness programs, and breathtaking natural beauty at Beatenberg Resort & Spa.",
      bookYourStay: "Book Your Stay",
      contactConcierge: "Contact Our Concierge",
      happyGuests: "Happy Guests",
      trustedByGuests: "Trusted by guests from around the world",
      certifiedExcellence: "Certified Excellence",
      certifiedExcellenceDesc: "Recognized by Switzerland's leading hospitality organizations",
      gastrosuisseRated: "Gastrosuisse Rated",
      swissHotelAssociation: "Swiss Hotel Association",
      swisstainableCommitted: "Swisstainable Committed",
      qualityAssurance: "Quality Assurance",
      startReservation: "Start Your Reservation",
      contactTeam: "Contact Our Team"
    },

    ratings: {
      hotel: {
        reviews: "reviews"
      },
      restaurant: {
        reviews: "reviews"
      }
    },
    contactModals: {
      spa: {
        title: "Contact Our Spa Team",
        subtitle: "Let us create your perfect wellness experience",
        phoneTitle: "Spa Direct Line",
        phoneDesc: "Speak with our spa specialists",
        emailTitle: "Spa Email",
        emailDesc: "Send us your treatment preferences",
        locationTitle: "Spa Location",
        locationDesc: "View the spa inside Beatenberg Resort & Spa on Google Maps",
        bookingTitle: "Online Booking",
        bookingDesc: "Book your treatments instantly",
        whatsappTitle: "WhatsApp Spa",
        whatsappDesc: "Quick spa consultation via WhatsApp",
        servicesTitle: "Our Spa Services",
        servicesList: [
          "Alpine Air Therapy treatments and wellness programs",
          "Therapeutic treatments and massage therapies",
          "Beauty treatments and wellness consultations",
          "Quick relaxation sessions and stress relief",
          "Private spa experiences and couple treatments",
          "Custom wellness packages and retreat programs",
          "Pre and post-treatment consultations"
        ],
        bookButton: "Book Spa Treatment",
        closeButton: "Close"
      },
      theatre: {
        title: "Contact Us About Our Theatre",
        subtitle: "Let us help you plan your event",
        phoneTitle: "Call Us",
        phoneDesc: "Talk to our events team",
        emailTitle: "Email Us",
        emailDesc: "Get in touch about your event",
        bookingTitle: "Event Planning Consultation",
        bookingDesc: "Schedule a consultation with our event planning team",
        whatsappTitle: "WhatsApp Us",
        whatsappDesc: "Chat with us on WhatsApp",
        servicesTitle: "Our Theatre Services",
        servicesList: [
          "Private IMAX theatre bookings for groups and events",
          "Corporate presentations and business meetings",
          "Private movie screenings and entertainment events",
          "Wedding receptions and special celebrations",
          "Educational presentations and training sessions",
          "Custom event planning and catering services",
          "Technical support and audiovisual equipment"
        ],
        featuresTitle: "Theatre Features",
        features: {
          capacity: "Up to 50 guests",
          screen: "Large format IMAX screen",
          sound: "Premium surround sound system",
          seating: "Comfortable tiered seating",
          lighting: "Professional event lighting",
          climate: "Climate-controlled environment"
        },
        bookButton: "Schedule Consultation",
        closeButton: "Close"
      },
      concierge: {
        title: "Contact Our Concierge",
        subtitle: "Let us help you plan your perfect Alpine experience",
        phoneTitle: "Phone",
        phoneDesc: "Speak directly with our concierge team",
        emailTitle: "Email",
        emailDesc: "Send us your questions or requests",
        locationTitle: "Location",
        locationDesc: "Visit us at our stunning Alpine location",
        whatsappTitle: "WhatsApp",
        whatsappDesc: "Instant messaging for quick assistance",
        servicesTitle: "What can our concierge help you with?",
        servicesList: [
          "Restaurant reservations and dining recommendations",
          "Activity bookings and local attraction tickets",
          "Transportation arrangements and airport transfers",
          "Spa treatments and wellness program bookings",
          "Private theatre bookings and corporate presentations",
          "Special occasion planning and celebrations",
          "Local area information and hiking trail guidance"
        ],
        bookButton: "Book Your Stay",
        closeButton: "Close"
      }
    },
    testimonials: {
      title: "Guest Experiences",
      description: "Recent highlights from Booking.com and Google Reviews",
      testimonial1: {
        quote: "Aarvee, our host, was excellent with recommendations and sights to see. The accommodation was perfect—clean, neat and comfortable. Breakfast was awesome with a full range of options. Cons? Leaving!",
        name: "Rayleen",
        country: "Booking.com"
      },
      testimonial2: {
        quote: "An atmosphere of breathtaking scenery and impeccable hospitality. Elegance and comfort at every moment—this place in the Jungfrau region is truly magical. I’d give it 5 stars and come back in a heartbeat.",
        name: "Mark",
        country: "Booking.com"
      },
      testimonial3: {
        quote: "Absolutely gracious staff. Anna was especially helpful in every way. Historic charm with all modern amenities—and the sunset view is unforgettable.",
        name: "Bo Dean",
        country: "Google Reviews"
      },
      testimonial4: {
        quote: "A hidden gem in the Interlaken area! Peaceful garden and an epic terrace view. Booked a massage after hiking and felt like new.",
        name: "Agustin Moyano",
        country: "Google Reviews"
      }
    },
    floatingFeatures: {
      freeWifi: {
        title: "Free WiFi",
        description: "Complimentary high-speed internet throughout your stay"
      },
      flatScreenTv: {
        title: "Flat Screen TV",
        description: "Modern smart TV with international channels and streaming"
      },
      inRoomSafe: {
        title: "In-Room Safe",
        description: "Secure storage for your valuables and travel documents"
      },
      alpineViews: {
        title: "Alpine Views",
        description: "Panoramic views of Lake Thun, Eiger, Mönch, Jungfrau, and Beatenberg village"
      },
      ensuiteBathroom: {
        title: "Ensuite Bathroom",
        description: "Private bathroom with modern fixtures and amenities"
      },
      luxuryBedding: {
        title: "Luxury Bedding",
        description: "Premium organic cotton bedding for ultimate comfort"
      },
      rusticPineInteriors: {
        title: "Rustic Pine Interiors",
        description: "Locally sourced pine wood, naturally finished with Swiss craftsmanship"
      },
      seasonalTemperatures: {
        title: "Seasonal Temperatures",
        description: "Summer ~ 10 to 22°C, Winter ~ −5 to 5°C"
      },
      ensuiteEspressoMachine: {
        title: "Ensuite Espresso Machine",
        description: "Premium espresso machine in Family and Junior Suites for your morning coffee"
      }
    },
    footer: {
      contact: "Contact",
      location: "Location",
      quickLinks: "Quick Links",
      organization: "NutraSwiss Organization",
      allRightsReserved: "© 2025 Beatenberg Resort & Spa | All Rights Reserved",
      hotelDescription: "A 4★ Deluxe Heritage Resort in Europe's longest village, Beatenberg, offering panoramic Alpine views, integrated NutraSwiss wellness, and authentic Swiss hospitality above Interlaken.",
      roomsSuites: "Rooms & Suites",
      wellnessSpa: "Wellness & Spa",
      dining: "Dining",
      experiences: "Experiences",
      eventsWeddings: "Events & Weddings",
      aboutUs: "About Us",
      contactLink: "Contact",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      cookies: "Cookies",
      hotelRegistration: "Hotel Registration: CHE-123.456.789 | Tourism Board License: BE-2024-HTL-001 | GASTROSUISSE Member ID: GS-4567",
      nutraSwissDescription: "Holistic Wellness Solutions",
      cliniqueNutraSwissDescription: "Wellness & Medical Spa",
      nahrinDescription: "Natural Health Products",
      laMaisonPondicherryDescription: "Heritage Hospitality",
      stBeatusCollegeDescription: "Educational Excellence",
      restaurantJungfrauDescription: "Alpine Gastronomy"
    },
    notFound: {
      title: "Lost in the mountains",
      description:
        "The page you are looking for does not exist. It may have been moved, removed, or you might have mistyped the address.",
      ctaHome: "Return home",
      ctaSpa: "Explore spa"
    },
    activities: {
      hero: {
        title: "BEATENBERG DAY GUEST EXPERIENCE",
        subtitle: "Play. Relax. Dine. Experience.",
        locatedAt: "Located at Beatenberg Resort & Spa"
      },
      grid: {
        everythingUnderOneRoof: "EVERYTHING UNDER ONE ROOF!",
        viewDetails: "CLICK FOR DETAILS",
        bookNow: "CLICK TO BOOK",
        bookDayPass: "Book Day Pass",
        includedInDayPass: "Included in Day Pass",
        viewAllActivities: "View All Activities",
        showOneAtATime: "Show One at a Time"
      },
      items: {
        bistro_dine_in: {
          title: "Carbon Mountain Cafe",
          subtitle: "Dine-In & Takeaway",
          description: "Gourmet Bites in a Relaxed Setting or Fresh Food to Go"
        },
        bistro_takeaway: {
          title: "Carbon Mountain Cafe - Takeaway",
          subtitle: "Quick & Fresh",
          description: "Freshly Prepared Food to Go"
        },
        birthdays: {
          title: "Birthdays & Private Parties",
          subtitle: "Celebrate with Us",
          description: "The Perfect Venue for Your Special Moments"
        },
        sauna: {
          title: "Sauna & thermal wellness",
          subtitle: "DETOX & REVIVE",
          description: "Swiss Pine Heat"
        },
        spa: {
          title: "NutraSwiss Spa",
          subtitle: "MASSAGE & THERAPY",
          description: "Relax & Recharge"
        },
        dining: {
          title: "Restaurant Jungfrau",
          subtitle: "FINE DINING",
          description: "Gourmet Cuisine"
        },
        cinema: {
          title: "Beatenberg Cinema",
          subtitle: "PRIVATE SCREENINGS",
          description: "Book Your Movie or Sports Event"
        },
        billiards: {
          title: "Billiards Room",
          subtitle: "Fun & Games",
          description: "Pool & Snooker"
        },
        pool: {
          title: "Outdoor Heated Pool",
          subtitle: "SWIM WITH A VIEW",
          description: "Year-Round Alpine Swimming"
        },
        sports: {
          title: "Sports Facilities",
          subtitle: "ACTIVE ALPINE LIVING",
          description: "Tennis Courts & Football Field"
        }
      },
      comingSoon: "Coming Soon",
      modal: {
        bookTitle: "Book Your Experience",
        bookDesc: "To reserve this facility or for private event inquiries, please contact our Concierge:",
        callNow: "Call Now",
        whatsapp: "WhatsApp Support",
        close: "Close",
        inquireNow: "Inquire Now",
        bookDayPass: "Book Day Pass",
        contactSupport: "Contact Support",
        phone: "Phone",
        email: "Email",
        whatsappLabel: "WhatsApp",
        dining: {
          description: "Experience fine dining at its finest in Restaurant Jungfrau, where gourmet cuisine meets breathtaking alpine views. Our award-winning restaurant offers an elegant dining experience with a focus on Swiss and international culinary excellence.",
          description2: "Enjoy carefully crafted dishes made with locally sourced ingredients, paired with an extensive wine selection. Our talented chefs create seasonal menus that celebrate the flavors of the Bernese Oberland.",
          description3: "Whether you're celebrating a special occasion, enjoying a romantic dinner, or simply treating yourself to an exceptional meal, Restaurant Jungfrau provides an unforgettable fine dining experience in the heart of the Swiss Alps.",
          dayPassValue: "Restaurant Jungfrau dining is included in the Day Guest Pass. Your Day Pass includes a five-course gourmet meal, allowing you to experience our full fine dining menu. Book your Day Pass to enjoy the complete culinary experience and all resort amenities.",
          learnMore: "View full menu and dining details"
        },
        sauna: {
          description: "Rejuvenate your body and mind in our authentic Swiss sauna and thermal wellness facilities. Experience the traditional benefits of heat therapy in a serene alpine setting.",
          description2: "Our sauna package includes access to four distinct thermal facilities, each offering unique benefits for detoxification, relaxation, and overall well-being. The Swiss pine heat creates a natural, aromatic environment that soothes both body and spirit.",
          description3: "Whether you're looking to unwind after a day of activities, improve circulation, or simply enjoy a moment of tranquility, our sauna and thermal wellness facilities offer the perfect escape.",
          dayPassValue: "The complete Sauna & Thermal Wellness package is included in the Day Guest Pass. Your Day Pass includes access to all four sauna facilities (Finnish Sauna, Bio Sauna, Steam Bath, and Ice Plunge Pool), plus complimentary coffee, tea, and detox drinks. Book your Day Pass to enjoy the full sauna experience and all resort amenities.",
          learnMore: "Explore our full wellness offerings"
        },
        bistro_dine_in: {
          description: "Enjoy casual alpine dining at Carbon Mountain Cafe, where fresh, locally-inspired cuisine meets a relaxed, welcoming atmosphere. Perfect for a quick bite, leisurely meal, or takeaway options.",
          description2: "Our cafe offers a diverse menu featuring gourmet bites, fresh salads, sandwiches, pizzas, and daily specials. Whether you're dining in with stunning mountain views or grabbing food to go, we provide quality food in a comfortable setting.",
          description3: "Ideal for families, groups, or solo travelers looking for delicious food without the formality. Our friendly service and flexible dining options make Carbon Mountain Cafe the perfect choice for any time of day.",
          dayPassValue: "Carbon Mountain Cafe dining is included in the Day Guest Pass. Your Day Pass includes one free non-alcoholic drink or hot beverage (Heissgetränk) and one free meal item (sandwich, salad, or pizza). Book your Day Pass to enjoy cafe dining, takeaway options, and all resort amenities.",
          learnMore: "View dine-in menu",
          learnMoreTakeaway: "Visit Takeaway Website"
        },
        spa: {
          description: "Indulge in premium spa treatments at NutraSwiss Spa, where advanced wellness therapies meet Swiss precision and care. Experience personalized massage and therapy services designed to restore balance and vitality.",
          description2: "Our expert therapists offer a range of treatments including relaxing massages, therapeutic bodywork, and specialized wellness therapies. Each treatment is tailored to your individual needs, using premium products and techniques.",
          description3: "Whether you're seeking relief from tension, deep relaxation, or a rejuvenating spa experience, NutraSwiss Spa provides professional care in a tranquil, luxurious environment.",
          dayPassValue: "NutraSwiss Spa treatments are included in the Day Guest Pass. Your Day Pass includes a complementary 15-minute massage, allowing you to experience our professional spa services. When booking your Day Pass, you'll have the option to upgrade to our signature treatments for a more comprehensive wellness experience. Book your Day Pass to enjoy spa services and all resort amenities.",
          learnMore: "View full spa menu and treatments"
        },
        cinema: {
          description: "Experience sports events and presentations on our state-of-the-art large screen cinema, the only private cinema with IMAX technology in the Bernese Oberland.",
          description2: "Perfect for watching live sports events, hosting presentations, corporate screenings, or special event viewings. Enjoy crystal-clear visuals and immersive sound on our premium large screen.",
          description3: "Movie screenings will be available soon. Currently, our cinema is ideal for sports viewing, business presentations, and special events. Whether you're watching the big game, hosting a corporate presentation, or planning a special event screening, our cinema offers a premium viewing experience.",
          dayPassValue: "Cinema access is included in the Day Guest Pass as a shared space. You can enjoy watching sports events and presentations alongside other Day Pass guests. Book your Day Pass to enjoy cinema access and all resort amenities.",
          dayPassValuePrivate: "Private Cinema Booking: If you wish to reserve the entire cinema room exclusively for your group or event, please contact us separately to arrange a private booking.",
          learnMore: "Learn more about our IMAX Theatre"
        },
        billiards: {
          description: "Unwind and enjoy friendly competition in our elegant Billiards Room. Perfect for casual games, social gatherings, or a relaxing break during your day at the resort.",
          description2: "Our well-appointed billiards room features professional tables and a comfortable atmosphere, ideal for guests of all skill levels.",
          description3: "Whether you're looking for a fun activity with friends or a moment of relaxation, the Billiards Room offers a classic entertainment experience.",
          dayPassValue: "Billiards Room access is included in the Day Guest Pass. Book your Day Pass to enjoy billiards, pool, and all resort amenities."
        },
        pool: {
          description: "Swim with breathtaking alpine views in our outdoor heated pool, coming soon to Beatenberg Resort & Spa.",
          description2: "Experience year-round swimming in a stunning mountain setting. Our heated pool will offer the perfect combination of relaxation and scenic beauty, allowing you to enjoy the Swiss Alps while taking a refreshing dip.",
          description3: "Whether you're looking for a morning swim, afternoon relaxation, or evening soak under the stars, our pool will provide an unforgettable alpine swimming experience.",
          dayPassValue: "The Outdoor Heated Pool will be included in the Day Guest Pass once it opens. Stay tuned for updates!"
        },
        sports: {
          description: "Stay active in the heart of the Swiss Alps with our comprehensive sports facilities, coming soon to Beatenberg Resort & Spa.",
          description2: "Our facilities will include tennis courts, basketball courts, football fields, and more, all set against the stunning backdrop of the Bernese Oberland mountains.",
          description3: "Whether you're looking for a competitive game, casual exercise, or family-friendly activities, our sports facilities will offer something for every active guest.",
          dayPassValue: "Sports Facilities will be included in the Day Guest Pass once they open. Stay tuned for updates!"
        }
      },
      footer: {
        allInOne: "FUN · RELAXATION · DINING · ENTERTAINMENT · ALL IN ONE PLACE!",
        benefits: [
          "Perfect for Families & Friends",
          "Indoor Comfort with Mountain Charm",
          "All-in-One Day Experience"
        ],
        tagline: "Where Moments Turn into Memories"
      },
      whyChoose: {
        title: "Your Local Luxury Escape",
        subtitle: "Experience luxury resort amenities without an overnight stay - perfect for spontaneous visits and regular local getaways.",
        imageAlt: "Beatenberg Resort & Spa - Premium day guest experience",
        premium: {
          title: "Premium Experience Without Commitment",
          description: "Enjoy luxury resort amenities without an overnight stay - perfect for spontaneous visits and day trips."
        },
        allInOne: {
          title: "All-in-One Destination",
          description: "Multiple activities in one location - spa, dining, cinema, pool, and more - no need to travel between venues."
        },
        flexible: {
          title: "Flexible Day Trips",
          description: "Visit as often as you like, mix and match activities, perfect for regular local getaways and spontaneous escapes."
        },
        easyAccess: {
          title: "Easy Access & Parking",
          description: "Close to home with convenient parking for up to 200 cars - ideal for quick escapes or planned day visits."
        }
      },
      locationAccess: {
        title: "Location & Access",
        subtitle: "Easy to reach with convenient parking and multiple transportation options. Perfect for day guests within the region.",
        mapTitle: "Beatenberg Resort & Spa Location",
        locatedAt: "Located at Beatenberg Resort & Spa",
        switzerland: "SWITZERLAND",
        bernerOberland: "Bernese Oberland",
        cantonBern: "Canton of Bern",
        address: {
          title: "Address",
          description: "Spirenwaldstrasse 351, 3803 Beatenberg, Switzerland",
          details: "Bernese Oberland, Canton of Bern",
          street: "Spirenwaldstrasse",
          number: "351, 3803",
          city: "Beatenberg,",
          country: "Switzerland"
        },
        parking: {
          title: "Parking",
          description: "On-Site Parking up to 200 cars",
          details: "Up to 200 cars • Free parking available"
        },
        bus: {
          title: "By Bus",
          description: "SBB Route 101 @ Hotel Entrance",
          details: "Bus stop @ Hotel Entrance (Terminal Point)"
        },
        train: {
          title: "By Train",
          description: "Beatenberg Station - 7 min walk",
          details: "Interlaken West - 20 min",
          beatenberg: "Beatenberg Station - 7 min",
          interlaken: "Interlaken West - 20 min"
        },
        air: {
          title: "By Air",
          description: "Nearest Airports",
          details: "Bern - 50 min • Zurich - 2h • Geneva - 2.5h",
          bern: "Bern - 50 min",
          zurich: "Zurich - 2h",
          geneva: "Geneva - 2.5h"
        }
      },
      amenitiesOverview: {
        title: "More to Enjoy at Beatenberg",
        subtitle: "Beyond the headline activities, discover additional premium amenities that complete your perfect day experience.",
        wellness: {
          title: "Wellness & Spa",
          clinique: {
            name: "Clinique Nutraswiss",
            description: "Integrated Health Spa with exclusive NutraSwiss treatments and nutraceuticals"
          },
          whirlpool: {
            name: "Whirlpool",
            description: "Relaxing hydrotherapy experience with stunning alpine views"
          }
        },
        sports: {
          title: "Sports & Fitness",
          fitness: {
            name: "Fitness Centre",
            description: "State-of-the-art Technogym equipment in an alpine setting"
          },
          tennis: {
            name: "Tennis Courts",
            description: "Hotel-owned sports grounds with professional tennis facilities"
          },
          basketball: {
            name: "Basketball Court",
            description: "Full-size court available for guests and groups"
          },
          football: {
            name: "Football Field",
            description: "Dedicated football pitch on hotel grounds"
          },
          volleyball: {
            name: "Volleyball Court",
            description: "Outdoor volleyball facilities for recreational play"
          }
        },
        dining: {
          title: "Dining & Bars",
          restaurant: {
            name: "Restaurant Jungfrau",
            description: "Fine dining with panoramic mountain views",
            capacity: "Capacity: 200"
          },
          cafe: {
            name: "Carbon Mountain Cafe",
            description: "Casual alpine bistro with local specialties",
            capacity: "Capacity: 120"
          },
          lounge: {
            name: "Fireplace Lounge & Bar",
            description: "Intimate bar setting with cozy fireplace atmosphere",
            capacity: "Capacity: 25"
          }
        },
        views: {
          title: "Scenic Views",
          panoramic: {
            name: "Panoramic Alpine Views",
            description: "Unobstructed views of Eiger, Mönch, Jungfrau peaks and Lake Thun"
          }
        }
      },
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Everything you need to know about the Day Guest Pass",
        ctaText: "Still have questions? Contact us",
        ctaButton: "Contact Us",
        questions: {
          whatIncluded: {
            question: "What is included in the Day Guest Pass?",
            answer: "The Day Guest Pass includes access to all resort amenities, a 15-minute massage (upgradeable to a full spa treatment), a 5-course meal at Restaurant Jungfrau, one item plus a drink from Carbon Mountain Cafe, and sauna package access. All of this is available for 200 CHF per person."
          },
          advanceBooking: {
            question: "Do I need to book in advance?",
            answer: "Yes, we recommend booking in advance to ensure availability, especially during peak seasons. You can book up to 12 guests per booking. We'll confirm your reservation and send you all the details via email."
          },
          upgradeMassage: {
            question: "Can I upgrade the included 15-minute massage?",
            answer: "Absolutely! Each guest can upgrade their included 15-minute massage to any full spa treatment from our menu. You'll only pay the difference between the full treatment price and the included massage value. Upgrades can be selected during the booking process."
          },
          guestLimit: {
            question: "How many guests can I book for?",
            answer: "You can book for 1 to 12 guests per Day Guest Pass booking. For larger groups, please contact our concierge team who can arrange special group packages."
          },
          multipleActivities: {
            question: "Can I visit multiple activities in one day?",
            answer: "Yes! The Day Guest Pass gives you access to all resort amenities throughout your visit. You can enjoy the spa, sauna, dining, cinema, billiards, and all other facilities available at the resort during your day visit."
          },
          parking: {
            question: "Is parking available?",
            answer: "Yes, parking is available at the resort. Please contact us for specific parking details and availability, especially during busy periods."
          },
          mealSelection: {
            question: "When do I select my meals?",
            answer: "Meal selection typically happens on the day of your visit. The 5-course meal at Restaurant Jungfrau is included, and you can choose from the day's menu. For the Carbon Mountain Cafe item, you can select from their menu when you visit. If you have dietary restrictions or preferences, please mention them in your booking request."
          },
          whatToBring: {
            question: "What should I bring?",
            answer: "We recommend bringing comfortable clothing, swimwear for the spa and sauna facilities, and any personal items you may need. Towels and robes are provided at the spa. You may also want to bring a camera to capture the stunning alpine views!"
          },
          familyFriendly: {
            question: "Are activities suitable for families?",
            answer: "Yes! The Day Guest Pass is perfect for families. Children can enjoy the cinema, billiards, and other resort amenities. Please note that spa treatments may have age restrictions, and the 5-course meal can be adapted for children. Contact us for family-specific arrangements."
          },
          operatingHours: {
            question: "What are the operating hours?",
            answer: "Operating hours vary by facility. The spa and wellness areas are typically open from early morning until evening. Restaurant Jungfrau serves lunch and dinner, and Carbon Mountain Cafe is open throughout the day. Specific hours will be confirmed with your booking confirmation."
          },
          cancellationPolicy: {
            question: "What is the cancellation policy?",
            answer: "Cancellation policies may vary depending on the time of year and booking type. Generally, we require at least 48 hours notice for cancellations. Please contact us directly to discuss cancellation options, and we'll work with you to find the best solution."
          }
        }
      },
      dayPass: {
        bookingTray: {
          title: "Day Guest Pass",
          dayPassDetails: "Day Guest Pass",
          perPerson: "/ person",
          inclusionAllAmenities: "Access to all resort amenities",
          inclusionMassage: "15-minute massage",
          inclusionMeal: "5-course Restaurant Jungfrau meal (CHF 55 value, drinks extra)",
          inclusionCafe: "One item + tea/coffee/non-alcoholic drink from Carbon Mountain Cafe",
          inclusionSauna: "Sauna package (included access)",
          includedValue: "CHF 40 value",
          numberOfGuests: "Number of Guests",
          guestLimit: "Up to 12 guests per booking",
          basePassPrice: "Base Pass",
          massageUpgrade: "Massage Upgrade",
          total: "Total",
          upgradeMassage: "Upgrade Massage",
          changeUpgrade: "Change Massage Upgrade",
          bookNow: "Book Now",
          backToOverview: "Back to Overview",
          upgradeYourMassage: "Upgrade Massages",
          upgradeDescription: "Choose a full spa treatment to upgrade from your included 15-minute massage.",
          upgradeDescriptionMultiple: "Select upgrades for each guest. Each guest can choose a different massage treatment.",
          includedMassage: "15-minute massage (included)",
          selectGuest: "Select Guest to Upgrade",
          selectingForGuest: "Selecting for",
          guest: "Guest",
          upgradeOptions: "Upgrade Options",
          upgradeFor: "Upgrade for",
          upgradeSelected: "Upgrade selected",
          includedSelected: "Included massage selected",
          confirmUpgrade: "Confirm Selection",
          bookingInformation: "Booking Information",
          bookingDescription: "Please provide your details to complete your Day Guest Pass booking.",
          fullName: "Full Name",
          email: "Email",
          phone: "Phone",
          preferredDate: "Preferred Date",
          preferredTime: "Preferred Time",
          selectDate: "Select Date",
          morning: "Morning",
          afternoon: "Afternoon",
          evening: "Evening",
          specialRequests: "Special Requests (Optional)",
          bookingSummary: "Booking Summary",
          requestedTimesNote: "*Requested times are subject to availability. We will contact you to confirm your booking.",
          submitBooking: "Submit Booking Request",
          fillAllFields: "Please fill in all required fields",
          submitError: "Failed to send inquiry. Please try again.",
          networkError: "Network error. Please check your connection and try again.",
          successMessage: "Booking request sent successfully!",
          thankYouReceived: "Thank you! Your booking request has been received.",
          confirmationEmail: "We will send you a confirmation email shortly.",
          upgradedTo: "Upgraded to"
        },
        dayPassPricing: {
          title: "Day Guest Pass Pricing",
          subtitle: "Experience all that Beatenberg Resort & Spa has to offer with our comprehensive Day Guest Pass. Everything you need for a perfect day in the Swiss Alps.",
          priceLabel: "Day Guest Pass",
          price: "CHF 200",
          perPerson: "per person",
          inclusionsTitle: "What's Included",
          inclusions: {
            amenities: "Full access to both restaurants, expansive resort grounds, relaxation areas, spa and sauna facilities, billiards room, lawn, and outdoor seating areas",
            massage: "15-minute relaxing massage (upgradeable to full spa treatments)",
            massageValue: "CHF 40 value",
            meal: "5-course fine dining experience at Restaurant Jungfrau",
            mealValue: "CHF 55 value (drinks extra)",
            cafe: "One item + tea/coffee/non-alcoholic drink from Carbon Mountain Cafe",
            sauna: "Full sauna package access"
          },
          valueBreakdown: {
            title: "Value Breakdown",
            description: "See the total value you receive with your Day Guest Pass:",
            massage: "15-minute Massage",
            massageValue: "CHF 40",
            meal: "5-course Restaurant Meal",
            mealValue: "CHF 55",
            cafe: "Carbon Cafe Item + Drink",
            cafeValue: "CHF 20",
            sauna: "Sauna Package Access",
            saunaValue: "CHF 35",
            amenities: "Resort Amenities Access",
            amenitiesValue: "CHF 50",
            totalValueLabel: "Total Value",
            totalValue: "CHF 200",
            passPriceLabel: "Day Pass Price",
            passPrice: "CHF 200",
            savings: "Exceptional value - all included at no extra cost"
          },
          cta: "Book Day Pass"
        }
      },
      experienceVideo: {
        title: "Experience the Day Guest Journey",
        subtitle: "Discover what awaits you with our Day Guest Pass - from world-class dining to rejuvenating spa treatments and exciting activities."
      },
      ctaContact: {
        title: "Ready to Experience Beatenberg?",
        subtitle: "Book your Day Guest Pass today and discover everything our resort has to offer. Our team is here to help make your visit unforgettable.",
        ctaTitle: "Book Your Visit",
        ctaDescription: "Reserve your Day Guest Pass and start planning your perfect day in the Swiss Alps.",
        ctaButtons: {
          bookVisit: "Book Your Visit",
          contactConcierge: "Contact Concierge",
          callNow: "Call Now"
        },
        contactTitle: "Get in Touch",
        contactMethods: {
          phone: {
            label: "Phone"
          },
          email: {
            label: "Email"
          },
          whatsapp: {
            label: "WhatsApp",
            value: "+41 76 622 01 22"
          }
        },
        operatingHours: {
          title: "Operating Hours",
          days: "Monday - Sunday",
          hours: "9:00 AM - 8:00 PM"
        }
      }
    },
    book: bookPageTranslationsEn
  },

  de: {
    common: {
      share: "Teilen"
    },
    banner: {
      unitOf: "Ein Hotel von"
    },
    nav: {
      home: "Startseite",
      rooms: "Zimmer & Suiten",
      wellness: "Wellness & Spa",
      dining: "Restaurant",
      experiences: "Erlebnisse",
      events: "Events & Hochzeiten",
      about: "Über uns",
      bookNow: "Jetzt buchen",
      activities: "Activity Arcade",
      journal: "Magazin",
      language: "Sprache",
      displayCurrency: "Anzeigewährung"
    },
    hero: {
      title: "BEATENBERG",
      subtitle: "RESORT & SPA",
      description: "Rustikaler Luxus in den Schweizer Alpen",
      bookStay: "Ihren Aufenthalt buchen",
      swissHeritageHotel: "Schweizer Heritage Hotel • Beatenberg/Interlaken",
      acreResort: "15-Morgen Resort",
      minsFromInterlaken: "15 Min. von Interlaken",
      stunningAlpineViews: "Atemberaubende Alpenblicke",
      since1881: "Seit 1881",
      beatenbergsOnly: "Beatenbergs Einziger Resort",
      fourStarGastrosuisse: "4-Sterne Gastrosuisse",
      ratedResort: "Zertifiziert",
      locationButton: "Beatenberg Resort und Spa Standort"
    },
    features: {
      alpineSanctuary: "Rustikaler Alpen-Luxus in Beatenberg/Interlaken",
      alpineDescription: "Das Beatenberg Resort & Spa ist ein 4★ Deluxe Heritage Resort in Beatenberg, dem längsten Dorf Europas, mit Blick auf den Thunersee und umrahmt von den majestätischen Gipfeln Jungfrau, Eiger und Mönch. Ein verstecktes Alpenjuwel, das traditionellen Schweizer Charme mit modernsten Einrichtungen verbindet und damit die ideale Wahl für Freizeitreisende, Gruppen, Wellnesssuchende, Hochzeiten und Firmenveranstaltungen.",
      alpineDescription2: "Hier ist jeder Moment eine Chance, sich mit der stillen Kraft der Natur zu verbinden. Unser engagiertes Team sorgt dafür, dass Ihr Aufenthalt so nahtlos ist wie die Aussicht und bietet eine Oase der Ruhe, ob Sie eine erholsame Auszeit oder ein großes Abenteuer suchen. Lassen Sie sich von den Alpen zu Ihrer nächsten Reise inspirieren.",
      exclusiveRates: "EXKLUSIVE PREISE",
      guestCardBenefits: "GASTKARTEN-VORTEILE",
      beatenbergAccess: "BEATENBERG ZUGANG",
      interlakenPrivileges: "INTERLAKEN PRIVILEGIEN",
      habkernDiscounts: "HABKERN RABATTE",
      freeShuttle: "KOSTENLOSER SHUTTLE",
      bookYourStay: "Ihren Aufenthalt buchen",
      integratedWellness: "Integrierte Schweizer Wellness",
      integratedWellnessDesc: "Exklusive NutraSwiss Spa & Nahrungsergänzungsmittel",
      guestCardBenefitsDesc: "Exklusive Rabatte und Zugang zu lokalen Attraktionen",
      gastrosuisseExcellence: "GASTROSUISSE Zertifizierte Exzellenz",
      gastrosuisseExcellenceDesc: "Anerkannt vom Schweizer Gastgewerbeverband für Qualitätsstandards",
      swisstainableCommitment: "Swisstainable Tourismus Engagement",
      swisstainableCommitmentDesc: "Verpflichtet zu nachhaltigen Tourismuspraktiken in der Schweiz",
      heritageCuisine: "Schweizer Heritage Küche",
      heritageCuisineDesc: "Farm-to-Table traditionelle Gerichte neu interpretiert",
      acreResort: "15-Morgen Resort in malerischem Alpendorf",
      acreResortDesc: "Ruhige Bergkulisse oberhalb des Thunersees",
      authenticHeritage: "Authentisches Erbe",
      authenticHeritageDesc: "Schweizer Gastfreundschaft seit 1881",
      alpineExperiences: "Unvergessliche Alpenerlebnisse",
      alpineExperiencesDesc: "Ganzjährige Aktivitäten & Schweizer Kulturerlebnis",
      dragToExplore: "Ziehen zum Erkunden",
      elegantBarLounge: "Elegante Bar & Lounge",
      elegantBarLoungeDesc: "Entspannen, gesellig sein und gute Zeiten genießen",
      recreationGames: "Erholung & Spiele",
      recreationGamesDesc: "Unterhaltung und Spiele für alle Altersgruppen",
      technogymFitness: "TECHNOGYM Fitness",
      technogymFitnessDesc: "Modernste Ausstattung in alpiner Umgebung",
      businessFacilities: "Geschäftseinrichtungen",
      businessFacilitiesDesc: "Professionelle Tagungsräume für Veranstaltungen",
      alpineBistro: "Alpen-Bistro",
      alpineBistroDesc: "Casual Dining in entspannter Atmosphäre",
      panoramicViews: "Panoramablick",
      panoramicViewsDesc: "Atemberaubende Aussicht auf Jungfrau & Thunersee"
    },
    rooms: {
      title: "Unsere Zimmer & Suiten",
      description: "Von gemütlichen Doppelzimmern bis hin zu geräumigen Familiensuiten - jedes Zimmer bietet Schweizer Komfort mit atemberaubenden Alpenblicken.",
      exploreRooms: "Zimmer erkunden",
      familySuite: "Heritage Familiensuite - Jungfrau Blick",
      juniorSuite: "Alpine Junior Suite - Panorama Blick",
      tripleRoom: "Klassisches Dreibettzimmer - Alpine Gemütlichkeit",
      premiumDouble: "Jungfrau Superior Doppel - Balkon & Seeblick",
      standardSingle: "Klassisches Einzelzimmer - Alpine Gemütlichkeit",
      alpineViews: "Alpenblicke",
      pineWoodInteriors: "Elegante Kiefernholz-Innenausstattung, die modernen Komfort mit Erbe-Charakter verbindet",
      organicCottonSheets: "Bio-Baumwollbettwäsche",
      privateBalconies: "Private Balkone mit Blick auf Mönch, Niesen, Niederhorn & Thunersee oder Beatenberg Dorf",
      seniorFriendly: "Optionen von gemütlichen Einzelzimmern bis zu Familiensuiten",
      exploreRoom: "Zimmer erkunden",
      viewRoomFeatures: "Zimmerausstattung",
      familySuiteDesc: "Geräumige Unterkunft perfekt für Familien, mit separaten Schlafzimmern und atemberaubenden Alpenblicken.",
      juniorSuiteDesc: "Luxuriöse Junior Suite mit geräumigen Wohnbereichen und Premium-Alpenkomfort.",
      tripleRoomDesc: "Geräumige Dreibettunterkunft perfekt für kleine Gruppen, mit komfortablen Betten und Alpencharme.",
      premiumDoubleDesc: "Raffiniertes Doppelzimmer mit modernen Annehmlichkeiten und Panorama-Alpenblicken.",
      standardSingleDesc: "Klassische Einzelunterkunft mit Alpengemütlichkeit, eleganten Holzeinrichtungen und modernen Annehmlichkeiten.",
      alpineViewsDesc: "Erwachen Sie zu spektakulären Aussichten auf die Berner Alpen von den Panoramafenstern Ihres Zimmers.",
      familySuiteFeatures: {
        masterBedroom: "1 Hauptschlafzimmer",
        additionalBedrooms: "2 Zusätzliche Schlafzimmer",
        hallLivingRoom: "Flur & Wohnzimmer",
        privateBalcony: "Privater Balkon"
      },
      familySuiteBadges: {
        occupancy: "6",
        roomSize: "65m²",
        bedding: "1 Queen + 4 Einzelbetten",
        bathrooms: "2 Badezimmer"
      },
      familySuiteDescription: {
        mobile: "Geräumige 65m² Suite mit 1 Queen + 4 Einzelbetten, Küchenzeile und Panoramablick auf Thunersee, Eiger, Mönch, Jungfrau und Beatenberg Dorf.",
        desktop: "Geräumige 65m² Suite mit 1 Queen + 4 Einzelbetten, Küchenzeile und Panoramablick auf Thunersee, Eiger, Mönch, Jungfrau und Beatenberg Dorf."
      },
      juniorSuiteFeatures: {
        loungeSeating: "Elegante Sitzgruppe",
        privateBalconyWithViews: "Privater Balkon mit Berg-/See-/Dorfblick",
        spaceForAdditionalBeds: "Platz für zusätzliche Betten"
      },
      juniorSuiteBadges: {
        occupancy: "4",
        roomSize: "45m²",
        bedding: "1 Queen + 1 Sofa",
        bathrooms: "1 Badezimmer"
      },
      juniorSuiteDescription: {
        mobile: "Luxuriöse 45m² Junior Suite mit separatem Wohnbereich, Sofabett und privatem Balkon mit atemberaubenden Alpenblicken.",
        desktop: "Luxuriöse 45m² Junior Suite mit separatem Wohnbereich, Sofabett und privatem Balkon mit atemberaubenden Alpenblicken."
      },
      tripleRoomFeatures: {
        perfectForSmallFamilies: "Perfekt für kleine Familien",
        scenicBalconyWithViews: "Malerischer Balkon mit Berg-, See- oder Dorfblick"
      },
      tripleRoomBadges: {
        occupancy: "3",
        roomSize: "30m²",
        bedding: "3 Einzelbetten",
        bathrooms: "1 Badezimmer"
      },
      tripleRoomDescription: {
        mobile: "Komfortables 30m² Dreibettzimmer mit drei Einzelbetten, perfekt für kleine Familien oder Gruppen.",
        desktop: "Komfortables 30m² Dreibettzimmer mit drei Einzelbetten, perfekt für kleine Familien oder Gruppen."
      },
      doubleRoomFeatures: {
        perfectForCouples: "Perfekt für Paare",
        balconyWithViews: "Mit/ohne Balkon; Berg-, See- oder Dorfblick"
      },
      doubleRoomBadges: {
        occupancy: "2",
        roomSize: "30m²",
        bedding: "1 Doppelbett",
        bathrooms: "1 Badezimmer"
      },
      doubleRoomDescription: {
        mobile: "Elegantes 30m² Doppelzimmer mit Doppelbett und modernen Annehmlichkeiten, ideal für Paare.",
        desktop: "Elegantes 30m² Doppelzimmer mit Doppelbett und modernen Annehmlichkeiten, ideal für Paare."
      },
      alpineDoubleTwin: "Alpine Doppel-/Zweibettzimmer - Balkon & Seeblick",
      alpineDoubleTwinFeatures: {
        flexibleBedding: "Flexible Doppel- oder Zweibettkonfiguration",
        privateBalconyWithViews: "Privater Balkon mit atemberaubendem Seeblick",
        perfectForCouplesOrFriends: "Perfekt für Paare oder Freunde auf Reisen"
      },
      alpineDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "32m²",
        bedding: "1 Doppelbett oder 2 Einzelbetten",
        bathrooms: "1 Badezimmer"
      },
      alpineDoubleTwinDescription: {
        mobile: "Flexibles Alpenzimmer mit Balkon und atemberaubendem Seeblick, perfekt für Paare oder Freunde.",
        desktop: "Unser Alpine Doppel-/Zweibettzimmer bietet 32m² komfortablen Raum mit flexiblen Bettkonfigurationen und einem privaten Balkon mit Blick auf den Thunersee. Perfekt für Paare oder Freunde, die sowohl Komfort als auch atemberaubende Alpenlandschaft suchen."
      },
      alpineClassicDoubleTwin: "Alpine Classic Doppel-/Zweibettzimmer - Dorfblick",
      alpineClassicDoubleTwinFeatures: {
        flexibleBedding: "Flexible Doppel- oder Zweibettkonfiguration",
        villageViews: "Charmante Dorfansichten und friedliche Atmosphäre"
      },
      alpineClassicDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "28m²",
        bedding: "1 Doppelbett oder 2 Einzelbetten",
        bathrooms: "1 Badezimmer"
      },
      alpineClassicDoubleTwinDescription: {
        mobile: "Klassisches Alpenzimmer mit charmantem Dorfblick und flexiblen Bettkonfigurationen, perfekt für Paare oder Freunde.",
        desktop: "Unser Alpine Classic Doppel-/Zweibettzimmer bietet 28m² komfortablen Raum mit flexiblen Bettkonfigurationen und friedlichem Dorfblick. Perfekt für Paare oder Freunde, die authentischen Alpencharme mit der Flexibilität von Doppel- oder Zweibettkonfigurationen suchen."
      },
      jungfrauSingle: "Jungfrau Einzelzimmer - Balkon & Seeblick",
      jungfrauSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Einzelbett",
        bathrooms: "1 Badezimmer"
      },
      jungfrauSingleDescription: {
        mobile: "Einzelzimmer mit privatem Balkon und atemberaubendem Seeblick, perfekt für Alleinreisende.",
        desktop: "Unser Jungfrau Einzelzimmer bietet 20m² komfortablen Raum mit einem privaten Balkon mit Blick auf den Thunersee. Perfekt für Alleinreisende, die sowohl Komfort als auch atemberaubende Alpenlandschaft mit direktem Zugang zur frischen Bergluft suchen."
      },
      jungfrauSingleFeatures: {
        balconyWithLakeView: "Privater Balkon mit atemberaubendem Seeblick",
        perfectForSoloTravelers: "Perfekt für Alleinreisende"
      },
      heritageSingle: "Heritage Einzelzimmer - Dorfblick",
      heritageSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Einzelbett",
        bathrooms: "1 Badezimmer"
      },
      heritageSingleDescription: {
        mobile: "Klassisches Einzelzimmer mit charmantem Dorfblick und Erbe-Charakter.",
        desktop: "Unser Heritage Einzelzimmer bietet 20m² komfortablen Raum mit friedlichem Dorfblick. Perfekt für Alleinreisende, die authentischen Alpencharme mit klassischer Schweizer Gastfreundschaft und traditionellem Charakter suchen."
      },
      heritageSingleFeatures: {
        villageViews: "Charmante Dorfansichten und friedliche Atmosphäre",
        classicCharm: "Klassischer Alpencharme und Erbe-Charakter"
      },
      singleRoomFeatures: {
        perfectForSoloTravelers: "Perfekt für Alleinreisende",
        balconyWithViews: "Mit/ohne Balkon; Berg-, See- oder Dorfblick"
      },
      singleRoomBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Einzelbett",
        bathrooms: "1 Badezimmer"
      },
      singleRoomDescription: {
        mobile: "Gemütliches 20m² Einzelzimmer mit Einzelbett, perfekt für Alleinreisende, die Komfort suchen.",
        desktop: "Gemütliches 20m² Einzelzimmer mit Einzelbett, perfekt für Alleinreisende, die Komfort suchen."
      }
    },
    restaurant: {
      title: "Kulinarische Exzellenz",
      description: "Von traditionellen Schweizer Spezialitäten bis hin zu innovativer Haute Cuisine feiert unser Restaurant die reichen Aromen der Alpen. Erleben Sie außergewöhnliche Küche in einer eleganten Bergkulisse.",
      exploreRestaurant: "Restaurant erkunden",

      // Hero Section
      hero: {
        logoAlt: "Restaurant Jungfrau Logo"
      },

      renovationNotice: {
        eyebrow: "Restaurant-Mitteilung",
        title: "Essen nur nach Vereinbarung",
        description:
          "Das Restaurant Jungfrau empfängt Gäste nach Reservierung. Kontaktieren Sie uns für Ihr alpines Speiseerlebnis.",
        completion: "",
        ctaPrimary: "Verstanden",
        ctaSecondary: "Kontaktieren Sie uns",
      },

      // About Section
      about: {
        backgroundText: "JUNGFRAU",
        tagline: "RESTAURANT JUNGFRAU",
        title: "ALPINE GASTRONOMIE\nAUF HÖCHSTEM NIVEAU.",
        description: "Restaurant Jungfrau verkörpert den Gipfel der alpinen Gastronomie, wo jahrhundertealte Schweizer Küchentraditionen harmonisch mit zeitgenössischen internationalen Techniken verschmelzen. Unsere Philosophie basiert auf drei Grundprinzipien: Authentizität, Nachhaltigkeit und Innovation.",
        phoneButton: "+41 33 841 00 21"
      },

      // Features Section
      features: {
        alpineViews: {
          title: "ALPENBLICKE",
          description: "Panoramablick Thunersee & Jungfrau"
        },
        localSourcing: {
          title: "REGIONALE BEZUGSQUELLEN",
          description: "Schweizer Bauern & Handwerker"
        },
        swissHeritage: {
          title: "SCHWEIZER ERBE",
          description: "Jahrhundertealte Traditionen"
        }
      },

      // Menu Section
      menu: {
        tagline: "SCHWEIZER ALPINE KÜCHE",
        title: "RESTAURANT JUNGFRAU SPEISEKARTE",
        description: "Erleben Sie unser 5-Gänge-Menü mit traditioneller Schweizer Alpenküche und frischen regionalen Zutaten. Jeder Gang wird sorgfältig zubereitet, um das reiche kulinarische Erbe der Region Berner Oberland zu feiern. Dies ist nur ein Einblick - sehen Sie unsere vollständige Speisekarte unten.",
        subtitle: "Ein Geschmack der Alpen - Est. 1881",
        tabs: {
          mainCourses: "Hauptgerichte",
          fiveCourseDinner: "5-Gänge-Menü",
          sides: "Beilagen",
          beverages: "Getränke"
        },
        viewFullMenu: "Vollständige Speisekarte anzeigen",
        dinnerHours: "Abendessen von 18:30 bis 21:00 | Reservierungen: +41 76 622 01 22",
        reservations: "Reservierungen: +41 76 622 01 22",
        pricesInclusive: "Alle Preise inklusive MwSt."
      },

      // Menu Items
      menuItems: {
        fiveCourseMenu: {
          name: "5-Gänge-Menü",
          description: "COURSE_1:Vorspeise: Schweizer Gartensalat|COURSE_2:Suppe: Gemüsesuppe des Tages|COURSE_3:Hauptgericht (wählen Sie eines):|COURSE_3_OPTION_1:Gegrillte Pouletbrust \"Jungfrau\" mit Gemüse|COURSE_3_OPTION_2:Gebackener Norwegischer Lachs Beatenberg|COURSE_3_OPTION_3:Eiger Pinsa mit Pilzen & Käse (Vegetarisch)|COURSE_3_OPTION_4:Asiatisches Curry (Poulet oder Vegetarisch) mit Knoblauch Naan|COURSE_4:Dessert: Tagesgebäck / Eiscreme / Schokoladenkuchen / Vanille-Glace-Torte|COURSE_5:Heißgetränk: Kaffee oder Tee"
        },
        healthySwissSalad: {
          name: "Gesunder Schweizer Salat",
          description: "Gesunder Schweizer Salat mit frischen regionalen Zutaten"
        },
        vegetableSoup: {
          name: "Gemüsesuppe des Tages",
          description: "Vegetarische Suppe des Tages mit saisonalen Gemüsen"
        },
        jungfrauClassic: {
          name: "Jungfrau Classic",
          description: "Gegrillte Hähnchenbrust mit Gemüse"
        },
        thunerseeDelight: {
          name: "Thunersee Delight",
          description: "Gegrilltes Lachsfilet mit Gemüse"
        },
        alpineHutRosti: {
          name: "Alpine Hut Rösti",
          description: "Vegetarisches Rösti mit hausgemachtem Käse"
        },
        eigerPeakPinsa: {
          name: "Eiger Peak Pinsa",
          description: "Vegetarische PIZZA mit Pilzen & Käse"
        },
        asianCurry: {
          name: "Asian Curry",
          description: "Hähnchen- oder vegetarisches Curry mit Reis und Raita"
        },
        frenchFries: {
          name: "Pommes Frites",
          description: "Knusprige goldene Pommes Frites"
        },
        smokedSalmonSandwich: {
          name: "Räucherlachs-Sandwich",
          description: "Räucherlachs-Sandwich auf frischem Brot mit leichtem Salatgarn."
        },
        eggCheeseSandwich: {
          name: "Ei & Käse Sandwich",
          description: "Warmes Ei- und Schweizer Käse-Sandwich, perfekt für einen schnellen alpinen Bissen."
        },
        healthySaladWithEgg: {
          name: "Gesunder Salat mit Ei",
          description: "Knackiger Gartensalat mit Ei und saisonalem Gemüse."
        },
        healthySalad: {
          name: "Gesunder Salat",
          description: "Frischer gemischter Salat mit Alpenkräutern und leichtem Dressing."
        },
        chickenNuggets: {
          name: "Chicken Nuggets (6 Stk.)",
          description: "Sechs knusprige Chicken Nuggets, ideal als Beilage zum Teilen oder Snack."
        },
        miniCroissant: {
          name: "Mini-Croissant (2 Stk.)",
          description: "Zwei buttrige Mini-Croissants, eine leichte süße Beilage oder Nachmittagsgenuss."
        },
        sweetPotatoFries: {
          name: "Süßkartoffel-Pommes",
          description: "Süßkartoffel-Pommes mit Alpengewürzen"
        },
        falafel: {
          name: "Falafel",
          description: "Traditionelle nahöstliche Falafel"
        },
        potatoRostiFries: {
          name: "Kartoffel-Rösti-Pommes",
          description: "Schweizer Kartoffel-Rösti-Pommes"
        },
        clavienCornalin: {
          name: "Clavien Cornalin",
          description: "Schweizer Rotwein (10 cl) - AOC Wallis"
        },
        yvorneRouge: {
          name: "Yvorne Rouge AOC",
          description: "Schweizer Rotwein (10 cl) - Chablais"
        },
        yvorneBlanc: {
          name: "Yvorne Blanc",
          description: "Schweizer Weißwein (10 cl) - Chablais"
        },
        rugenbrauAmber: {
          name: "Rugenbräu Amber",
          description: "Schweizer Amber Bier (33 cl) - Lokal gebraut"
        },
        rugenbrauSpezial: {
          name: "Rugenbräu Spezial Hell",
          description: "Schweizer Premium Lager (33 cl)"
        },
        rivella: {
          name: "Rivella Original",
          description: "Kultiges Schweizer Erfrischungsgetränk (33 cl)"
        },
        iceTea: {
          name: "Ramseier Eistee",
          description: "Schweizer Minz- oder Schwarztee (33 cl)"
        },
        apfelschorle: {
          name: "Ramseier Apfelschorle",
          description: "Schweizer Apfelsaft mit Kohlensäure (33 cl)"
        }
      },

      // Featured Items
      featuredItems: {
        tagline: "MENÜ-HIGHLIGHTS",
        title: "AUSGEWÄHLTE ARTIKEL"
      },

      // Dining Venues
      diningVenues: {
        tagline: "SPEISEERLEBNIS",
        title: "SPEISERÄUME",
        description: "Mehrere Speiseerlebnisse im Restaurant Jungfrau, jedes mit einzigartiger Atmosphäre und atemberaubenden Alpenblicken",
        mainRestaurant: {
          title: "Hauptrestaurant",
          subtitle: "PANORAMA-SPEISESAAL",
          description: "Unser Hauptspeiseraum bietet Platz für bis zu 200 Gäste mit atemberaubendem Blick über den Thunersee und die Jungfrau-Bergkette.",
          features: [
            "Sitzplatzkapazität: Bis zu 200 Gäste",
            "Panoramablick auf den Thunersee",
            "Jungfrau-Bergkette als Kulisse",
            "Ideal für Gruppen und Veranstaltungen"
          ],
          perfectFor: "Gruppen, Veranstaltungen, Familienspeisen"
        },
        terraceDining: {
          title: "Terrassen-Speisen",
          subtitle: "ALPINE TERRASSE",
          description: "Überdachter Außenbereich mit einziehbaren Glaswänden und Heizsystemen, mit ungehindertem Seeblick und stimmungsvoller Beleuchtung.",
          features: [
            "Kapazität: 60-80 Gäste",
            "Ungehinderter Seeblick",
            "Einziehbare Glaswände",
            "Integrierte Bar"
          ],
          perfectFor: "Intimes Speisen, Besondere Anlässe"
        },
        outdoorTable: {
          title: "Außentisch",
          subtitle: "PRIVATER AUSSENTISCH",
          description: "Intime kulinarische Erlebnisse mit personalisierten Menüs, mit Live-Akustikmusik und traditionellen Schweizer Stücken.",
          features: [
            "Intime kulinarische Erlebnisse",
            "Personalisierte Menüs",
            "Alpine Naturgeräusche",
            "Traditionelle Schweizer Stücke"
          ],
          perfectFor: "Besondere Feiern, Weinproben"
        },
        alpineBreakfast: {
          title: "Alpen-Frühstück",
          subtitle: "ALPEN-FRÜHSTÜCKSBUFFET",
          description: "Alpen-Frühstücksbuffet mit regionalen Produkten, traditionellen Schweizer Frühstücksartikeln und frischen Bergzutaten.",
          features: [
            "Regionale Produkte",
            "Traditionelle Schweizer Frühstücksartikel",
            "Frische Bergzutaten",
            "Panorama-Morgenblick"
          ],
          perfectFor: "Morgenspeisen, Hotelgäste"
        }
      },

      // Scrolling Text
      scrollingText: {
        swissHeritage: "SCHWEIZER ERBE",
        alpineGastronomy: "ALPINE GASTRONOMIE",
        farmToTable: "VOM HOF AUF DEN TISCH",
        fiveCourseDinner: "5-GÄNGE-MENÜ",
        swissWines: "SCHWEIZER WEINE",
        localBeverages: "LOKALE GETRÄNKE",
        alpineSides: "ALPINE BEILAGEN",
        freshSalads: "FRISCHE SALATE",
        gastrosuisseCertified: "GASTROSUISSE ZERTIFIZIERT"
      },

      // Gallery
      gallery: {
        eyebrow: "VISUELLES ERLEBNIS",
        title: "Restaurant-Galerie",
        subtitle: "Erleben Sie unsere Atmosphäre und kulinarische Kunst"
      },

      // Testimonials
      testimonials: {
        // Testimonial content is kept in original language as they are real customer reviews
      },

      // GASTROSUISSE Certification
      gastrosuisse: {
        tagline: "SCHWEIZER EXZELLENZ",
        title: "GASTROSUISSE ZERTIFIZIERT",
        description: "Anerkannt vom Schweizer Gastgewerbeverband für die Einhaltung höchster Qualitäts-, Service- und kulinarischer Exzellenzstandards",
        certificationBenefits: {
          title: "ZERTIFIZIERUNGSVORTEILE",
          benefits: [
            "Anerkennung des Schweizer Gastgewerbeverbands",
            "Einhaltung von Qualitäts- und Servicestandards",
            "Branchenanerkennung und Glaubwürdigkeit",
            "Premium-Mitgliedschaftsvorteile"
          ]
        },
        qualityStandards: {
          title: "QUALITÄTSSTANDARDS",
          standards: [
            "Regelmäßige Qualitätsbewertungen und Inspektionen",
            "Mitarbeiterschulungs- und Entwicklungsprogramme",
            "Bewahrung der Schweizer Küchentradition",
            "Gästezufriedenheit und Erlebnis-Exzellenz"
          ]
        },
        additionalAccreditations: {
          title: "ZUSÄTZLICHE AKKREDITIERUNGEN",
          accreditations: [
            "Schweizer Hotelverband - Premium-Mitglied",
            "Swisstainable Tourismus-Zertifizierung",
            "Bern Welcome Zugehörigkeit"
          ]
        }
      },

      // Contact Section
      contact: {
        tagline: "KONTAKT AUFNEHMEN",
        title: "Besuchen Sie uns heute",
        subtitle: "Wir würden Ihnen gerne ein unvergessliches Erlebnis bieten",
        visitLocation: {
          title: "Besuchen Sie unseren Standort",
          address: [
            "Beatenberg Resort & Spa",
            "Spirenwaldstrasse 351",
            "3803 Beatenberg, Schweiz",
            "Europas längstes Dorf"
          ]
        },
        callUs: {
          title: "Rufen Sie uns jetzt an",
          details: [
            "+41 33 841 00 21",
            "Restaurant Jungfrau",
            "Beatenberg Resort & Spa"
          ]
        },
        email: {
          title: "E-Mail-Adresse",
          details: [
            "info@beatenberg-resortspa.ch"
          ]
        }
      },

      // Christmas & New Year Dinner Registration Form
      christmasNewYearForm: {
        title: "Weihnachts- und Silvester-Dinner-Reservierung",
        intro: "Nehmen Sie an einem besonderen festlichen kulinarischen Erlebnis teil. Bitte füllen Sie das untenstehende Formular aus, um Ihren Tisch zu reservieren.",
        pricingInfo: "CHF 111 pro Person",
        minimumParty: "Reservierungen ab mindestens 2 Personen",
        festiveMenusTitle: "Festliche Menüs",
        christmasMenuLabel: "Weihnachtsmenü",
        newYearMenuLabel: "Silvestermenü",
        nameLabel: "Vollständiger Name",
        namePlaceholder: "Geben Sie Ihren vollständigen Namen ein",
        emailLabel: "E-Mail-Adresse",
        emailPlaceholder: "ihre.email@beispiel.com",
        mobileLabel: "Handynummer",
        mobilePlaceholder: "Geben Sie Ihre Handynummer ein",
        countLabel: "Anzahl der Gäste",
        countPlaceholder: "Anzahl der Gäste auswählen",
        eventLabel: "Veranstaltung",
        eventChristmas: "Heiligabend - 24. Dezember 2025",
        eventNewYear: "Silvester - 31. Dezember 2025",
        timeSlotLabel: "Bevorzugter Zeitfenster",
        timeSlot1: "18:00–20:00",
        timeSlot2: "20:15–22:15",
        submitButton: "Reservierung absenden",
        submittingButton: "Wird übermittelt...",
        successMessage: "Vielen Dank! Ihre Reservierung wurde erfolgreich übermittelt. Wir werden Sie in Kürze kontaktieren, um Ihre Reservierung zu bestätigen.",
        errorMessage: "Beim Übermitteln Ihrer Reservierung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.",
        validationRequired: "Dieses Feld ist erforderlich",
        validationEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
        validationPhone: "Bitte geben Sie eine gültige Telefonnummer ein",
        validationCount: "Bitte wählen Sie die Anzahl der Gäste (2-12)",
        honeypotLabel: "Lassen Sie dieses Feld leer",
        modalTitle: "Reservierung bestätigt!",
        modalSubtitle: "Ihre Reservierung wurde erhalten",
        modalNameLabel: "Name",
        modalEventLabel: "Veranstaltung",
        modalTimeSlotLabel: "Zeitfenster",
        modalGuestsLabel: "Gäste",
        modalCloseButton: "Schließen"
      }
    },
    spa: {
      serviceList: {
        addToList: "In Liste aufnehmen",
        addedToList: "Zur Liste hinzugefügt",
        title: "Ihre Serviceliste",
        empty: "Ihre Liste ist derzeit leer",
        details: "Anfrage-Details",
        name: "Vollständiger Name",
        email: "E-Mail-Adresse",
        phone: "Telefonnummer",
        date: "Bevorzugtes Datum",
        time: "Bevorzugte Zeit",
        morning: "Morgens",
        afternoon: "Nachmittags",
        evening: "Abends",
        subtotal: "Geschätzte Summe",
        sendInquiry: "Anfrage senden",
        successTitle: "Vielen Dank",
        successMessage: "Ihre Anfrage ist eingegangen. Unser Concierge wird Sie in Kürze kontaktieren.",
        errorTitle: "Übermittlung fehlgeschlagen",
        errorMessage: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.",
        closeList: "Liste schließen",
        tryAgain: "Erneut versuchen",
        browseServices: "Behandlungen durchsuchen",
        selections: "Auswahl",
        catalog: "Katalog",
        selectDate: "Datum auswählen",
        selectTime: "Zeit auswählen",
        preferredDateCET: "Bevorzugtes Datum (CET) *",
        requestAppointment: "Termin anfragen",
        reviewSelections: "Auswahl überprüfen",
        addToMyList: "Zu meiner Liste hinzufügen",
        viewMyList: "Meine Liste anzeigen",
        yourRelaxationAwaits: "Ihre Entspannung wartet",
        selectTreatmentsMessage: "Wählen Sie Behandlungen aus unserem Menü aus, um Ihren persönlichen Wellness-Plan zu erstellen.",
        viewSpaMenu: "Spa-Menü anzeigen",
        totalEstimated: "Geschätzte Summe",
        thankYouReceived: "Vielen Dank! Ihre Anfrage wurde erhalten.",
        requestedTimesNote: "*Angefragte Zeiten unterliegen der Verfügbarkeit. Wir werden Sie kontaktieren, um Ihren Termin zu bestätigen."
      },
      title: "Ihr Wellness-Escape",
      description: "Entfliehen Sie in unser ruhiges Alpenrefugium, wo Entspannung auf Erholung trifft. Erleben Sie erstklassige Spa-Behandlungen, therapeutische Massagen und Wellness-Programme, die darauf ausgelegt sind, Geist, Körper und Seele in den unberührten Schweizer Bergen zu regenerieren.",
      exploreSpa: "Spa erkunden",
      holisticWellnessTitle: "Ganzheitliches Wellness-Zentrum",
      holisticWellnessDesc: "Erleben Sie umfassende Wellness-Programme, die medizinische Expertise mit traditionellen Heilmethoden in unserer ruhigen Alpenumgebung verbinden.",
      medicalSpaTitle: "Medical Spa Exzellenz",
      medicalSpaDesc: "Hochmoderne Medical Spa-Behandlungen zur Förderung von Heilung, Verjüngung und optimaler Gesundheit in luxuriöser Umgebung.",
      detoxTitle: "Entgiftung & Reinigung",
      detoxDesc: "Umfassende Entgiftungsprogramme zur Reinigung Ihres Körpers von Giftstoffen und Wiederherstellung des natürlichen Gleichgewichts durch spezialisierte Behandlungen und Ernährung.",
      relaxationBalconyTitle: "Entspannungsbalkon",
      relaxationBalconyDesc: "Genießen Sie friedliche Momente auf unserem Panoramabalkon mit atemberaubenden Alpenblicken, perfekt für Meditation und ruhige Reflexion.",
      therapeuticMassageTitle: "Therapeutische Massage",
      therapeuticMassageDesc: "Spezialisierte Massagebehandlungen einschließlich Ganzkörper-, Kopf- und Rückenmassagen für gezielte Entspannung und Heilung.",

      // Hero Section
      hero: {
        mainTitle: "Erleben Sie Wellness, verstärkt durch die Natur",
        subMessage: "Wo reine Alpenluft und reines Bergwasser auf Schweizer therapeutische Exzellenz treffen",
        ctaButton: "Alpine Wellness erleben"
      },

      // Nature and Science Section
      natureScience: {
        title: "Natürliche Heilung & Schweizer Exzellenz",
        subtitle: "Unterstützt von NutraSwiss und Nahrin — die Reinheit der Natur kombiniert mit jahrzehntelanger Schweizer Expertise in natürlicher Gesundheit",
        images: {
          massage: "Therapeutische Massagebehandlung",
          therapist: "Professioneller Spa-Therapeut",
          treatment: "Spa-Behandlungsraum",
          wellness: "Wellness-Erfrischungen",
          alpine: "Alpine Wellness-Erfahrung",
          relaxation: "Entspannungsbereich"
        },
        blocks: {
          air: {
            title: "Reine Alpenluft",
            description: "Jeder Atemzug, den Sie im Berner Oberland auf 1.200 m Höhe nehmen, wird natürlich von der alpinen Umgebung gefiltert und verbessert die natürlichen Heilungsprozesse Ihres Körpers. Die saubere, sauerstoffreiche Luft verbessert die Atemgesundheit und steigert das Energieniveau."
          },
          water: {
            title: "Reines Bergwasser",
            description: "Unsere Behandlungen verwenden reines Alpenwasser aus Bergquellen, das natürliche Mineralien und Reinheit zu jedem Erlebnis hinzufügt. Bergquellwasser-Behandlungen mit reinem Alpenwasser verbessern die therapeutischen Vorteile jeder Sitzung."
          },
          science: {
            title: "Schweizer Wissenschaftsexzellenz",
            description: "Unterstützt von über 60 Jahren Schweizer Expertise in natürlicher Gesundheit durch Nahrin und NutraSwiss kombinieren unsere Behandlungen traditionelle Schweizer Wellness-Traditionen mit moderner Wissenschaft und verwenden Premium-Botanikformulierungen aus 100% natürlichen Inhaltsstoffen."
          }
        }
      },

      renovationNotice: {
        eyebrow: "Spa-Mitteilung",
        title: "Kostenlose Sauna & Thermal-Wellness für Hotelgäste",
        description:
          "Das Sauna- & Thermal-Wellness-Paket ist für Gäste, die im Hotel übernachten, inklusive.",
        completion: "",
        ctaPrimary: "Jetzt buchen",
        ctaSecondary: "Kontaktieren Sie uns",
        imageAlt: "Alpine Spa und Sauna im Beatenberg Resort",
      },

      // Introduction Section
      introduction: {
        title: "Willkommen in der Clinique NutraSwiss",
        description: "Ihr Zufluchtsort der Entspannung und des rustikalen Luxus im Herzen der Schweizer Alpen. Nach einem Tag der Erkundung der atemberaubenden Schönheit der Schweiz - von der majestätischen Jungfrau-Region bis zu den kristallklaren Gewässern des Thunersees - ziehen Sie sich in unser Spa für das ultimative Alpine Entspannungserlebnis zurück.",
        perfectEndTitle: "Der perfekte Abschluss Ihres Schweizer Abenteuers",
        perfectEndDesc: "Wir verbinden traditionelle Schweizer Wellness-Traditionen mit modernem Komfort und bieten Ihnen die perfekte Ergänzung zu Ihrem Schweizer Abenteuer. Ob Sie die Bergpfade gewandert sind, historische Schlösser erkundet haben oder einfach die spektakuläre Alpenlandschaft genossen haben - unsere Behandlungen helfen Ihnen, sich in authentischem Schweizer Stil zu entspannen und zu regenerieren.",
        mountainViews: "Panoramablicke auf die Berge aus jedem Behandlungsraum",
        alpineBotanicals: "Traditionelle Schweizer Behandlungen mit lokal bezogenen Alpenbotanicals",
        swissHospitality: "Authentische Schweizer Gastfreundschaft mit warmer, persönlicher Betreuung",
        nutraSwissScience: "NutraSwiss Ernährungswissenschaft, die traditionelle Schweizer Wellness mit evidenzbasierten Behandlungen und personalisierten Protokollen verbessert",
        perfectForYouTitle: "Perfekt für Sie",
        convenientLocation: "Bequeme Lage in Ihrem Hotel - keine Notwendigkeit, woanders hinzufahren",
        flexibleScheduling: "Flexible Terminplanung, die zu Ihrer Sightseeing-Route passt",
        multilingualStaff: "Mehrsprachiges Personal, fließend in Englisch, Deutsch, Französisch und Italienisch",
        complimentaryConsultation: "Kostenlose Beratung zur Auswahl der perfekten Behandlung"
      },

      // Power of Touch Section
      powerOfTouch: {
        category: "Therapeutische Heilung",
        title: "DIE KRAFT DER BERÜHRUNG",
        description: "Erleben Sie die transformative Kraft geschickter therapeutischer Berührung in unserem ruhigen Alpenrefugium. Unsere zertifizierten Massagetherapeuten verbinden alte Heilweisheit mit zeitgenössischen Techniken und verwenden hochwertige Schweizer Produkte, um das Gleichgewicht von Körper und Geist wiederherzustellen. Von Tiefengewebsmassage bis hin zu sanften Entspannungstechniken - jede Behandlung ist auf Ihre individuellen Bedürfnisse zugeschnitten und schafft eine tief persönliche Reise der Wiederherstellung und Erneuerung.",
        ctaButton: "Buchen Sie Ihr therapeutisches Erlebnis"
      },

      // Beauty Section
      beauty: {
        category: "Alpine Wellness",
        title: "DIE KUNST",
        subtitle: "der Erneuerung",
        ctaButton: "Schönheitsbehandlungen entdecken"
      },

      // Quick Relaxation Section
      quickRelaxation: {
        category: "Schnelle Entspannung",
        title: "SCHNELLE ENTSPANNUNG",
        description: "Erleben Sie die transformative Kraft geschickter therapeutischer Berührung in unserem ruhigen Alpenrefugium. Unsere zertifizierten Massagetherapeuten verbinden alte Heilweisheit mit zeitgenössischen Techniken und verwenden hochwertige Schweizer Produkte, um das Gleichgewicht von Körper und Geist wiederherzustellen. Von Tiefengewebsmassage bis hin zu sanften Entspannungstechniken - jede Behandlung ist auf Ihre individuellen Bedürfnisse zugeschnitten und schafft eine tief persönliche Reise der Wiederherstellung und Erneuerung.",
        ctaButton: "Buchen Sie Ihre schnelle Entspannung",
        modal: {
          title: "Schnelle Entspannungsoptionen",
          description: "Perfekt für beschäftigte Touristen, die zwischen den Sehenswürdigkeiten etwas Verwöhnung einbauen möchten. Unsere schnellen Entspannungsoptionen sind darauf ausgelegt, in Ihren Schweizer Abenteuerplan zu passen und dabei maximale Entspannungsvorteile zu bieten.",
          closeButton: "Modal schließen",
          options: {
            expressMassage: {
              title: "Express Kopf- und Schultermassage",
              description: "Perfekt zur Linderung von Verspannungen nach dem Sightseeing. Schnelle und effektive Massage mit Fokus auf Nacken, Schultern und Kopf."
            },
            footTreatment: {
              title: "Schweizer Alpen-Fußbehandlung",
              description: "Beleben Sie müde Füße nach der Erkundung der Berge. Beinhaltet Fußbad, Massage und Alpen-Kräuterbehandlung."
            },
            meditation: {
              title: "Bergblick-Meditation",
              description: "Geführte Meditationssitzung mit Panoramablick auf die Alpen. Perfekt für mentale Entspannung und Achtsamkeit."
            },
            teaRitual: {
              title: "Alpen-Kräutertee-Ritual",
              description: "Traditionelle Schweizer Teezeremonie mit lokal bezogenen Alpenkräutern. Ein friedlicher Moment der Besinnung."
            }
          },
          benefits: {
            title: "Warum schnelle Entspannung wählen?",
            flexibleScheduling: {
              title: "Flexible Terminplanung",
              description: "Behandlungen, die in Ihren Sightseeing-Plan passen, von 20-45 Minuten."
            },
            alpineExperience: {
              title: "Alpenerlebnis",
              description: "Authentische Schweizer Behandlungen mit lokalen Alpenkräutern und traditionellen Techniken."
            },
            instantRelief: {
              title: "Sofortige Linderung",
              description: "Schnelle und effektive Behandlungen zur Linderung von Verspannungen nach Wandern und Sightseeing."
            },
            perfectForTourists: {
              title: "Perfekt für Touristen",
              description: "Speziell für Besucher entwickelt, die die Sehenswürdigkeiten der Schweiz erkunden."
            }
          },
          booking: {
            title: "Bereit, Ihre schnelle Entspannung zu buchen?",
            description: "Buchen Sie Ihr Erlebnis, indem Sie unseren Concierge kontaktieren. Unser engagiertes Team hilft Ihnen bei der Terminplanung Ihrer Behandlungen und beantwortet alle Fragen zu unseren Spa-Services. Kontaktieren Sie uns an der Rezeption während Ihres Aufenthalts.",
            button: "Concierge kontaktieren"
          }
        }
      },

      // Treatments & Services Section
      treatmentsAndServices: {
        title: "Ihre Wellness-Reise",
        introduction: "Ihre Wellness-Reise wird von dem Besten der Schweizer Natur und Wissenschaft unterstützt. Wir verwenden ausschließlich Premium-Botanikformulierungen aus 100% natürlichen Inhaltsstoffen und stützen uns auf über sechs Jahrzehnte Schweizer Expertise in Ernährung und natürlicher Gesundheit. Jede Anwendung ist darauf ausgelegt, mit der alpinen Umgebung im Einklang zu stehen.",
        learnMore: "Mehr erfahren",
        bookNow: "Behandlung buchen",
        therapeuticMassages: {
          title: "Therapeutische Massagebehandlungen",
          fullBody: {
            name: "Ganzkörpermassage / Full Body Massage",
            pricing: "30 Min: 75 CHF",
            duration: "30 Minuten",
            description: "Entspannung von Muskelkater und Regeneration nach dem Sport. Verbessert Durchblutung und Stoffwechsel."
          },
          relaxing: {
            name: "Entspannungsmassage / Relaxing Massage",
            pricing: "30 Min: 72 CHF",
            duration: "30 Minuten",
            description: "Wohltuende Ganzkörpermassage mit warmem Lavendelöl. Löst innere und äußere Verspannungen."
          },
          sport: {
            name: "Sportmassage / Sport Massage",
            pricing: "30 Min: 81 CHF",
            duration: "30 Minuten",
            description: "Kraftvolle Massagetechniken für Muskeln, Bindegewebe und Faszien nach dem Sport."
          },
          footReflexology: {
            name: "Fußreflexzonenmassage / Foot Reflexology Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 Minuten",
            description: "Aktiviert Selbstheilungskräfte und verbessert Organfunktionen durch Druck auf Fuß-Nervenpunkte."
          },
          footBack: {
            name: "Fuß- & Rückenmassage / Foot & Back Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 Minuten",
            description: "Kombination aus Fußreflexzonenmassage und intensiver Rückenmassage."
          },
          backNeck: {
            name: "Massage bei Rücken- und Nackenschmerzen / Massage for Back and Neck Pain",
            pricing: "30 Min: 75 CHF",
            duration: "30 Minuten",
            description: "Kombinierte therapeutische und Wohlfühlmassage für Rücken und Nacken mit entzündungshemmenden Tinkturen."
          },
          peeling: {
            name: "Peeling & Ganzkörpermassage / Peeling & Full Body Massage",
            pricing: "30 Min: 98 CHF",
            duration: "30 Minuten",
            description: "Entfernung abgestorbener Hautzellen mit Peelinghandschuh, gefolgt von einer pflegenden Ganzkörpermassage."
          },
          energetic: {
            name: "Energetische Massage / Energetic Massage",
            pricing: "30 Min: 66 CHF",
            duration: "30 Minuten",
            description: "Individuelle Unterstützung der Selbstheilung durch verschiedene Körper- und Energiearbeiten."
          }
        },
        saunaThermal: {
          title: "Sauna & Thermal Wellness",
          package: {
            title: "Sauna und Thermal Wellness Paket",
            price: "45 CHF (pro Saunagang)",
            description: "Genießen Sie exklusiven Zugang zu unserer Saunazone. Dieses Paket beinhaltet die Nutzung aller Einrichtungen sowie komplementären Kaffee, Tee und Detox-Getränke.",
            hours: "SAUNA-ZEITEN: 11:00 - 20:00"
          },
          finnish: {
            name: "Finnische Sauna / Finnish Sauna",
            temperature: "90°C",
            description: "Die intensive Trockenhitze wird traditionell genutzt, um das allgemeine Wohlbefinden zu unterstützen und den Stoffwechsel anzuregen. Der Wechsel zwischen Hitze und Abkühlung kann die Durchblutung fördern und zur Erholung beitragen."
          },
          bio: {
            name: "Bio Sauna",
            temperature: "60°C",
            description: "Sanfte Temperaturen in Kombination mit Lichttherapie schaffen eine Atmosphäre der Ruhe. Diese mildere Variante wird oft als besonders angenehm empfunden und bietet ein entspannendes Wärmeerlebnis, das sanft zum Kreislauf sein kann."
          },
          steam: {
            name: "Dampfbad / Steam Bath",
            temperature: "45°C",
            description: "Warmer Nebel hilft, die Poren zu öffnen und kann die Hautreinigung unterstützen. Die hohe Luftfeuchtigkeit wird häufig als wohltuend für die Atemwege und ideal zur Entspannung empfunden."
          },
          icePlunge: {
            name: "Eistauchbecken / Ice Plunge Pool",
            description: "Der Kältekick nach dem Schwitzen wirkt vitalisierend auf Körper und Geist. Diese Abkühlung kann die Durchblutung anregen und sorgt für ein erfrischendes, belebendes Hautgefühl."
          }
        },
        specialPrograms: {
          title: "Spezialisierte Wellness-Programme",
          pelvipower: {
            name: "PelviPower™ Training",
            price: "29 CHF (30 Min)",
            type: "Magnetfeld-Therapie",
            description: "Trainieren Sie Ihre Körpermitte und den Beckenboden einfach und effektiv im Sitzen. Die Magnetwellen stimulieren die Muskulatur bis in die Tiefe, ohne körperliche Anstrengung.",
            note: "Empfohlen von Ärzten & Therapeuten",
            benefitsTitle: "Vorteile",
            benefits: {
              women: {
                title: "Frauen",
                desc: "Menopause | Erhöhte Libido"
              },
              men: {
                title: "Männer",
                desc: "Vitalität | Erhöhte Potenz"
              },
              incontinence: {
                title: "Inkontinenz",
                desc: "Blasenschwäche / Leaking"
              },
              back: {
                title: "Rücken",
                desc: "Entspannung | Stärkung"
              },
              mothers: {
                title: "Junge Mütter",
                desc: "Erholung nach der Geburt"
              },
              sports: {
                title: "Sport",
                desc: "Leistung | Stabilität"
              }
            }
          }
        }
      },

      // Booking Section
      booking: {
        title: "Buchung & Kontakt",
        description: "Bereit, das ultimative Alpine Wellness-Retreat zu erleben? Kontaktieren Sie uns, um Ihre Behandlungen zu buchen und Ihre Reise zum Wohlbefinden zu beginnen",
        contactInfoTitle: "Kontaktinformationen",
        address: "Clinique NutraSwiss\nBeatenberg Resort & Spa\nSpirenwaldstrasse 351\n3803 Beatenberg, Schweiz",
        reservations: "Direktbuchung über Hotel-Concierge",
        email: "Kontakt über Hotel-Rezeption",
        accessibility: "Gelegen auf dem Gelände des Beatenberg Resort & Spa\nEinfacher Zugang von allen Hotelunterkünften",
        guestBenefitsTitle: "Gästevorteile",
        complimentaryConsultation: "Kostenlose Beratung für Erstbesucher",
        detoxTea: "Entgiftungstee und gesunde Snacks nach Behandlungen",
        winterGarden: "Zugang zum exklusiven Wintergarten mit Behandlungen",
        guestCardBenefits: "Gästekarten-Vorteile für regionale Attraktionen",
        advanceReservations: "Vorausbuchung für alle Behandlungen empfohlen",
        packageDeals: "Paketangebote für mehrere Behandlungen verfügbar",
        groupPrograms: "Gruppen-Wellness-Programme können arrangiert werden",
        bookNowButton: "Jetzt Ihre Behandlung buchen"
      },

      // Alpine Air Therapy Modal
      alpineAirTherapy: {
        title: "Alpine Lufttherapie & Einrichtungen",
        description: "Erleben Sie die transformative Kraft reiner Alpenluft in unseren hochmodernen Wellness-Einrichtungen. Unsere einzigartige Alpine Lufttherapie verbindet die heilenden Eigenschaften der Bergluft mit traditionellen Schweizer Wellness-Praktiken.",
        relaxationAreas: "Entspannungsbereiche",
        coreFacilities: "Kerneinrichtungen",
        uniqueFeatures: "Einzigartige Merkmale",
        benefitsTitle: "Vorteile der Alpinen Lufttherapie",
        pureMountainAir: "Reine Bergluft",
        pureMountainAirDesc: "Saubere, sauerstoffreiche Luft in 1.200m Höhe verbessert die Atemwegsgesundheit und steigert das Energieniveau.",
        seasonalTherapy: "Saisonale Therapie",
        seasonalTherapyDesc: "Ganzjährige Alpine Lufttherapie mit spezialisierten saisonalen Behandlungen und Kräuterinfusionen.",
        readyToExperience: "Bereit, Alpine Lufttherapie zu erleben?",
        contactConciergeDesc: "Buchen Sie Ihr Erlebnis, indem Sie unseren Concierge kontaktieren. Unser engagiertes Team hilft Ihnen bei der Terminplanung Ihrer Behandlungen und beantwortet alle Fragen zu unseren Spa-Services. Kontaktieren Sie uns an der Rezeption während Ihres Aufenthalts.",
        contactConciergeButton: "Concierge kontaktieren",
        facilities: {
          panoramicLounge: "Panorama-Entspannungslounge mit bodentiefen Fenstern",
          alpineGarden: "Alpengarten-Terrasse für Entspannung im Freien",
          swissSauna: "Traditionelle Schweizer Sauna mit Alpenkräuter-Infusionen",
          steamRoom: "Dampfbad mit lokal bezogenen Bergkräutern",
          infraredSauna: "Infrarot-Sauna für sanfte, tiefe Wärme-Entspannung",
          meditationCorner: "Ruhige Meditations-Ecke mit Bergblick",
          bioSpa: "Sauna & Bio Spa mit Alpenkräuter-Therapien",
          fitnessCenter: "Hochmodernes Fitnesscenter mit Technogym-Ausrüstung",
          yogaPavilion: "Dedizierter Meditations- und Yoga-Pavillon",
          wellnessTerrace: "Outdoor-Wellness-Terrasse für saisonale Alpine Lufttherapie",
          hydrotherapyPools: "Hydrotherapie-Pools mit verschiedenen Temperaturen",
          massageRooms: "Massage- & Heiltherapie-Räume",
          winterGarden: "Exklusiver Wintergarten mit Infrarot-Sauna-Zugang",
          mountainSpringWater: "Bergquellwasser-Behandlungen mit reiner Alpenwasser",
          alpineBotanicals: "Lokale Alpenbotanicals in allen Behandlungen und Produkten",
          swissDesign: "Traditionelles Schweizer Design mit modernem Komfort",
          peptideTherapies: "Schweizer entwickelte Peptid-Therapien",
          healingPractices: "Traditionelle Schweizer Heilpraktiken kombiniert mit moderner Wellness-Wissenschaft",
          panoramicViews: "Panoramablicke auf den Thunersee und die Berner Alpen während Behandlungen"
        }
      },

      // Beauty & Wellness Programs Modal
      beautyWellnessPrograms: {
        title: "Schönheits- & Wellness-Programme",
        description: "Entdecken Sie unsere umfassenden Wellness-Retreats, die darauf ausgelegt sind, Körper, Geist und Seele im Herzen der Schweizer Alpen zu verwandeln. Jedes Programm kombiniert modernste Schweizer Wellness-Technologie mit traditionellen Alpen-Heilpraktiken.",
        readyToTransform: "Bereit, Ihre Wellness-Reise zu verwandeln?",
        contactConciergeDesc: "Buchen Sie Ihr Erlebnis, indem Sie unseren Concierge kontaktieren. Unser engagiertes Team hilft Ihnen bei der Terminplanung Ihrer Behandlungen und beantwortet alle Fragen zu unseren Spa-Services. Kontaktieren Sie uns an der Rezeption während Ihres Aufenthalts.",
        contactConciergeButton: "Concierge kontaktieren",
        categories: {
          detoxRenewal: "Entgiftung & Erneuerung",
          detoxRenewalDesc: "Umfassende Reinigungsprogramme für zelluläre Erneuerung und Toxin-Eliminierung",
          aestheticEnhancement: "Ästhetische Verbesserung",
          aestheticEnhancementDesc: "Fortgeschrittene Hautverjüngung und Schönheitsverbesserungsbehandlungen",
          personalizedWellness: "Personalisierte Wellness",
          personalizedWellnessDesc: "Genetische Analyse und personalisierte Gesundheitsoptimierungsprogramme"
        },
        programs: {
          alpineGlow: {
            title: "Alpine Glow® Retreat",
            duration: "8 Tage",
            description: "Entdecken Sie Alpine Glow: Ein 8-tägiges Retreat mit Gewichtsverlust, Verjüngung und Wellness durch Yoga, gesunde Ernährung und Achtsamkeitspraktiken. Tauchen Sie ein in die Schönheit der Schweizer Alpen und kehren Sie mit einem revitalisierten Geist zurück."
          },
          renewalRadiance: {
            title: "Renewal Radiance® Detox",
            duration: "10 Tage",
            description: "Weltklasse-Entgiftungsprogramm für Chemotherapie- und Krebspatienten. Mit Fokus auf zelluläre Reinigung und Toxin-Eliminierung bieten wir einen 10-tägigen bis 3-wöchigen Aufenthalt mit Entgiftungsmenüs, Therapien und personalisierter Ernährungsplanung."
          },
          nutraVitality: {
            title: "NutraVitality® Verjüngung",
            duration: "7 Tage",
            description: "Tauchen Sie ein in unser 7-tägiges NutraVitality Verjüngungs-Retreat für ganzheitliche Verjüngung. Erleben Sie eine Mischung aus modernster Wissenschaft und luxuriösem Komfort, um Körper, Geist und Seele zu revitalisieren."
          },
          nutraCleanse: {
            title: "NutraCleanse® Erneuerungs-Retreat",
            duration: "7 Tage",
            description: "Umfassendes Entgiftungs- und Erneuerungsprogramm mit Fokus auf zelluläre Reinigung und Toxin-Eliminierung für optimale Wellness und Vitalität."
          },
          nutraBalance: {
            title: "NutraBalance® Gewichts-Wellness-Retreat",
            duration: "7 Tage",
            description: "Ganzheitlicher Ansatz zum Gewichtsmanagement, der Ernährungswissenschaft, metabolische Optimierung und nachhaltige Lebensstiländerungen kombiniert."
          },
          nutraRadiance: {
            title: "NutraRadiance® Ästhetische Verbesserung",
            duration: "7 Tage",
            description: "Fortgeschrittene ästhetische Behandlungen und Hautverjüngungstherapien für natürliche Schönheitsverbesserung und Anti-Aging-Ergebnisse."
          },
          nutraGenomic: {
            title: "NutraGenomic® Insight Retreat",
            duration: "7 Tage",
            description: "Personalisierte Wellness-Programm basierend auf genetischer Analyse und Ernährungsgenomik für optimierte Gesundheitsergebnisse."
          },
          nutraTotal: {
            title: "NutraTotal® Wellness-Bewertung",
            duration: "7 Tage",
            description: "Umfassende Gesundheitsbewertung und Entwicklung eines personalisierten Wellness-Plans durch fortgeschrittene diagnostische Tests."
          },
          nutraDerm: {
            title: "NutraDerm® Strahlende Erneuerung",
            duration: "7 Tage",
            description: "Spezialisierte dermatologische Behandlungen und Hauterneuerungstherapien für gesunde, strahlende und jugendlich aussehende Haut."
          },
          nutraFlex: {
            title: "NutraFlex® Mobilitätsverbesserung",
            duration: "7 Tage",
            description: "Fortgeschrittenes Mobilitäts- und Flexibilitätstraining, das Physiotherapie, Bewegungswissenschaft und Erholungstechniken kombiniert."
          }
        }
      },

      // Therapeutic Treatments Modal
      therapeuticTreatments: {
        title: "Therapeutische Behandlungen & Services",
        description: "Erleben Sie die transformative Kraft geschickter therapeutischer Berührung in unserem ruhigen Alpenrefugium. Unsere zertifizierten Massagetherapeuten verbinden alte Heilweisheit mit zeitgenössischen Techniken und verwenden Premium-Produkte aus der Schweiz.",
        readyToBook: "Bereit, Ihre therapeutische Erfahrung zu buchen?",
        contactConciergeDesc: "Buchen Sie Ihr Erlebnis, indem Sie unseren Concierge kontaktieren. Unser engagiertes Team hilft Ihnen bei der Terminplanung Ihrer Behandlungen und beantwortet alle Fragen zu unseren Spa-Services. Kontaktieren Sie uns an der Rezeption während Ihres Aufenthalts.",
        contactConciergeButton: "Concierge kontaktieren",
        relaxationRejuvenation: {
          title: "Entspannungs- & Verjüngungsbehandlungen",
          treatments: {
            alpineMountainMassage: {
              title: "Alpine Bergmassage",
              price: "CHF 120-180",
              duration: "60-90 Minuten",
              description: "Eine tief entspannende Ganzkörpermassage mit traditionellen Schweizer Techniken und Alpenkräuterölen. Perfekt nach einem Tag Wandern oder Sightseeing."
            },
            lakeThunAromatherapy: {
              title: "Thunersee Aromatherapie",
              price: "CHF 100",
              duration: "60 Minuten",
              description: "Tauchen Sie ein in die Düfte der Schweizer Alpen mit unserer Signature-Aromatherapie-Behandlung mit lokal bezogenen Alpenkräutern."
            },
            swissStoneTherapy: {
              title: "Schweizer Stein-Therapie",
              price: "CHF 140",
              duration: "75 Minuten",
              description: "Erleben Sie die Wärme erhitzter Schweizer Steine kombiniert mit traditionellen Massagetechniken für tiefe Muskelentspannung."
            },
            alpineHerbalSteamBath: {
              title: "Alpenkräuter-Dampfbad",
              price: "CHF 80",
              duration: "45 Minuten",
              description: "Entspannen Sie in unserem traditionellen Dampfbad mit Alpenkräutern. Perfekt zum Entspannen nach der Erkundung der Berge."
            }
          }
        },
        advancedWellness: {
          title: "Fortgeschrittene Wellness-Behandlungen",
          treatments: {
            bioScan: {
              title: "BioScan",
              price: "CHF 440",
              duration: "60-90 Minuten",
              description: "Tiefgreifende Analyse für optimales Wohlbefinden mit speziellen Messtechniken. Inklusive personalisierter Lebensmittelliste und homöopathischer Heilmittel."
            },
            nutritionCoaching: {
              title: "Ernährungscoaching",
              price: "CHF 240",
              duration: "60 Minuten",
              description: "Nahrung als Medizin - individuell angepasste Ernährungsansätze für Ihr bestes Selbst, von mediterraner Diät bis zu gezielten Entgiftungsprogrammen."
            },
            lightTherapy: {
              title: "Lichttherapie",
              price: "CHF 80",
              duration: "30 Minuten",
              description: "Mit Biophotonen stärkt diese sanfte Therapie das Immunsystem und stimuliert die eigenen Heilungskräfte des Körpers."
            },
            pelviPowerTraining: {
              title: "PelviPower Training",
              price: "CHF 29",
              duration: "20-30 Minuten",
              description: "Innovative Methode zur Stärkung der Rumpfmuskulatur mit etwa 25.000 sanften Kontraktionen während einer Sitzung."
            },
            infraredSauna: {
              title: "Infrarot-Sauna",
              price: "Inklusive",
              description: "Tiefe Wärme für tiefe Entspannung - entgiften und entspannen auf zellulärer Ebene. Kostenlos inklusive bei anderen Behandlungen."
            }
          }
        }
      }
    },
    gymPool: {
      technogymTitle: "TECHNOGYM Fitness Center",
      technogymDesc: "Vollständig ausgestattet mit Premium Technogym Geräten",
      premiumEquipment: "Premium Ausrüstung",
      premiumEquipmentDesc: "Hochmoderne Technogym Maschinen für Cardio, Krafttraining und funktionelles Training",
      personalTraining: "Personal Training",
      personalTrainingDesc: "Professionelle Trainer für personalisierte Trainingseinheiten verfügbar",
      access24: "24/7 Zugang",
      access24Desc: "Rund-um-die-Uhr Zugang für Gäste zur Aufrechterhaltung ihrer Fitnessroutine",
      alpinePool: "Alpines Schwimmbad",
      alpinePoolDesc: "Innenpool mit Panoramablick auf die Berge",
      heatedPool: "Beheiztes Becken",
      heatedPoolDesc: "Ganzjähriges Schwimmen in unserem temperaturkontrollierten Innenpool",
      alpineViews: "Alpenblicke",
      alpineViewsDesc: "Schwimmen Sie mit atemberaubendem Blick auf die Schweizer Alpen",
      familyFriendly: "Familienfreundlich",
      familyFriendlyDesc: "Sichere und komfortable Umgebung für Gäste jeden Alters",
      availableToGuests: "Verfügbar für alle Resort-Gäste",
      bookYourStay: "Ihren Aufenthalt buchen"
    },
    cinema: {
      title: "Privates Luxus Kino Theater",
      subtitle: "Ihr Privates Kino & Firmenpräsentationsort",
      seatingCapacity: "Sitzplatzkapazität",
      seatingCapacityDesc: "Platz für bis zu 50 Gäste für private Vorführungen und Veranstaltungen",
      corporateEvents: "Firmenveranstaltungen",
      corporateEventsDesc: "Perfekt für MICE-Events, Präsentationen und Teambuilding-Aktivitäten",
      exclusiveExperience: "Exklusives Erlebnis",
      exclusiveExperienceDesc: "Das einzige private Kino mit IMAX-Technologie im Berner Oberland",
      bookTheatre: "Theater buchen",
      bookYourStay: "Ihren Aufenthalt buchen",
      locatedAt: "Gelegen im Beatenberg Resort & Spa"
    },
    events: {
      title: "Events & Feiern",
      description: "Professionelle Event-Infrastruktur kombiniert mit authentischer Schweizer Gastfreundschaft. Perfekt für Firmenmeetings, Hochzeiten und besondere Feiern.",
      seminarRooms: "Seminarzimmer",
      seminarRoomsDesc: "Moderne AV-Technologie für Firmenmeetings und Workshops",
      hdProjectors: "HD-Projektoren",
      videoConferencing: "Videokonferenzen",
      wirelessPresentation: "Drahtlose Präsentation",
      breakoutSpaces: "Breakout-Bereiche",
      learnMore: "Mehr erfahren",
      banquetHalls: "Bankettsäle",
      banquetHallsDesc: "Elegante Räume für Galadinners, Konferenzen und private Veranstaltungen",
      fineDiningSetup: "Fine Dining Setup",
      professionalCatering: "Professionelles Catering",
      flexibleSeating: "Flexible Bestuhlung",
      seating200: "Platz für bis zu 200 Personen",
      weddingVenues: "Hochzeitslocations",
      weddingVenuesDesc: "Panoramische Alpenkulisse mit vollständiger Planungsunterstützung",
      alpineViews: "Alpenblicke",
      weddingPlanning: "Hochzeitsplanung",
      photographySpots: "Fotospots",
      receptionAreas: "Empfangsbereiche",
      premiumFeatures: "Premium Event-Features",
      digitalLedWall: "Digitale LED-Wand",
      highSpeedInternet: "Hochgeschwindigkeits-Internet",
      audioSystems: "Audio-Systeme",
      eventPlanning: "Event-Planung",
      perfectForOccasion: "Perfekt für jeden Anlass",
      perfectForOccasionDesc: "Unsere vielseitigen Event-Räume decken eine breite Palette von Bedürfnissen und Feiern ab",
      privateParties: "Private Feiern",
      privatePartiesDesc: "Feiern & besondere Anlässe",
      groups: "Gruppen",
      groupsDesc: "Reisegruppen & Zusammenkünfte",
      wellnessRetreats: "Wellness-Retreats",
      wellnessRetreatsDesc: "Gesundheits- & Wellness-Programme",
      corporateEvents: "Firmenveranstaltungen",
      corporateEventsDesc: "Meetings & Konferenzen",
      convenientParking: "Bequeme Parkplätze",
      convenientParkingDesc: "Vor-Ort Kapazität für bis zu 200 Autos & Busse",
      planYourEvent: "Ihr Event planen",
      requestQuote: "Angebot anfordern",
      professionalPlanning: "Professionelle Event-Planung verfügbar",
      // New comprehensive events page translations
      heroTitle: "Unvergessliche",
      heroSubtitle: "Momente schaffen",
      heroDescription: "Luxus-Hochzeiten und Premium-Events, gefertigt mit Präzision, Leidenschaft und unvergleichlicher Aufmerksamkeit für Details.",
      planWedding: "Ihre Hochzeit planen",
      corporateEventsButton: "Firmenveranstaltungen",
      customVenueOffers: "Individuelle Location-Angebote",
      dreamWeddings: "Traumhochzeiten",
      dreamWeddingsDescription: "Jede Liebesgeschichte verdient einen perfekten Anfang. Wir schaffen magische Hochzeitserlebnisse, die Ihre einzigartige Reise widerspiegeln.",
      luxuryCeremonies: "Luxus-Zeremonien",
      floralDesign: "Blumendesign",
      themeDesign: "Themendesign",
      entertainment: "Unterhaltung",
      discussWeddingPackages: "Hochzeitspakete besprechen",
      corporateExcellence: "Firmen-Exzellenz",
      corporateExcellenceDescription: "Professionelle Events, die Beziehungen stärken, Teams inspirieren und die Exzellenz Ihrer Marke zur Schau stellen.",
      conferences: "Konferenzen",
      productLaunches: "Produktlaunches",
      teamBuilding: "Teambuilding",
      annualGalas: "Jahresgalas",
      discussCorporatePackages: "Firmenpakete besprechen",
      privatePartiesTitle: "Private Feiern",
      privatePartiesDescription: "Feiern Sie die besonderen Momente des Lebens mit unvergesslichen Partys, die auf Ihren Stil und Ihre Vision zugeschnitten sind.",
      birthdayCelebrations: "Geburtstagsfeiern",
      anniversaries: "Jahrestage",
      luxuryGalas: "Luxus-Galas",
      discussPrivatePartyPackages: "Private Party-Pakete besprechen",
      completeEventExperiences: "Vollständige Event-Erlebnisse",
      completeEventExperiencesDescription: "Von intimen Zusammenkünften bis hin zu großen Feiern - wir verwirklichen Ihre Vision mit sorgfältiger Planung und makelloser Ausführung.",
      weddingPlanningService: "Hochzeitsplanung",
      weddingPlanningDescription: "Vollständige Hochzeitskoordination vom Antrag bis zur Feier, die Ihren perfekten Tag mit Aufmerksamkeit für jedes Detail schafft.",
      weddingPlanningFeatures: "Schweizer Gastfreundschaft, Anbieterkoordination, Zeitplanmanagement, 24/7 Koordination bis zum Event",
      weddingPlanningDetails: "Von intimen Zeremonien bis hin zu großen Feiern kümmern wir uns um jeden Aspekt Ihres besonderen Tages. Unsere erfahrenen Hochzeitsplaner arbeiten eng mit Ihnen zusammen, um ein personalisiertes Erlebnis zu schaffen, das Ihre einzigartige Liebesgeschichte widerspiegelt.",
      weddingPlanningHighlights: "Individuelles Zeremoniedesign und -aufbau, Professionelles Anbietermanagement, Detaillierte Zeitplankoordination, Vor-Ort Event-Betreuung",
      corporateEventsService: "Firmenveranstaltungen",
      corporateEventsServiceDescription: "Professionelle Firmenveranstaltungen, die bleibende Eindrücke bei Kunden und Kollegen hinterlassen.",
      corporateEventsServiceFeatures: "Konferenzplanung, Produktlaunches, Teambuilding, Jahresgalas",
      corporateEventsServiceDetails: "Heben Sie Ihre Geschäftsveranstaltungen mit unseren umfassenden Firmen-Event-Services auf ein neues Level. Wir verstehen die Bedeutung professioneller Präsentation und nahtloser Ausführung für Ihre Geschäftsziele.",
      corporateEventsServiceHighlights: "Executive-Konferenzplanung, Markenpräsentations-Setup, AV-Ausrüstung und Technologie, Catering und Gastfreundschaft",
      privateCelebrations: "Private Feiern",
      privateCelebrationsDescription: "Intime Zusammenkünfte und Meilenstein-Feiern, die Ihren einzigartigen Stil widerspiegeln.",
      privateCelebrationsFeatures: "Geburtstagsfeiern, Jahrestage, Abschiedsfeiern, Ringzeremonien, Viele weitere glückliche Anlässe",
      privateCelebrationsDetails: "Feiern Sie die besonderen Momente des Lebens mit personalisierten Events, die das Wesen Ihres Anlasses einfangen. Von Meilenstein-Geburtstagen bis hin zu intimen Jahrestagen schaffen wir unvergessliche Erlebnisse.",
      privateCelebrationsHighlights: "Personalisiertes Themenentwicklung, Individuelle Unterhaltungsbuchung, Gourmet-Catering-Optionen, Fotografie und Dokumentation, Gästeerlebnis-Management",
      serviceOverview: "Service-Übersicht",
      keyFeatures: "Hauptmerkmale",
      whatWeProvide: "Was wir bieten",
      getStartedWith: "Beginnen Sie mit",
      premiumEventFeatures: "Premium Event-Features",
      fullServiceEventPlanning: "Vollservice-Eventplanung",
      fullServiceEventPlanningDesc: "Vollständige Eventkoordination von der Konzeption bis zur Ausführung",
      gourmetCateringServices: "Gourmet-Catering-Services",
      gourmetCateringServicesDesc: "Schweizer Kulinarische Expertise trifft auf internationale Aromen",
      stateOfTheArtTechnology: "Hochmoderne Technologie",
      stateOfTheArtTechnologyDesc: "Hochmoderne AV-Ausrüstung und nahtlose Konnektivität",
      wellnessIntegration: "Wellness-Integration",
      wellnessIntegrationDesc: "Alpine Wellness-Erlebnisse für Event-Teilnehmer",
      digitalLedWallFeature: "Digitale LED-Wand",
      digitalLedWallFeatureDesc: "Flexible Layouts für individualisierte Events",
      audioSystemsFeature: "Audio-Systeme",
      audioSystemsFeatureDesc: "Professionelle Sound-Ausrüstung inklusive",
      eventPlanningFeature: "Event-Planung",
      eventPlanningFeatureDesc: "Vollservice-Eventkoordination",
      premiumAmenities: "Premium-Ausstattung",
      premiumAmenitiesDesc: "Luxus-Einrichtungen und Services",
      convenientParkingTitle: "Bequeme Parkplätze",
      convenientParkingCapacity: "Vor-Ort Kapazität für bis zu 200 Autos & Busse",
      complimentaryParking: "Kostenlose Parkplätze für alle Event-Gäste",
      ourPortfolio: "UNSER PORTFOLIO",
      eventGallery: "Event-Galerie",
      eventGallerySubtitle: "Ein Einblick in die außergewöhnlichen Events, die wir das Privileg hatten zu schaffen",
      whatOurClientsSay: "Was unsere Kunden sagen",
      whatOurClientsSayDescription: "Die Freude und Zufriedenheit unserer Kunden ist unser größter Erfolg.",
      readyToCreate: "Bereit, etwas Außergewöhnliches zu schaffen?",
      readyToCreateDescription: "Lassen Sie uns Ihre Vision zum Leben erwecken. Kontaktieren Sie uns heute für eine personalisierte Beratung und entdecken Sie, wie wir Ihr Event unvergesslich machen können.",
      planningTimeline: "Planungszeitrahmen",
      planningTimelineDesc: "6-18 Monate im Voraus",
      eventCapacity: "Event-Kapazität",
      eventCapacityDesc: "20-500 Gäste",
      documentation: "Dokumentation",
      documentationDesc: "Professionelle Fotografie",
      entertainmentFeature: "Unterhaltung",
      entertainmentFeatureDesc: "Live-Musik & DJ-Services",
      // Testimonials
      testimonial1Name: "Sarah & Michael",
      testimonial1Event: "Hochzeitsfeier",
      testimonial1Text: "Unsere Hochzeit war absolut perfekt. Jedes Detail wurde mit solcher Sorgfalt und Professionalität behandelt. Wir hätten uns nichts Besseres wünschen können!",
      testimonial2Name: "David Chen",
      testimonial2Event: "Firmen-Launch",
      testimonial2Text: "Das Produktlaunch-Event übertraf alle Erwartungen. Die Aufmerksamkeit für Details und makellose Ausführung ließ unser Unternehmen glänzen.",
      testimonial3Name: "Jennifer Williams",
      testimonial3Event: "Jahrestagsfeier",
      testimonial3Text: "Sie verwandelten unsere Vision in die Realität. Die Feier war elegant, raffiniert und genau das, wovon wir geträumt hatten."
    },
    attractions: {
      title: "Entdecken Sie das Berner Oberland",
      description: "Erkunden Sie die beliebtesten Attraktionen der Schweiz, sortiert nach Besucherpopularität. Vom ikonischen Jungfraujoch bis zu versteckten Alpenjuwelen.",
      niederhorn: {
        name: "Niederhorn Gipfel",
        description: "Erleben Sie atemberaubende Panoramablicke auf den Thunersee, die Berner Alpen und die Eiger-, Mönch- und Jungfrau-Gipfel. Erreichbar mit der Gondelbahn von Beatenberg, bietet dieser Gipfel Wanderwege, ein Restaurant und atemberaubende Fotogelegenheiten.",
        shortDescription: "Panoramablick auf den Thunersee und die Berner Alpen",
        features: ["Gondelbahn-Zugang", "Wanderwege", "Panoramablicke", "Restaurant", "Fotospots"],
        guestBenefits: ["10% Rabatt auf Gondelbahn-Tickets", "Kostenlose Parkplätze an der Gondelbahn-Station", "Kostenlose Wanderkarte", "Wetter-Updates über Hotel-App"]
      },
      "st-beatus-caves": {
        name: "St. Beatus Höhlen",
        description: "Eines der größten Höhlensysteme der Schweiz mit atemberaubenden Stalaktiten, unterirdischen Seen und faszinierenden geologischen Formationen. Nach dem Heiligen Beatus benannt, sind diese Höhlen seit Jahrhunderten ein Pilgerort.",
        shortDescription: "Größtes Höhlensystem der Schweiz mit unterirdischen Seen",
        features: ["Geführte Touren", "Unterirdischer See", "Stalaktiten", "Geologische Formationen", "Museum"],
        guestBenefits: ["Vorrangige Buchung", "Kostenloser Audio-Guide", "Restaurant-Rabatt", "Hotel-Shuttle verfügbar"]
      },
      "harder-kulm": {
        name: "Harder Kulm",
        description: "Erreichbar mit der historischen Standseilbahn, bietet Harder Kulm spektakuläre Ausblicke auf Interlaken, den Thunersee und den Brienzersee. Der Gipfel verfügt über Spazierwege, ein Restaurant und den berühmten Harder Kulm Aussichtspunkt.",
        shortDescription: "Historische Standseilbahn zu Panorama-Aussichtspunkten",
        features: ["Standseilbahn", "Panoramablicke", "Spazierwege", "Restaurant", "Aussichtsterrasse"],
        guestBenefits: ["15% Rabatt auf Standseilbahn-Tickets", "Kostenloser Hotel-Shuttle", "Restaurant-Reservierung Vorrang", "Wetter-Updates"]
      },
      "jungfraujoch": {
        name: "Jungfraujoch - Gipfel Europas",
        description: "Der höchste Bahnhof Europas auf 3.454m mit Blick auf den Aletschgletscher und die umliegenden Gipfel. Bietet das Eispalast, die Sphinx-Observatorium und atemberaubende Alpenlandschaft.",
        shortDescription: "Europas höchster Bahnhof mit Eispalast",
        features: ["Höchster Bahnhof Europas", "Eispalast", "Sphinx-Observatorium", "Aletschgletscher-Blick", "Restaurant"],
        guestBenefits: ["Kostenloser Shuttle-Service", "Vorrangige Buchung", "Restaurant-Rabatt", "Wetter-Updates"],
        distance: "45 Min. Fahrt",
        duration: "Halbtagesausflug"
      },
      "thun-castle": {
        name: "Schloss Thun",
        description: "Ikone mittelalterliche Burg mit Blick auf die Stadt Thun und den Thunersee. Bietet ein Museum mit regionaler Geschichte, schöne Gärten und Panoramablicke auf die umliegenden Berge.",
        shortDescription: "Mittelalterliche Burg mit Museum und Seeblick",
        features: ["Mittelalterliche Architektur", "Regionales Museum", "Schöne Gärten", "Panoramablicke", "Historische Ausstellungen"],
        guestBenefits: ["Kostenlose Parkplätze", "Audio-Guide inbegriffen", "Gartenzugang", "Fotoerlaubnis inbegriffen"],
        distance: "30 Min. Fahrt",
        duration: "1-2 Stunden Besuch"
      },
      "spiez-castle": {
        name: "Schloss Spiez",
        description: "Romanische Kirche und malerische Lage am Thunersee. Bietet einen historischen Weinkeller, schöne Gärten und atemberaubende Seeblicke. Eines der meistfotografierten Schlösser der Schweiz.",
        shortDescription: "Romanisches Schloss mit Weinkeller und Seeblick",
        features: ["Romanische Architektur", "Weinkeller", "Seeblicke", "Schöne Gärten", "Historische Bedeutung"],
        guestBenefits: ["Weinprobe-Rabatt", "Gartenführung inbegriffen", "Fotospots", "Kostenlose Parkplätze"],
        distance: "35 Min. Fahrt",
        duration: "1-2 Stunden Besuch"
      },
      "schynige-platte": {
        name: "Schynige Platte",
        description: "Historische Zahnradbahn bringt Sie zu einem alpinen Botanischen Garten auf 2.000m. Bietet über 650 Pflanzensorten, spektakuläre Ausblicke auf die Jungfrau-Region und Spazierwege.",
        shortDescription: "Historische Zahnradbahn zum Alpengarten",
        features: ["Historische Zahnradbahn", "Alpiner Botanischer Garten", "Panoramablicke", "Spazierwege", "Restaurant"],
        guestBenefits: ["20% Rabatt auf Zugtickets", "Kostenloser Alpengarten-Führer", "Vorrangige Reservierungen", "Hotel-Shuttle-Service"],
        distance: "25 Min. Fahrt",
        duration: "3-4 Stunden Ausflug"
      },
      "trummelebach-falls": {
        name: "Trümmelbachfälle",
        description: "UNESCO-anerkanntes Naturwunder mit 10 gletschergespeisten Wasserfällen im Berginneren. Europas größtes unterirdisches Wasserfallsystem.",
        shortDescription: "10 gletschergespeiste Wasserfälle im Berginneren",
        features: ["Unterirdische Wasserfälle", "Geführte Touren", "Aufzug-Zugang", "UNESCO-Stätte", "Restaurant"],
        guestBenefits: ["Vorrangige Buchung", "Audio-Guide inbegriffen", "Restaurant-Rabatt", "Kostenlose Parkplätze"],
        distance: "30 Min. Fahrt",
        duration: "1-2 Stunden Besuch"
      },
      "giessbach-falls": {
        name: "Giessbachfälle",
        description: "14 Wasserfälle stürzen 500m den Berg hinunter in den Brienzersee. Bietet historisches Grand Hotel und malerische Spazierwege.",
        shortDescription: "14 Wasserfälle stürzen 500m in den Brienzersee",
        features: ["14 Wasserfälle", "Historisches Grand Hotel", "Spazierwege", "Bootsfahrten", "Panoramablicke"],
        guestBenefits: ["Bootsfahrt-Rabatt", "Hotel-Shuttle-Service", "Vorrangige Reservierungen", "Kostenlose Parkplätze"],
        distance: "40 Min. Fahrt",
        duration: "1-2 Stunden Besuch"
      },
      "lake-thun-brienz": {
        name: "Thuner- und Brienzersee",
        description: "Zwei unberührte Alpenseen bieten Bootsfahrten, Wassersport und malerische Dörfer. Der Thunersee bietet dramatische Bergkulissen, während der Brienzersee friedliche Gewässer bietet.",
        shortDescription: "Zwei unberührte Alpenseen mit Bootsfahrten",
        features: ["Bootsfahrten", "Wassersport", "Schwimmbereiche", "Malerische Dörfer", "Angeln"],
        guestBenefits: ["Bootsfahrt-Rabatt", "Wassersport-Ausrüstungsverleih", "Vorrangige Buchung", "Kostenloser Shuttle"],
        distance: "20 Min. Fahrt",
        duration: "2-4 Stunden Schifffahrt"
      },
      "adventure-park-interlaken": {
        name: "Interlaken Seilpark",
        description: "Aufregender Abenteuerpark mit Seilrutschen, Seilgärten, Kletterwänden und Team-Herausforderungen. Perfekt für Familien und Abenteuersuchende.",
        shortDescription: "Seilrutschen, Seilgärten und Abenteueraktivitäten",
        features: ["Seilrutschen", "Seilgärten", "Kletterwände", "Team-Herausforderungen", "Familienaktivitäten"],
        guestBenefits: ["Vorrangige Buchung", "Gruppenrabatt", "Ausrüstung inbegriffen", "Hotel-Shuttle"],
        distance: "25 Min. Fahrt",
        duration: "2-3 Stunden Aktivität"
      },
      "paragliding": {
        name: "Paragliding",
        description: "Tandem-Paragliding-Flüge von malerischen Startplätzen mit atemberaubenden Luftblicken auf die Jungfrau-Region und Alpentäler.",
        shortDescription: "Tandem-Paragliding mit Alpenblick",
        features: ["Tandem-Flüge", "Zertifizierte Piloten", "Atemberaubende Aussichten", "Foto-Service", "Sicherheitsausrüstung"],
        guestBenefits: ["Vorrangige Buchung", "Foto/Video-Paket-Rabatt", "Hotel-Abholung", "Wetter-Updates"],
        distance: "20 Min. Fahrt",
        duration: "2-3 Stunden Erlebnis"
      },
      "cross-country-skiing": {
        name: "Langlauf",
        description: "Präparierte Langlaufstrecken rund um Beatenberg und Habkern. Perfekt für alle Könnensstufen mit atemberaubender Alpenlandschaft.",
        shortDescription: "Präparierte Strecken für Langlauf",
        features: ["Präparierte Strecken", "Alle Könnensstufen", "Malerische Routen", "Ausrüstungsverleih", "Unterricht verfügbar"],
        guestBenefits: ["Kostenloser Streckenzugang", "Ausrüstungsverleih-Rabatt", "Unterricht inbegriffen", "Hotel-Shuttle"],
        distance: "15 Min. Fahrt",
        duration: "2-4 Stunden Aktivität"
      },
      "beatenberg-panorama-trail": {
        name: "Beatenberg Panoramaweg",
        description: "Malerischer Wanderweg mit Panoramablick auf den Thunersee und die Berner Alpen. Einfache bis mittlere Wege mit lehrreichen Wegweisern.",
        shortDescription: "Malerischer Weg mit Thunersee-Panoramablick",
        features: ["Panoramablicke", "Lehrreiche Wegweiser", "Einfache Wege", "Wildtierbeobachtung", "Fotospots"],
        guestBenefits: ["Kostenloser Zugang", "Wanderkarte im Hotel", "Geführte Touren verfügbar", "Rastplätze"],
        distance: "5 Min. zu Fuß",
        duration: "1-2 Stunden Wanderung"
      },
      "saxeten-village": {
        name: "Saxeten Dorf",
        description: "Verstecktes Bergdorf mit traditionellen Chalets, Alpenwiesen und authentischer Schweizer Bergkultur. Perfekt für das Erleben des ländlichen Schweiz.",
        shortDescription: "Traditionelles Bergdorf mit Chalets",
        features: ["Traditionelle Chalets", "Bergkultur", "Alpenwiesen", "Lokales Handwerk", "Friedliche Atmosphäre"],
        guestBenefits: ["Lokaler Führer inbegriffen", "Traditionelle Mahlzeiterfahrung", "Fotoerlaubnisse", "Hotel-Shuttle"],
        distance: "20 Min. Fahrt",
        duration: "1-2 Stunden Besuch"
      }
    },
    localAttractions: {
      hero: {
        title: "Entdecken Sie Alpine Abenteuer",
        subtitle: "Erkunden Sie die atemberaubende Schönheit des Berner Oberlandes",
        bookStay: "Jetzt Ihren Aufenthalt buchen"
      },
      quickAccess: {
        title: "Lokale Attraktionen erkunden",
        description: "Entdecken Sie das reiche kulturelle Erbe, die natürliche Schönheit und Abenteuermöglichkeiten in der Region Berner Oberland, alle in Reichweite des Beatenberg Resort & Spa.",
        allAttractions: "Alle Attraktionen",
        within30min: "Innerhalb 30 Min.",
        natureHiking: "Natur & Wandern",
        cultureHistory: "Kultur & Geschichte",
        activities: "Aktivitäten",
        allAttractionsDesc: "Vollständiger Führer zu allen lokalen Attraktionen",
        within30minDesc: "Einfach erreichbare Attraktionen",
        natureHikingDesc: "Bergpfade und Naturwunder",
        cultureHistoryDesc: "Historische Stätten und kulturelle Erlebnisse",
        activitiesDesc: "Abenteuer- und Freizeitaktivitäten",
        learnMore: "Mehr erfahren",
        noAttractionsFound: "Keine Attraktionen in dieser Kategorie gefunden."
      },
      seasonal: {
        title: "Saisonale Aktivitäten",
        description: "Erleben Sie das Beste jeder Jahreszeit mit unserer kuratierten Auswahl an Aktivitäten, von Sommerwanderungen bis zu Winterski-Abenteuern.",
        summer: "Sommer",
        winter: "Winter",
        yearRound: "Ganzjährig",
        alpineHiking: "Alpine Wanderwege",
        alpineHikingDesc: "Entdecken Sie über 200km markierte Wanderwege mit verschiedenen Schwierigkeitsgraden, von sanften Seeuferwanderungen bis zu anspruchsvollen Bergbesteigungen.",
        alpineHikingActivities: ["Geführte Wandertouren", "Berggipfel-Besteigungen", "Seeufer-Spaziergänge", "Wildblumen-Identifikation"],
        skiSnowboard: "Ski & Snowboard Paradies",
        skiSnowboardDesc: "Zugang zu erstklassigen Skigebieten innerhalb von 30 Minuten, einschließlich Grindelwald, Wengen und Mürren mit unserem kostenlosen Shuttle-Service.",
        skiSnowboardActivities: ["Skigebiet-Zugang", "Snowboard-Parks", "Langlauf", "Schneeschuh-Expeditionen"],
        lakeWaterSports: "Thunersee Wassersport",
        lakeWaterSportsDesc: "Erleben Sie kristallklares Wasser mit Kajakfahren, Stand-up-Paddling, Segeln und malerischen Bootsfahrten auf der schönen Thunersee der Schweiz.",
        lakeWaterSportsActivities: ["Kajak & Kanu", "Stand-up-Paddling", "Segelunterricht", "Bootsfahrten"],
        photography: "Fotografie-Expeditionen",
        photographyDesc: "Fangen Sie atemberaubende Alpenlandschaften und versteckte Juwelen mit professionellen Fototouren und Workshops unter der Leitung lokaler Experten ein.",
        photographyActivities: ["Landschaftsfotografie", "Wildtierfotografie", "Sonnenaufgang/Sonnenuntergang-Shootings", "Foto-Workshops"],
        culinary: "Schweizer Kulinarische Erlebnisse",
        culinaryDesc: "Tauchen Sie ein in Schweizer Küchentraditionen mit Kochkursen, Weinproben und Besuchen bei lokalen Produzenten und Handwerkern.",
        culinaryActivities: ["Kochkurse", "Weinproben", "Käseherstellung", "Hofbesuche"],
        mountainAdventures: "Bergabenteuer",
        mountainAdventuresDesc: "Von Paragliding bis Klettern, erleben Sie adrenalingeladene Aktivitäten in einer der spektakulärsten Berglandschaften Europas.",
        mountainAdventuresActivities: ["Paragliding", "Klettern", "Via Ferrata", "Canyoning"],
        activitiesInclude: "Aktivitäten beinhalten:",
        bookYourStay: "Ihren Aufenthalt buchen"
      },
      guestBenefits: {
        title: "Gastkarten-Vorteile",
        description: "Als Gast des Beatenberg Resort & Spa genießen Sie exklusive Vorteile und Rabatte bei lokalen Attraktionen und Dienstleistungen in der Beatenberg- und Habkern-Region.",
        freeBenefits: "Kostenlose Vorteile",
        discountBenefits: "Rabatt-Vorteile",
        howToGet: "Wie Sie Ihre Gastkarte erhalten",
        howToGetDesc: "Ihre Gastkarte ist automatisch in Ihrem Aufenthalt im Beatenberg Resort & Spa enthalten. Holen Sie sie bei der Ankunft an der Rezeption ab und genießen Sie sofort exklusive Vorteile.",
        validDuringStay: "Gültig während Ihres Aufenthalts • Regionale Partnerschaften inbegriffen",
        partner: "Partner",
        benefits: {
          freeTransport: {
            title: "Kostenloser Öffentlicher Verkehr",
            description: "Kostenlose Fahrt mit dem öffentlichen Verkehr in den ausgewiesenen Zonen um Beatenberg und Habkern"
          },
          libraryAccess: {
            title: "Bibliothekszugang",
            description: "Kostenlose Ausleihe von Büchern, DVDs und CDs aus der Beatenberg Bibliothek"
          },
          eBikeDiscount: {
            title: "E-Bike Verleih",
            description: "50% Rabatt auf E-Bike Verleih bei Beatenberg Tourismus"
          },
          miniGolfDiscount: {
            title: "Mini Golf",
            description: "50% Rabatt auf Mini Golf im Hotel Dorint Blüemlisalp"
          },
          schynigePlatteDiscount: {
            title: "Schynige Platte Bahn",
            description: "20% Rabatt auf Fahrten mit der Schynige Platte Bahn"
          },
          niesenbahnDiscount: {
            title: "Niesenbahn Standseilbahn",
            description: "20% Rabatt auf Tickets für die Niesenbahn Standseilbahn"
          },
          harderKulmDiscount: {
            title: "Harder Kulm Standseilbahn",
            description: "15% Rabatt auf Fahrten mit der Harder Kulm Standseilbahn"
          },
          escapeRoomDiscount: {
            title: "Escape Rooms",
            description: "15% Rabatt auf Escape Rooms und Mystery Tours in Interlaken"
          },
          niederhornDiscount: {
            title: "Niederhorn Gondelbahn",
            description: "20% Rabatt auf Fahrten mit der Niederhorn Gondelbahn"
          },
          swimmingPoolAccess: {
            title: "Habkern Schwimmbad",
            description: "50% Rabatt auf den Eintritt ins Habkern Schwimmbad"
          },
          busAlpinService: {
            title: "Bus Alpin Service",
            description: "50% Rabatt auf Bus Alpin Services bei mindestens zweitägigem Aufenthalt"
          },
          gameBorrowing: {
            title: "Spielebibliothek",
            description: "Kostenlose Ausleihe von Spielen bei Beatenberg Tourismus"
          }
        }
      },
      transportation: {
        title: "Transport & Barrierefreiheit",
        description: "Das Berner Oberland zu erkunden ist einfach mit ausgezeichnetem öffentlichen Verkehr, Hotel-Shuttles und malerischen Routen, die Sie zu allen wichtigen Attraktionen verbinden.",
        publicTransport: "Öffentlicher Verkehr",
        keyRoutes: "Hauptstrecken vom Beatenberg Resort & Spa",
        destination: "Ziel",
        method: "Methode",
        duration: "Dauer",
        frequency: "Häufigkeit",
        freeWithGuestCard: "Kostenlos mit Gastkarte",
        freeWithGuestCardDesc: "Genießen Sie kostenlosen öffentlichen Verkehr in ausgewiesenen Zonen um Beatenberg und Habkern mit Ihrer Beatenberg Resort & Spa Gastkarte.",
        hotelShuttles: "Hotel-Shuttle-Services",
        bookingInfo: "Buchungsinformationen",
        bookingInfoDesc: "Shuttle-Services können an der Rezeption oder durch Anruf bei unserem Concierge gebucht werden. Vorausbuchung wird für Flughafentransfers und besondere Anlässe empfohlen.",
        concierge: "Concierge: +41 33 841 00 21",
        drivingDirections: "Fahrrichtungen & Parken",
        fromZurich: "Vom Flughafen Zürich (2,5 Stunden)",
        fromGeneva: "Vom Flughafen Genf (3 Stunden)",
        parkingAtHotel: "Parken im Hotel",
        parkingAtHotelDesc: "Kostenlose Parkplätze sind für alle Gäste verfügbar. Unser Parkbereich bietet malerische Aussichten und ist bequem in der Nähe des Haupteingangs gelegen.",
        accessibility: "Barrierefreiheits-Informationen",
        wheelchairAccessible: "Rollstuhlgerechte Attraktionen",
        mobilityServices: "Mobilitätsdienste",
        contactConcierge: "Kontaktieren Sie unseren Concierge",
        contactConciergeDesc: "für Hilfe bei der Planung barrierefreier Reiserouten und der Organisation spezialisierter Transporte. Wir sind verpflichtet, sicherzustellen, dass alle Gäste das Berner Oberland-Erlebnis genießen können.",
        accessibilityAttractions: [
          "Niederhorn Gipfel (zugänglich mit Seilbahn)",
          "St. Beatus Höhlen (Hauptbereiche rollstuhlgerecht)",
          "Thun Schloss (Erdgeschoss zugänglich)",
          "Spiez Schloss Gärten (rollstuhlgerechte Wege)",
          "Die meisten Bus- und Bahnhöfe in der Region"
        ],
        mobilityServicesList: [
          "Rollstuhlverleih im Hotel verfügbar",
          "Barrierefreie Taxidienste können arrangiert werden",
          "Die meisten Seilbahnen und Standseilbahnen sind rollstuhlgerecht",
          "Hotel-Shuttle-Fahrzeuge für Rollstühle ausgestattet"
        ],
        drivingDirectionsZurich: [
          "Folgen Sie der A1 Autobahn Richtung Bern",
          "Nehmen Sie Ausfahrt 41 (Thun Süd) Richtung Thun",
          "Folgen Sie E27 Richtung Interlaken",
          "Nehmen Sie Ausfahrt Beatenberg/Habkern",
          "Folgen Sie den Schildern zum Beatenberg Resort & Spa (ca. 10 Minuten)"
        ],
        drivingDirectionsGeneva: [
          "Nehmen Sie die A1 Autobahn Richtung Lausanne",
          "Fahren Sie weiter auf der A9 Richtung Montreux",
          "Nehmen Sie Ausfahrt 29 (Aigle) und folgen Sie den Schildern nach Interlaken",
          "Folgen Sie E27 Richtung Interlaken",
          "Nehmen Sie Ausfahrt Beatenberg/Habkern"
        ],
        frequencies: {
          every30min: "Alle 30 Min",
          everyHour: "Stündlich",
          every2Hours: "Alle 2 Stunden",
          asNeeded: "Nach Bedarf",
          onRequest: "Auf Anfrage",
          onDemand: "Bei Bedarf",
          scheduled: "Nach Fahrplan",
          daily24_7: "24/7 Buchung"
        },
        shuttleServices: {
          skiShuttle: {
            service: "Ski-Shuttle",
            destinations: "Jungfrau-Region Skigebiete",
            schedule: "Täglich 8:00 - 17:00 Uhr"
          },
          airportTransfer: {
            service: "Flughafentransfer",
            destinations: "Bern/Basel/Zürich Flughäfen",
            schedule: "Auf Anfrage"
          },
          cityTransfer: {
            service: "Stadttransfer",
            destinations: "Interlaken, Thun, Spiez",
            schedule: "Täglich 7:00 - 22:00 Uhr"
          },
          attractionShuttle: {
            service: "Attraktionen-Shuttle",
            destinations: "Hauptattraktionen innerhalb 30 Min",
            schedule: "Täglich 9:00 - 18:00 Uhr"
          }
        }
      },
      cta: {
        title: "Bereit zu erkunden?",
        description: "Buchen Sie Ihren Aufenthalt im Beatenberg Resort & Spa und entdecken Sie die Magie des Berner Oberlandes. Ihr Abenteuer wartet mit erstklassigen Unterkünften, außergewöhnlicher Küche und unvergesslichen Erlebnissen.",
        bookYourStay: "Ihren Aufenthalt buchen",
        speakToExpert: "Mit Experten sprechen",
        bookYourStayDesc: "Reservieren Sie Ihr Zimmer mit exklusiven Preisen und Gastkarten-Vorteilen inbegriffen.",
        speakToExpertDesc: "Unser Concierge-Team kann Ihnen helfen, Ihre perfekte Berner Oberland-Reiseroute zu planen.",
        questions: "Fragen? Rufen Sie unser Reservierungsteam an unter"
      }
    },
    guestBenefits: {
      freeTransport: {
        title: "Kostenloser Öffentlicher Verkehr",
        description: "Kostenlose Fahrt mit dem öffentlichen Verkehr in ausgewiesenen Zonen um Beatenberg und Habkern"
      },
      libraryAccess: {
        title: "Bibliothekszugang",
        description: "Kostenlose Ausleihe von Büchern, DVDs und CDs aus der Beatenberg Bibliothek"
      },
      eBikeDiscount: {
        title: "E-Bike Verleih",
        description: "50% Rabatt auf E-Bike Verleih bei Beatenberg Tourismus"
      },
      miniGolfDiscount: {
        title: "Mini Golf",
        description: "50% Rabatt auf Mini Golf im Hotel Dorint Blüemlisalp"
      },
      schynigePlatteDiscount: {
        title: "Schynige Platte Bahn",
        description: "20% Rabatt auf Fahrten mit der Schynige Platte Bahn"
      },
      niesenbahnDiscount: {
        title: "Niesenbahn Standseilbahn",
        description: "20% Rabatt auf Tickets für die Niesenbahn Standseilbahn"
      },
      harderKulmDiscount: {
        title: "Harder Kulm Standseilbahn",
        description: "15% Rabatt auf Fahrten mit der Harder Kulm Standseilbahn"
      },
      escapeRoomDiscount: {
        title: "Escape Rooms",
        description: "15% Rabatt auf Escape Rooms und Mystery Tours in Interlaken"
      },
      niederhornDiscount: {
        title: "Niederhorn Gondelbahn",
        description: "20% Rabatt auf Fahrten mit der Niederhorn Gondelbahn"
      },
      swimmingPoolAccess: {
        title: "Habkern Schwimmbad",
        description: "50% Rabatt auf den Eintritt ins Habkern Schwimmbad"
      },
      busAlpinService: {
        title: "Bus Alpin Service",
        description: "50% Rabatt auf Bus Alpin Services bei mindestens zweitägigem Aufenthalt"
      },
      gameBorrowing: {
        title: "Spielbibliothek",
        description: "Kostenlose Ausleihe von Spielen bei Beatenberg Tourismus"
      }
    },
    moments: {
      title: "Momente der Freude im Beatenberg Resort & Spa",
      description: "Entdecken Sie die schönen Momente und Erlebnisse, die jeden Aufenthalt in unserem Resort wirklich besonders machen",
      showNewMoments: "Neue Momente anzeigen"
    },
    cta: {
      title: "Bereit, Ihr perfektes Alpen-Erlebnis zu schaffen?",
      description: "Erleben Sie die Magie des Schweizer Alpenluxus, erstklassige Wellness-Programme und atemberaubende natürliche Schönheit im Beatenberg Resort & Spa.",
      bookYourStay: "Ihren Aufenthalt buchen",
      contactConcierge: "Unseren Concierge kontaktieren",
      happyGuests: "Zufriedene Gäste",
      trustedByGuests: "Vertraut von Gästen aus aller Welt",
      certifiedExcellence: "Zertifizierte Exzellenz",
      certifiedExcellenceDesc: "Anerkannt von der führenden Schweizer Gastgewerbeorganisation",
      gastrosuisseRated: "Gastrosuisse Bewertet",
      swissHotelAssociation: "Schweizer Hotelverband",
      swisstainableCommitted: "Swisstainable Verpflichtet",
      qualityAssurance: "Qualitätssicherung",
      startReservation: "Reservierung starten",
      contactTeam: "Unser Team kontaktieren"
    },

    ratings: {
      hotel: {
        reviews: "Bewertungen"
      },
      restaurant: {
        reviews: "Bewertungen"
      }
    },
    contactModals: {
      spa: {
        title: "Kontaktieren Sie unser Spa-Team",
        subtitle: "Lassen Sie uns Ihr perfektes Wellness-Erlebnis schaffen",
        phoneTitle: "Spa-Direktleitung",
        phoneDesc: "Sprechen Sie mit unseren Spa-Spezialisten",
        emailTitle: "Spa-E-Mail",
        emailDesc: "Senden Sie uns Ihre Behandlungspräferenzen",
        locationTitle: "Spa-Standort",
        locationDesc: "Sehen Sie das Spa im Beatenberg Resort & Spa auf Google Maps",
        bookingTitle: "Online-Buchung",
        bookingDesc: "Buchen Sie Ihre Behandlungen sofort",
        whatsappTitle: "WhatsApp Spa",
        whatsappDesc: "Schnelle Spa-Beratung über WhatsApp",
        servicesTitle: "Unsere Spa-Services",
        servicesList: [
          "Alpine Lufttherapie-Behandlungen und Wellness-Programme",
          "Therapeutische Behandlungen und Massagetherapien",
          "Schönheitsbehandlungen und Wellness-Beratungen",
          "Schnelle Entspannungssitzungen und Stressabbau",
          "Private Spa-Erlebnisse und Paarbehandlungen",
          "Maßgeschneiderte Wellness-Pakete und Retreat-Programme",
          "Vor- und Nachbehandlungsberatungen"
        ],
        bookButton: "Spa-Behandlung buchen",
        closeButton: "Schließen"
      },
      theatre: {
        title: "Kontaktieren Sie uns über unser Theater",
        subtitle: "Wir helfen Ihnen bei der Planung Ihrer Veranstaltung",
        phoneTitle: "Rufen Sie uns an",
        phoneDesc: "Sprechen Sie mit unserem Eventteam",
        emailTitle: "Schreiben Sie uns",
        emailDesc: "Kontaktieren Sie uns wegen Ihrer Veranstaltung",
        bookingTitle: "Veranstaltungsplanung Beratung",
        bookingDesc: "Vereinbaren Sie eine Beratung mit unserem Eventplanung-Team",
        whatsappTitle: "WhatsApp",
        whatsappDesc: "Chatten Sie mit uns über WhatsApp",
        servicesTitle: "Unsere Theater-Services",
        servicesList: [
          "Private IMAX-Theater-Buchungen für Gruppen und Veranstaltungen",
          "Firmenpräsentationen und Geschäftstreffen",
          "Private Filmvorführungen und Unterhaltungsveranstaltungen",
          "Hochzeitsempfänge und besondere Feiern",
          "Bildungspräsentationen und Schulungssitzungen",
          "Maßgeschneiderte Veranstaltungsplanung und Catering-Services",
          "Technischer Support und audiovisuelle Ausrüstung"
        ],
        featuresTitle: "Theater-Features",
        features: {
          capacity: "Bis zu 50 Gäste",
          screen: "Großformat IMAX-Bildschirm",
          sound: "Premium-Surround-Sound-System",
          seating: "Bequeme gestufte Sitzplätze",
          lighting: "Professionelle Veranstaltungsbeleuchtung",
          climate: "Klimatisierte Umgebung"
        },
        bookButton: "Beratung vereinbaren",
        closeButton: "Schließen"
      },
      concierge: {
        title: "Unseren Concierge kontaktieren",
        subtitle: "Lassen Sie uns Ihnen helfen, Ihr perfektes Alpen-Erlebnis zu planen",
        phoneTitle: "Telefon",
        phoneDesc: "Sprechen Sie direkt mit unserem Concierge-Team",
        emailTitle: "E-Mail",
        emailDesc: "Senden Sie uns Ihre Fragen oder Anfragen",
        locationTitle: "Standort",
        locationDesc: "Besuchen Sie uns an unserem atemberaubenden Alpenstandort",
        whatsappTitle: "WhatsApp",
        whatsappDesc: "Sofortnachrichten für schnelle Hilfe",
        servicesTitle: "Womit kann Ihnen unser Concierge helfen?",
        servicesList: [
          "Restaurantreservierungen und Essensempfehlungen",
          "Aktivitätsbuchungen und Tickets für lokale Attraktionen",
          "Transportarrangements und Flughafentransfers",
          "Spa-Behandlungen und Wellness-Programmbuchungen",
          "Private Theaterbuchungen und Firmenpräsentationen",
          "Planung besonderer Anlässe und Feiern",
          "Lokale Informationen und Wanderwegführung"
        ],
        bookButton: "Ihren Aufenthalt buchen",
        closeButton: "Schließen"
      }
    },
    footer: {
      contact: "Kontakt",
      location: "Standort",
      quickLinks: "Schnelllinks",
      organization: "NutraSwiss Organisation",
      allRightsReserved: "© 2025 Beatenberg Resort & Spa | Alle Rechte vorbehalten",
      hotelDescription: "Ein 4★ Deluxe Heritage Resort im längsten Dorf Europas, Beatenberg, mit Panoramablick auf die Alpen, integriertem NutraSwiss Wellness und authentischer Schweizer Gastfreundschaft oberhalb von Interlaken.",
      roomsSuites: "Zimmer & Suiten",
      wellnessSpa: "Wellness & Spa",
      dining: "Gastronomie",
      experiences: "Erlebnisse",
      eventsWeddings: "Events & Hochzeiten",
      aboutUs: "Über uns",
      contactLink: "Kontakt",
      privacyPolicy: "Datenschutz",
      termsOfService: "Nutzungsbedingungen",
      cookies: "Cookies",
      hotelRegistration: "Hotel-Registrierung: CHE-123.456.789 | Tourismus-Lizenz: BE-2024-HTL-001 | GASTROSUISSE Mitglieds-ID: GS-4567",
      nutraSwissDescription: "Ganzheitliche Wellness-Lösungen",
      cliniqueNutraSwissDescription: "Wellness & Medical Spa",
      nahrinDescription: "Natürliche Gesundheitsprodukte",
      laMaisonPondicherryDescription: "Heritage Gastfreundschaft",
      stBeatusCollegeDescription: "Bildungsexzellenz",
      restaurantJungfrauDescription: "Alpine Gastronomie"
    },
    notFound: {
      title: "In den Bergen verirrt",
      description:
        "Die gesuchte Seite gibt es nicht. Sie wurde vielleicht verschoben oder entfernt, oder die Adresse enthält einen Tippfehler.",
      ctaHome: "Zur Startseite",
      ctaSpa: "Spa entdecken"
    },
    activities: {
      hero: {
        title: "BEATENBERG TAGESGAST ERLEBNIS",
        subtitle: "Spielen. Entspannen. Essen. Erleben.",
        locatedAt: "Im Beatenberg Resort & Spa"
      },
      grid: {
        everythingUnderOneRoof: "ALLES UNTER EINEM DACH!",
        viewDetails: "KLICKEN FÜR DETAILS",
        bookNow: "KLICKEN ZUM BUCHEN",
        bookDayPass: "Tagespass Buchen",
        includedInDayPass: "Im Tagespass Enthalten",
        viewAllActivities: "Alle Aktivitäten Anzeigen",
        showOneAtATime: "Eine nach der Anderen"
      },
      items: {
        bistro_dine_in: {
          title: "Carbon Mountain Cafe",
          subtitle: "Vor Ort & Zum Mitnehmen",
          description: "Gourmet-Bisse in entspannter Atmosphäre oder frische Speisen zum Mitnehmen"
        },
        bistro_takeaway: {
          title: "Carbon Mountain Cafe - Takeaway",
          subtitle: "Schnell & Frisch",
          description: "Frisch zubereitete Speisen zum Mitnehmen"
        },
        birthdays: {
          title: "Geburtstage & Private Feiern",
          subtitle: "Feiern Sie mit uns",
          description: "Der ideale Ort für Ihre besonderen Momente"
        },
        sauna: {
          title: "Sauna & Thermal Wellness",
          subtitle: "DETOX & BELEBUNG",
          description: "Zirbenholz-Wärme"
        },
        spa: {
          title: "NutraSwiss Spa",
          subtitle: "MASSAGE & THERAPIE",
          description: "Entspannen & Auftanken"
        },
        dining: {
          title: "Restaurant Jungfrau",
          subtitle: "FINE DINING",
          description: "Gourmet-Küche"
        },
        cinema: {
          title: "Beatenberg Kino",
          subtitle: "PRIVATE VORFÜHRUNGEN",
          description: "Buchen Sie Ihren Film oder Sportabend"
        },
        billiards: {
          title: "Billardzimmer",
          subtitle: "Spiel & Spaß",
          description: "Pool & Snooker"
        },
        pool: {
          title: "Beheizter Aussenpool",
          subtitle: "SCHWIMMEN MIT AUSSICHT",
          description: "Ganzjähriges alpines Schwimmen"
        },
        sports: {
          title: "Sportanlagen",
          subtitle: "AKTIVES ALPINES LEBEN",
          description: "Tennisplätze & Fußballfeld"
        }
      },
      comingSoon: "Demnächst",
      modal: {
        bookTitle: "Ihr Erlebnis buchen",
        bookDesc: "Um diese Einrichtung zu reservieren oder für private Veranstaltungsanfragen, kontaktieren Sie bitte unseren Concierge:",
        callNow: "Jetzt anrufen",
        whatsapp: "WhatsApp-Support",
        close: "Schließen",
        inquireNow: "Jetzt anfragen",
        bookDayPass: "Tagespass Buchen",
        contactSupport: "Kontakt & Support",
        phone: "Telefon",
        email: "E-Mail",
        whatsappLabel: "WhatsApp",
        dining: {
          description: "Erleben Sie Feinschmecker-Küche auf höchstem Niveau im Restaurant Jungfrau, wo Gourmet-Küche auf atemberaubende Alpenblicke trifft. Unser preisgekröntes Restaurant bietet ein elegantes Speiseerlebnis mit Fokus auf Schweizer und internationale kulinarische Exzellenz.",
          description2: "Genießen Sie sorgfältig zubereitete Gerichte aus regionalen Zutaten, kombiniert mit einer umfangreichen Weinauswahl. Unsere talentierten Köche erstellen saisonale Menüs, die die Aromen des Berner Oberlands feiern.",
          description3: "Ob Sie einen besonderen Anlass feiern, ein romantisches Abendessen genießen oder sich einfach eine außergewöhnliche Mahlzeit gönnen - das Restaurant Jungfrau bietet ein unvergessliches Feinschmecker-Erlebnis im Herzen der Schweizer Alpen.",
          dayPassValue: "Das Restaurant Jungfrau ist im Tagesgast-Pass enthalten. Ihr Tagespass beinhaltet ein fünfgängiges Gourmet-Menü, mit dem Sie unser vollständiges Feinschmecker-Menü erleben können. Buchen Sie Ihren Tagespass, um das vollständige kulinarische Erlebnis und alle Resort-Annehmlichkeiten zu genießen.",
          learnMore: "Vollständiges Menü und Restaurant-Details anzeigen"
        },
        sauna: {
          description: "Verjüngen Sie Körper und Geist in unseren authentischen Schweizer Sauna- und Thermal-Wellness-Einrichtungen. Erleben Sie die traditionellen Vorteile der Wärmetherapie in einer ruhigen alpinen Umgebung.",
          description2: "Unser Sauna-Paket umfasst Zugang zu vier verschiedenen Thermal-Einrichtungen, die jeweils einzigartige Vorteile für Entgiftung, Entspannung und allgemeines Wohlbefinden bieten. Die Zirbenholz-Wärme schafft eine natürliche, aromatische Umgebung, die Körper und Geist beruhigt.",
          description3: "Ob Sie sich nach einem Tag voller Aktivitäten entspannen, die Durchblutung verbessern oder einfach einen Moment der Ruhe genießen möchten - unsere Sauna- und Thermal-Wellness-Einrichtungen bieten den perfekten Rückzugsort.",
          dayPassValue: "Das komplette Sauna- & Thermal-Wellness-Paket ist im Tagesgast-Pass enthalten. Ihr Tagespass beinhaltet Zugang zu allen vier Sauna-Einrichtungen (Finnische Sauna, Bio-Sauna, Dampfbad und Eistauchbecken) sowie kostenlosen Kaffee, Tee und Entgiftungsgetränken. Buchen Sie Ihren Tagespass, um das vollständige Sauna-Erlebnis und alle Resort-Annehmlichkeiten zu genießen.",
          learnMore: "Unsere vollständigen Wellness-Angebote erkunden"
        },
        bistro_dine_in: {
          description: "Genießen Sie entspanntes alpines Essen im Carbon Mountain Cafe, wo frische, lokal inspirierte Küche auf eine entspannte, einladende Atmosphäre trifft. Perfekt für einen schnellen Biss, eine gemütliche Mahlzeit oder zum Mitnehmen.",
          description2: "Unser Café bietet eine vielfältige Speisekarte mit Gourmet-Häppchen, frischen Salaten, Sandwiches, Pizzen und Tagesgerichten. Ob Sie vor Ort mit atemberaubendem Bergblick essen oder Essen zum Mitnehmen bestellen - wir bieten qualitativ hochwertiges Essen in einer komfortablen Umgebung.",
          description3: "Ideal für Familien, Gruppen oder Alleinreisende, die leckeres Essen ohne Formalismus suchen. Unser freundlicher Service und flexible Essensoptionen machen das Carbon Mountain Cafe zur perfekten Wahl für jede Tageszeit.",
          dayPassValue: "Das Carbon Mountain Cafe ist im Tagesgast-Pass enthalten. Ihr Tagespass beinhaltet ein kostenloses alkoholfreies Getränk oder Heißgetränk (Heissgetränk) und ein kostenloses Gericht (Sandwich, Salat oder Pizza). Buchen Sie Ihren Tagespass, um Café-Essen, Mitnahme-Optionen und alle Resort-Annehmlichkeiten zu genießen.",
          learnMore: "Vor-Ort-Menü anzeigen",
          learnMoreTakeaway: "Mitnahme-Website besuchen"
        },
        spa: {
          description: "Gönnen Sie sich Premium-Spa-Behandlungen im NutraSwiss Spa, wo fortschrittliche Wellness-Therapien auf Schweizer Präzision und Fürsorge treffen. Erleben Sie personalisierte Massage- und Therapiedienstleistungen, die darauf ausgelegt sind, Balance und Vitalität wiederherzustellen.",
          description2: "Unsere erfahrenen Therapeuten bieten eine Reihe von Behandlungen, einschließlich entspannender Massagen, therapeutischer Körperarbeit und spezialisierter Wellness-Therapien. Jede Behandlung wird auf Ihre individuellen Bedürfnisse zugeschnitten und verwendet Premium-Produkte und -Techniken.",
          description3: "Ob Sie Linderung von Verspannungen, tiefe Entspannung oder ein verjüngendes Spa-Erlebnis suchen - das NutraSwiss Spa bietet professionelle Betreuung in einer ruhigen, luxuriösen Umgebung.",
          dayPassValue: "NutraSwiss Spa-Behandlungen sind im Tagesgast-Pass enthalten. Ihr Tagespass beinhaltet eine komplementäre 15-minütige Massage, mit der Sie unsere professionellen Spa-Dienstleistungen erleben können. Bei der Buchung Ihres Tagespasses haben Sie die Möglichkeit, auf unsere Signature-Behandlungen für ein umfassenderes Wellness-Erlebnis aufzurüsten. Buchen Sie Ihren Tagespass, um Spa-Dienstleistungen und alle Resort-Annehmlichkeiten zu genießen.",
          learnMore: "Vollständiges Spa-Menü und Behandlungen anzeigen"
        },
        cinema: {
          description: "Erleben Sie Sportveranstaltungen und Präsentationen auf unserer hochmodernen Großbild-Leinwand, dem einzigen privaten Kino mit IMAX-Technologie im Berner Oberland.",
          description2: "Perfekt zum Ansehen von Live-Sportveranstaltungen, für Präsentationen, Firmenvorführungen oder besondere Veranstaltungsvorführungen. Genießen Sie kristallklare Bilder und immersiven Sound auf unserer Premium-Großbild-Leinwand.",
          description3: "Filmvorführungen werden bald verfügbar sein. Derzeit ist unser Kino ideal für Sportübertragungen, Geschäftspräsentationen und besondere Veranstaltungen. Ob Sie das große Spiel ansehen, eine Firmenpräsentation veranstalten oder eine besondere Veranstaltungsvorführung planen - unser Kino bietet ein Premium-Seherlebnis.",
          dayPassValue: "Der Kinobesuch ist im Tagesgast-Pass als gemeinsamer Raum enthalten. Sie können Sportveranstaltungen und Präsentationen zusammen mit anderen Tagespass-Gästen ansehen. Buchen Sie Ihren Tagespass, um Kinobesuch und alle Resort-Annehmlichkeiten zu genießen.",
          dayPassValuePrivate: "Private Kinobuchung: Wenn Sie den gesamten Kinoraum exklusiv für Ihre Gruppe oder Veranstaltung reservieren möchten, kontaktieren Sie uns bitte separat, um eine private Buchung zu arrangieren.",
          learnMore: "Mehr über unser IMAX-Theater erfahren"
        },
        billiards: {
          description: "Entspannen Sie sich und genießen Sie freundschaftliche Wettkämpfe in unserem eleganten Billardzimmer. Perfekt für lockere Spiele, gesellige Zusammenkünfte oder eine entspannende Pause während Ihres Tages im Resort.",
          description2: "Unser gut ausgestattetes Billardzimmer verfügt über professionelle Tische und eine komfortable Atmosphäre, ideal für Gäste aller Spielstärken.",
          description3: "Ob Sie eine unterhaltsame Aktivität mit Freunden oder einen Moment der Entspannung suchen - das Billardzimmer bietet ein klassisches Unterhaltungserlebnis.",
          dayPassValue: "Der Zugang zum Billardzimmer ist im Tagesgast-Pass enthalten. Buchen Sie Ihren Tagespass, um Billard, Pool und alle Resort-Annehmlichkeiten zu genießen."
        },
        pool: {
          description: "Schwimmen Sie mit atemberaubendem Alpenblick in unserem beheizten Außenpool, der demnächst im Beatenberg Resort & Spa eröffnet wird.",
          description2: "Erleben Sie ganzjähriges Schwimmen in einer atemberaubenden Bergkulisse. Unser beheizter Pool wird die perfekte Kombination aus Entspannung und landschaftlicher Schönheit bieten und Ihnen ermöglichen, die Schweizer Alpen zu genießen, während Sie ein erfrischendes Bad nehmen.",
          description3: "Ob Sie einen Morgenschwimmen, eine Nachmittagsentspannung oder einen abendlichen Aufenthalt unter den Sternen suchen - unser Pool wird ein unvergessliches alpines Schwimmerlebnis bieten.",
          dayPassValue: "Der beheizte Außenpool wird nach seiner Eröffnung im Tagesgast-Pass enthalten sein. Bleiben Sie dran für Updates!"
        },
        sports: {
          description: "Bleiben Sie aktiv im Herzen der Schweizer Alpen mit unseren umfassenden Sportanlagen, die demnächst im Beatenberg Resort & Spa eröffnet werden.",
          description2: "Unsere Einrichtungen werden Tennisplätze, Basketballplätze, Fußballfelder und mehr umfassen, alles vor der atemberaubenden Kulisse der Berner Oberland-Berge.",
          description3: "Ob Sie ein wettkampforientiertes Spiel, lockere Bewegung oder familienfreundliche Aktivitäten suchen - unsere Sportanlagen werden für jeden aktiven Gast etwas bieten.",
          dayPassValue: "Die Sportanlagen werden nach ihrer Eröffnung im Tagesgast-Pass enthalten sein. Bleiben Sie dran für Updates!"
        }
      },
      footer: {
        allInOne: "SPASS · ENTSPANNUNG · ESSEN · UNTERHALTUNG · ALLES AN EINEM ORT!",
        benefits: [
          "Perfekt für Familien & Freunde",
          "Innenkomfort mit Bergcharme",
          "Alles an einem Tag"
        ],
        tagline: "Wo Momente zu Erinnerungen werden"
      },
      whyChoose: {
        title: "Warum Beatenberg für Ihren perfekten Tag?",
        subtitle: "Erleben Sie luxuriöse Resort-Amenities ohne Übernachtung - perfekt für spontane Besuche und regelmäßige lokale Ausflüge.",
        imageAlt: "Beatenberg Resort & Spa - Premium Tagesgast-Erlebnis",
        premium: {
          title: "Premium-Erlebnis ohne Verpflichtung",
          description: "Genießen Sie luxuriöse Resort-Amenities ohne Übernachtung - perfekt für spontane Besuche und Tagesausflüge."
        },
        allInOne: {
          title: "Alles-in-Einem Ziel",
          description: "Mehrere Aktivitäten an einem Ort - Spa, Essen, Kino, Pool und mehr - keine Notwendigkeit, zwischen verschiedenen Orten zu reisen."
        },
        flexible: {
          title: "Flexible Tagesausflüge",
          description: "Besuchen Sie so oft Sie möchten, kombinieren Sie Aktivitäten, perfekt für regelmäßige lokale Ausflüge und spontane Escapes."
        },
        easyAccess: {
          title: "Einfacher Zugang & Parkplätze",
          description: "Nah zu Hause mit bequemen Parkplätzen für bis zu 200 Autos - ideal für schnelle Escapes oder geplante Tagesbesuche."
        }
      },
      locationAccess: {
        title: "Standort & Anfahrt",
        subtitle: "Einfach erreichbar mit bequemen Parkplätzen und mehreren Verkehrsmöglichkeiten. Perfekt für Tagesgäste in der Region.",
        mapTitle: "Beatenberg Resort & Spa Standort",
        locatedAt: "Gelegen im Beatenberg Resort & Spa",
        switzerland: "SCHWEIZ",
        bernerOberland: "Berner Oberland",
        cantonBern: "Kanton Bern",
        address: {
          title: "Adresse",
          description: "Spirenwaldstrasse 351, 3803 Beatenberg, Schweiz",
          details: "Berner Oberland, Kanton Bern",
          street: "Spirenwaldstrasse",
          number: "351, 3803",
          city: "Beatenberg,",
          country: "Schweiz"
        },
        parking: {
          title: "Parkplätze",
          description: "Parkplätze vor Ort bis zu 200 Autos",
          details: "Bis zu 200 Autos • Kostenlose Parkplätze verfügbar"
        },
        bus: {
          title: "Mit dem Bus",
          description: "SBB Route 101 @ Hotel Eingang",
          details: "Bushaltestelle @ Hotel Eingang (Endstation)"
        },
        train: {
          title: "Mit dem Zug",
          description: "Beatenberg Bahnhof - 7 Min zu Fuß",
          details: "Interlaken West - 20 Min",
          beatenberg: "Beatenberg Bahnhof - 7 Min",
          interlaken: "Interlaken West - 20 Min"
        },
        air: {
          title: "Mit dem Flugzeug",
          description: "Nächste Flughäfen",
          details: "Bern - 50 Min • Zürich - 2h • Genf - 2,5h",
          bern: "Bern - 50 Min",
          zurich: "Zürich - 2h",
          geneva: "Genf - 2,5h"
        }
      },
      amenitiesOverview: {
        title: "Mehr zu genießen in Beatenberg",
        subtitle: "Neben den Hauptaktivitäten entdecken Sie weitere Premium-Amenities, die Ihr perfektes Tageserlebnis vervollständigen.",
        wellness: {
          title: "Wellness & Spa",
          clinique: {
            name: "Clinique Nutraswiss",
            description: "Integriertes Health Spa mit exklusiven NutraSwiss-Behandlungen und Nahrungsergänzungsmitteln"
          },
          whirlpool: {
            name: "Whirlpool",
            description: "Entspannendes Hydrotherapie-Erlebnis mit atemberaubenden Alpenblicken"
          }
        },
        sports: {
          title: "Sport & Fitness",
          fitness: {
            name: "Fitness-Center",
            description: "Modernste Technogym-Ausrüstung in alpiner Umgebung"
          },
          tennis: {
            name: "Tennisplätze",
            description: "Hotel-eigene Sportanlagen mit professionellen Tennis-Einrichtungen"
          },
          basketball: {
            name: "Basketballplatz",
            description: "Vollständiger Platz für Gäste und Gruppen verfügbar"
          },
          football: {
            name: "Fußballfeld",
            description: "Eigenes Fußballfeld auf dem Hotelgelände"
          },
          volleyball: {
            name: "Volleyballplatz",
            description: "Outdoor-Volleyballanlagen für Freizeitspiele"
          }
        },
        dining: {
          title: "Essen & Bars",
          restaurant: {
            name: "Restaurant Jungfrau",
            description: "Feine Küche mit Panoramablick auf die Berge",
            capacity: "Kapazität: 200"
          },
          cafe: {
            name: "Carbon Mountain Cafe",
            description: "Casual alpines Bistro mit lokalen Spezialitäten",
            capacity: "Kapazität: 120"
          },
          lounge: {
            name: "Fireplace Lounge & Bar",
            description: "Intime Bar-Atmosphäre mit gemütlichem Kaminambiente",
            capacity: "Kapazität: 25"
          }
        },
        views: {
          title: "Aussichten",
          panoramic: {
            name: "Panoramablick auf die Alpen",
            description: "Ungestörte Aussicht auf Eiger, Mönch, Jungfrau und den Thunersee"
          }
        }
      },
      faq: {
        title: "Häufig gestellte Fragen",
        subtitle: "Alles, was Sie über den Tagesgast-Pass wissen müssen",
        ctaText: "Haben Sie noch Fragen? Kontaktieren Sie uns",
        ctaButton: "Kontakt aufnehmen",
        questions: {
          whatIncluded: {
            question: "Was ist im Tagesgast-Pass enthalten?",
            answer: "Der Tagesgast-Pass umfasst Zugang zu allen Resort-Amenities, eine 15-minütige Massage (aufwerten auf eine vollständige Spa-Behandlung), ein 5-Gänge-Menü im Restaurant Jungfrau, einen Artikel plus ein Getränk vom Carbon Mountain Cafe und Sauna-Paket-Zugang. All dies ist für 200 CHF pro Person verfügbar."
          },
          advanceBooking: {
            question: "Muss ich im Voraus buchen?",
            answer: "Ja, wir empfehlen eine Vorausbuchung, um die Verfügbarkeit sicherzustellen, insbesondere in der Hauptsaison. Sie können bis zu 12 Gäste pro Buchung buchen. Wir bestätigen Ihre Reservierung und senden Ihnen alle Details per E-Mail."
          },
          upgradeMassage: {
            question: "Kann ich die enthaltene 15-minütige Massage aufwerten?",
            answer: "Absolut! Jeder Gast kann seine enthaltene 15-minütige Massage auf eine beliebige vollständige Spa-Behandlung aus unserem Menü aufwerten. Sie zahlen nur die Differenz zwischen dem vollen Behandlungspreis und dem enthaltenen Massagewert. Upgrades können während des Buchungsprozesses ausgewählt werden."
          },
          guestLimit: {
            question: "Für wie viele Gäste kann ich buchen?",
            answer: "Sie können für 1 bis 12 Gäste pro Tagesgast-Pass-Buchung buchen. Für größere Gruppen kontaktieren Sie bitte unser Concierge-Team, das spezielle Gruppenpakete arrangieren kann."
          },
          multipleActivities: {
            question: "Kann ich mehrere Aktivitäten an einem Tag besuchen?",
            answer: "Ja! Der Tagesgast-Pass gibt Ihnen Zugang zu allen Resort-Amenities während Ihres Besuchs. Sie können das Spa, die Sauna, das Essen, das Kino, Billard und alle anderen Einrichtungen genießen, die im Resort während Ihres Tagesbesuchs verfügbar sind."
          },
          parking: {
            question: "Ist Parken verfügbar?",
            answer: "Ja, Parkplätze sind im Resort verfügbar. Bitte kontaktieren Sie uns für spezielle Parkdetails und Verfügbarkeit, insbesondere in geschäftigen Zeiten."
          },
          mealSelection: {
            question: "Wann wähle ich meine Mahlzeiten?",
            answer: "Die Mahlzeitenauswahl erfolgt normalerweise am Tag Ihres Besuchs. Das 5-Gänge-Menü im Restaurant Jungfrau ist enthalten, und Sie können aus der Tageskarte wählen. Für den Carbon Mountain Cafe-Artikel können Sie aus deren Menü wählen, wenn Sie besuchen. Wenn Sie diätetische Einschränkungen oder Präferenzen haben, erwähnen Sie diese bitte in Ihrer Buchungsanfrage."
          },
          whatToBring: {
            question: "Was sollte ich mitbringen?",
            answer: "Wir empfehlen, bequeme Kleidung, Badebekleidung für die Spa- und Saunaeinrichtungen und alle persönlichen Gegenstände mitzubringen, die Sie benötigen könnten. Handtücher und Bademäntel werden im Spa bereitgestellt. Sie möchten vielleicht auch eine Kamera mitbringen, um die atemberaubenden Alpenblicke festzuhalten!"
          },
          familyFriendly: {
            question: "Sind die Aktivitäten familienfreundlich?",
            answer: "Ja! Der Tagesgast-Pass ist perfekt für Familien. Kinder können das Kino, Billard und andere Resort-Amenities genießen. Bitte beachten Sie, dass Spa-Behandlungen Altersbeschränkungen haben können und das 5-Gänge-Menü für Kinder angepasst werden kann. Kontaktieren Sie uns für familienbezogene Arrangements."
          },
          operatingHours: {
            question: "Was sind die Öffnungszeiten?",
            answer: "Die Öffnungszeiten variieren je nach Einrichtung. Das Spa und die Wellness-Bereiche sind normalerweise von frühmorgens bis abends geöffnet. Das Restaurant Jungfrau serviert Mittag- und Abendessen, und das Carbon Mountain Cafe ist den ganzen Tag über geöffnet. Spezifische Zeiten werden mit Ihrer Buchungsbestätigung bestätigt."
          },
          cancellationPolicy: {
            question: "Wie ist die Stornierungsrichtlinie?",
            answer: "Die Stornierungsrichtlinien können je nach Jahreszeit und Buchungstyp variieren. Im Allgemeinen benötigen wir mindestens 48 Stunden Vorlaufzeit für Stornierungen. Bitte kontaktieren Sie uns direkt, um Stornierungsoptionen zu besprechen, und wir arbeiten mit Ihnen zusammen, um die beste Lösung zu finden."
          }
        }
      },
      dayPass: {
        bookingTray: {
          title: "Tagesgast-Pass",
          dayPassDetails: "Tagesgast-Pass",
          perPerson: "/ Person",
          inclusionAllAmenities: "Zugang zu allen Resort-Amenities",
          inclusionMassage: "15-minütige Massage",
          inclusionMeal: "5-Gänge-Menü im Restaurant Jungfrau (CHF 55 Wert, Getränke extra)",
          inclusionCafe: "Ein Artikel + Tee/Kaffee/nicht-alkoholisches Getränk vom Carbon Mountain Cafe",
          inclusionSauna: "Sauna-Paket (inkl. Zugang)",
          includedValue: "CHF 40 Wert",
          numberOfGuests: "Anzahl der Gäste",
          guestLimit: "Bis zu 12 Gäste pro Buchung",
          basePassPrice: "Basis-Pass",
          massageUpgrade: "Massage-Upgrade",
          total: "Gesamt",
          upgradeMassage: "Massage upgraden",
          changeUpgrade: "Massage-Upgrade ändern",
          bookNow: "Jetzt buchen",
          backToOverview: "Zurück zur Übersicht",
          upgradeYourMassage: "Massagen upgraden",
          upgradeDescription: "Wählen Sie eine vollständige Spa-Behandlung, um von Ihrer inkludierten 15-minütigen Massage zu upgraden.",
          upgradeDescriptionMultiple: "Wählen Sie Upgrades für jeden Gast. Jeder Gast kann eine andere Massagebehandlung wählen.",
          includedMassage: "15-minütige Massage (inkludiert)",
          selectGuest: "Gast zum Upgraden auswählen",
          selectingForGuest: "Auswahl für",
          guest: "Gast",
          upgradeOptions: "Upgrade-Optionen",
          upgradeFor: "Upgrade für",
          upgradeSelected: "Upgrade ausgewählt",
          includedSelected: "Inkludierte Massage ausgewählt",
          confirmUpgrade: "Auswahl bestätigen",
          bookingInformation: "Buchungsinformationen",
          bookingDescription: "Bitte geben Sie Ihre Daten ein, um Ihre Tagesgast-Pass-Buchung abzuschließen.",
          fullName: "Vollständiger Name",
          email: "E-Mail",
          phone: "Telefon",
          preferredDate: "Bevorzugtes Datum",
          preferredTime: "Bevorzugte Zeit",
          selectDate: "Datum auswählen",
          morning: "Morgen",
          afternoon: "Nachmittag",
          evening: "Abend",
          specialRequests: "Besondere Wünsche (Optional)",
          bookingSummary: "Buchungsübersicht",
          requestedTimesNote: "*Angeforderte Zeiten sind abhängig von der Verfügbarkeit. Wir werden Sie kontaktieren, um Ihre Buchung zu bestätigen.",
          submitBooking: "Buchungsanfrage absenden",
          fillAllFields: "Bitte füllen Sie alle erforderlichen Felder aus",
          submitError: "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
          networkError: "Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
          successMessage: "Buchungsanfrage erfolgreich gesendet!",
          thankYouReceived: "Vielen Dank! Ihre Buchungsanfrage wurde erhalten.",
          confirmationEmail: "Wir senden Ihnen in Kürze eine Bestätigungs-E-Mail.",
          upgradedTo: "Upgegradet auf"
        },
        dayPassPricing: {
          title: "Tagesgast-Pass Preise",
          subtitle: "Erleben Sie alles, was das Beatenberg Resort & Spa zu bieten hat, mit unserem umfassenden Tagesgast-Pass. Alles, was Sie für einen perfekten Tag in den Schweizer Alpen brauchen.",
          priceLabel: "Tagesgast-Pass",
          price: "CHF 200",
          perPerson: "pro Person",
          inclusionsTitle: "Was ist enthalten",
          inclusions: {
            amenities: "Zugang zu allen Resort-Amenities (Pool, Billard, Sportanlagen, Kino, etc.)",
            massage: "15-minütige Massage (aufrüstbar)",
            massageValue: "CHF 40 Wert",
            meal: "5-Gänge-Menü im Restaurant Jungfrau",
            mealValue: "CHF 55 Wert (Getränke extra)",
            cafe: "Ein Artikel + Tee/Kaffee/nicht-alkoholisches Getränk vom Carbon Mountain Cafe",
            sauna: "Sauna-Paket (inkl. Zugang)"
          },
          valueBreakdown: {
            title: "Wertaufschlüsselung",
            description: "Sehen Sie den Gesamtwert, den Sie mit Ihrem Tagesgast-Pass erhalten:",
            massage: "15-minütige Massage",
            massageValue: "CHF 40",
            meal: "5-Gänge-Restaurant-Menü",
            mealValue: "CHF 55",
            cafe: "Carbon Cafe Artikel + Getränk",
            cafeValue: "CHF 20",
            sauna: "Sauna-Paket Zugang",
            saunaValue: "CHF 35",
            amenities: "Resort-Amenities Zugang",
            amenitiesValue: "CHF 50",
            totalValueLabel: "Gesamtwert",
            totalValue: "CHF 200",
            passPriceLabel: "Tagespass-Preis",
            passPrice: "CHF 200",
            savings: "Außergewöhnlicher Wert - alles ohne zusätzliche Kosten enthalten"
          },
          cta: "Tagespass buchen"
        }
      },
      experienceVideo: {
        title: "Erleben Sie die Tagesgast-Reise",
        subtitle: "Entdecken Sie, was Sie mit unserem Tagesgast-Pass erwartet - von erstklassigem Essen über verjüngende Spa-Behandlungen bis hin zu aufregenden Aktivitäten."
      },
      ctaContact: {
        title: "Bereit, Beatenberg zu erleben?",
        subtitle: "Buchen Sie noch heute Ihren Tagesgast-Pass und entdecken Sie alles, was unser Resort zu bieten hat. Unser Team ist hier, um Ihren Besuch unvergesslich zu machen.",
        ctaTitle: "Buchen Sie Ihren Besuch",
        ctaDescription: "Reservieren Sie Ihren Tagesgast-Pass und beginnen Sie mit der Planung Ihres perfekten Tages in den Schweizer Alpen.",
        ctaButtons: {
          bookVisit: "Besuch buchen",
          contactConcierge: "Concierge kontaktieren",
          callNow: "Jetzt anrufen"
        },
        contactTitle: "Kontakt aufnehmen",
        contactMethods: {
          phone: {
            label: "Telefon"
          },
          email: {
            label: "E-Mail"
          },
          whatsapp: {
            label: "WhatsApp",
            value: "+41 76 622 01 22"
          }
        },
        operatingHours: {
          title: "Öffnungszeiten",
          days: "Montag - Sonntag",
          hours: "9:00 - 20:00 Uhr"
        }
      }
    },
    book: bookPageTranslationsDe,
    testimonials: {
      title: "Gast-Erfahrungen",
      description: "Entdecken Sie, warum Reisende aus aller Welt das Beatenberg Resort & Spa wählen",
      testimonial1: {
        quote: "Absolut großzügiges Personal. Anna war besonders hilfreich in vielerlei Hinsicht, von der Begrüßung bis hin zu einem mehr als komfortablen und entspannenden Aufenthalt. Ihre Freundlichkeit war ansteckend und hilfreich über alle Erwartungen hinaus. Ich kann dieses Hotel nur empfehlen. Es hat einen Charme von seinen über 300 Jahren Existenz bis zur Eleganz aller modernen Annehmlichkeiten, die für einen entspannenden Aufenthalt notwendig sind. Ganz zu schweigen von der erstaunlichen Aussicht bei Sonnenuntergang. Unvergesslich!",
        name: "Amit Trivedi",
        country: "Indien"
      },
      testimonial2: {
        quote: "Das Park Hotel hat unseren Aufenthalt in der Schweiz sehr angenehm gemacht. Das Hotel ist friedlich, da es in einer sehr ruhigen und entspannten Umgebung liegt. Die Aussichten sind großartig und der gesamte Service des Personals, besonders Mani Kandan. Er war höflich und ein wunderbarer Gastgeber. Ein großartiger Ort.",
        name: "Priya Sharma",
        country: "Indien"
      },
      testimonial3: {
        quote: "Wow! 🤩 Das Aufwachen mit dieser See- und Bergaussicht war unwirklich. Ich hatte einen Balkon in meinem Zimmer und saß dort stundenlang. Das Personal war super süß und die Massage? 10/10. Kann es kaum erwarten, zum Parkhotel Beatenberg zurückzukehren! 💆‍♀️🌄",
        name: "Agustina Kraitz",
        country: "Argentinien"
      },
      testimonial4: {
        quote: "Ein wunderbarer Aufenthalt. Die Lage und die Gastfreundschaft des Personals waren hervorragend. Wir wohnten im Zimmer mit Balkon und ich denke, es ist die spektakulärste Aussicht, die ich je von einem Hotelzimmer aus hatte. Das Hotel hat auch einen spektakulären Garten. Schließlich war Oscar äußerst freundlich und effizient. Hat unseren Aufenthalt so reibungslos gemacht.",
        name: "Amar Nanavati",
        country: "Indien"
      }
    },
    floatingFeatures: {
      freeWifi: {
        title: "Kostenloses WiFi",
        description: "Kostenloses Highspeed-Internet während Ihres gesamten Aufenthalts"
      },
      flatScreenTv: {
        title: "Flachbild-TV",
        description: "Moderner Smart-TV mit internationalen Kanälen und Streaming"
      },
      inRoomSafe: {
        title: "Zimmersafe",
        description: "Sichere Aufbewahrung für Ihre Wertsachen und Reisedokumente"
      },
      alpineViews: {
        title: "Alpenblicke",
        description: "Panoramablick auf Thunersee, Eiger, Mönch, Jungfrau und Beatenberg Dorf"
      },
      ensuiteBathroom: {
        title: "Anschlussbadezimmer",
        description: "Privates Badezimmer mit modernen Armaturen und Annehmlichkeiten"
      },
      luxuryBedding: {
        title: "Luxus-Bettwäsche",
        description: "Premium Bio-Baumwollbettwäsche für ultimativen Komfort"
      },
      rusticPineInteriors: {
        title: "Rustikale Kieferninterieurs",
        description: "Lokales Kiefernholz, naturally verarbeitet mit Schweizer Handwerkskunst"
      },
      seasonalTemperatures: {
        title: "Jahreszeitliche Temperaturen",
        description: "Sommer ~ 10 bis 22°C, Winter ~ −5 bis 5°C"
      },
      ensuiteEspressoMachine: {
        title: "Anschluss-Espressomaschine",
        description: "Premium-Espressomaschine in Family und Junior Suites für Ihren Morgenkaffee"
      }
    }
  },

  fr: {
    common: {
      share: "Partager"
    },
    banner: {
      unitOf: "Un Hôtel par"
    },
    nav: {
      home: "Accueil",
      rooms: "Chambres & Suites",
      wellness: "Bien-être & Spa",
      dining: "Restaurant",
      experiences: "Expériences",
      events: "Événements & Mariages",
      about: "À propos",
      bookNow: "Réserver",
      activities: "Arcade d'Activités",
      journal: "Journal",
      language: "Langue",
      displayCurrency: "Devise d’affichage"
    },
    hero: {
      title: "BEATENBERG",
      subtitle: "RESORT & SPA",
      description: "Luxe rustique dans les Alpes suisses",
      bookStay: "Réservez votre séjour",
      swissHeritageHotel: "Hôtel Heritage Suisse • Beatenberg/Interlaken",
      acreResort: "Resort de 15 Acres",
      minsFromInterlaken: "15 min d'Interlaken",
      stunningAlpineViews: "Vues Alpines Spectaculaires",
      since1881: "Depuis 1881",
      beatenbergsOnly: "Le Seul Resort de Beatenberg",
      fourStarGastrosuisse: "4 Étoiles Gastrosuisse",
      ratedResort: "Certifié",
      locationButton: "Emplacement Beatenberg Resort et Spa"
    },
    features: {
      alpineSanctuary: "Luxe Alpin Rustique à Beatenberg/Interlaken",
      alpineDescription: "Le Beatenberg Resort & Spa est un Resort Heritage 4★ Deluxe situé à Beatenberg, le plus long village d'Europe, surplombant le lac de Thoune et encadré par les majestueux sommets Jungfrau, Eiger et Mönch. Un joyau alpin caché qui combine le charme traditionnel suisse avec des installations de pointe, en faisant le choix idéal pour les voyageurs de loisirs, les groupes, les amateurs de bien-être, les mariages et les événements d'entreprise.",
      alpineDescription2: "Ici, chaque moment est une chance de se reconnecter avec le pouvoir silencieux de la nature. Notre équipe dévouée est là pour s'assurer que votre séjour est aussi fluide que les vues, offrant un havre de paix que vous cherchiez une retraite reposante ou une grande aventure. Laissez les Alpes inspirer votre prochain voyage.",
      exclusiveRates: "TARIFS EXCLUSIFS",
      guestCardBenefits: "AVANTAGES CARTE INVITÉ",
      beatenbergAccess: "ACCÈS BEATENBERG",
      interlakenPrivileges: "PRIVILÈGES INTERLAKEN",
      habkernDiscounts: "REMISES HABKERN",
      freeShuttle: "NAVETTE GRATUITE",
      bookYourStay: "Réservez Votre Séjour",
      integratedWellness: "Bien-être Suisse Intégré",
      integratedWellnessDesc: "Spa NutraSwiss exclusif & nutraceutiques",
      guestCardBenefitsDesc: "Remises exclusives et accès aux attractions locales",
      gastrosuisseExcellence: "Excellence Certifiée GASTROSUISSE",
      gastrosuisseExcellenceDesc: "Reconnu par l'association d'hospitalité suisse pour les standards de qualité",
      swisstainableCommitment: "Engagement Tourisme Swisstainable",
      swisstainableCommitmentDesc: "Dédié aux pratiques de tourisme durable en Suisse",
      heritageCuisine: "Cuisine Heritage Suisse",
      heritageCuisineDesc: "Plats traditionnels farm-to-table réinventés",
      acreResort: "Resort de 15 Acres dans un Village Alpin Scénique",
      acreResortDesc: "Cadre montagnard tranquille au-dessus du lac de Thoune",
      authenticHeritage: "Heritage Authentique",
      authenticHeritageDesc: "Hospitalité suisse depuis 1881",
      alpineExperiences: "Expériences Alpines Inoubliables",
      alpineExperiencesDesc: "Activités toute l'année & immersion culturelle suisse",
      dragToExplore: "Glisser pour explorer",
      elegantBarLounge: "Bar & Lounge Élégant",
      elegantBarLoungeDesc: "Détendez-vous, socialisez et profitez de bons moments",
      recreationGames: "Loisirs & Jeux",
      recreationGamesDesc: "Divertissement et jeux pour tous les âges",
      technogymFitness: "Fitness TECHNOGYM",
      technogymFitnessDesc: "Équipement de pointe en cadre alpin",
      businessFacilities: "Installations d'Affaires",
      businessFacilitiesDesc: "Espaces de réunion professionnels pour événements",
      alpineBistro: "Bistro Alpin",
      alpineBistroDesc: "Restaurant décontracté dans une atmosphère détendue",
      panoramicViews: "Vues Panoramiques",
      panoramicViewsDesc: "Vistas à couper le souffle sur la Jungfrau & le lac de Thoune"
    },
    rooms: {
      title: "Nos Chambres & Suites",
      description: "Des chambres doubles confortables aux suites familiales spacieuses, chaque chambre offre le confort suisse avec des vues alpines à couper le souffle.",
      exploreRooms: "Explorer les chambres",
      familySuite: "Suite Familiale Heritage - Vue Jungfrau",
      juniorSuite: "Suite Junior Alpine - Vue Panoramique",
      tripleRoom: "Chambre Triple Classique - Confort Alpin",
      premiumDouble: "Double Supérieure Jungfrau - Balcon & Vue Lac",
      standardSingle: "Chambre Simple Classique - Confort Alpin",
      alpineViews: "Vues Alpines",
      pineWoodInteriors: "Intérieurs élégants en pin alliant confort moderne et caractère patrimonial",
      organicCottonSheets: "Draps en coton bio",
      privateBalconies: "Balcons privés avec vue sur Mönch, Niesen, Niederhorn & lac de Thoune ou village de Beatenberg",
      seniorFriendly: "Options allant des chambres simples confortables aux suites familiales",
      exploreRoom: "Explorer la chambre",
      viewRoomFeatures: "Équipements",
      familySuiteDesc: "Hébergement spacieux parfait pour les familles, avec chambres séparées et vues alpines époustouflantes.",
      juniorSuiteDesc: "Suite junior luxueuse offrant des espaces de vie spacieux et un confort alpin premium.",
      tripleRoomDesc: "Hébergement triple spacieux parfait pour les petits groupes, avec literie confortable et charme alpin.",
      premiumDoubleDesc: "Chambre double sophistiquée avec équipements modernes et vues alpines panoramiques.",
      standardSingleDesc: "Hébergement simple classique avec confort alpin, décoration en bois élégante et équipements modernes.",
      alpineViewsDesc: "Réveillez-vous avec des vues spectaculaires sur les Alpes bernoises depuis les fenêtres panoramiques de votre chambre.",
      familySuiteFeatures: {
        masterBedroom: "1 Chambre Principale",
        additionalBedrooms: "2 Chambres Supplémentaires",
        hallLivingRoom: "Hall & Salon",
        privateBalcony: "Balcon Privé"
      },
      familySuiteBadges: {
        occupancy: "6",
        roomSize: "65m²",
        bedding: "1 Queen + 4 Lits simples",
        bathrooms: "2 Salles de bain"
      },
      familySuiteDescription: {
        mobile: "Suite spacieuse de 65m² avec 1 Queen + 4 lits simples, kitchenette et vues panoramiques sur le lac de Thoune, Eiger, Mönch, Jungfrau et le village de Beatenberg.",
        desktop: "Suite spacieuse de 65m² avec 1 Queen + 4 lits simples, kitchenette et vues panoramiques sur le lac de Thoune, Eiger, Mönch, Jungfrau et le village de Beatenberg."
      },
      juniorSuiteFeatures: {
        loungeSeating: "Assise Salon Élégante",
        privateBalconyWithViews: "Balcon Privé avec Vue Montagne/Lac/Village",
        spaceForAdditionalBeds: "Espace pour Lits Supplémentaires"
      },
      juniorSuiteBadges: {
        occupancy: "4",
        roomSize: "45m²",
        bedding: "1 Queen + 1 Canapé",
        bathrooms: "1 Salle de bain"
      },
      juniorSuiteDescription: {
        mobile: "Suite junior luxueuse de 45m² avec espace de vie séparé, canapé-lit et balcon privé avec vues alpines époustouflantes.",
        desktop: "Suite junior luxueuse de 45m² avec espace de vie séparé, canapé-lit et balcon privé avec vues alpines époustouflantes."
      },
      tripleRoomFeatures: {
        perfectForSmallFamilies: "Parfait pour les petites familles",
        scenicBalconyWithViews: "Balcon pittoresque avec vue montagne, lac ou village"
      },
      tripleRoomBadges: {
        occupancy: "3",
        roomSize: "30m²",
        bedding: "3 Lits simples",
        bathrooms: "1 Salle de bain"
      },
      tripleRoomDescription: {
        mobile: "Chambre triple confortable de 30m² avec trois lits simples, parfaite pour les petites familles ou groupes.",
        desktop: "Chambre triple confortable de 30m² avec trois lits simples, parfaite pour les petites familles ou groupes."
      },
      doubleRoomFeatures: {
        perfectForCouples: "Parfait pour les couples",
        balconyWithViews: "Avec/sans balcon; vues montagne, lac ou village"
      },
      doubleRoomBadges: {
        occupancy: "2",
        roomSize: "30m²",
        bedding: "1 Lit double",
        bathrooms: "1 Salle de bain"
      },
      doubleRoomDescription: {
        mobile: "Chambre double élégante de 30m² avec lit double et équipements modernes, idéale pour les couples.",
        desktop: "Chambre double élégante de 30m² avec lit double et équipements modernes, idéale pour les couples."
      },
      alpineDoubleTwin: "Chambre Alpine Double/Lits Jumeaux - Balcon & Vue Lac",
      alpineDoubleTwinFeatures: {
        flexibleBedding: "Configuration flexible lit double ou lits jumeaux",
        privateBalconyWithViews: "Balcon privé avec vue imprenable sur le lac",
        perfectForCouplesOrFriends: "Parfait pour les couples ou amis voyageant ensemble"
      },
      alpineDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "32m²",
        bedding: "1 Lit double ou 2 Lits jumeaux",
        bathrooms: "1 Salle de bain"
      },
      alpineDoubleTwinDescription: {
        mobile: "Chambre alpine flexible avec balcon et vue imprenable sur le lac, parfaite pour couples ou amis.",
        desktop: "Notre chambre Alpine Double/Lits Jumeaux offre 32m² d'espace confortable avec des options de couchage flexibles et un balcon privé donnant sur le lac de Thoune. Parfait pour les couples ou amis recherchant confort et paysage alpin à couper le souffle."
      },
      alpineClassicDoubleTwin: "Alpine Classic Double/Lits Jumeaux - Vue Village",
      alpineClassicDoubleTwinFeatures: {
        flexibleBedding: "Configuration flexible lit double ou lits jumeaux",
        villageViews: "Vues charmantes du village et atmosphère paisible"
      },
      alpineClassicDoubleTwinBadges: {
        occupancy: "2",
        roomSize: "28m²",
        bedding: "1 Lit double ou 2 Lits jumeaux",
        bathrooms: "1 Salle de bain"
      },
      alpineClassicDoubleTwinDescription: {
        mobile: "Chambre alpine classique avec vues charmantes du village et couchage flexible, parfaite pour couples ou amis.",
        desktop: "Notre chambre Alpine Classic Double/Lits Jumeaux offre 28m² d'espace confortable avec des options de couchage flexibles et des vues paisibles du village. Parfait pour les couples ou amis recherchant le charme alpin authentique avec la flexibilité des configurations double ou lits jumeaux."
      },
      jungfrauSingle: "Chambre Simple Jungfrau - Balcon & Vue Lac",
      jungfrauSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Lit simple",
        bathrooms: "1 Salle de bain"
      },
      jungfrauSingleDescription: {
        mobile: "Chambre simple avec balcon privé et vues imprenables sur le lac, parfaite pour voyageurs solo.",
        desktop: "Notre chambre Simple Jungfrau offre 20m² d'espace confortable avec un balcon privé donnant sur le lac de Thoune. Parfaite pour les voyageurs solo recherchant confort et paysage alpin à couper le souffle avec accès direct à l'air frais de montagne."
      },
      jungfrauSingleFeatures: {
        balconyWithLakeView: "Balcon privé avec vues imprenables sur le lac",
        perfectForSoloTravelers: "Parfait pour les voyageurs solo"
      },
      heritageSingle: "Chambre Simple Heritage - Vue Village",
      heritageSingleBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Lit simple",
        bathrooms: "1 Salle de bain"
      },
      heritageSingleDescription: {
        mobile: "Chambre simple classique avec vues charmantes du village et caractère patrimonial.",
        desktop: "Notre chambre Simple Heritage offre 20m² d'espace confortable avec des vues paisibles du village. Parfaite pour les voyageurs solo recherchant le charme alpin authentique avec l'hospitalité suisse classique et le caractère traditionnel."
      },
      heritageSingleFeatures: {
        villageViews: "Vues charmantes du village et atmosphère paisible",
        classicCharm: "Charme alpin classique et caractère patrimonial"
      },
      singleRoomFeatures: {
        perfectForSoloTravelers: "Parfait pour les voyageurs solo",
        balconyWithViews: "Avec/sans balcon; vues montagne, lac ou village"
      },
      singleRoomBadges: {
        occupancy: "1",
        roomSize: "20m²",
        bedding: "1 Lit simple",
        bathrooms: "1 Salle de bain"
      },
      singleRoomDescription: {
        mobile: "Chambre simple confortable de 20m² avec lit simple, parfaite pour les voyageurs solo recherchant le confort.",
        desktop: "Chambre simple confortable de 20m² avec lit simple, parfaite pour les voyageurs solo recherchant le confort."
      }
    },
    restaurant: {
      title: "Excellence Culinaire",
      description: "Des spécialités suisses traditionnelles à la fine cuisine innovante, notre restaurant célèbre les riches saveurs des Alpes. Découvrez une cuisine exceptionnelle dans un cadre montagnard élégant.",
      exploreRestaurant: "Explorer le restaurant",

      // Hero Section
      hero: {
        logoAlt: "Logo Restaurant Jungfrau"
      },

      renovationNotice: {
        eyebrow: "Avis Restaurant",
        title: "Restauration sur rendez-vous uniquement",
        description:
          "Le Restaurant Jungfrau accueille ses hôtes sur réservation. Contactez-nous pour organiser votre expérience gastronomique alpine.",
        completion: "",
        ctaPrimary: "Compris",
        ctaSecondary: "Parlez-nous",
      },

      // About Section
      about: {
        backgroundText: "JUNGFRAU",
        tagline: "RESTAURANT JUNGFRAU",
        title: "GASTRONOMIE ALPINE\nÀ SON MEILLEUR.",
        description: "Le Restaurant Jungfrau incarne le summum de la gastronomie alpine, où les traditions culinaires suisses séculaires se marient harmonieusement avec les techniques internationales contemporaines. Notre philosophie s'enracine dans trois principes fondamentaux : authenticité, durabilité et innovation.",
        phoneButton: "+41 33 841 00 21"
      },

      // Features Section
      features: {
        alpineViews: {
          title: "VUES ALPINES",
          description: "Panorama du lac de Thoune & Jungfrau"
        },
        localSourcing: {
          title: "APPROVISIONNEMENT LOCAL",
          description: "Agriculteurs & artisans suisses"
        },
        swissHeritage: {
          title: "PATRIMOINE SUISSE",
          description: "Traditions séculaires"
        }
      },

      // Menu Section
      menu: {
        tagline: "CUISINE ALPINE SUISSE",
        title: "MENU RESTAURANT JUNGFRAU",
        description: "Découvrez notre dîner signature de 5 services mettant en vedette la cuisine alpine suisse traditionnelle avec des ingrédients locaux frais. Chaque service est soigneusement préparé pour célébrer le riche patrimoine culinaire de la région de l'Oberland bernois. Ceci n'est qu'un aperçu - consultez notre menu complet ci-dessous.",
        subtitle: "Un goût des Alpes - Est. 1881",
        tabs: {
          mainCourses: "Plats principaux",
          fiveCourseDinner: "Dîner 5 Services",
          sides: "Accompagnements",
          beverages: "Boissons"
        },
        viewFullMenu: "Voir le menu complet",
        dinnerHours: "Dîner de 18h30 à 21h00 | Réservations : +41 76 622 01 22",
        reservations: "Réservations : +41 76 622 01 22",
        pricesInclusive: "Tous les prix incluent la TVA"
      },

      // Menu Items
      menuItems: {
        fiveCourseMenu: {
          name: "Menu 5 Services",
          description: "COURSE_1:Salade: Salade de Jardin Suisse|COURSE_2:Soupe: Soupe de Légumes du Jour|COURSE_3:Plat Principal (choisissez-en un):|COURSE_3_OPTION_1:Poulet Grillé \"Jungfrau\" aux Légumes|COURSE_3_OPTION_2:Saumon Norvégien Cuit au Four Beatenberg|COURSE_3_OPTION_3:Pizza Eiger aux Champignons et Fromage (Végétarien)|COURSE_3_OPTION_4:Curry Asiatique (Poulet ou Végétarien) avec Naan à l'Ail|COURSE_4:Dessert: Pâtisserie du Jour / Sélection de Glaces / Gâteau au Chocolat / Gâteau Glacé à la Vanille|COURSE_5:Boisson Chaude: Café ou Thé"
        },
        healthySwissSalad: {
          name: "Salade Suisse Saine",
          description: "Salade Suisse Saine avec Ingrédients Locaux Frais"
        },
        vegetableSoup: {
          name: "Soupe de Légumes du Jour",
          description: "Soupe Végétarienne du Jour avec Légumes de Saison"
        },
        jungfrauClassic: {
          name: "Jungfrau Classic",
          description: "Poitrine de poulet grillée avec légumes"
        },
        thunerseeDelight: {
          name: "Thunersee Delight",
          description: "Filet de saumon grillé avec légumes"
        },
        alpineHutRosti: {
          name: "Rösti Alpine Hut",
          description: "Rösti végétarien avec fromage fait maison"
        },
        eigerPeakPinsa: {
          name: "Pinsa Eiger Peak",
          description: "PIZZA végétarienne avec champignons et fromage"
        },
        asianCurry: {
          name: "Curry Asiatique",
          description: "Curry de poulet ou végétarien avec riz et raita"
        },
        frenchFries: {
          name: "Frites",
          description: "Frites dorées et croustillantes"
        },
        smokedSalmonSandwich: {
          name: "Sandwich au Saumon Fumé",
          description: "Sandwich au saumon fumé sur pain frais avec garniture de salade légère."
        },
        eggCheeseSandwich: {
          name: "Sandwich Œuf & Fromage",
          description: "Sandwich chaud aux œufs et fromage suisse, parfait pour une bouchée alpine rapide."
        },
        healthySaladWithEgg: {
          name: "Salade Saine aux Œufs",
          description: "Salade de jardin croquante garnie d'œufs et de légumes de saison."
        },
        healthySalad: {
          name: "Salade Saine",
          description: "Salade mixte fraîche aux herbes alpines et vinaigrette légère."
        },
        chickenNuggets: {
          name: "Nuggets de Poulet (6 Pcs)",
          description: "Six nuggets de poulet croustillants, idéaux comme accompagnement à partager ou en-cas."
        },
        miniCroissant: {
          name: "Mini Croissant (2 Pcs)",
          description: "Deux mini croissants beurrés, un accompagnement sucré léger ou une gourmandise d'après-midi."
        },
        sweetPotatoFries: {
          name: "Frites de Patate Douce",
          description: "Frites de patate douce aux épices alpines"
        },
        falafel: {
          name: "Falafel",
          description: "Falafel traditionnel du Moyen-Orient"
        },
        potatoRostiFries: {
          name: "Frites de Rösti de Pomme de Terre",
          description: "Frites de rösti de pomme de terre à la suisse"
        },
        clavienCornalin: {
          name: "Clavien Cornalin",
          description: "Vin rouge suisse (10 cl) - AOC Valais"
        },
        yvorneRouge: {
          name: "Yvorne Rouge AOC",
          description: "Vin rouge suisse (10 cl) - Chablais"
        },
        yvorneBlanc: {
          name: "Yvorne Blanc",
          description: "Vin blanc suisse (10 cl) - Chablais"
        },
        rugenbrauAmber: {
          name: "Rugenbräu Amber",
          description: "Bière ambrée suisse (33 cl) - Brassée localement"
        },
        rugenbrauSpezial: {
          name: "Rugenbräu Spezial Hell",
          description: "Lager suisse premium (33 cl)"
        },
        rivella: {
          name: "Rivella Original",
          description: "Boisson rafraîchissante suisse emblématique (33 cl)"
        },
        iceTea: {
          name: "Ramseier Thé Glacé",
          description: "Thé à la menthe ou noir suisse (33 cl)"
        },
        apfelschorle: {
          name: "Ramseier Jus de Pomme Pétillant",
          description: "Jus de pomme suisse avec eau pétillante (33 cl)"
        }
      },

      // Featured Items
      featuredItems: {
        tagline: "POINTS FORTS DU MENU",
        title: "ARTICLES EN VEDETTE"
      },

      // Dining Venues
      diningVenues: {
        tagline: "EXPÉRIENCE GASTRONOMIQUE",
        title: "SALLES DE RESTAURATION",
        description: "Plusieurs expériences gastronomiques au Restaurant Jungfrau, chacune offrant une ambiance unique et des vues alpines à couper le souffle",
        mainRestaurant: {
          title: "Restaurant Principal",
          subtitle: "SALLE DE RESTAURATION PANORAMIQUE",
          description: "Notre salle de restauration principale offre une capacité d'accueil jusqu'à 200 invités avec des vues à couper le souffle sur le lac de Thoune et la chaîne de montagnes Jungfrau.",
          features: [
            "Capacité d'accueil : Jusqu'à 200 invités",
            "Vues panoramiques sur le lac de Thoune",
            "Chaîne de montagnes Jungfrau en arrière-plan",
            "Idéal pour les groupes et événements"
          ],
          perfectFor: "Groupes, Événements, Repas en famille"
        },
        terraceDining: {
          title: "Restauration en Terrasse",
          subtitle: "TERRASSE ALPINE",
          description: "Espace extérieur couvert avec murs de verre rétractables et systèmes de chauffage, offrant des vues dégagées sur le lac et un éclairage d'ambiance.",
          features: [
            "Capacité : 60-80 invités",
            "Vues dégagées sur le lac",
            "Murs de verre rétractables",
            "Zone bar intégrée"
          ],
          perfectFor: "Repas intimes, Occasions spéciales"
        },
        outdoorTable: {
          title: "Table Extérieure",
          subtitle: "TABLE EXTÉRIEURE PRIVÉE",
          description: "Expériences culinaires intimes avec menus personnalisés, mettant en vedette de la musique acoustique live et des pièces suisses traditionnelles.",
          features: [
            "Expériences culinaires intimes",
            "Menus personnalisés",
            "Sons de la nature alpine",
            "Pièces suisses traditionnelles"
          ],
          perfectFor: "Célébrations spéciales, Dégustations de vin"
        },
        alpineBreakfast: {
          title: "Petit-déjeuner Alpin",
          subtitle: "BUFFET PETIT-DÉJEUNER ALPIN",
          description: "Buffet petit-déjeuner alpin avec des produits d'approvisionnement local, mettant en vedette des articles de petit-déjeuner suisses traditionnels et des ingrédients de montagne frais.",
          features: [
            "Produits d'approvisionnement local",
            "Articles de petit-déjeuner suisses traditionnels",
            "Ingrédients de montagne frais",
            "Vues matinales panoramiques"
          ],
          perfectFor: "Repas du matin, Invités de l'hôtel"
        }
      },

      // Scrolling Text
      scrollingText: {
        swissHeritage: "PATRIMOINE SUISSE",
        alpineGastronomy: "GASTRONOMIE ALPINE",
        farmToTable: "DE LA FERME À LA TABLE",
        fiveCourseDinner: "DÎNER 5 SERVICES",
        swissWines: "VINS SUISSES",
        localBeverages: "BOISSONS LOCALES",
        alpineSides: "ACCOMPAGNEMENTS ALPINS",
        freshSalads: "SALADES FRAÎCHES",
        gastrosuisseCertified: "CERTIFIÉ GASTROSUISSE"
      },

      // Gallery
      gallery: {
        eyebrow: "EXPÉRIENCE VISUELLE",
        title: "Galerie du Restaurant",
        subtitle: "Découvrez notre ambiance et notre art culinaire"
      },

      // Testimonials
      testimonials: {
        // Testimonial content is kept in original language as they are real customer reviews
      },

      // GASTROSUISSE Certification
      gastrosuisse: {
        tagline: "EXCELLENCE SUISSE",
        title: "CERTIFIÉ GASTROSUISSE",
        description: "Reconnu par l'association suisse de l'hôtellerie pour maintenir les plus hauts standards de qualité, de service et d'excellence culinaire",
        certificationBenefits: {
          title: "AVANTAGES DE CERTIFICATION",
          benefits: [
            "Reconnaissance de l'association suisse de l'hôtellerie",
            "Conformité aux standards de qualité et de service",
            "Reconnaissance et crédibilité de l'industrie",
            "Avantages du statut de membre premium"
          ]
        },
        qualityStandards: {
          title: "STANDARDS DE QUALITÉ",
          standards: [
            "Évaluations et inspections de qualité régulières",
            "Programmes de formation et de développement du personnel",
            "Préservation de la tradition culinaire suisse",
            "Excellence de la satisfaction et de l'expérience des invités"
          ]
        },
        additionalAccreditations: {
          title: "ACCÉDITATIONS ADDITIONNELLES",
          accreditations: [
            "Association Suisse des Hôtels - Membre Premium",
            "Certification Tourisme Swisstainable",
            "Affiliation Bern Welcome"
          ]
        }
      },

      // Contact Section
      contact: {
        tagline: "ENTRER EN CONTACT",
        title: "Visitez-nous aujourd'hui",
        subtitle: "Nous aimerions vous servir une expérience inoubliable",
        visitLocation: {
          title: "Visitez notre emplacement",
          address: [
            "Beatenberg Resort & Spa",
            "Spirenwaldstrasse 351",
            "3803 Beatenberg, Suisse",
            "Le plus long village d'Europe"
          ]
        },
        callUs: {
          title: "Appelez-nous maintenant",
          details: [
            "+41 33 841 00 21",
            "Restaurant Jungfrau",
            "Beatenberg Resort & Spa"
          ]
        },
        email: {
          title: "Adresse e-mail",
          details: [
            "info@beatenberg-resortspa.ch"
          ]
        }
      },

      // Christmas & New Year Dinner Registration Form
      christmasNewYearForm: {
        title: "Réservation au dîner de Noël et du Nouvel An",
        intro: "Rejoignez-nous pour une expérience culinaire festive spéciale. Veuillez remplir le formulaire ci-dessous pour réserver votre table.",
        pricingInfo: "CHF 111 par personne",
        minimumParty: "Réservations à partir de 2 personnes minimum",
        festiveMenusTitle: "Menus Festifs",
        christmasMenuLabel: "Menu de Noël",
        newYearMenuLabel: "Menu du Nouvel An",
        nameLabel: "Nom complet",
        namePlaceholder: "Entrez votre nom complet",
        emailLabel: "Adresse e-mail",
        emailPlaceholder: "votre.email@exemple.com",
        mobileLabel: "Numéro de portable",
        mobilePlaceholder: "Entrez votre numéro de portable",
        countLabel: "Nombre d'invités",
        countPlaceholder: "Sélectionnez le nombre d'invités",
        eventLabel: "Événement",
        eventChristmas: "Réveillon de Noël - 24 décembre 2025",
        eventNewYear: "Réveillon du Nouvel An - 31 décembre 2025",
        timeSlotLabel: "Créneau horaire préféré",
        timeSlot1: "18:00–20:00",
        timeSlot2: "20:15–22:15",
        submitButton: "Soumettre la réservation",
        submittingButton: "Envoi en cours...",
        successMessage: "Merci ! Votre réservation a été soumise avec succès. Nous vous contacterons sous peu pour confirmer votre réservation.",
        errorMessage: "Une erreur s'est produite lors de la soumission de votre réservation. Veuillez réessayer ou nous contacter directement.",
        validationRequired: "Ce champ est obligatoire",
        validationEmail: "Veuillez entrer une adresse e-mail valide",
        validationPhone: "Veuillez entrer un numéro de téléphone valide",
        validationCount: "Veuillez sélectionner le nombre d'invités (2-12)",
        honeypotLabel: "Laissez ce champ vide",
        modalTitle: "Réservation confirmée !",
        modalSubtitle: "Votre réservation a été reçue",
        modalNameLabel: "Nom",
        modalEventLabel: "Événement",
        modalTimeSlotLabel: "Créneau horaire",
        modalGuestsLabel: "Invités",
        modalCloseButton: "Fermer"
      }
    },
    spa: {
      serviceList: {
        addToList: "Ajouter à la liste",
        addedToList: "Ajouté à la liste",
        title: "Votre liste de services",
        empty: "Votre liste est actuellement vide",
        details: "Détails de la demande",
        name: "Nom complet",
        email: "Adresse e-mail",
        phone: "Numéro de téléphone",
        date: "Date préférée",
        time: "Heure préférée",
        morning: "Matin",
        afternoon: "Après-midi",
        evening: "Soir",
        subtotal: "Total estimé",
        sendInquiry: "Envoyer la demande",
        successTitle: "Merci",
        successMessage: "Votre demande a été reçue. Notre concierge vous contactera sous peu.",
        errorTitle: "Échec de l'envoi",
        errorMessage: "Un problème est survenu. Veuillez réessayer ou nous contacter directement.",
        closeList: "Fermer la liste",
        tryAgain: "Réessayer",
        browseServices: "Parcourir les soins",
        selections: "Sélections",
        catalog: "Catalogue",
        selectDate: "Sélectionner une date",
        selectTime: "Sélectionner une heure",
        preferredDateCET: "Date préférée (CET) *",
        requestAppointment: "Demander un rendez-vous",
        reviewSelections: "Réviser les sélections",
        addToMyList: "Ajouter à ma liste",
        viewMyList: "Voir ma liste",
        yourRelaxationAwaits: "Votre détente vous attend",
        selectTreatmentsMessage: "Sélectionnez des soins dans notre menu pour créer votre plan de bien-être personnalisé.",
        viewSpaMenu: "Voir le menu spa",
        totalEstimated: "Total estimé",
        thankYouReceived: "Merci ! Votre demande a été reçue.",
        requestedTimesNote: "*Les heures demandées sont sujettes à disponibilité. Nous vous contacterons pour confirmer votre rendez-vous."
      },
      title: "Votre Échappée Bien-être",
      description: "Échappez-vous dans notre sanctuaire alpin tranquille où la relaxation rencontre la régénération. Découvrez des traitements spa de classe mondiale, des massages thérapeutiques et des programmes de bien-être conçus pour restaurer votre esprit, corps et âme dans les montagnes suisses immaculées.",
      exploreSpa: "Explorer le spa",
      holisticWellnessTitle: "Centre de Bien-être Holistique",
      holisticWellnessDesc: "Découvrez des programmes de bien-être complets combinant l'expertise médicale avec les méthodes de guérison traditionnelles dans notre cadre alpin serein.",
      medicalSpaTitle: "Excellence du Spa Médical",
      medicalSpaDesc: "Traitements de spa médical de pointe conçus pour promouvoir la guérison, la régénération et une santé optimale dans un environnement luxueux.",
      detoxTitle: "Détox & Nettoyage",
      detoxDesc: "Programmes de détox complets pour nettoyer votre corps des toxines et restaurer l'équilibre naturel grâce à des traitements spécialisés et une nutrition adaptée.",
      relaxationBalconyTitle: "Balcon de Relaxation",
      relaxationBalconyDesc: "Profitez de moments paisibles sur notre balcon panoramique avec des vues alpines époustouflantes, parfait pour la méditation et la réflexion silencieuse.",
      therapeuticMassageTitle: "Massage Thérapeutique",
      therapeuticMassageDesc: "Traitements de massage spécialisés incluant massage complet, massage de la tête et du dos pour une relaxation et une guérison ciblées.",

      // Hero Section
      hero: {
        mainTitle: "Découvrez le bien-être renforcé par la nature",
        subMessage: "Où l'air alpin pur et l'eau de montagne pure rencontrent l'excellence thérapeutique suisse",
        ctaButton: "Découvrir le Bien-être Alpin"
      },

      // Nature and Science Section
      natureScience: {
        title: "Guérison Naturelle & Excellence Suisse",
        subtitle: "Soutenus par NutraSwiss et Nahrin — combinant la pureté de la nature avec des décennies d'expertise suisse en santé naturelle",
        images: {
          massage: "Traitement de massage thérapeutique",
          therapist: "Thérapeute spa professionnel",
          treatment: "Salle de traitement spa",
          wellness: "Rafraîchissements bien-être",
          alpine: "Expérience de bien-être alpin",
          relaxation: "Zone de détente"
        },
        blocks: {
          air: {
            title: "Air Alpin Pur",
            description: "Chaque respiration que vous prenez dans l'Oberland bernois à 1 200 m d'altitude est naturellement filtrée par l'environnement alpin, améliorant les processus de guérison naturels de votre corps. L'air propre et riche en oxygène améliore la santé respiratoire et augmente les niveaux d'énergie."
          },
          water: {
            title: "Eau de Montagne Pure",
            description: "Nos traitements incorporent de l'eau alpine pure provenant de sources de montagne, ajoutant des minéraux naturels et de la pureté à chaque expérience. Les traitements à l'eau de source de montagne utilisant de l'eau alpine pure améliorent les bienfaits thérapeutiques de chaque séance."
          },
          science: {
            title: "Excellence Scientifique Suisse",
            description: "Soutenus par plus de 60 ans d'expertise suisse en santé naturelle grâce à Nahrin et NutraSwiss, nos traitements combinent les traditions de bien-être suisses traditionnelles avec la science moderne, utilisant des formulations botaniques premium fabriquées à partir d'ingrédients 100% naturels."
          }
        }
      },

      renovationNotice: {
        eyebrow: "Avis Spa",
        title: "Sauna & bien-être thermal gratuits pour les clients de l'hôtel",
        description:
          "Le forfait sauna & bien-être thermal est offert aux clients séjournant à l'hôtel.",
        completion: "",
        ctaPrimary: "Réservez votre séjour",
        ctaSecondary: "Parlez-nous",
        imageAlt: "Spa et sauna alpins au Beatenberg Resort",
      },

      // Introduction Section
      introduction: {
        title: "Bienvenue à la Clinique NutraSwiss",
        description: "Votre sanctuaire de relaxation et de luxe rustique au cœur des Alpes suisses. Après une journée d'exploration de la beauté à couper le souffle de la Suisse - de la majestueuse région de la Jungfrau aux eaux cristallines du lac de Thoune - retirez-vous dans notre spa pour l'expérience ultime de relaxation alpine.",
        perfectEndTitle: "La fin parfaite de votre aventure suisse",
        perfectEndDesc: "Nous combinons les traditions de bien-être suisses traditionnelles avec le confort moderne, vous offrant le complément parfait à votre aventure suisse. Que vous ayez randonné sur les sentiers de montagne, exploré des châteaux historiques, ou simplement profité du paysage alpin spectaculaire, nos traitements vous aideront à vous détendre et à vous régénérer dans le style suisse authentique.",
        mountainViews: "Vues panoramiques sur les montagnes depuis chaque salle de traitement",
        alpineBotanicals: "Traitements suisses traditionnels utilisant des botaniques alpines locales",
        swissHospitality: "Hospitalité suisse authentique avec un service chaleureux et personnalisé",
        nutraSwissScience: "Science nutritionnelle NutraSwiss améliorant le bien-être suisse traditionnel avec des traitements basés sur des preuves et des protocoles personnalisés",
        perfectForYouTitle: "Parfait pour vous",
        convenientLocation: "Emplacement pratique dans votre hôtel - pas besoin de voyager ailleurs",
        flexibleScheduling: "Planification flexible pour s'adapter à votre itinéraire de visites",
        multilingualStaff: "Personnel multilingue parlant couramment l'anglais, l'allemand, le français et l'italien",
        complimentaryConsultation: "Consultation gratuite pour choisir le traitement parfait"
      },

      // Power of Touch Section
      powerOfTouch: {
        category: "Guérison thérapeutique",
        title: "LE POUVOIR DU TOUCHER",
        description: "Découvrez le pouvoir transformateur du toucher thérapeutique qualifié dans notre sanctuaire alpin serein. Nos massothérapeutes certifiés combinent la sagesse de guérison ancienne avec des techniques contemporaines, utilisant des produits suisses de qualité supérieure pour restaurer l'équilibre du corps et de l'esprit. De la thérapie des tissus profonds aux techniques de relaxation douces, chaque traitement est adapté à vos besoins individuels, créant un voyage profondément personnel de restauration et de renouvellement.",
        ctaButton: "Réservez votre expérience thérapeutique"
      },

      // Beauty Section
      beauty: {
        category: "Bien-être alpin",
        title: "L'ART",
        subtitle: "du Renouvellement",
        ctaButton: "Découvrir les traitements de beauté"
      },

      // Quick Relaxation Section
      quickRelaxation: {
        category: "Relaxation rapide",
        title: "RELAXATION RAPIDE",
        description: "Découvrez le pouvoir transformateur du toucher thérapeutique qualifié dans notre sanctuaire alpin serein. Nos massothérapeutes certifiés combinent la sagesse de guérison ancienne avec des techniques contemporaines, utilisant des produits suisses de qualité supérieure pour restaurer l'équilibre du corps et de l'esprit. De la thérapie des tissus profonds aux techniques de relaxation douces, chaque traitement est adapté à vos besoins individuels, créant un voyage profondément personnel de restauration et de renouvellement.",
        ctaButton: "Réservez votre relaxation rapide",
        modal: {
          title: "Options de relaxation rapide",
          description: "Parfait pour les touristes occupés qui veulent se faire dorloter entre les visites. Nos options de relaxation rapide sont conçues pour s'intégrer dans votre emploi du temps d'aventure suisse tout en offrant des avantages de relaxation maximaux.",
          closeButton: "Fermer la fenêtre",
          options: {
            expressMassage: {
              title: "Massage express tête et épaules",
              description: "Parfait pour soulager les tensions du tourisme. Massage rapide et efficace axé sur le cou, les épaules et la tête."
            },
            footTreatment: {
              title: "Traitement des pieds alpin suisse",
              description: "Revitalisez vos pieds fatigués après l'exploration des montagnes. Inclut bain de pieds, massage et traitement aux herbes alpines."
            },
            meditation: {
              title: "Méditation avec vue sur la montagne",
              description: "Séance de méditation guidée avec vue panoramique sur les Alpes. Parfait pour la relaxation mentale et la pleine conscience."
            },
            teaRitual: {
              title: "Rituel de thé aux herbes alpines",
              description: "Cérémonie de thé suisse traditionnelle avec des herbes alpines d'origine locale. Un moment paisible de réflexion."
            }
          },
          benefits: {
            title: "Pourquoi choisir la relaxation rapide ?",
            flexibleScheduling: {
              title: "Planification flexible",
              description: "Traitements conçus pour s'adapter à votre emploi du temps de tourisme, de 20 à 45 minutes."
            },
            alpineExperience: {
              title: "Expérience alpine",
              description: "Traitements suisses authentiques utilisant des herbes alpines locales et des techniques traditionnelles."
            },
            instantRelief: {
              title: "Soulagement instantané",
              description: "Traitements rapides et efficaces pour soulager les tensions de la randonnée et du tourisme."
            },
            perfectForTourists: {
              title: "Parfait pour les touristes",
              description: "Conçu spécifiquement pour les visiteurs explorant les attractions de la Suisse."
            }
          },
          booking: {
            title: "Prêt à réserver votre relaxation rapide ?",
            description: "Réservez votre expérience en contactant notre concierge. Notre équipe dédiée vous aidera à planifier vos traitements et répondra à toutes vos questions sur nos services spa. Contactez-nous à la réception pendant votre séjour.",
            button: "Contacter le concierge"
          }
        }
      },

      // Booking Section
      booking: {
        title: "Réservation & Contact",
        description: "Prêt à découvrir la retraite de bien-être alpine ultime ? Contactez-nous pour réserver vos traitements et commencer votre voyage vers le bien-être",
        contactInfoTitle: "Informations de contact",
        address: "Clinique NutraSwiss\nBeatenberg Resort & Spa\nSpirenwaldstrasse 351\n3803 Beatenberg, Suisse",
        reservations: "Réservation directe via le concierge de l'hôtel",
        email: "Contact via la réception de l'hôtel",
        accessibility: "Situé dans les locaux du Beatenberg Resort & Spa\nAccès facile depuis tous les hébergements de l'hôtel",
        guestBenefitsTitle: "Avantages pour les invités",
        complimentaryConsultation: "Consultation gratuite pour les nouveaux visiteurs",
        detoxTea: "Thé détox et collations saines après les traitements",
        winterGarden: "Accès au jardin d'hiver exclusif avec traitements",
        guestCardBenefits: "Avantages de la carte d'invité pour les attractions régionales",
        advanceReservations: "Réservations à l'avance recommandées pour tous les traitements",
        packageDeals: "Offres de forfaits disponibles pour plusieurs traitements",
        groupPrograms: "Programmes de bien-être de groupe peuvent être organisés",
        bookNowButton: "Réservez votre traitement maintenant"
      },

      // Alpine Air Therapy Modal
      alpineAirTherapy: {
        title: "Thérapie par l'Air Alpin & Installations",
        description: "Découvrez le pouvoir transformateur de l'air alpin pur dans nos installations de bien-être de pointe. Notre thérapie unique par l'air alpin combine les propriétés curatives de l'air de montagne avec les pratiques de bien-être suisses traditionnelles.",
        relaxationAreas: "Zones de relaxation",
        coreFacilities: "Installations principales",
        uniqueFeatures: "Caractéristiques uniques",
        benefitsTitle: "Avantages de la Thérapie par l'Air Alpin",
        pureMountainAir: "Air pur de montagne",
        pureMountainAirDesc: "L'air pur et riche en oxygène à 1 200m d'altitude améliore la santé respiratoire et stimule les niveaux d'énergie.",
        seasonalTherapy: "Thérapie saisonnière",
        seasonalTherapyDesc: "Thérapie par l'air alpin toute l'année avec des traitements saisonniers spécialisés et des infusions d'herbes.",
        readyToExperience: "Prêt à découvrir la Thérapie par l'Air Alpin ?",
        contactConciergeDesc: "Réservez votre expérience en contactant notre concierge. Notre équipe dédiée vous aidera à planifier vos traitements et répondra à toutes vos questions sur nos services spa. Contactez-nous à la réception pendant votre séjour.",
        contactConciergeButton: "Contacter le concierge",
        facilities: {
          panoramicLounge: "Salon de relaxation panoramique avec fenêtres du sol au plafond",
          alpineGarden: "Terrasse du jardin alpin pour la relaxation en plein air",
          swissSauna: "Sauna suisse traditionnel avec infusions d'herbes alpines",
          steamRoom: "Hammam avec herbes de montagne locales",
          infraredSauna: "Sauna infrarouge pour une relaxation douce et profonde",
          meditationCorner: "Coin de méditation tranquille avec vue sur les montagnes",
          bioSpa: "Sauna & Bio Spa avec thérapies aux herbes alpines",
          fitnessCenter: "Centre de fitness de pointe avec équipement Technogym",
          yogaPavilion: "Pavillon dédié à la méditation et au yoga",
          wellnessTerrace: "Terrasse de bien-être extérieure pour la thérapie par l'air alpin saisonnière",
          hydrotherapyPools: "Piscines d'hydrothérapie avec températures variables",
          massageRooms: "Salles de massage et thérapie de guérison",
          winterGarden: "Jardin d'hiver exclusif avec accès au sauna infrarouge",
          mountainSpringWater: "Traitements à l'eau de source de montagne utilisant l'eau alpine pure",
          alpineBotanicals: "Botaniques alpines locales dans tous les traitements et produits",
          swissDesign: "Design suisse traditionnel avec confort moderne",
          peptideTherapies: "Thérapies peptidiques développées en Suisse",
          healingPractices: "Pratiques de guérison suisses traditionnelles combinées avec la science moderne du bien-être",
          panoramicViews: "Vues panoramiques sur le lac de Thoune et les Alpes bernoises pendant les traitements"
        }
      },

      // Beauty & Wellness Programs Modal
      beautyWellnessPrograms: {
        title: "Programmes Beauté & Bien-être",
        description: "Découvrez nos retraites de bien-être complètes conçues pour transformer votre corps, esprit et âme au cœur des Alpes suisses. Chaque programme combine la technologie de bien-être suisse de pointe avec les pratiques de guérison alpines traditionnelles.",
        readyToTransform: "Prêt à transformer votre parcours de bien-être ?",
        contactConciergeDesc: "Réservez votre expérience en contactant notre concierge. Notre équipe dédiée vous aidera à planifier vos traitements et répondra à toutes vos questions sur nos services spa. Contactez-nous à la réception pendant votre séjour.",
        contactConciergeButton: "Contacter le concierge",
        categories: {
          detoxRenewal: "Détox & Renouvellement",
          detoxRenewalDesc: "Programmes de nettoyage complets pour le renouvellement cellulaire et l'élimination des toxines",
          aestheticEnhancement: "Amélioration Esthétique",
          aestheticEnhancementDesc: "Traitements avancés de rajeunissement de la peau et d'amélioration de la beauté",
          personalizedWellness: "Bien-être Personnalisé",
          personalizedWellnessDesc: "Analyse génétique et programmes d'optimisation de la santé personnalisés"
        },
        programs: {
          alpineGlow: {
            title: "Retraite Alpine Glow®",
            duration: "8 Jours",
            description: "Découvrez Alpine Glow : Une retraite de 8 jours offrant perte de poids, rajeunissement et bien-être grâce au yoga, à une nutrition saine et aux pratiques de pleine conscience. Immergez-vous dans la beauté des Alpes suisses et repartez avec un esprit revitalisé."
          },
          renewalRadiance: {
            title: "Détox Renewal Radiance®",
            duration: "10 Jours",
            description: "Programme de détox de classe mondiale pour les patients de chimiothérapie et de cancer. Avec un accent sur le nettoyage cellulaire et l'élimination des toxines, nous offrons un séjour de 10 jours à 3 semaines avec des menus détox, des thérapies et une planification nutritionnelle personnalisée."
          },
          nutraVitality: {
            title: "Rajeunissement NutraVitality®",
            duration: "7 Jours",
            description: "Immergez-vous dans notre retraite de rajeunissement NutraVitality de 7 jours pour un rajeunissement holistique. Découvrez un mélange de science de pointe et de confort luxueux pour revitaliser le corps, l'esprit et l'âme."
          },
          nutraCleanse: {
            title: "Retraite de Renouvellement NutraCleanse®",
            duration: "7 Jours",
            description: "Programme complet de détox et de renouvellement axé sur le nettoyage cellulaire et l'élimination des toxines pour un bien-être et une vitalité optimaux."
          },
          nutraBalance: {
            title: "Retraite de Bien-être Poids NutraBalance®",
            duration: "7 Jours",
            description: "Approche holistique de la gestion du poids combinant la science nutritionnelle, l'optimisation métabolique et les changements de mode de vie durables."
          },
          nutraRadiance: {
            title: "Amélioration Esthétique NutraRadiance®",
            duration: "7 Jours",
            description: "Traitements esthétiques avancés et thérapies de rajeunissement de la peau pour une amélioration naturelle de la beauté et des résultats anti-âge."
          },
          nutraGenomic: {
            title: "Retraite Insight NutraGenomic®",
            duration: "7 Jours",
            description: "Programme de bien-être personnalisé basé sur l'analyse génétique et la génomique nutritionnelle pour des résultats de santé optimisés."
          },
          nutraTotal: {
            title: "Évaluation de Bien-être NutraTotal®",
            duration: "7 Jours",
            description: "Évaluation complète de la santé et développement d'un plan de bien-être personnalisé grâce à des tests diagnostiques avancés."
          },
          nutraDerm: {
            title: "Renouvellement Radieux NutraDerm®",
            duration: "7 Jours",
            description: "Traitements dermatologiques spécialisés et thérapies de renouvellement de la peau pour une peau saine, radieuse et d'apparence jeune."
          },
          nutraFlex: {
            title: "Amélioration de la Mobilité NutraFlex®",
            duration: "7 Jours",
            description: "Programme avancé d'entraînement de mobilité et de flexibilité combinant la physiothérapie, la science du mouvement et les techniques de récupération."
          }
        }
      },

      // Therapeutic Treatments Modal
      therapeuticTreatments: {
        title: "Traitements Thérapeutiques & Services",
        description: "Découvrez le pouvoir transformateur du toucher thérapeutique qualifié dans notre sanctuaire alpin serein. Nos massothérapeutes certifiés allient la sagesse de guérison ancienne aux techniques contemporaines, utilisant des produits suisses de qualité supérieure.",
        readyToBook: "Prêt à réserver votre expérience thérapeutique ?",
        contactConciergeDesc: "Réservez votre expérience en contactant notre concierge. Notre équipe dédiée vous aidera à planifier vos traitements et répondra à toutes vos questions sur nos services spa. Contactez-nous à la réception pendant votre séjour.",
        contactConciergeButton: "Contacter le concierge",
        relaxationRejuvenation: {
          title: "Traitements de Relaxation & Rajeunissement",
          treatments: {
            alpineMountainMassage: {
              title: "Massage Alpin de Montagne",
              price: "CHF 120-180",
              duration: "60-90 minutes",
              description: "Un massage relaxant complet du corps utilisant les techniques suisses traditionnelles et les huiles d'herbes alpines. Parfait après une journée de randonnée ou de tourisme."
            },
            lakeThunAromatherapy: {
              title: "Aromathérapie du Lac de Thoune",
              price: "CHF 100",
              duration: "60 minutes",
              description: "Immergez-vous dans les parfums des Alpes suisses avec notre traitement d'aromathérapie signature utilisant des herbes alpines d'origine locale."
            },
            swissStoneTherapy: {
              title: "Thérapie par les Pierres Suisses",
              price: "CHF 140",
              duration: "75 minutes",
              description: "Découvrez la chaleur des pierres suisses chauffées combinée aux techniques de massage traditionnelles pour une relaxation musculaire profonde."
            },
            alpineHerbalSteamBath: {
              title: "Bain de Vapeur aux Herbes Alpines",
              price: "CHF 80",
              duration: "45 minutes",
              description: "Détendez-vous dans notre hammam traditionnel infusé d'herbes alpines. Parfait pour se détendre après avoir exploré les montagnes."
            }
          }
        },
        advancedWellness: {
          title: "Traitements de Bien-être Avancés",
          treatments: {
            bioScan: {
              title: "BioScan",
              price: "CHF 440",
              duration: "60-90 minutes",
              description: "Analyse approfondie pour un bien-être optimal utilisant des techniques de mesure spéciales. Inclut une liste d'aliments personnalisée et des remèdes homéopathiques."
            },
            nutritionCoaching: {
              title: "Coaching Nutritionnel",
              price: "CHF 240",
              duration: "60 minutes",
              description: "La nourriture comme médecine - approches nutritionnelles adaptées individuellement pour votre meilleur vous, du régime méditerranéen aux programmes de détox ciblés."
            },
            lightTherapy: {
              title: "Thérapie par la Lumière",
              price: "CHF 80",
              duration: "30 minutes",
              description: "Utilisant les biophotons, cette thérapie douce renforce le système immunitaire et stimule les propres pouvoirs de guérison du corps."
            },
            pelviPowerTraining: {
              title: "Formation PelviPower",
              price: "CHF 29",
              duration: "20-30 minutes",
              description: "Méthode innovante pour renforcer les muscles du tronc avec environ 25 000 contractions douces pendant une séance."
            },
            infraredSauna: {
              title: "Sauna Infrarouge",
              price: "Inclus",
              description: "Chaleur profonde pour une relaxation profonde - détoxifiez et détendez-vous au niveau cellulaire. Inclus gratuitement avec d'autres traitements."
            }
          }
        }
      },

      // Treatments & Services Section
      treatmentsAndServices: {
        title: "Votre Parcours de Bien-être",
        introduction: "Votre parcours de bien-être est soutenu par le meilleur de la nature et de la science suisses. Nous utilisons exclusivement des formulations botaniques premium fabriquées à partir d'ingrédients 100% naturels, en nous appuyant sur plus de six décennies d'expertise suisse en santé nutritionnelle et naturelle. Chaque application est conçue pour s'harmoniser avec l'environnement alpin extérieur.",
        learnMore: "En savoir plus",
        bookNow: "Réservez votre traitement",
        therapeuticMassages: {
          title: "Traitements de Massage Thérapeutique",
          fullBody: {
            name: "Massage Complet du Corps",
            pricing: "30 Min: 75 CHF",
            duration: "30 minutes",
            description: "Relaxation des muscles endoloris et régénération après l'exercice. Améliore la circulation sanguine et stimule le métabolisme."
          },
          relaxing: {
            name: "Massage Relaxant",
            pricing: "30 Min: 72 CHF",
            duration: "30 minutes",
            description: "Massage complet apaisant avec de l'huile de lavande chaude. Soulage les tensions internes et externes."
          },
          sport: {
            name: "Massage Sportif",
            pricing: "30 Min: 81 CHF",
            duration: "30 minutes",
            description: "Techniques de massage puissantes pour les muscles, les tissus conjonctifs et les fascias après le sport."
          },
          footReflexology: {
            name: "Massage de Réflexologie Plantaire",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Active l'auto-guérison et améliore les fonctions des organes par pression sur les points nerveux des pieds."
          },
          footBack: {
            name: "Massage des Pieds & du Dos",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Combinaison de réflexologie plantaire et de massage intensif du dos."
          },
          backNeck: {
            name: "Massage pour Douleurs du Dos et du Cou",
            pricing: "30 Min: 75 CHF",
            duration: "30 minutes",
            description: "Massage thérapeutique et bien-être combiné pour le dos et le cou utilisant des teintures anti-inflammatoires."
          },
          peeling: {
            name: "Gommage & Massage Complet du Corps",
            pricing: "30 Min: 98 CHF",
            duration: "30 minutes",
            description: "Élimination des cellules mortes de la peau avec un gant de gommage, suivi d'un massage complet nourrissant du corps."
          },
          energetic: {
            name: "Massage Énergétique",
            pricing: "30 Min: 66 CHF",
            duration: "30 minutes",
            description: "Soutien individuel de l'auto-guérison grâce à divers types de travail corporel et énergétique."
          }
        },
        saunaThermal: {
          title: "Sauna & Bien-être Thermal",
          package: {
            title: "Forfait Sauna & Spa",
            price: "45 CHF (par séance)",
            description: "Profitez d'un accès exclusif à notre zone sauna. Ce forfait comprend l'utilisation de toutes les installations ainsi que du café, du thé et des boissons détox gratuits.",
            hours: "HEURES DU SAUNA: 11:00 - 20:00"
          },
          finnish: {
            name: "Sauna Finlandais",
            temperature: "90°C",
            description: "La chaleur sèche intense est traditionnellement utilisée pour soutenir le bien-être général et stimuler le métabolisme. L'alternance entre chaleur et refroidissement peut favoriser la circulation et contribuer à la récupération."
          },
          bio: {
            name: "Bio Sauna",
            temperature: "60°C",
            description: "Des températures douces combinées à la luminothérapie créent une atmosphère de calme. Cette variante plus douce est souvent perçue comme particulièrement agréable et offre une expérience de chaleur relaxante qui est douce pour la circulation."
          },
          steam: {
            name: "Bain de Vapeur",
            temperature: "45°C",
            description: "La vapeur chaude aide à ouvrir les pores et peut favoriser le nettoyage de la peau. L'humidité élevée est fréquemment perçue comme apaisante pour les voies respiratoires et idéale pour la relaxation."
          },
          icePlunge: {
            name: "Bassin de Plongée Glacée",
            description: "Le coup de froid après la transpiration a un effet revitalisant sur le corps et l'esprit. Ce refroidissement peut stimuler la circulation et procure une sensation de fraîcheur et de dynamisme à la peau."
          }
        },
        specialPrograms: {
          title: "Programmes de Bien-être Spécialisés",
          pelvipower: {
            name: "Formation PelviPower™",
            price: "29 CHF (30 Min)",
            type: "Thérapie par Champ Magnétique",
            description: "Entraînez votre tronc et votre plancher pelvien simplement et efficacement en position assise. Les ondes magnétiques stimulent les muscles en profondeur sans effort physique.",
            note: "Recommandé par les Médecins & Thérapeutes",
            benefitsTitle: "Avantages",
            benefits: {
              women: {
                title: "Femmes",
                desc: "Ménopause | Augmentation de la libido"
              },
              men: {
                title: "Hommes",
                desc: "Vitalité | Augmentation de la puissance"
              },
              incontinence: {
                title: "Incontinence",
                desc: "Faiblesse de la vessie / Fuites"
              },
              back: {
                title: "Dos",
                desc: "Relaxation | Renforcement"
              },
              mothers: {
                title: "Jeunes Mères",
                desc: "Récupération après l'accouchement"
              },
              sports: {
                title: "Sport",
                desc: "Performance | Stabilité"
              }
            }
          }
        }
      }
    },
    gymPool: {
      technogymTitle: "Centre de Fitness TECHNOGYM",
      technogymDesc: "Entièrement équipé avec des équipements Technogym premium",
      premiumEquipment: "Équipement Premium",
      premiumEquipmentDesc: "Machines Technogym de pointe pour cardio, musculation et fitness fonctionnel",
      personalTraining: "Entraînement Personnel",
      personalTrainingDesc: "Entraîneurs professionnels disponibles pour des séances personnalisées",
      access24: "Accès 24/7",
      access24Desc: "Accès 24h/24 pour les invités afin de maintenir leur routine de fitness",
      alpinePool: "Piscine Alpine",
      alpinePoolDesc: "Piscine intérieure avec vue panoramique sur les montagnes",
      heatedPool: "Piscine Chauffée",
      heatedPoolDesc: "Natation toute l'année dans notre piscine intérieure à température contrôlée",
      alpineViews: "Vues Alpines",
      alpineViewsDesc: "Nagez en profitant de vues à couper le souffle sur les Alpes suisses",
      familyFriendly: "Convivial pour les Familles",
      familyFriendlyDesc: "Environnement sûr et confortable pour les invités de tous âges",
      availableToGuests: "Disponible pour tous les invités du resort",
      bookYourStay: "Réservez votre séjour"
    },
    cinema: {
      title: "Théâtre Cinéma Privé de Luxe",
      subtitle: "Votre Cinéma Privé & Lieu de Présentation d'Entreprise",
      seatingCapacity: "Capacité d'Accueil",
      seatingCapacityDesc: "Accueille jusqu'à 50 invités pour des projections privées et événements",
      corporateEvents: "Événements d'Entreprise",
      corporateEventsDesc: "Parfait pour les événements MICE, présentations et activités de team building",
      exclusiveExperience: "Expérience Exclusive",
      exclusiveExperienceDesc: "Le seul cinéma privé avec technologie IMAX dans l'Oberland bernois",
      bookTheatre: "Réserver le Théâtre",
      bookYourStay: "Réservez votre séjour",
      locatedAt: "Situé au Beatenberg Resort & Spa"
    },
    events: {
      title: "Événements & Célébrations",
      description: "Infrastructure événementielle professionnelle combinée à l'hospitalité suisse authentique. Parfait pour les réunions d'entreprise, mariages et célébrations spéciales.",
      seminarRooms: "Salles de Séminaire",
      seminarRoomsDesc: "Technologie AV moderne pour réunions d'entreprise et ateliers",
      hdProjectors: "Projecteurs HD",
      videoConferencing: "Visioconférence",
      wirelessPresentation: "Présentation Sans Fil",
      breakoutSpaces: "Espaces de Pause",
      learnMore: "En Savoir Plus",
      banquetHalls: "Salles de Banquet",
      banquetHallsDesc: "Espaces élégants pour dîners de gala, conférences et fonctions privées",
      fineDiningSetup: "Configuration Fine Dining",
      professionalCatering: "Traiteur Professionnel",
      flexibleSeating: "Assise Flexible",
      seating200: "Placement pour jusqu'à 200 personnes",
      weddingVenues: "Lieux de Mariage",
      weddingVenuesDesc: "Arrière-plans alpins panoramiques avec support de planification complet",
      alpineViews: "Vues Alpines",
      weddingPlanning: "Planification de Mariage",
      photographySpots: "Spots de Photographie",
      receptionAreas: "Zones de Réception",
      premiumFeatures: "Fonctionnalités Événementielles Premium",
      digitalLedWall: "Mur LED Numérique",
      highSpeedInternet: "Internet Haut Débit",
      audioSystems: "Systèmes Audio",
      eventPlanning: "Planification d'Événements",
      perfectForOccasion: "Parfait pour Toute Occasion",
      perfectForOccasionDesc: "Nos espaces événementiels polyvalents répondent à un large éventail de besoins et célébrations",
      privateParties: "Fêtes Privées",
      privatePartiesDesc: "Célébrations & occasions spéciales",
      groups: "Groupes",
      groupsDesc: "Groupes de voyage & rassemblements",
      wellnessRetreats: "Retraites Bien-être",
      wellnessRetreatsDesc: "Programmes de santé & bien-être",
      corporateEvents: "Événements d'Entreprise",
      corporateEventsDesc: "Réunions & conférences",
      convenientParking: "Parking Pratique",
      convenientParkingDesc: "Capacité sur site pour jusqu'à 200 voitures & bus",
      planYourEvent: "Planifiez Votre Événement",
      requestQuote: "Demander un Devis",
      professionalPlanning: "Planification d'événements professionnelle disponible",
      // New comprehensive events page translations
      heroTitle: "Créer des",
      heroSubtitle: "Moments Inoubliables",
      heroDescription: "Mariages de luxe et événements premium créés avec précision, passion et une attention inégalée aux détails.",
      planWedding: "Planifiez Votre Mariage",
      corporateEventsButton: "Événements d'Entreprise",
      customVenueOffers: "Offres de Lieu Personnalisées",
      dreamWeddings: "Mariages de Rêve",
      dreamWeddingsDescription: "Chaque histoire d'amour mérite un début parfait. Nous créons des expériences de mariage magiques qui reflètent votre voyage unique.",
      luxuryCeremonies: "Cérémonies de Luxe",
      floralDesign: "Design Floral",
      themeDesign: "Design Thématique",
      entertainment: "Divertissement",
      discussWeddingPackages: "Discuter des Forfaits Mariage",
      corporateExcellence: "Excellence d'Entreprise",
      corporateExcellenceDescription: "Événements professionnels qui renforcent les relations, inspirent les équipes et mettent en valeur l'excellence de votre marque.",
      conferences: "Conférences",
      productLaunches: "Lancements de Produits",
      teamBuilding: "Team Building",
      annualGalas: "Galas Annuels",
      discussCorporatePackages: "Discuter des Forfaits d'Entreprise",
      privatePartiesTitle: "Fêtes Privées",
      privatePartiesDescription: "Célébrez les moments spéciaux de la vie avec des fêtes inoubliables adaptées à votre style et vision.",
      birthdayCelebrations: "Célébrations d'Anniversaire",
      anniversaries: "Anniversaires",
      luxuryGalas: "Galas de Luxe",
      discussPrivatePartyPackages: "Discuter des Forfaits de Fêtes Privées",
      completeEventExperiences: "Expériences Événementielles Complètes",
      completeEventExperiencesDescription: "Des rassemblements intimes aux grandes célébrations, nous donnons vie à votre vision avec une planification méticuleuse et une exécution parfaite.",
      weddingPlanningService: "Planification de Mariage",
      weddingPlanningDescription: "Coordination complète de mariage de la demande en mariage à la réception, créant votre jour parfait avec attention à chaque détail.",
      weddingPlanningFeatures: "Hospitalité Suisse, Coordination de Fournisseurs, Gestion de Planning, Coordination 24/7 jusqu'à l'événement",
      weddingPlanningDetails: "Des cérémonies intimes aux grandes célébrations, nous gérons chaque aspect de votre jour spécial. Nos planificateurs de mariage expérimentés travaillent étroitement avec vous pour créer une expérience personnalisée qui reflète votre histoire d'amour unique.",
      weddingPlanningHighlights: "Conception et installation de cérémonie personnalisée, Gestion professionnelle de fournisseurs, Coordination détaillée de planning, Supervision d'événement sur site",
      corporateEventsService: "Événements d'Entreprise",
      corporateEventsServiceDescription: "Rassemblements d'entreprise professionnels qui laissent des impressions durables sur les clients et collègues.",
      corporateEventsServiceFeatures: "Planification de Conférence, Lancements de Produits, Team Building, Galas Annuels",
      corporateEventsServiceDetails: "Élevez vos événements d'entreprise avec nos services d'événements d'entreprise complets. Nous comprenons l'importance de la présentation professionnelle et de l'exécution transparente pour vos objectifs commerciaux.",
      corporateEventsServiceHighlights: "Planification de conférence exécutive, Configuration de présentation de marque, Équipement et technologie AV, Restauration et hospitalité",
      privateCelebrations: "Célébrations Privées",
      privateCelebrationsDescription: "Rassemblements intimes et célébrations de jalons conçus pour refléter votre style unique.",
      privateCelebrationsFeatures: "Fêtes d'Anniversaire, Anniversaires, Fêtes d'Adieu, Cérémonies d'Anneaux, Beaucoup d'autres occasions heureuses",
      privateCelebrationsDetails: "Célébrez les moments spéciaux de la vie avec des événements personnalisés qui capturent l'essence de votre occasion. Des anniversaires de jalons aux anniversaires intimes, nous créons des expériences mémorables.",
      privateCelebrationsHighlights: "Développement de thème personnalisé, Réservation de divertissement personnalisé, Options de restauration gastronomique, Photographie et documentation, Gestion de l'expérience des invités",
      serviceOverview: "Aperçu du Service",
      keyFeatures: "Caractéristiques Clés",
      whatWeProvide: "Ce que Nous Offrons",
      getStartedWith: "Commencer avec",
      premiumEventFeatures: "Fonctionnalités Événementielles Premium",
      fullServiceEventPlanning: "Planification d'Événements à Service Complet",
      fullServiceEventPlanningDesc: "Coordination complète d'événement de la conception à l'exécution",
      gourmetCateringServices: "Services de Restauration Gastronomique",
      gourmetCateringServicesDesc: "L'expertise culinaire suisse rencontre les saveurs internationales",
      stateOfTheArtTechnology: "Technologie de Pointe",
      stateOfTheArtTechnologyDesc: "Équipement AV de pointe et connectivité transparente",
      wellnessIntegration: "Intégration Bien-être",
      wellnessIntegrationDesc: "Expériences de bien-être alpines pour les participants aux événements",
      digitalLedWallFeature: "Mur LED Numérique",
      digitalLedWallFeatureDesc: "Dispositions flexibles pour événements personnalisés",
      audioSystemsFeature: "Systèmes Audio",
      audioSystemsFeatureDesc: "Équipement sonore professionnel inclus",
      eventPlanningFeature: "Planification d'Événements",
      eventPlanningFeatureDesc: "Coordination d'événements à service complet",
      premiumAmenities: "Aménagements Premium",
      premiumAmenitiesDesc: "Installations et services de luxe",
      convenientParkingTitle: "Parking Pratique",
      convenientParkingCapacity: "Capacité sur site pour jusqu'à 200 voitures & bus",
      complimentaryParking: "Parking gratuit pour tous les invités d'événement",
      ourPortfolio: "NOTRE PORTFOLIO",
      eventGallery: "Galerie d'Événements",
      eventGallerySubtitle: "Un aperçu des événements extraordinaires que nous avons eu le privilège de créer",
      whatOurClientsSay: "Ce que Disent Nos Clients",
      whatOurClientsSayDescription: "La joie et la satisfaction de nos clients sont notre plus grand accomplissement.",
      readyToCreate: "Prêt à Créer Quelque Chose d'Extraordinaire ?",
      readyToCreateDescription: "Donnons vie à votre vision. Contactez-nous aujourd'hui pour une consultation personnalisée et découvrez comment nous pouvons rendre votre événement inoubliable.",
      planningTimeline: "Délai de Planification",
      planningTimelineDesc: "6-18 mois à l'avance",
      eventCapacity: "Capacité d'Événement",
      eventCapacityDesc: "20-500 invités",
      documentation: "Documentation",
      documentationDesc: "Photographie professionnelle",
      entertainmentFeature: "Divertissement",
      entertainmentFeatureDesc: "Musique live & services DJ",
      // Testimonials
      testimonial1Name: "Sarah & Michael",
      testimonial1Event: "Réception de Mariage",
      testimonial1Text: "Notre mariage était absolument parfait. Chaque détail a été géré avec tant de soin et de professionnalisme. Nous n'aurions pas pu demander mieux !",
      testimonial2Name: "David Chen",
      testimonial2Event: "Lancement d'Entreprise",
      testimonial2Text: "L'événement de lancement de produit a dépassé toutes les attentes. L'attention aux détails et l'exécution parfaite ont fait briller notre entreprise.",
      testimonial3Name: "Jennifer Williams",
      testimonial3Event: "Célébration d'Anniversaire",
      testimonial3Text: "Ils ont transformé notre vision en réalité. La célébration était élégante, sophistiquée et exactement ce dont nous avions rêvé."
    },
    attractions: {
      title: "Découvrez l'Oberland Bernois",
      description: "Explorez les attractions les plus populaires de Suisse, classées par popularité des visiteurs. Du Jungfraujoch emblématique aux joyaux alpins cachés.",
      niederhorn: {
        name: "Pic Niederhorn",
        description: "Découvrez des vues panoramiques à couper le souffle sur le lac de Thoune, les Alpes bernoises et les sommets Eiger, Mönch et Jungfrau. Accessible par téléphérique depuis Beatenberg, ce pic offre des sentiers de randonnée, un restaurant et des opportunités de photos époustouflantes.",
        shortDescription: "Vues panoramiques sur le lac de Thoune et les Alpes bernoises",
        features: ["Accès par téléphérique", "Sentiers de randonnée", "Vues panoramiques", "Restaurant", "Spots de photographie"],
        guestBenefits: ["10% de réduction sur les billets de téléphérique", "Parking gratuit à la station de téléphérique", "Carte de sentier gratuite", "Mises à jour météo via l'app hôtel"],
        distance: "15 min en voiture",
        duration: "2-4 heures d'exploration"
      },
      "st-beatus-caves": {
        name: "Grottes de St. Beatus",
        description: "Parmi les plus grands systèmes de grottes de Suisse, ces grottes calcaires présentent de magnifiques stalactites, des lacs souterrains et des formations géologiques fascinantes. Nommées d'après Saint Beatus, ces grottes sont un lieu de pèlerinage depuis des siècles.",
        shortDescription: "Plus grand système de grottes de Suisse avec lacs souterrains",
        features: ["Visites guidées", "Lac souterrain", "Stalactites", "Formations géologiques", "Musée"],
        guestBenefits: ["Réservation prioritaire", "Guide audio gratuit", "Réduction restaurant", "Navette hôtel disponible"],
        distance: "25 min en voiture",
        duration: "1-2 heures de visite des grottes"
      },
      "harder-kulm": {
        name: "Harder Kulm",
        description: "Atteint par funiculaire historique, Harder Kulm offre des vues spectaculaires sur Interlaken, le lac de Thoune et le lac de Brienz. Le sommet dispose de sentiers de promenade, d'un restaurant et du célèbre point de vue Harder Kulm.",
        shortDescription: "Funiculaire historique vers points de vue panoramiques",
        features: ["Funiculaire", "Vues panoramiques", "Sentiers de promenade", "Restaurant", "Terrasse d'observation"],
        guestBenefits: ["15% de réduction sur les billets de funiculaire", "Navette hôtel gratuite", "Réservation restaurant prioritaire", "Mises à jour météo"],
        distance: "20 min en voiture",
        duration: "2-3 heures de visite"
      },
      "jungfraujoch": {
        name: "Jungfraujoch - Sommet de l'Europe",
        description: "La plus haute gare d'Europe à 3.454m, offrant des vues sur le glacier d'Aletsch et les sommets environnants. Comprend le Palais de Glace, l'Observatoire Sphinx et des paysages alpins à couper le souffle.",
        shortDescription: "Plus haute gare d'Europe avec Palais de Glace",
        features: ["Plus haute gare d'Europe", "Palais de Glace", "Observatoire Sphinx", "Vues sur le glacier d'Aletsch", "Restaurant"],
        guestBenefits: ["Service de navette gratuit", "Réservation prioritaire", "Réduction restaurant", "Mises à jour météo"],
        distance: "45 min en voiture",
        duration: "Excursion d'une demi-journée"
      },
      "thun-castle": {
        name: "Château de Thoune",
        description: "Château médiéval emblématique surplombant la ville de Thoune et le lac de Thoune. Comprend un musée avec l'histoire régionale, de beaux jardins et des vues panoramiques sur les montagnes environnantes.",
        shortDescription: "Château médiéval avec musée et vues sur le lac",
        features: ["Architecture médiévale", "Musée régional", "Beaux jardins", "Vues panoramiques", "Expositions historiques"],
        guestBenefits: ["Parking gratuit", "Guide audio inclus", "Accès aux jardins", "Permis photo inclus"],
        distance: "30 min en voiture",
        duration: "1-2 heures de visite"
      },
      "spiez-castle": {
        name: "Château de Spiez",
        description: "Église romane et emplacement pittoresque sur le lac de Thoune. Comprend une cave à vin historique, de beaux jardins et des vues époustouflantes sur le lac. L'un des châteaux les plus photographiés de Suisse.",
        shortDescription: "Château roman avec cave à vin et vues sur le lac",
        features: ["Architecture romane", "Cave à vin", "Vues sur le lac", "Beaux jardins", "Signification historique"],
        guestBenefits: ["Réduction dégustation de vin", "Visite des jardins incluse", "Spots de photographie", "Parking gratuit"],
        distance: "35 min en voiture",
        duration: "1-2 heures de visite"
      },
      "schynige-platte": {
        name: "Schynige Platte",
        description: "Le train à crémaillère historique vous emmène à un jardin botanique alpin à 2.000m. Comprend plus de 650 espèces de plantes, des vues spectaculaires sur la région de la Jungfrau et des sentiers de promenade.",
        shortDescription: "Train à crémaillère historique vers le jardin alpin",
        features: ["Train à crémaillère historique", "Jardin botanique alpin", "Vues panoramiques", "Sentiers de promenade", "Restaurant"],
        guestBenefits: ["20% de réduction sur les billets de train", "Guide du jardin botanique gratuit", "Réservations prioritaires", "Service de navette hôtel"],
        distance: "25 min en voiture",
        duration: "3-4 heures d'excursion"
      },
      "trummelebach-falls": {
        name: "Chutes de Trümmelbach",
        description: "Merveille naturelle reconnue par l'UNESCO avec 10 chutes d'eau alimentées par les glaciers à l'intérieur de la montagne. Le plus grand système de chutes d'eau souterraines d'Europe.",
        shortDescription: "10 chutes d'eau alimentées par les glaciers à l'intérieur de la montagne",
        features: ["Chutes d'eau souterraines", "Visites guidées", "Accès par ascenseur", "Site UNESCO", "Restaurant"],
        guestBenefits: ["Réservation prioritaire", "Guide audio inclus", "Réduction restaurant", "Parking gratuit"],
        distance: "30 min en voiture",
        duration: "1-2 heures de visite"
      },
      "giessbach-falls": {
        name: "Chutes de Giessbach",
        description: "14 chutes d'eau cascadent 500m de la montagne dans le lac de Brienz. Comprend le Grand Hôtel historique et des sentiers de promenade pittoresques.",
        shortDescription: "14 chutes d'eau cascadent 500m dans le lac de Brienz",
        features: ["14 chutes d'eau", "Grand Hôtel historique", "Sentiers de promenade", "Excursions en bateau", "Vues panoramiques"],
        guestBenefits: ["Réduction excursion en bateau", "Service de navette hôtel", "Réservations prioritaires", "Parking gratuit"],
        distance: "40 min en voiture",
        duration: "1-2 heures de visite"
      },
      "lake-thun-brienz": {
        name: "Lacs de Thoune et Brienz",
        description: "Deux lacs alpins immaculés offrant des croisières en bateau, des sports nautiques et des villages pittoresques. Le lac de Thoune présente des arrière-plans montagnards dramatiques tandis que le lac de Brienz offre des eaux paisibles.",
        shortDescription: "Deux lacs alpins immaculés avec croisières en bateau",
        features: ["Croisières en bateau", "Sports nautiques", "Zones de baignade", "Villages pittoresques", "Pêche"],
        guestBenefits: ["Réduction croisière en bateau", "Location d'équipement de sports nautiques", "Réservation prioritaire", "Navette gratuite"],
        distance: "20 min en voiture",
        duration: "2-4 heures de croisière"
      },
      "adventure-park-interlaken": {
        name: "Parc d'Aventure Interlaken",
        description: "Parc d'aventure palpitant avec tyroliennes, parcours d'accrobranche, murs d'escalade et défis d'équipe. Parfait pour les familles et les amateurs d'aventure.",
        shortDescription: "Tyroliennes, parcours d'accrobranche et activités d'aventure",
        features: ["Tyroliennes", "Parcours d'accrobranche", "Murs d'escalade", "Défis d'équipe", "Activités familiales"],
        guestBenefits: ["Réservation prioritaire", "Réduction groupe", "Équipement inclus", "Navette hôtel"],
        distance: "25 min en voiture",
        duration: "2-3 heures d'activité"
      },
      "paragliding": {
        name: "Parapente",
        description: "Vols en parapente en tandem depuis des sites de lancement pittoresques offrant des vues aériennes à couper le souffle sur la région de la Jungfrau et les vallées alpines.",
        shortDescription: "Parapente en tandem avec vues alpines",
        features: ["Vols en tandem", "Pilotes certifiés", "Vues époustouflantes", "Service photo", "Équipement de sécurité"],
        guestBenefits: ["Réservation prioritaire", "Réduction package photo/vidéo", "Ramassage hôtel", "Mises à jour météo"],
        distance: "20 min en voiture",
        duration: "2-3 heures d'expérience"
      },
      "cross-country-skiing": {
        name: "Ski de Fond",
        description: "Sentiers de ski de fond damés autour de Beatenberg et Habkern. Parfait pour tous les niveaux avec des paysages alpins époustouflants.",
        shortDescription: "Sentiers damés pour le ski de fond",
        features: ["Sentiers damés", "Tous niveaux", "Routes pittoresques", "Location d'équipement", "Leçons disponibles"],
        guestBenefits: ["Accès gratuit aux sentiers", "Réduction location d'équipement", "Leçons incluses", "Navette hôtel"],
        distance: "15 min en voiture",
        duration: "2-4 heures d'activité"
      },
      "beatenberg-panorama-trail": {
        name: "Sentier Panoramique de Beatenberg",
        description: "Sentier de randonnée pittoresque offrant des vues panoramiques sur le lac de Thoune et les Alpes bernoises. Chemins faciles à modérés avec des panneaux éducatifs.",
        shortDescription: "Sentier pittoresque avec vues panoramiques sur le lac de Thoune",
        features: ["Vues panoramiques", "Panneaux éducatifs", "Chemins faciles", "Observation de la faune", "Spots de photographie"],
        guestBenefits: ["Accès gratuit", "Carte de sentier à l'hôtel", "Visites guidées disponibles", "Aires de repos"],
        distance: "5 min à pied",
        duration: "1-2 heures de randonnée"
      },
      "saxeten-village": {
        name: "Village de Saxeten",
        description: "Village de montagne caché avec des chalets traditionnels, des prairies alpines et une culture montagnarde suisse authentique. Parfait pour découvrir la Suisse rurale.",
        shortDescription: "Village de montagne traditionnel avec chalets",
        features: ["Chalets traditionnels", "Culture montagnarde", "Prairies alpines", "Artisanat local", "Atmosphère paisible"],
        guestBenefits: ["Guide local inclus", "Expérience de repas traditionnel", "Permis de photographie", "Navette hôtel"],
        distance: "20 min en voiture",
        duration: "1-2 heures de visite"
      }
    },
    localAttractions: {
      hero: {
        title: "Découvrez les Aventures Alpines",
        subtitle: "Explorez la beauté à couper le souffle de l'Oberland Bernois",
        bookStay: "Réservez Votre Séjour Maintenant"
      },
      quickAccess: {
        title: "Explorer les Attractions Locales",
        description: "Découvrez le riche patrimoine culturel, la beauté naturelle et les opportunités d'aventure dans la région de l'Oberland Bernois, toutes à portée de main du Beatenberg Resort & Spa.",
        allAttractions: "Toutes les Attractions",
        within30min: "En 30 min",
        natureHiking: "Nature & Randonnée",
        cultureHistory: "Culture & Histoire",
        activities: "Activités",
        allAttractionsDesc: "Guide complet de toutes les attractions locales",
        within30minDesc: "Attractions facilement accessibles",
        natureHikingDesc: "Sentiers de montagne et merveilles naturelles",
        cultureHistoryDesc: "Sites historiques et expériences culturelles",
        activitiesDesc: "Activités d'aventure et récréatives",
        learnMore: "En Savoir Plus",
        noAttractionsFound: "Aucune attraction trouvée dans cette catégorie."
      },
      seasonal: {
        title: "Activités Saisonnières",
        description: "Découvrez le meilleur de chaque saison avec notre sélection d'activités, des randonnées d'été aux aventures de ski d'hiver.",
        summer: "Été",
        winter: "Hiver",
        yearRound: "Toute l'année",
        alpineHiking: "Sentiers de Randonnée Alpine",
        alpineHikingDesc: "Découvrez plus de 200km de sentiers de randonnée balisés avec différents niveaux de difficulté, des promenades douces au bord du lac aux ascensions de montagne difficiles.",
        alpineHikingActivities: ["Randonnées guidées", "Ascensions de sommets", "Promenades au bord du lac", "Identification de fleurs sauvages"],
        skiSnowboard: "Paradis du Ski & Snowboard",
        skiSnowboardDesc: "Accès aux stations de ski de classe mondiale en 30 minutes, y compris Grindelwald, Wengen et Mürren avec notre service de navette gratuit.",
        skiSnowboardActivities: ["Accès aux stations de ski", "Parcs de snowboard", "Ski de fond", "Expéditions en raquettes"],
        lakeWaterSports: "Sports Nautiques du Lac de Thoune",
        lakeWaterSportsDesc: "Découvrez des eaux cristallines avec le kayak, le paddle, la voile et des croisières pittoresques sur le magnifique lac de Thoune en Suisse.",
        lakeWaterSportsActivities: ["Kayak & canoë", "Stand-up paddle", "Cours de voile", "Croisières en bateau"],
        photography: "Expéditions Photographiques",
        photographyDesc: "Capturez des paysages alpins époustouflants et des joyaux cachés avec des tours photographiques professionnels et des ateliers dirigés par des experts locaux.",
        photographyActivities: ["Photographie de paysage", "Photographie de faune", "Séances lever/coucher de soleil", "Ateliers photo"],
        culinary: "Expériences Culinaires Suisses",
        culinaryDesc: "Plongez dans les traditions culinaires suisses avec des cours de cuisine, des dégustations de vin et des visites chez des producteurs et artisans locaux.",
        culinaryActivities: ["Cours de cuisine", "Dégustations de vin", "Fabrication de fromage", "Visites de fermes"],
        mountainAdventures: "Aventures en Montagne",
        mountainAdventuresDesc: "Du parapente à l'escalade, découvrez des activités pleines d'adrénaline dans l'un des cadres montagnards les plus spectaculaires d'Europe.",
        mountainAdventuresActivities: ["Parapente", "Escalade", "Via ferrata", "Canyoning"],
        activitiesInclude: "Les activités incluent :",
        bookYourStay: "Réservez Votre Séjour"
      },
      guestBenefits: {
        title: "Avantages de la Carte Invité",
        description: "En tant qu'invité du Beatenberg Resort & Spa, profitez d'avantages exclusifs et de remises sur les attractions locales et services dans la région de Beatenberg et Habkern.",
        freeBenefits: "Avantages Gratuits",
        discountBenefits: "Avantages avec Remise",
        howToGet: "Comment Obtenir Votre Carte Invité",
        howToGetDesc: "Votre carte invité est automatiquement incluse dans votre séjour au Beatenberg Resort & Spa. Récupérez-la à la réception à l'arrivée et commencez à profiter d'avantages exclusifs immédiatement.",
        validDuringStay: "Valide pendant votre séjour • Partenariats régionaux inclus",
        partner: "Partenaire",
        benefits: {
          freeTransport: {
            title: "Transport Public Gratuit",
            description: "Voyage gratuit sur les transports publics programmés dans les zones désignées autour de Beatenberg et Habkern"
          },
          libraryAccess: {
            title: "Accès à la Bibliothèque",
            description: "Emprunt gratuit de livres, DVD et CD de la Bibliothèque de Beatenberg"
          },
          eBikeDiscount: {
            title: "Location de Vélos Électriques",
            description: "50% de réduction sur la location de vélos électriques chez Beatenberg Tourisme"
          },
          miniGolfDiscount: {
            title: "Mini Golf",
            description: "50% de réduction sur le mini golf à l'Hôtel Dorint Blüemlisalp"
          },
          schynigePlatteDiscount: {
            title: "Chemin de Fer de Schynige Platte",
            description: "20% de réduction sur les trajets en chemin de fer de Schynige Platte"
          },
          niesenbahnDiscount: {
            title: "Funiculaire de Niesenbahn",
            description: "20% de réduction sur les billets du funiculaire de Niesenbahn"
          },
          harderKulmDiscount: {
            title: "Funiculaire de Harder Kulm",
            description: "15% de réduction sur les trajets en funiculaire de Harder Kulm"
          },
          escapeRoomDiscount: {
            title: "Salles d'Évasion",
            description: "15% de réduction sur les Salles d'Évasion et Tours Mystères à Interlaken"
          },
          niederhornDiscount: {
            title: "Téléphérique de Niederhorn",
            description: "20% de réduction sur les trajets en téléphérique de Niederhorn"
          },
          swimmingPoolAccess: {
            title: "Piscine de Habkern",
            description: "50% de réduction sur l'entrée à la piscine de Habkern"
          },
          busAlpinService: {
            title: "Service Bus Alpin",
            description: "50% de réduction sur les services Bus Alpin lors d'un séjour d'au moins deux nuits"
          },
          gameBorrowing: {
            title: "Bibliothèque de Jeux",
            description: "Emprunt gratuit de jeux chez Beatenberg Tourisme"
          }
        }
      },
      transportation: {
        title: "Transport & Accessibilité",
        description: "Se déplacer dans l'Oberland Bernois est facile avec d'excellents transports publics, des navettes d'hôtel et des routes pittoresques vous connectant à toutes les attractions majeures.",
        publicTransport: "Transport Public",
        keyRoutes: "Routes Principales du Beatenberg Resort & Spa",
        destination: "Destination",
        method: "Méthode",
        duration: "Durée",
        frequency: "Fréquence",
        freeWithGuestCard: "Gratuit avec Carte Invité",
        freeWithGuestCardDesc: "Profitez du transport public gratuit dans les zones désignées autour de Beatenberg et Habkern avec votre carte invité Beatenberg Resort & Spa.",
        hotelShuttles: "Services de Navette Hôtel",
        bookingInfo: "Informations de Réservation",
        bookingInfoDesc: "Les services de navette peuvent être réservés à la réception ou en appelant notre concierge. La réservation à l'avance est recommandée pour les transferts aéroport et occasions spéciales.",
        concierge: "Concierge : +41 33 841 00 21",
        drivingDirections: "Directions de Conduite & Stationnement",
        fromZurich: "De l'Aéroport de Zurich (2,5 heures)",
        fromGeneva: "De l'Aéroport de Genève (3 heures)",
        parkingAtHotel: "Stationnement à l'Hôtel",
        parkingAtHotelDesc: "Le stationnement gratuit est disponible pour tous les invités. Notre zone de stationnement offre des vues pittoresques et est commodément située près de l'entrée principale.",
        accessibility: "Informations d'Accessibilité",
        wheelchairAccessible: "Attractions Accessibles en Fauteuil Roulant",
        mobilityServices: "Services de Mobilité",
        contactConcierge: "Contactez notre concierge",
        contactConciergeDesc: "pour assistance dans la planification d'itinéraires accessibles et l'organisation de transports spécialisés. Nous nous engageons à garantir que tous les invités puissent profiter de l'expérience de l'Oberland Bernois.",
        accessibilityAttractions: [
          "Pic de Niederhorn (accessible par téléphérique)",
          "Grottes de St. Beatus (zones principales accessibles en fauteuil roulant)",
          "Château de Thun (rez-de-chaussée accessible)",
          "Jardins du Château de Spiez (chemins adaptés aux fauteuils roulants)",
          "La plupart des gares de bus et de train de la région"
        ],
        mobilityServicesList: [
          "Location de fauteuils roulants disponible à l'hôtel",
          "Services de taxi accessibles peuvent être organisés",
          "La plupart des téléphériques et funiculaires sont accessibles en fauteuil roulant",
          "Véhicules de navette hôtel équipés pour fauteuils roulants"
        ],
        drivingDirectionsZurich: [
          "Suivez l'autoroute A1 vers Berne",
          "Prenez la sortie 41 (Thun Süd) vers Thun",
          "Suivez E27 vers Interlaken",
          "Prenez la sortie Beatenberg/Habkern",
          "Suivez les panneaux vers Beatenberg Resort & Spa (environ 10 minutes)"
        ],
        drivingDirectionsGeneva: [
          "Prenez l'autoroute A1 vers Lausanne",
          "Continuez sur A9 vers Montreux",
          "Prenez la sortie 29 (Aigle) et suivez les panneaux vers Interlaken",
          "Suivez E27 vers Interlaken",
          "Prenez la sortie Beatenberg/Habkern"
        ],
        frequencies: {
          every30min: "Toutes les 30 min",
          everyHour: "Toutes les heures",
          every2Hours: "Toutes les 2 heures",
          asNeeded: "Selon les besoins",
          onRequest: "Sur demande",
          onDemand: "À la demande",
          scheduled: "Selon horaire",
          daily24_7: "Réservation 24/7"
        },
        shuttleServices: {
          skiShuttle: {
            service: "Navette Ski",
            destinations: "Stations de ski de la région Jungfrau",
            schedule: "Quotidien 8h00 - 17h00"
          },
          airportTransfer: {
            service: "Transfert Aéroport",
            destinations: "Aéroports de Berne/Bâle/Zurich",
            schedule: "Sur demande"
          },
          cityTransfer: {
            service: "Transfert Ville",
            destinations: "Interlaken, Thun, Spiez",
            schedule: "Quotidien 7h00 - 22h00"
          },
          attractionShuttle: {
            service: "Navette Attractions",
            destinations: "Attractions principales en 30 min",
            schedule: "Quotidien 9h00 - 18h00"
          }
        }
      },
      cta: {
        title: "Prêt à Explorer ?",
        description: "Réservez votre séjour au Beatenberg Resort & Spa et découvrez la magie de l'Oberland Bernois. Votre aventure vous attend avec des hébergements de classe mondiale, une cuisine exceptionnelle et des expériences inoubliables.",
        bookYourStay: "Réservez Votre Séjour",
        speakToExpert: "Parler à un Expert",
        bookYourStayDesc: "Réservez votre chambre avec des tarifs exclusifs et les avantages de la carte invité inclus.",
        speakToExpertDesc: "Notre équipe concierge peut vous aider à planifier votre itinéraire parfait de l'Oberland Bernois.",
        questions: "Questions ? Appelez notre équipe de réservation au"
      }
    },
    guestBenefits: {
      freeTransport: {
        title: "Transport Public Gratuit",
        description: "Voyage gratuit sur les transports publics programmés dans les zones désignées autour de Beatenberg et Habkern"
      },
      libraryAccess: {
        title: "Accès à la Bibliothèque",
        description: "Emprunt gratuit de livres, DVD et CD de la Bibliothèque de Beatenberg"
      },
      eBikeDiscount: {
        title: "Location E-bike",
        description: "50% de réduction sur les locations d'e-bikes chez Beatenberg Tourisme"
      },
      miniGolfDiscount: {
        title: "Mini Golf",
        description: "50% de réduction sur le mini golf à l'Hôtel Dorint Blüemlisalp"
      },
      schynigePlatteDiscount: {
        title: "Chemin de Fer Schynige Platte",
        description: "20% de réduction sur les voyages en chemin de fer Schynige Platte"
      },
      niesenbahnDiscount: {
        title: "Funiculaire Niesenbahn",
        description: "20% de réduction sur les billets du funiculaire Niesenbahn"
      },
      harderKulmDiscount: {
        title: "Funiculaire Harder Kulm",
        description: "15% de réduction sur les voyages en funiculaire Harder Kulm"
      },
      escapeRoomDiscount: {
        title: "Escape Rooms",
        description: "15% de réduction sur les Escape Rooms et Mystery Tours à Interlaken"
      },
      niederhornDiscount: {
        title: "Téléphérique Niederhorn",
        description: "20% de réduction sur les voyages en téléphérique Niederhorn"
      },
      swimmingPoolAccess: {
        title: "Piscine Habkern",
        description: "50% de réduction sur l'entrée à la piscine Habkern"
      },
      busAlpinService: {
        title: "Service Bus Alpin",
        description: "50% de réduction sur les services Bus Alpin lors d'un séjour d'au moins deux nuits"
      },
      gameBorrowing: {
        title: "Bibliothèque de Jeux",
        description: "Emprunt gratuit de jeux chez Beatenberg Tourisme"
      }
    },
    moments: {
      title: "Moments de Joie au Beatenberg Resort & Spa",
      description: "Découvrez les beaux moments et expériences qui rendent chaque séjour dans notre resort vraiment spécial",
      showNewMoments: "Afficher de Nouveaux Moments"
    },
    cta: {
      title: "Prêt à Créer Votre Échappée Alpine Parfaite ?",
      description: "Découvrez la magie du luxe alpin suisse, des programmes de bien-être de classe mondiale et la beauté naturelle à couper le souffle au Beatenberg Resort & Spa.",
      bookYourStay: "Réservez Votre Séjour",
      contactConcierge: "Contactez Notre Concierge",
      happyGuests: "Invités Heureux",
      trustedByGuests: "Fait confiance par des invités du monde entier",
      certifiedExcellence: "Excellence Certifiée",
      certifiedExcellenceDesc: "Reconnu par les principales organisations d'hospitalité de Suisse",
      gastrosuisseRated: "Évalué Gastrosuisse",
      swissHotelAssociation: "Association Hôtelière Suisse",
      swisstainableCommitted: "Engagé Swisstainable",
      qualityAssurance: "Assurance Qualité",
      startReservation: "Commencer votre réservation",
      contactTeam: "Contacter notre équipe"
    },

    ratings: {
      hotel: {
        reviews: "avis"
      },
      restaurant: {
        reviews: "avis"
      }
    },
    testimonials: {
      title: "Expériences d'Invités",
      description: "Découvrez pourquoi les voyageurs du monde entier choisissent le Beatenberg Resort & Spa",
      testimonial1: {
        quote: "Personnel absolument gracieux. Anna était particulièrement utile à bien des égards, de m'accueillir à rendre mon séjour plus que confortable et relaxant. Sa gentillesse était contagieuse et utile au-delà de toute attente. Je ne peux pas recommander assez cet hôtel. Il a un charme de ses plus de 300 ans d'existence à la grâce de toutes les commodités modernes nécessaires pour un séjour relaxant et reposant. Sans parler de la vue incroyable au coucher du soleil. Inoubliable !",
        name: "Amit Trivedi",
        country: "Inde"
      },
      testimonial2: {
        quote: "Le Park Hotel a rendu notre séjour en Suisse très agréable. L'hôtel est paisible considérant qu'il est situé dans un environnement très serein et calme. Les vues sont magnifiques et le service global du personnel, surtout Mani Kandan. Il était courtois et un hôte merveilleux. Un endroit formidable.",
        name: "Priya Sharma",
        country: "Inde"
      },
      testimonial3: {
        quote: "Wow ! 🤩 Se réveiller avec cette vue sur le lac et la montagne était irréel. J'avais un balcon dans ma chambre et j'y suis resté assis pendant des heures. Le personnel était super gentil et le massage ? 10/10. J'ai hâte de revenir au Parkhotel Beatenberg ! 💆‍♀️🌄",
        name: "Agustina Kraitz",
        country: "Argentine"
      },
      testimonial4: {
        quote: "Un séjour merveilleux. L'emplacement et l'hospitalité du personnel étaient superbes. Nous avons séjourné dans la chambre avec balcon et je pense que c'est la vue la plus spectaculaire que j'aie jamais eue d'une chambre d'hôtel. L'hôtel a aussi un jardin spectaculaire. Enfin, Oscar était extrêmement amical et efficace. A vraiment rendu notre séjour si fluide.",
        name: "Amar Nanavati",
        country: "Inde"
      }
    },
    floatingFeatures: {
      freeWifi: {
        title: "WiFi Gratuit",
        description: "Internet haut débit gratuit pendant tout votre séjour"
      },
      flatScreenTv: {
        title: "TV Écran Plat",
        description: "Smart TV moderne avec chaînes internationales et streaming"
      },
      inRoomSafe: {
        title: "Coffre-fort en Chambre",
        description: "Stockage sécurisé pour vos objets de valeur et documents de voyage"
      },
      alpineViews: {
        title: "Vues Alpines",
        description: "Vue panoramique sur le lac de Thoune, Eiger, Mönch, Jungfrau et le village de Beatenberg"
      },
      ensuiteBathroom: {
        title: "Salle de Bain Privée",
        description: "Salle de bain privée avec équipements modernes et commodités"
      },
      luxuryBedding: {
        title: "Literie de Luxe",
        description: "Literie en coton biologique premium pour un confort ultime"
      },
      rusticPineInteriors: {
        title: "Intérieurs Rustiques en Pin",
        description: "Bois de pin local, finition naturelle avec artisanat suisse"
      },
      seasonalTemperatures: {
        title: "Températures Saisonnières",
        description: "Été ~ 10 à 22°C, Hiver ~ −5 à 5°C"
      },
      ensuiteEspressoMachine: {
        title: "Machine à Espresso en Chambre",
        description: "Machine à espresso premium dans les Suites Familiales et Junior pour votre café du matin"
      }
    },
    footer: {
      contact: "Contact",
      location: "Emplacement",
      quickLinks: "Liens Rapides",
      organization: "Organisation NutraSwiss",
      allRightsReserved: "© 2025 Beatenberg Resort & Spa | Tous droits réservés",
      hotelDescription: "Un Resort Heritage 4★ Deluxe dans le plus long village d'Europe, Beatenberg, offrant des vues panoramiques alpines, un wellness NutraSwiss intégré et une hospitalité suisse authentique au-dessus d'Interlaken.",
      roomsSuites: "Chambres & Suites",
      wellnessSpa: "Wellness & Spa",
      dining: "Restaurant",
      experiences: "Expériences",
      eventsWeddings: "Événements & Mariages",
      aboutUs: "À propos",
      contactLink: "Contact",
      privacyPolicy: "Politique de confidentialité",
      termsOfService: "Conditions d'utilisation",
      cookies: "Cookies",
      hotelRegistration: "Enregistrement hôtelier: CHE-123.456.789 | Licence du bureau du tourisme: BE-2024-HTL-001 | ID membre GASTROSUISSE: GS-4567",
      nutraSwissDescription: "Solutions de bien-être holistiques",
      cliniqueNutraSwissDescription: "Wellness & Spa médical",
      nahrinDescription: "Produits de santé naturels",
      laMaisonPondicherryDescription: "Hospitalité patrimoniale",
      stBeatusCollegeDescription: "Excellence éducative",
      restaurantJungfrauDescription: "Gastronomie alpine"
    },
    notFound: {
      title: "Perdu en montagne",
      description:
        "La page que vous cherchez n'existe pas. Elle a peut-être été déplacée, supprimée, ou l'adresse est incorrecte.",
      ctaHome: "Retour à l'accueil",
      ctaSpa: "Découvrir le spa"
    },
    contactModals: {
      spa: {
        title: "Contactez Notre Équipe Spa",
        subtitle: "Laissez-nous créer votre expérience de bien-être parfaite",
        phoneTitle: "Ligne Directe Spa",
        phoneDesc: "Parlez avec nos spécialistes du spa",
        emailTitle: "E-mail Spa",
        emailDesc: "Envoyez-nous vos préférences de traitement",
        locationTitle: "Emplacement du Spa",
        locationDesc: "Voir le spa au Beatenberg Resort & Spa sur Google Maps",
        bookingTitle: "Réservation en Ligne",
        bookingDesc: "Réservez vos traitements instantanément",
        whatsappTitle: "WhatsApp Spa",
        whatsappDesc: "Consultation spa rapide via WhatsApp",
        servicesTitle: "Nos Services Spa",
        servicesList: [
          "Traitements de thérapie par l'air alpin et programmes de bien-être",
          "Traitements thérapeutiques et massothérapies",
          "Traitements de beauté et consultations de bien-être",
          "Séances de relaxation rapide et soulagement du stress",
          "Expériences spa privées et traitements pour couples",
          "Forfaits bien-être personnalisés et programmes de retraite",
          "Consultations pré et post-traitement"
        ],
        bookButton: "Réserver un Traitement Spa",
        closeButton: "Fermer"
      },
      theatre: {
        title: "Contactez-nous pour Notre Théâtre",
        subtitle: "Nous vous aidons à planifier votre événement",
        phoneTitle: "Appelez-nous",
        phoneDesc: "Parlez avec notre équipe événementielle",
        emailTitle: "Envoyez-nous un e-mail",
        emailDesc: "Contactez-nous pour votre événement",
        bookingTitle: "Consultation Planification d'Événement",
        bookingDesc: "Planifiez une consultation avec notre équipe de planification d'événements",
        whatsappTitle: "WhatsApp",
        whatsappDesc: "Discutez avec nous sur WhatsApp",
        servicesTitle: "Nos Services Théâtre",
        servicesList: [
          "Réservations de théâtre IMAX privé pour groupes et événements",
          "Présentations d'entreprise et réunions d'affaires",
          "Projections de films privées et événements de divertissement",
          "Réceptions de mariage et célébrations spéciales",
          "Présentations éducatives et sessions de formation",
          "Planification d'événements personnalisée et services de restauration",
          "Support technique et équipement audiovisuel"
        ],
        featuresTitle: "Caractéristiques du Théâtre",
        features: {
          capacity: "Jusqu'à 50 invités",
          screen: "Écran IMAX grand format",
          sound: "Système de son surround premium",
          seating: "Sièges étagés confortables",
          lighting: "Éclairage d'événement professionnel",
          climate: "Environnement climatisé"
        },
        bookButton: "Planifier une Consultation",
        closeButton: "Fermer"
      },
      concierge: {
        title: "Contactez Notre Concierge",
        subtitle: "Laissez-nous vous aider à planifier votre expérience alpine parfaite",
        phoneTitle: "Téléphone",
        phoneDesc: "Parlez directement avec notre équipe de conciergerie",
        emailTitle: "E-mail",
        emailDesc: "Envoyez-nous vos questions ou demandes",
        locationTitle: "Emplacement",
        locationDesc: "Visitez-nous dans notre emplacement alpin époustouflant",
        whatsappTitle: "WhatsApp",
        whatsappDesc: "Messagerie instantanée pour une assistance rapide",
        servicesTitle: "En quoi notre concierge peut-il vous aider ?",
        servicesList: [
          "Réservations de restaurant et recommandations culinaires",
          "Réservations d'activités et billets d'attractions locales",
          "Arrangements de transport et transferts aéroport",
          "Réservations de traitements spa et programmes de bien-être",
          "Réservations de théâtre privé et présentations d'entreprise",
          "Planification d'occasions spéciales et célébrations",
          "Informations sur la région locale et guidage des sentiers de randonnée"
        ],
        bookButton: "Réservez Votre Séjour",
        closeButton: "Fermer"
      }
    },
    activities: {
      hero: {
        title: "EXPÉRIENCE JOURNÉE INVITÉ BEATENBERG",
        subtitle: "Jouer. Se détendre. Dîner. Expérimenter.",
        locatedAt: "Situé au Beatenberg Resort & Spa"
      },
      grid: {
        everythingUnderOneRoof: "TOUT SOUS UN MÊME TOIT !",
        viewDetails: "CLIQUEZ POUR LES DÉTAILS",
        bookNow: "CLIQUEZ POUR RÉSERVER",
        bookDayPass: "Réserver le Pass Journée",
        includedInDayPass: "Inclus dans le Pass Journée",
        viewAllActivities: "Voir Toutes les Activités",
        showOneAtATime: "Afficher une par une"
      },
      items: {
        bistro_dine_in: {
          title: "Carbon Mountain Cafe",
          subtitle: "Sur Place & À Emporter",
          description: "Bouchées Gourmet dans un Cadre Détendu ou Plats Frais à Emporter"
        },
        bistro_takeaway: {
          title: "Carbon Mountain Cafe - À Emporter",
          subtitle: "Rapide & Frais",
          description: "Plats Fraîchement Préparés à Emporter"
        },
        birthdays: {
          title: "Anniversaires & Fêtes Privées",
          subtitle: "Célébrez avec Nous",
          description: "Le Lieu Idéal pour vos Moments Spéciaux"
        },
        spa: {
          title: "Spa NutraSwiss",
          subtitle: "MASSAGE & THÉRAPIE",
          description: "Détente & Recharge"
        },
        sauna: {
          title: "Sauna & Bien-être Thermal",
          subtitle: "DETOX & RENAISSANCE",
          description: "Chaleur du Pin Suisse"
        },
        dining: {
          title: "Restaurant Jungfrau",
          subtitle: "DÎNER RAFFINÉ",
          description: "Cuisine Gastronomique"
        },
        cinema: {
          title: "Cinéma de Beatenberg",
          subtitle: "PROJECTIONS PRIVÉES",
          description: "Réservez votre film ou événement sportif"
        },
        billiards: {
          title: "Salle de Billard",
          subtitle: "Jeux & Amusement",
          description: "Billard & Snooker"
        },
        pool: {
          title: "Piscine Extérieure Chauffée",
          subtitle: "NAGER AVEC VUE",
          description: "Baignade Alpine Toute l'Année"
        },
        sports: {
          title: "Installations Sportives",
          subtitle: "VIE ALPINE ACTIVE",
          description: "Terrains de Tennis & Football"
        }
      },
      comingSoon: "À Venir",
      modal: {
        bookTitle: "Réservez votre expérience",
        bookDesc: "Pour réserver cette installation ou pour des demandes d'événements privés, veuillez contacter notre concierge :",
        callNow: "Appeler maintenant",
        whatsapp: "Support WhatsApp",
        close: "Fermer",
        inquireNow: "S'informer maintenant",
        bookDayPass: "Réserver le Pass Journée",
        contactSupport: "Contact & Support",
        phone: "Téléphone",
        email: "E-mail",
        whatsappLabel: "WhatsApp",
        dining: {
          description: "Découvrez la gastronomie raffinée au Restaurant Jungfrau, où la cuisine gastronomique rencontre des vues alpines à couper le souffle. Notre restaurant primé offre une expérience culinaire élégante axée sur l'excellence culinaire suisse et internationale.",
          description2: "Savourez des plats soigneusement préparés avec des ingrédients locaux, accompagnés d'une sélection de vins étendue. Nos chefs talentueux créent des menus saisonniers qui célèbrent les saveurs de l'Oberland bernois.",
          description3: "Que vous célébriez une occasion spéciale, que vous profitiez d'un dîner romantique ou que vous vous offriez simplement un repas exceptionnel, le Restaurant Jungfrau offre une expérience gastronomique inoubliable au cœur des Alpes suisses.",
          dayPassValue: "Le Restaurant Jungfrau est inclus dans le Pass Journée. Votre Pass Journée comprend un repas gastronomique de cinq plats, vous permettant de découvrir notre menu gastronomique complet. Réservez votre Pass Journée pour profiter de l'expérience culinaire complète et de toutes les commodités de la station.",
          learnMore: "Voir le menu complet et les détails du restaurant"
        },
        sauna: {
          description: "Régénérez votre corps et votre esprit dans nos installations authentiques de sauna et de bien-être thermal suisses. Découvrez les bienfaits traditionnels de la thermothérapie dans un cadre alpin serein.",
          description2: "Notre forfait sauna comprend l'accès à quatre installations thermales distinctes, chacune offrant des avantages uniques pour la détoxification, la relaxation et le bien-être général. La chaleur du pin suisse crée un environnement naturel et aromatique qui apaise le corps et l'esprit.",
          description3: "Que vous cherchiez à vous détendre après une journée d'activités, à améliorer la circulation ou simplement à profiter d'un moment de tranquillité, nos installations de sauna et de bien-être thermal offrent l'évasion parfaite.",
          dayPassValue: "Le forfait complet Sauna & Bien-être Thermal est inclus dans le Pass Journée. Votre Pass Journée comprend l'accès aux quatre installations de sauna (Sauna Finlandais, Bio Sauna, Bain de Vapeur et Piscine de Plongée Glacée), ainsi que du café, du thé et des boissons détox gratuits. Réservez votre Pass Journée pour profiter de l'expérience sauna complète et de toutes les commodités de la station.",
          learnMore: "Explorer nos offres de bien-être complètes"
        },
        bistro_dine_in: {
          description: "Profitez d'une cuisine alpine décontractée au Carbon Mountain Cafe, où une cuisine fraîche inspirée localement rencontre une atmosphère détendue et accueillante. Parfait pour un en-cas rapide, un repas tranquille ou des options à emporter.",
          description2: "Notre café propose un menu varié comprenant des bouchées gastronomiques, des salades fraîches, des sandwiches, des pizzas et des plats du jour. Que vous mangiez sur place avec une vue imprenable sur les montagnes ou que vous preniez de la nourriture à emporter, nous offrons une nourriture de qualité dans un cadre confortable.",
          description3: "Idéal pour les familles, les groupes ou les voyageurs solos à la recherche d'une nourriture délicieuse sans formalité. Notre service amical et nos options de restauration flexibles font du Carbon Mountain Cafe le choix parfait à tout moment de la journée.",
          dayPassValue: "Le Carbon Mountain Cafe est inclus dans le Pass Journée. Votre Pass Journée comprend une boisson non alcoolisée ou une boisson chaude (Heissgetränk) gratuite et un article de repas gratuit (sandwich, salade ou pizza). Réservez votre Pass Journée pour profiter de la restauration au café, des options à emporter et de toutes les commodités de la station.",
          learnMore: "Voir le menu sur place",
          learnMoreTakeaway: "Visiter le site Web à emporter"
        },
        spa: {
          description: "Offrez-vous des traitements spa premium au NutraSwiss Spa, où les thérapies de bien-être avancées rencontrent la précision et les soins suisses. Découvrez des services de massage et de thérapie personnalisés conçus pour restaurer l'équilibre et la vitalité.",
          description2: "Nos thérapeutes experts offrent une gamme de traitements, notamment des massages relaxants, des soins corporels thérapeutiques et des thérapies de bien-être spécialisées. Chaque traitement est adapté à vos besoins individuels, utilisant des produits et des techniques premium.",
          description3: "Que vous recherchiez un soulagement de la tension, une relaxation profonde ou une expérience spa rajeunissante, le NutraSwiss Spa offre des soins professionnels dans un environnement tranquille et luxueux.",
          dayPassValue: "Les traitements NutraSwiss Spa sont inclus dans le Pass Journée. Votre Pass Journée comprend un massage complémentaire de 15 minutes, vous permettant de découvrir nos services spa professionnels. Lors de la réservation de votre Pass Journée, vous aurez la possibilité de passer à nos traitements signature pour une expérience de bien-être plus complète. Réservez votre Pass Journée pour profiter des services spa et de toutes les commodités de la station.",
          learnMore: "Voir le menu spa complet et les traitements"
        },
        cinema: {
          description: "Découvrez des événements sportifs et des présentations sur notre cinéma grand écran de pointe, le seul cinéma privé avec technologie IMAX dans l'Oberland bernois.",
          description2: "Parfait pour regarder des événements sportifs en direct, organiser des présentations, des projections d'entreprise ou des projections d'événements spéciaux. Profitez d'images cristallines et d'un son immersif sur notre grand écran premium.",
          description3: "Les projections de films seront bientôt disponibles. Actuellement, notre cinéma est idéal pour les diffusions sportives, les présentations d'entreprise et les événements spéciaux. Que vous regardiez le grand match, organisiez une présentation d'entreprise ou planifiiez une projection d'événement spécial, notre cinéma offre une expérience de visionnage premium.",
          dayPassValue: "L'accès au cinéma est inclus dans le Pass Journée en tant qu'espace partagé. Vous pouvez profiter de regarder des événements sportifs et des présentations aux côtés d'autres invités du Pass Journée. Réservez votre Pass Journée pour profiter de l'accès au cinéma et de toutes les commodités de la station.",
          dayPassValuePrivate: "Réservation de Cinéma Privé : Si vous souhaitez réserver la salle de cinéma entière exclusivement pour votre groupe ou événement, veuillez nous contacter séparément pour organiser une réservation privée.",
          learnMore: "En savoir plus sur notre Théâtre IMAX"
        },
        billiards: {
          description: "Détendez-vous et profitez d'une compétition amicale dans notre élégante Salle de Billard. Parfait pour des jeux décontractés, des rassemblements sociaux ou une pause relaxante pendant votre journée au resort.",
          description2: "Notre salle de billard bien équipée dispose de tables professionnelles et d'une atmosphère confortable, idéale pour les invités de tous niveaux.",
          description3: "Que vous recherchiez une activité amusante avec des amis ou un moment de détente, la Salle de Billard offre une expérience de divertissement classique.",
          dayPassValue: "L'accès à la Salle de Billard est inclus dans le Pass Journée. Réservez votre Pass Journée pour profiter du billard, du pool et de toutes les commodités de la station."
        },
        pool: {
          description: "Nagez avec une vue alpine à couper le souffle dans notre piscine extérieure chauffée, bientôt disponible au Beatenberg Resort & Spa.",
          description2: "Découvrez la baignade toute l'année dans un cadre montagnard époustouflant. Notre piscine chauffée offrira la combinaison parfaite de détente et de beauté scénique, vous permettant de profiter des Alpes suisses tout en prenant une baignade rafraîchissante.",
          description3: "Que vous recherchiez une baignade matinale, une détente l'après-midi ou un bain sous les étoiles le soir, notre piscine offrira une expérience de baignade alpine inoubliable.",
          dayPassValue: "La Piscine Extérieure Chauffée sera incluse dans le Pass Journée une fois qu'elle sera ouverte. Restez à l'écoute pour les mises à jour !"
        },
        sports: {
          description: "Restez actif au cœur des Alpes suisses avec nos installations sportives complètes, bientôt disponibles au Beatenberg Resort & Spa.",
          description2: "Nos installations comprendront des courts de tennis, des terrains de basketball, des terrains de football et plus encore, le tout avec le magnifique décor des montagnes de l'Oberland bernois.",
          description3: "Que vous recherchiez un jeu compétitif, de l'exercice décontracté ou des activités familiales, nos installations sportives offriront quelque chose pour chaque invité actif.",
          dayPassValue: "Les Installations Sportives seront incluses dans le Pass Journée une fois qu'elles seront ouvertes. Restez à l'écoute pour les mises à jour !"
        }
      },
      footer: {
        allInOne: "DIVERTISSEMENT · DÉTENTE · RESTAURATION · LOISIRS · TOUT AU MÊME ENDROIT !",
        benefits: [
          "Parfait pour les familles et les amis",
          "Confort intérieur avec charme montagnard",
          "Tout en une journée"
        ],
        tagline: "Où les moments deviennent des souvenirs"
      },
      whyChoose: {
        title: "Pourquoi Beatenberg pour votre journée parfaite ?",
        subtitle: "Profitez des équipements de luxe du resort sans séjour - parfait pour les visites spontanées et les escapades locales régulières.",
        imageAlt: "Beatenberg Resort & Spa - Expérience premium pour visiteurs de jour",
        premium: {
          title: "Expérience Premium sans Engagement",
          description: "Profitez des équipements de luxe du resort sans séjour - parfait pour les visites spontanées et les excursions d'une journée."
        },
        allInOne: {
          title: "Destination Tout-en-Un",
          description: "Plusieurs activités en un seul endroit - spa, restauration, cinéma, piscine et plus encore - pas besoin de voyager entre différents lieux."
        },
        flexible: {
          title: "Excursions Flexibles",
          description: "Visitez aussi souvent que vous le souhaitez, combinez les activités, parfait pour les escapades locales régulières et les évasions spontanées."
        },
        easyAccess: {
          title: "Accès Facile & Stationnement",
          description: "Près de chez vous avec un stationnement pratique pour jusqu'à 200 voitures - idéal pour des évasions rapides ou des visites planifiées."
        }
      },
      locationAccess: {
        title: "Emplacement & Accès",
        subtitle: "Facilement accessible avec un stationnement pratique et plusieurs options de transport. Parfait pour les visiteurs de jour dans la région.",
        mapTitle: "Emplacement du Beatenberg Resort & Spa",
        locatedAt: "Situé au Beatenberg Resort & Spa",
        switzerland: "SUISSE",
        bernerOberland: "Oberland bernois",
        cantonBern: "Canton de Berne",
        address: {
          title: "Adresse",
          description: "Spirenwaldstrasse 351, 3803 Beatenberg, Suisse",
          details: "Oberland bernois, Canton de Berne",
          street: "Spirenwaldstrasse",
          number: "351, 3803",
          city: "Beatenberg,",
          country: "Suisse"
        },
        parking: {
          title: "Stationnement",
          description: "Parking sur place jusqu'à 200 voitures",
          details: "Jusqu'à 200 voitures • Parking gratuit disponible"
        },
        bus: {
          title: "En Bus",
          description: "Ligne SBB 101 @ Entrée de l'hôtel",
          details: "Arrêt de bus @ Entrée de l'hôtel (Point terminal)"
        },
        train: {
          title: "En Train",
          description: "Gare de Beatenberg - 7 min à pied",
          details: "Interlaken Ouest - 20 min",
          beatenberg: "Gare de Beatenberg - 7 min",
          interlaken: "Interlaken Ouest - 20 min"
        },
        air: {
          title: "En Avion",
          description: "Aéroports les plus proches",
          details: "Berne - 50 min • Zurich - 2h • Genève - 2,5h",
          bern: "Berne - 50 min",
          zurich: "Zurich - 2h",
          geneva: "Genève - 2,5h"
        }
      },
      amenitiesOverview: {
        title: "Plus à découvrir à Beatenberg",
        subtitle: "Au-delà des activités principales, découvrez d'autres équipements premium qui complètent votre expérience de journée parfaite.",
        wellness: {
          title: "Bien-être & Spa",
          clinique: {
            name: "Clinique Nutraswiss",
            description: "Spa de santé intégré avec traitements NutraSwiss exclusifs et nutraceutiques"
          },
          whirlpool: {
            name: "Whirlpool",
            description: "Expérience d'hydrothérapie relaxante avec vues alpines époustouflantes"
          }
        },
        sports: {
          title: "Sports & Fitness",
          fitness: {
            name: "Centre de Fitness",
            description: "Équipement Technogym de pointe dans un cadre alpin"
          },
          tennis: {
            name: "Courts de Tennis",
            description: "Terrains de sport appartenant à l'hôtel avec installations de tennis professionnelles"
          },
          basketball: {
            name: "Terrain de Basketball",
            description: "Terrain complet disponible pour les invités et les groupes"
          },
          football: {
            name: "Terrain de Football",
            description: "Terrain de football dédié sur le domaine de l'hôtel"
          },
          volleyball: {
            name: "Terrain de Volleyball",
            description: "Installations de volleyball en plein air pour les loisirs"
          }
        },
        dining: {
          title: "Restauration & Bars",
          restaurant: {
            name: "Restaurant Jungfrau",
            description: "Cuisine fine avec vues panoramiques sur les montagnes",
            capacity: "Capacité : 200"
          },
          cafe: {
            name: "Carbon Mountain Cafe",
            description: "Bistro alpin décontracté avec spécialités locales",
            capacity: "Capacité : 120"
          },
          lounge: {
            name: "Fireplace Lounge & Bar",
            description: "Ambiance bar intime avec atmosphère de cheminée chaleureuse",
            capacity: "Capacité : 25"
          }
        },
        views: {
          title: "Vues Scéniques",
          panoramic: {
            name: "Vues Panoramiques Alpines",
            description: "Vues dégagées sur les pics Eiger, Mönch, Jungfrau et le lac de Thoune"
          }
        }
      },
      faq: {
        title: "Questions Fréquemment Posées",
        subtitle: "Tout ce que vous devez savoir sur le Pass Visiteur de Jour",
        ctaText: "Vous avez encore des questions ? Contactez-nous",
        ctaButton: "Nous contacter",
        questions: {
          whatIncluded: {
            question: "Qu'est-ce qui est inclus dans le Pass Visiteur de Jour ?",
            answer: "Le Pass Visiteur de Jour comprend l'accès à toutes les commodités de la station, un massage de 15 minutes (améliorable en traitement spa complet), un repas 5 services au Restaurant Jungfrau, un article plus une boisson du Carbon Mountain Cafe et l'accès au forfait sauna. Tout cela est disponible pour 200 CHF par personne."
          },
          advanceBooking: {
            question: "Dois-je réserver à l'avance ?",
            answer: "Oui, nous recommandons de réserver à l'avance pour garantir la disponibilité, surtout pendant les saisons de pointe. Vous pouvez réserver jusqu'à 12 invités par réservation. Nous confirmerons votre réservation et vous enverrons tous les détails par e-mail."
          },
          upgradeMassage: {
            question: "Puis-je améliorer le massage de 15 minutes inclus ?",
            answer: "Absolument ! Chaque invité peut améliorer son massage de 15 minutes inclus en n'importe quel traitement spa complet de notre menu. Vous ne paierez que la différence entre le prix du traitement complet et la valeur du massage inclus. Les améliorations peuvent être sélectionnées pendant le processus de réservation."
          },
          guestLimit: {
            question: "Pour combien d'invités puis-je réserver ?",
            answer: "Vous pouvez réserver pour 1 à 12 invités par réservation de Pass Visiteur de Jour. Pour les groupes plus importants, veuillez contacter notre équipe de conciergerie qui peut organiser des forfaits de groupe spéciaux."
          },
          multipleActivities: {
            question: "Puis-je visiter plusieurs activités en une journée ?",
            answer: "Oui ! Le Pass Visiteur de Jour vous donne accès à toutes les commodités de la station tout au long de votre visite. Vous pouvez profiter du spa, du sauna, de la restauration, du cinéma, du billard et de toutes les autres installations disponibles à la station pendant votre visite d'une journée."
          },
          parking: {
            question: "Le stationnement est-il disponible ?",
            answer: "Oui, le stationnement est disponible à la station. Veuillez nous contacter pour les détails de stationnement spécifiques et la disponibilité, surtout pendant les périodes chargées."
          },
          mealSelection: {
            question: "Quand dois-je sélectionner mes repas ?",
            answer: "La sélection des repas se fait généralement le jour de votre visite. Le repas 5 services au Restaurant Jungfrau est inclus, et vous pouvez choisir dans le menu du jour. Pour l'article du Carbon Mountain Cafe, vous pouvez sélectionner dans leur menu lorsque vous visitez. Si vous avez des restrictions alimentaires ou des préférences, veuillez les mentionner dans votre demande de réservation."
          },
          whatToBring: {
            question: "Que dois-je apporter ?",
            answer: "Nous recommandons d'apporter des vêtements confortables, des maillots de bain pour les installations spa et sauna, et tous les articles personnels dont vous pourriez avoir besoin. Les serviettes et peignoirs sont fournis au spa. Vous voudrez peut-être aussi apporter un appareil photo pour capturer les vues alpines époustouflantes !"
          },
          familyFriendly: {
            question: "Les activités sont-elles adaptées aux familles ?",
            answer: "Oui ! Le Pass Visiteur de Jour est parfait pour les familles. Les enfants peuvent profiter du cinéma, du billard et d'autres commodités de la station. Veuillez noter que les traitements spa peuvent avoir des restrictions d'âge, et le repas 5 services peut être adapté pour les enfants. Contactez-nous pour des arrangements spécifiques aux familles."
          },
          operatingHours: {
            question: "Quels sont les horaires d'ouverture ?",
            answer: "Les horaires d'ouverture varient selon les installations. Le spa et les zones de bien-être sont généralement ouverts du petit matin jusqu'au soir. Le Restaurant Jungfrau sert le déjeuner et le dîner, et le Carbon Mountain Cafe est ouvert toute la journée. Les heures spécifiques seront confirmées avec votre confirmation de réservation."
          },
          cancellationPolicy: {
            question: "Quelle est la politique d'annulation ?",
            answer: "Les politiques d'annulation peuvent varier selon la période de l'année et le type de réservation. En général, nous demandons un préavis d'au moins 48 heures pour les annulations. Veuillez nous contacter directement pour discuter des options d'annulation, et nous travaillerons avec vous pour trouver la meilleure solution."
          }
        }
      },
      dayPass: {
        bookingTray: {
          title: "Pass Visiteur de Jour",
          dayPassDetails: "Pass Visiteur de Jour",
          perPerson: "/ personne",
          inclusionAllAmenities: "Accès à toutes les commodités de la station",
          inclusionMassage: "Massage de 15 minutes",
          inclusionMeal: "Repas 5 services au Restaurant Jungfrau (valeur CHF 55, boissons en supplément)",
          inclusionCafe: "Un article + thé/café/boisson non alcoolisée du Carbon Mountain Cafe",
          inclusionSauna: "Forfait sauna (accès inclus)",
          includedValue: "Valeur CHF 40",
          numberOfGuests: "Nombre d'invités",
          guestLimit: "Jusqu'à 12 invités par réservation",
          basePassPrice: "Pass de base",
          massageUpgrade: "Amélioration de massage",
          total: "Total",
          upgradeMassage: "Améliorer le massage",
          changeUpgrade: "Changer l'amélioration de massage",
          bookNow: "Réserver maintenant",
          backToOverview: "Retour à l'aperçu",
          upgradeYourMassage: "Améliorer les massages",
          upgradeDescription: "Choisissez un traitement spa complet pour améliorer votre massage de 15 minutes inclus.",
          upgradeDescriptionMultiple: "Sélectionnez des améliorations pour chaque invité. Chaque invité peut choisir un traitement de massage différent.",
          includedMassage: "Massage de 15 minutes (inclus)",
          selectGuest: "Sélectionner l'invité à améliorer",
          selectingForGuest: "Sélection pour",
          guest: "Invitée",
          upgradeOptions: "Options d'amélioration",
          upgradeFor: "Amélioration pour",
          upgradeSelected: "Amélioration sélectionnée",
          includedSelected: "Massage inclus sélectionné",
          confirmUpgrade: "Confirmer la sélection",
          bookingInformation: "Informations de réservation",
          bookingDescription: "Veuillez fournir vos coordonnées pour compléter votre réservation de Pass Visiteur de Jour.",
          fullName: "Nom complet",
          email: "E-mail",
          phone: "Téléphone",
          preferredDate: "Date préférée",
          preferredTime: "Heure préférée",
          selectDate: "Sélectionner une date",
          morning: "Matin",
          afternoon: "Après-midi",
          evening: "Soir",
          specialRequests: "Demandes spéciales (Optionnel)",
          bookingSummary: "Résumé de la réservation",
          requestedTimesNote: "*Les heures demandées sont sujettes à disponibilité. Nous vous contacterons pour confirmer votre réservation.",
          submitBooking: "Soumettre la demande de réservation",
          fillAllFields: "Veuillez remplir tous les champs requis",
          submitError: "Échec de l'envoi de la demande. Veuillez réessayer.",
          networkError: "Erreur réseau. Veuillez vérifier votre connexion et réessayer.",
          successMessage: "Demande de réservation envoyée avec succès !",
          thankYouReceived: "Merci ! Votre demande de réservation a été reçue.",
          confirmationEmail: "Nous vous enverrons un e-mail de confirmation sous peu.",
          upgradedTo: "Amélioré vers"
        },
        dayPassPricing: {
          title: "Tarifs Pass Journée",
          subtitle: "Découvrez tout ce que le Beatenberg Resort & Spa a à offrir avec notre Pass Journée complet. Tout ce dont vous avez besoin pour une journée parfaite dans les Alpes suisses.",
          priceLabel: "Pass Visiteur de Jour",
          price: "CHF 200",
          perPerson: "par personne",
          inclusionsTitle: "Ce qui est inclus",
          inclusions: {
            amenities: "Accès à toutes les commodités de la station (piscine, billard, installations sportives, cinéma, etc.)",
            massage: "Massage de 15 minutes (améliorable)",
            massageValue: "Valeur CHF 40",
            meal: "Repas 5 services au Restaurant Jungfrau",
            mealValue: "Valeur CHF 55 (boissons en supplément)",
            cafe: "Un article + thé/café/boisson non alcoolisée du Carbon Mountain Cafe",
            sauna: "Forfait sauna (accès inclus)"
          },
          valueBreakdown: {
            title: "Répartition de la valeur",
            description: "Découvrez la valeur totale que vous recevez avec votre Pass Journée :",
            massage: "Massage de 15 minutes",
            massageValue: "CHF 40",
            meal: "Repas Restaurant 5 services",
            mealValue: "CHF 55",
            cafe: "Article Carbon Cafe + Boisson",
            cafeValue: "CHF 20",
            sauna: "Accès Forfait Sauna",
            saunaValue: "CHF 35",
            amenities: "Accès Commodités Station",
            amenitiesValue: "CHF 50",
            totalValueLabel: "Valeur totale",
            totalValue: "CHF 200",
            passPriceLabel: "Prix Pass Journée",
            passPrice: "CHF 200",
            savings: "Valeur exceptionnelle - tout inclus sans frais supplémentaires"
          },
          cta: "Réserver Pass Journée"
        }
      },
      experienceVideo: {
        title: "Découvrez l'expérience Journée Invité",
        subtitle: "Découvrez ce qui vous attend avec notre Pass Journée - de la restauration de classe mondiale aux traitements spa revigorants et aux activités passionnantes."
      },
      ctaContact: {
        title: "Prêt à découvrir Beatenberg?",
        subtitle: "Réservez votre Pass Journée dès aujourd'hui et découvrez tout ce que notre resort a à offrir. Notre équipe est là pour rendre votre visite inoubliable.",
        ctaTitle: "Réservez votre visite",
        ctaDescription: "Réservez votre Pass Journée et commencez à planifier votre journée parfaite dans les Alpes suisses.",
        ctaButtons: {
          bookVisit: "Réserver votre visite",
          contactConcierge: "Contacter le concierge",
          callNow: "Appeler maintenant"
        },
        contactTitle: "Nous contacter",
        contactMethods: {
          phone: {
            label: "Téléphone"
          },
          email: {
            label: "E-mail"
          },
          whatsapp: {
            label: "WhatsApp",
            value: "+41 76 622 01 22"
          }
        },
        operatingHours: {
          title: "Heures d'ouverture",
          days: "Lundi - Dimanche",
          hours: "9h00 - 20h00"
        }
      }
    },
    book: bookPageTranslationsFr
  }
};

export const getTranslation = (language: Language, key: string): string => {
  const keys = key.split('.');
  let value: any = translations[language];

  for (const k of keys) {
    value = value?.[k];
  }

  // Preserve empty strings and non-string values (e.g. feature arrays).
  // Only fall back to the key when the path is missing.
  if (value === undefined || value === null) {
    return key;
  }

  return value;
};
